/**
 * src/shared/i18n/catalogue.ts
 * `#796` — **LE CATALOGUE PARTAGÉ FRONT/BACK — UNE SEULE VÉRITÉ.**
 *
 * ⚠⚠ **CE FICHIER N'IMPORTE RIEN**, et c'est délibéré : pur data, il compile
 *   sous le `tsconfig` du back **et** celui du front (importé via l'alias Vite
 *   `@i18n`). La langue est une propriété du **lecteur** — le même catalogue
 *   sert l'écran (front) et, aux paliers suivants, les exports/e-mails/récits
 *   (back). Doctrine Hkm : **clé + params partout**.
 *
 * ⚠⚠ **CLÉS TYPÉES** : `en` est typé `typeof fr` — **une clé oubliée dans `en`
 *   ne compile pas**. Pas de `[missing]` à l'écran : le compilateur l'attrape.
 *
 * ⚠ **DÉRIVÉ DU GLOSSAIRE** (`MD files/GLOSSAIRE_I18N.md`, source de décision).
 *   Sigles invariants (ND, WD, ASP, PDM, IHS, SKU) : identiques FR/EN. IHS glose
 *   = « In-House Share » (validé Hkm `#796`).
 */

/** ⚠ `fr` est la **source** ; `en` en dérive (mêmes clés, garanti par le type). */
export const fr = {
  // ── Navigation — modules (nom + sous-titre RÉELLEMENT rendu) ───────────────
  //   ⚠ RÈGLE : on traduit ce qui est à l'écran, on n'invente rien. Seuls les
  //   6 sous-titres qui existent portent une clé `.sous` (Geo-Data, Brand vs
  //   Brand, Cohabitation, Comparateur, Lab, Flash Info). Phénomènes / SFM /
  //   Data Accuracy / Marché n'ont PAS de sous-titre à l'écran — pas de clé.
  'nav.marche':                 'Marché',
  'nav.phenomenes':             'Phénomènes',
  'nav.sfmPerf':                'SFM Team Performance',
  'nav.geoData':                'Geo-Données',
  'nav.geoData.sous':           'Cartographie & métriques',
  'nav.brandVsBrand':           'Marque vs Marque',
  'nav.brandVsBrand.sous':      'Comprendre & cibler',
  'nav.cohabitation':           'Cohabitation',
  'nav.cohabitation.sous':      'Officiel vs PI/CI',
  'nav.comparateur':            'Comparateur SKU',
  'nav.comparateur.sous':       'Prix & spécifications',
  'nav.lab':                    'Labo Libre service',
  'nav.lab.sous':               'Rapports sur mesure',
  'nav.dataAccuracy':           'Exactitude des données',
  'nav.flashInfo':              'Flash Info',
  'nav.flashInfo.sous':         'Fil hebdo',

  // ── Navigation — back-office / secondaire ──────────────────────────────────
  'nav.tableauDeBord':          'Tableau de bord',
  'nav.referentiels':           'Référentiels',
  'nav.administration':         'Administration',
  'nav.historiqueImports':      'Historique imports',
  'nav.configuration':          'Configuration',
  'nav.segmentation':           'Segmentation',
  'nav.parametres':             'Paramètres',
  'nav.validation':             'Validation',
  'nav.monitoring':             'Monitoring',
  'nav.auditLogs':              'Journaux d’audit',
  'nav.marketClusters':         'Clusters de marché',
  'nav.rfidRawdata':            'RFID / Rawdata',
  'nav.centreNotifications':    'Centre de notifications',
  'nav.welcome':                'Accueil',
  'nav.importer':               'Importer',
  'nav.performance':            'Performance',
  'nav.dataQuality':            'Qualité des données',
  'nav.lancementsOfficiels':    'Lancements officiels',

  // ── Sections Marché (S1-S9) ────────────────────────────────────────────────
  'marche.s1':                  'Vue d’ensemble',
  'marche.s3':                  'Régions',
  'marche.s4':                  'Segments',
  'marche.s6':                  'Top Modèles',
  'marche.s8':                  'IHS',
  'marche.s9':                  'ASP',

  // ── Marché (MX RM Flow) — GROUPE A : en-tête + cartes + filtres (`#800`) ────
  //   ⚠ FR = T0 `#799` BYTE-POUR-BYTE (Δ=0). EN = validé Hkm 05/09. Invariants
  //   (Retail, Wholesale, SKU, Mother SKU, ND, WD, IHS, Stock Share, W-1, Avg 4W,
  //   SWLY, Transsion, ON/OFF, Reset, MX RM Flow) restent LITTÉRAUX (hors catalogue).
  'marche.entete.semaines':         'Semaines…',
  'marche.entete.semainesIndispo':  'Semaines indisponibles',
  'marche.entete.aucuneSemaine':    'Aucune semaine importée',
  'marche.entete.compHebdoSeule':   'Comparaison disponible uniquement en vue hebdomadaire (12 sem.)',
  'marche.entete.exportPpt':        'Exporter → PowerPoint',
  'marche.entete.rapportEnCours':   'Rapport en cours…',
  'marche.ppt.bilanSuccesTitre':    'Rapport généré',
  'marche.ppt.bilanSuccesCorps':    '{{n}} prises, fichier téléchargé.',
  'marche.ppt.bilanEchecTitre':     'Génération interrompue',
  'marche.ppt.bilanEchecCorps':     'Prise {{rang}}/{{total}} — {{surface}} — {{cause}}',

  'marche.carte.nonDisponible':     'Non disponible',
  'marche.carte.aucuneVenteUnivers':'Aucune vente sur l\'univers visité',
  'marche.carte.pcs':               'PCS',
  'marche.carte.moyAgg':            ' · moy. {{x}}/sem · {{n}} sem',
  'marche.carte.refIndispo':        'Référence indisponible pour ce mode de comparaison',
  'marche.carte.na':                'N/A',
  'marche.carte.visitesPos':        'Visites POS',
  'marche.carte.vsRouting':         '{{pct}} Vs Routing',
  'marche.carte.ventesUnites':      'Ventes (unités)',
  'marche.carte.caUsd':             'CA (USD)',
  'marche.carte.ci':                'CI',
  'marche.carte.ciInflow':          'CI Inflow',
  'commun.reessayer':         'Réessayer',
  'marche.carte.erreurSyntheseSamsung': 'Erreur de chargement de la synthèse Samsung.',
  'marche.carte.erreurSyntheseMarche':  'Erreur de chargement de la synthèse marché.',

  'commun.region':           'Région',
  'marche.filtre.national':         'National',
  'marche.filtre.wilaya':           'Wilaya',
  'marche.filtre.toutes':           'Toutes',
  'marche.filtre.segPrix':          'Seg prix',
  // ⚠⚠ `#839` — **PROMU DEPUIS `marche.filtre.*`.** *Cette chaîne servait DÉJÀ
//   huit modules sous un nom de Marché — Flash Info, Geo, Comparateur, BvB,
//   Cohabitation, Exactitude, Labo, et le chrome. Le `#819` dit qu'une chaîne
//   servie par plusieurs modules va dans `commun.*` ; elle y va enfin.*
  'commun.chargement':              'Chargement…',
  'marche.filtre.indisponible':     'Indisponible',
  'commun.tousSegments':     'Tous segments',
  'marche.filtre.segPdv':           'Seg PDV',
  'marche.filtre.marque':           'Marque',
  'marche.filtre.toutesMarques':    'Toutes marques',
  'marche.filtre.personnalise':     'Personnalisé',
  'marche.filtre.appliquer':        'Appliquer',
  'marche.filtre.derniereSem':      'Dernière sem.',
  'marche.filtre.derniereSemW':     'Dernière sem. ({{wk}})',
  'marche.filtre.prof4':            '4 sem.',
  'marche.filtre.prof8':            '8 sem.',
  'marche.filtre.prof3m':           '3 mois',
  'marche.filtre.prof6m':           '6 mois',
  'marche.filtre.prof12m':          '12 mois',
  'marche.filtre.ytd':              'Année en cours',
  'marche.filtre.profYtd':          'Année',

  // ── Marché GROUPE B — S1 Vue d'ensemble (`#801`, Δ=0 FR) ───────────────────
  'marche.s1.partsMarche':          'Parts de marché — par marque',
  'marche.s1.aucunResultat':        'Aucun résultat pour ce filtre',
  'marche.s1.partVolPct':           'Part Vol %',
  'marche.s1.partValPct':           'Part Val %',
  'marche.s1.unites':               'Unités',
  'marche.s1.varPdmVol':            'Variation de la part de marché (Volume) vs semaine précédente (points)',
  'marche.s1.varPdmVal':            'Variation de la part de marché (Valeur) vs semaine précédente (points)',
  'marche.s1.ouvrirMarque':         'Ouvrir le dossier de la marque',
  'marche.s1.ouvrirModele':         'Ouvrir le dossier du modèle',
  'marche.s1.erreurParts':          'Erreur de chargement des parts de marché.',
  'commun.volume':               'Volume',
  'marche.s1.classerUnites':        'Classer et mesurer en unités vendues',
  'marche.s1.classerCa':            'Classer et mesurer en chiffre d’affaires (USD)',
  'marche.s1.modeles':              'Modèles',
  'marche.s1.erreurTop5':           'Erreur de chargement du Top 5 SKUs.',
  'marche.s1.aucuneVentePerimetre': 'Aucune vente sur ce périmètre.',
  'marche.s1.modeleNonId':          'Modèle non identifiable',
  'marche.s1.moyHebdoExcl':         'Moyenne hebdomadaire de {{refs}} — {{ancre}} est exclue de sa propre référence.',
  'marche.s1.moyHebdo':             'Moyenne hebdomadaire de {{refs}}.',
  'marche.s1.ttW1':                 'Comparaison à la semaine précédente.',
  'marche.s1.tt4w':                 'Moyenne des 3 semaines PRÉCÉDENTES — la semaine affichée est exclue de sa propre référence (fenêtre totale : 4 semaines).',
  'marche.s1.ttSwly':               'Comparaison à la même semaine de l’année précédente.',

  // ── Marché GROUPE B — S2 PDM (`#801`) ──────────────────────────────────────
  'marche.s2.poidsRegion':          'Poids région',
  'marche.s2.poidsNational':        'Poids national',
  'marche.s2.footnote':             '{{n}} wilayas · semaine {{week}} · {{m}} marques + Others',
  'marche.s2.titreSlide19':         'Parts de marché par wilaya (slide 19)',
  'marche.s2.sousSemaine':          'semaine {{week}}',
  'marche.s2.erreurMatrice':        'Erreur de chargement de la matrice.',
  'marche.s2.pasMatrice':           'Pas de matrice brand × wilaya pour le périmètre courant.',
  'marche.s2.titreMvS':             'Marché vs Samsung',
  'marche.s2.recordLow12':          '▼ plus bas 12s',
  'marche.s2.recordLow26':          '▼ plus bas 26s',
  'marche.s2.recordLow52':          '▼ plus bas 52s',
  'marche.s2.recordHigh12':         '▲ plus haut 12s',
  'marche.s2.recordHigh26':         '▲ plus haut 26s',
  'marche.s2.recordHigh52':         '▲ plus haut 52s',
  'marche.s2.erreurTitre':          'Erreur de chargement ({{titre}}).',
  'marche.s2.pasSerieVol':          'Pas de série volume pour le périmètre courant.',
  'marche.s2.pasSerieVal':          'Pas de série valeur pour le périmètre courant.',
  'marche.s2.pdmVolQty':            'PDM Volume (Qty)',
  'marche.s2.pdmValAmt':            'PDM Valeur (Amt)',

  // ── Marché GROUPE B — S3 Régions (`#801`) ──────────────────────────────────
  'marche.s3.erreurPoids':          'Erreur de chargement du poids régional.',
  'marche.s3.aucuneDonnee':         'Aucune donnée',
  'marche.s3.aucunPoids':           'Aucun poids régional pour le périmètre courant.',
  'marche.s3.titreVol':             'Volume — poids % par semaine',
  'marche.s3.titreVal':             'Valeur — poids % par semaine',

  // ── Marché GROUPE B — S4 Segments (`#801`) ─────────────────────────────────
  'marche.s4.segSelectionne':       '● {{code}} sélectionné',
  'marche.s4.erreurSegments':       'Erreur de chargement des segments de prix.',
  'marche.s4.aucuneDonneeSeg':      'Aucune donnée de segment',
  'marche.s4.aucunSegment':         'Aucun segment de prix pour la période et le canal sélectionnés.',
  'marche.s4.volumeQty':            'Volume (QTY)',
  'marche.s4.valeurAmt':            'Valeur (AMT)',
  'marche.s4.detailSegPre':         'Détail segment',
  'marche.s4.detailSegPost':        '— Top modèles',
  'marche.s4.fermer':               'Fermer ✕',
  'marche.s4.erreurL2':             'Erreur de chargement L2.',
  'marche.s4.aucunSku':             'Aucun SKU pour ce segment.',
  'marche.s4.msParMarque':          'M/S par marque dans la tranche (vol.)',
  'marche.s4.aucuneDonneeTele':     'Aucune donnée télescopique',
  'marche.s4.aucuneEvolution':      'Aucune évolution disponible pour la période et le canal sélectionnés.',
  'marche.s4.evolutionTranche':     'Évolution par tranche de prix',
  'marche.s4.tranche':              'Tranche',

  // ── Marché GROUPE B — S5 Part de linéaire (`#801`) ─────────────────────────
  //   ⚠ 6 lignes ★ = Δ≠0 FR VOULU (K06 = Part de linéaire ; réf. interne retirée) :
  //   titreBrand · pasShelfBrand · legende · titreEvo · impossibleEvo · titreSku.
  'marche.s5.titreBrand':           'Part de linéaire par marque',
  'marche.s5.pasShelfBrand':        'Pas de part de linéaire pour ce périmètre.',
  'marche.s5.legende':              'Σ marques + Others = 100 %. Seuil de visibilité 3 %. ★ = marque cliente.',
  'marche.s5.titreEvo':             'Évolution part de linéaire par marque — axe télescopique',
  'marche.s5.impossibleEvo':        'Impossible de charger l\'évolution part de linéaire.',
  'marche.s5.titreSkuMother':       'Part de linéaire par modèle — top 20',
  'marche.s5.titreSkuSku':          'Part de linéaire par SKU — top 20',
  'marche.s5.selBadge':             '● {{label}} sélectionné — clic pour désélectionner',
  'marche.s5.impossibleData':       'Impossible de charger les données.',
  'marche.s5.pasHistorique':        'Pas d\'historique sur ce périmètre.',
  'marche.s5.impossibleTopSku':     'Impossible de charger le top SKU.',
  'marche.s5.pasSku':               'Pas de SKU pour ce périmètre.',
  'marche.s5.legSkuA':              'Taille = shelf marché (dénominateur invariant au drill). Couleur = marque. 2 parts par tuile : ',
  'marche.s5.legSkuB':              ' (vs total marché) et ',
  'marche.s5.legSkuC':              ' (vs sa marque). Stock = somme des observations à la semaine ancre. « — » si non renseigné.',

  // ── Marché GROUPE C — NAV / EXPORT / WHOLESALE (`#802`) ─────────────────────
  'marche.nav.s6':                  'S6 Top Modèles',
  'marche.nav.s7':                  'S7 Dispo',
  'marche.nav.s8':                  'S8 IHS',
  'marche.nav.s9':                  'S9 ASP',
  'marche.nav.index':               'Index',
  'marche.nav.sommaire':            'Sommaire des surfaces',
  'marche.export.exporterDonnees':  'Exporter les données',
  'marche.export.exporter':         'Exporter',
  'marche.export.enCours':          'Export…',
  'marche.export.echecDroits':      'Export refusé : ce compte n\'a pas le droit d\'exporter cette surface.',
  'marche.export.echec':            'L\'export a échoué — {{motif}}',
  'marche.export.format':           'Format d\'export',
  'marche.export.reduireSurface':   'Réduire {{surface}}',
  'marche.export.agrandirSurface':  'Agrandir {{surface}}',
  'marche.export.aucuneDonneePeriode': 'Aucune donnée sur la période.',
  'marche.export.reduire':          'Réduire',
  'marche.export.deplier':          'Déplier',
  'marche.export.replier':          'Replier',
  'marche.ws.clustersWholesale':    'Clusters Wholesale',
  'marche.ws.totalSamsung':         'Total : {{n}} unités Samsung',
  'marche.ws.tousClusters':         '← Tous les clusters',
  'marche.ws.pdmParCluster':        'PDM par cluster',
  'marche.ws.partSamsung':          'Part de marché Samsung par Market Cluster Wholesale',
  'marche.ws.aucuneDonnee':         'Aucune donnée wholesale sur la période.',
  'marche.ws.source':               'Source : {{source}}',
  'marche.ws.pdmSamsung':           'PDM Samsung',
  'marche.ws.clusterCodePdv':       '{{code}} · {{n}} PDV',
  'marche.ws.clusterId':            'Cluster #{{id}}',
  'marche.ws.pdmSamsungLabel':      'PDM Samsung : ',
  'marche.ws.unitesLabel':          'Unités : ',
  'marche.ws.caLabel':              'CA : ',
  'marche.ws.fermerDetail':         'Fermer le détail cluster',
  'marche.ws.aucunPdv':             'Aucun PDV dans ce cluster sur la période.',
  'marche.ws.pdmPct':               'PDM %',
  'marche.ws.comparatif':           'Comparatif PDM Volume par canal',
  'marche.ws.aucuneComparative':    'Aucune donnée comparative sur la période.',
  'marche.ws.sourcePeriode':        'Source : {{source}} · Période : {{period}}',

  // ── Marché GROUPE C — S6 Top Modèles (`#802`) ──────────────────────────────
  'marche.s6.marqCompact':          'Marq.',
  'marche.s6.subtitle':             'Marché : {{n}} unités · Σ MS top {{top}} = {{v}}%',
  'marche.s6.titre':                'Top {{top}} Modèles — sell-out',
  'marche.s6.erreurTop':            'Erreur de chargement du Top Modèles.',
  'marche.s6.aucunSkuSamsung':      'Aucun SKU Samsung dans le top sur le périmètre courant.',
  'marche.s6.aucunModele':          'Aucun modèle pour le périmètre courant.',

  // ── Marché GROUPE C — S7 Dispo (`#802`) ────────────────────────────────────
  'marche.s7.pdvMaille':            '{{n}} PDV (maille)',
  'marche.s7.pdvMarche':            '{{n}} PDV (marché)',
  'marche.s7.echantillonFaible':    '⚠ {{n}} PDV — échantillon faible',
  'marche.s7.titreNdWd':            'ND / WD — au grain modèle',
  'marche.s7.filtreBarre':          'Filtré par la barre globale',
  'marche.s7.snapTitre':            'Dispo (barres) · ND et WD (courbes) — top 15',
  'marche.s7.erreurDispo':          'Erreur de chargement de la disponibilité modèle.',
  'marche.s7.aucunModeleMaille':    'Aucun modèle pour la maille courante.',
  'marche.s7.dispoPct':             'Dispo %',
  'marche.s7.evoDispo':             'Évolution disponibilité',
  'marche.s7.evoNd':                'Évolution ND',
  'marche.s7.evoWd':                'Évolution WD',
  'marche.s7.titreSemaines':        '{{titre}} — {{n}} semaines',
  'marche.s7.mesure':               'Mesure',
  'marche.s7.wdPasAbsolu':          'WD n\'a pas de valeur absolue : son numérateur additionne des ventes, pas des points de vente.',
  'marche.s7.valeurAbsolue':        'Valeur absolue',
  'marche.s7.erreurEvo':            'Erreur de chargement de l\'évolution.',
  'marche.s7.aucuneEvoMaille':      'Aucune évolution pour la maille courante.',

  // ── Marché GROUPE C — S8 IHS (`#802`) ──────────────────────────────────────
  'marche.s8.titreIhsModele':       'IHS par modèle — maille combinable',
  'marche.s8.erreurIhsModele':      'Erreur de chargement IHS modèle.',
  'marche.s8.aucunIhsModeleMaille': 'Pas d\'IHS modèle pour la maille courante.',
  'marche.s8.tooltipIhsMarche':     'IHS marché : {{v}} %',
  'marche.s8.tooltipPartMarque':    'Part {{brand}} : {{v}} %',
  'marche.s8.erreurEvoIhs':         'Erreur de chargement de l\'évolution IHS.',
  'marche.s8.aucuneEvoIhsMaille':   'Aucune évolution IHS pour la maille.',
  'marche.s8.evoIhsSemaines':       'Évolution IHS — {{n}} semaines',
  'marche.s8.titreDonut':           'IHS par marque — part de répartition',
  'marche.s8.totalIhs':             'Total IHS : {{v}}',
  'marche.s8.erreurIhsMarque':      'Erreur de chargement IHS marque.',
  'marche.s8.aucunIhsPerimetre':    'Pas d\'IHS pour le périmètre courant.',
  'marche.s8.titreTele':            'Évolution IHS par marque — axe télescopique',
  'marche.s8.erreurEvoIhsImpossible': 'Impossible de charger l\'évolution IHS.',
  'marche.s8.aucunHistoIhs':        'Pas d\'historique IHS sur ce périmètre.',

  // ── Marché GROUPE C — S9 ASP (`#802`) ──────────────────────────────────────
  'marche.s9.exclureMarques':       'Exclure marques',
  'marche.s9.exclues':              'Exclues ({{n}})',
  'marche.s9.filtreMarques':        'Filtre marques',
  'commun.reinitialiser':        'Réinitialiser',
  'marche.s9.titreAspTele':         'Évolution ASP · axe télescopique',
  'marche.s9.titreAspTeleDepth':    'Évolution ASP · axe télescopique ({{y}} ans + {{m}} mois + {{w}} sem)',
  'marche.s9.erreurAsp':            'Erreur de chargement (ASP).',
  'marche.s9.aucuneSerieAsp':       'Pas de série ASP pour le périmètre courant.',
  'marche.s9.retourMarques':        'Retour à la vue marques',
  'marche.s9.grainAgregation':      'Grain d\'agrégation (toggle module Mother/SKU)',
  'marche.s9.erreurAspSku':         'Erreur de chargement (ASP SKU).',
  'marche.s9.aucuneSerieAspSku':    'Pas de série ASP SKU pour cette marque sur le périmètre courant.',
  'marche.s9.retour':               '← Retour',
  'marche.s9.grain':                'Grain {{g}}',
  'marche.s9.titreAspTopSku':       'Évolution ASP · {{n}} Top SKU',
  'marche.s9.titreAsp':             'Évolution ASP',
  'marche.s9.deltaUsd':             'Écart en dollars vs la semaine précédente.',
  'marche.s9.deltaPct':             'Écart en pourcentage vs la semaine précédente.',
  'marche.s9.aucunSkuPerimetre':    'Aucun SKU sur ce périmètre.',
  'marche.s9.ouvrirFicheSku':       '{{sku}} — ouvrir la fiche SKU 360',
  'marche.s9.rankingAsp':           'Ranking ASP — axe télescopique',
  'marche.s9.rankingAspSku':        'Ranking ASP SKU — axe 3M+8W',

  // ── Marché GROUPE C — Pop-up SKU 360 (`#802`) ──────────────────────────────
  // 6 lignes ★ = FR délibéré (ND pas DN ×2, WD pondéré, Part de linéaire,
  //   Approvisionnement, PI/CI vs Officiel).
  'marche.sku360.dossierModele':    'Dossier modèle',
  'marche.sku360.aucunDossier':     'Aucun dossier pour ce modèle',
  'marche.sku360.rienAfficher':     'Rien à afficher sur la période et le canal sélectionnés.',
  'marche.sku360.modeleIndispo':    'Modèle indisponible',
  'marche.sku360.impossibleCharger':'Impossible de charger le dossier de ce modèle.',
  'marche.sku360.dossierIndispo':   'Dossier indisponible pour ce modèle.',
  'marche.sku360.skuDeclinaison':   'SKU · déclinaison',
  'marche.sku360.modeleAgrege':     'Modèle agrégé · {{n}} SKU',
  'marche.sku360.ramStock':         'RAM/Stock.',
  'marche.sku360.ramValue':         '{{ram}} / {{rom}} Go',
  'marche.sku360.configVendue':     'Config la plus vendue',
  'marche.sku360.declinaisons':     '{{n}} déclinaisons',
  'marche.sku360.perfCommerciale':  'Performance commerciale',
  'marche.sku360.ventesSellout':    'Ventes sell-out',
  'marche.sku360.uneSem':           '1 sem.',
  'marche.sku360.marge':            'Marge ({{suf}})',
  'marche.sku360.margeIndispo':     'Marge indisponible : pas de relevé {{canal}} sur la fenêtre. Un écart ne s\'invente pas — vide, jamais zéro.',
  'marche.sku360.presenceTerrain':  'Présence terrain',
  'marche.sku360.stockOuShelf':     'stock ou shelf',
  'marche.sku360.subPdv':           '{{a}} / {{b}} PDV',
  'marche.sku360.rangMarche':       '{{ord}} rang marché',
  'marche.sku360.repartitionRegionale': 'Répartition régionale',
  'marche.sku360.ventesModelePct':  '% ventes modèle',
  'marche.sku360.trajectoireVentes':'Trajectoire ventes',
  'marche.sku360.erreurTrajectoire':'Erreur de chargement de la trajectoire.',
  'marche.sku360.aucuneDonnee8Sem': 'Aucune donnée sur les 8 dernières semaines.',
  'marche.sku360.historiquePartiel':'historique partiel ({{n}}/8 sem)',
  'marche.sku360.dn':               'ND',
  'marche.sku360.vsDn':             '{{delta}} vs ND',
  'marche.sku360.wdPondere':        'WD pondéré',
  'marche.sku360.sourcingContribution': 'Approvisionnement & Contribution',
  'marche.sku360.piciOfficiel':     'PI/CI vs Officiel',

  // ── Marché GROUPE C — Pop-up Brand 360 (`#802`) ────────────────────────────
  // 1 ligne ★ = FR délibéré (DN→ND, réutilise marche.sku360.dn).
  'marche.brand360.dossierMarque':  'Dossier marque',
  'marche.brand360.marqueIndispo':  'Marque indisponible',
  'marche.brand360.impossibleCharger': 'Impossible de charger le dossier de cette marque.',
  'marche.brand360.aucunDossier':   'Aucun dossier pour cette marque',
  'marche.brand360.section1':       '1 · Identité & Portfolio',
  'marche.brand360.section2':       '2 · Commercial',
  'marche.brand360.section3':       '3 · Distribution & Évolution',
  'marche.brand360.topModeles':     'Top modèles de la marque',
  'marche.brand360.aucunModeleVendu': 'Aucun modèle vendu sur la période.',
  'marche.brand360.ouvrirModele':   'Ouvrir le dossier modèle',
  'marche.brand360.repartitionSegments': 'Répartition segments prix',
  'marche.brand360.intraMarque':    'intra-marque',
  'marche.brand360.pctVentesMarque':'{{region}} · {{pct}} % des ventes marque',
  'marche.brand360.repartitionRegions': 'Répartition régions',
  'marche.brand360.presenceCartes': 'Présence (5 cartes)',
  'marche.brand360.distributionGeo':'Distribution géographique',
  'marche.brand360.aucuneDonneeRegionale': 'Aucune donnée régionale.',
  'marche.brand360.trajectoireTele':'Trajectoire télescopique · {{week}}',
  'marche.brand360.aucunPointTrajectoire': 'Aucun point de trajectoire disponible.',
  'marche.brand360.aucuneRepartitionSegment': 'Aucune répartition segment (marque sans ventes sur la période).',
  'marche.brand360.aucuneVenteRepartir': 'Aucune vente à répartir.',
  // ⚠ `#836` — la légende du graphe de trajectoire, en dur depuis le `#806` :
  //   le détecteur la jetait à cause de ses deux `=`.
  'marche.brand360.legendeTrajectoire': '{{n}} points · barres = ventes · ligne = PDM Vol %',

  // ── Rapport PPT Marché (`#802`) — rendu au back via traduire+langueCourante ─
  // ⚠ `#1055` B2 — le RAPPORT EST EN ANGLAIS (décision Hkm) : ces libellés sont
  //   identiques FR/EN. `ppt.titreRapport`/`sousTitre`/`pied` ne sont plus lus
  //   (page de garde 3 lignes + pied supprimé), gardés inertes le temps d'un lot.
  'ppt.titreRapport':               'Rapport Marché — MX RM Flow',
  'ppt.sousTitre':                  'SFMT Dashboard · Samsung Algérie',
  'ppt.pied':                       'SFMT · Orbicore · Confidentiel',
  'ppt.gardeLigne2':                'MX RM Presentation Week {{week}}',
  'ppt.gardeLigne3':                'SFMT Dashboard – SAMSUNG Algeria',
  'ppt.ctxRegion':                  'Région {{r}}',
  'ppt.ctxRegions':                 'Régions {{list}}',
  'ppt.ctxFicheSku':                'Fiche SKU 360 — rang {{n}}',
  'ppt.ctxSegment':                 'Segment {{label}} $',
  'ppt.ctxCohabEnsemble':           'Cohabitation {{marque}} — officiel vs PI/CI',
  'ppt.ctxCohabTemps':              'Cohabitation — les deux circuits dans le temps',

  // ── Phénomènes GROUPE A (`#804`) — Δ=0 FR sauf 2 ★ (Tous, sans « V4 ») ──────
  // A.1 en-tête + conteneur
  'phenomenes.titrePage':           'Phénomènes Marché',
  'phenomenes.rbac':                'Vous n\'avez pas les droits pour consulter les phénomènes marché.',
  'phenomenes.vueVivante':          'VUE VIVANTE',
  'phenomenes.vueVivanteSuffixe':   '· phénomènes de {{brand}} calculés à la volée (non persistés, pas de suivi Acquitter/Résoudre).',
  'phenomenes.chargement':          'Chargement des phénomènes',
  'phenomenes.aucunMarche':         'Aucun phénomène marché cette semaine',
  'phenomenes.aucunTerrain':        'Aucun phénomène terrain cette semaine',
  'phenomenes.calme':               'Analysé — aucun signal détecté sur cette semaine (marché calme).',
  'phenomenes.pasDispo':            'Analyse pas encore disponible',
  'phenomenes.echoue':              'L\'analyse de cette semaine a échoué{{msg}} — nouvelle tentative au prochain import.',
  'phenomenes.pasTourne':           'L\'analyse de cette semaine n\'a pas encore tourné — revenez après le prochain import.',
  'phenomenes.detectionPartielle':  'Détection partielle : certains indicateurs n\'ont pas pu être calculés{{detail}}. Relancez la détection pour compléter.',
  'phenomenes.lectureEchec':        'Lecture des phénomènes en échec — réessayez.',
  'phenomenes.perimee':             'Analyse périmée — un import est plus récent',
  'phenomenes.perimeeMsg':          'La détection de cette semaine date d\'AVANT le dernier import{{detail}}. Ce qui s\'affiche a été calculé sur un historique qui a changé depuis — ce n\'est pas « rien à signaler ». Relancez la détection (bouton « Relancer la détection ») pour cette semaine.',
  'phenomenes.importDu':            'import du {{date}}',
  'phenomenes.analyseDu':           ', analyse du {{date}})',
  'phenomenes.histInsuffisant':     'Historique insuffisant',
  'phenomenes.histInsuffisantMsg':  'Analysé, mais {{n}} pattern(s) restent dormants par manque de recul (ex. comparaison annuelle) — pas d\'anomalie, juste pas assez de semaines en base.',
  'phenomenes.aucunMajeur':         'Aucun phénomène majeur cette semaine',
  // A.2 filtres
  'phenomenes.canalOverviewAria':   'Canal Overview',
  'phenomenes.tousIndispo':         'Mode « Tous » indisponible pour une marque tierce (calcul par canal).',
  'phenomenes.marqueFocusAria':     'Marque focus Overview',
  'phenomenes.marcheTotal':         'Marché total (Samsung ★)',
  'phenomenes.semaineDeuxPoints':   'Semaine :',
  'phenomenes.ouvrirLectureStrat':  'Ouvrir la Lecture stratégique',
  'phenomenes.ouvrirPositionnement':'Ouvrir le Positionnement des marques',
  'phenomenes.positionnement':      'Positionnement des marques',
  'phenomenes.configurerSeuils':    'Configurer les seuils',
  'phenomenes.seuils':              'Seuils',
  'phenomenes.contexteEvenementiel':'Contexte événementiel',
  // A.3 badges sens / fiabilité (ampleur.* réutilisé pour MAJEUR/MODÉRÉ/MINEUR)
  'phenomenes.sensFavorable':       'Favorable',
  'phenomenes.sensDefavorable':     'Défavorable',
  'phenomenes.sensNeutre':          'Neutre',
  'phenomenes.confirme':            'Confirmé',
  'phenomenes.aConfirmer':          'À confirmer',
  // A.4 redétection
  'phenomenes.relanceTitle':        'Relance la détection sur toutes les semaines présentes — utile après un chargement de semaine antérieure',
  'phenomenes.relancePending':      'Relance…',
  'phenomenes.relancer':            'Relancer la détection',
  // A.5 vue d'ensemble
  'phenomenes.cockpit':             'Cockpit hebdomadaire · {{sem}} · {{n}} {{noun}}',
  'phenomenes.phenomeneSing':       'phénomène',
  'phenomenes.phenomenePlur':       'phénomènes',
  'phenomenes.aucunCetteSemaine':   'Aucun phénomène cette semaine',
  'phenomenes.cockpitVide':         'Le cockpit Overview affichera les agrégats dès qu\'une détection sera enregistrée.',
  'phenomenes.total':               'Total',
  'phenomenes.majeurs':             'Majeurs',
  'phenomenes.menaces':             'Menaces',
  'phenomenes.opportunites':        'Opportunités',
  'phenomenes.confirmes':           'Confirmés',
  'phenomenes.neutres':             'Neutres',
  'phenomenes.parSens':             'Par sens',
  'phenomenes.parPattern':          'Par pattern',
  'phenomenes.parFamilleKpi':       'Par famille KPI',
  'phenomenes.record':              'Record',
  'phenomenes.acceleration':        'Accélération',
  'phenomenes.ecartStatistique':    'Écart statistique',
  'phenomenes.glissementAnnuel':    'Glissement annuel',
  'phenomenes.familleDistribution': 'Distribution',
  'phenomenes.familleVisibilite':   'Visibilité étagère',
  'phenomenes.familleVolumesPrix':  'Volumes & prix',
  'phenomenes.familleStock':        'Stock & ruptures',
  'phenomenes.familleConcurrence':  'Concurrence',
  'phenomenes.familleActivites':    'Activités terrain',
  // A.6 patterns de détection (RECORDS/STREAKS/Z-SCORE/YOY invariants littéraux)
  'phenomenes.patternsDetection':   'Patterns de détection',
  'phenomenes.clicDeplier':         'Clic = déplier',
  'phenomenes.patAccelerations':    'ACCÉLÉRATIONS',
  'phenomenes.subPics12':           'Pics 12 sem.',
  'phenomenes.subSeries':           'Séries durables',
  'phenomenes.subVariations':       'Variations brutales',
  'phenomenes.subAnomalies':        'Anomalies stat.',
  'phenomenes.subEcarts':           'Écarts annuels',
  'phenomenes.indispoHistorique':   'Indisponible — historique < 52 semaines',
  'phenomenes.replier':             '↑ replier',
  'phenomenes.plusAutres':          '+ {{n}} autres',
  'phenomenes.moins52':             '< 52 sem',
  'phenomenes.aucunSignal':         'aucun signal',
  // A.7 cartes Faits marquants
  'phenomenes.investigationDispo':  'Investigation disponible',
  'phenomenes.investigation':       'Investigation',
  'phenomenes.detecteParPatterns':  'Détecté par {{n}} patterns → fiabilité renforcée : {{signatures}}',
  'phenomenes.vsMoyenneSem':        'vs moyenne {{n}} sem. :',
  'phenomenes.vsValue':             'vs {{value}}',
  'phenomenes.moyenneSemParen':     '(moyenne {{n}} sem.)',
  'phenomenes.surfaceSeverite':     'Phénomènes — {{niveau}}',
  'phenomenes.niveauMajeur':        'majeur',
  'phenomenes.niveauModere':        'modéré',
  'phenomenes.niveauMineur':        'mineur',
  'phenomenes.thRef':               'Réf.',
  'phenomenes.verdictDefavorable':  'DÉFAVORABLE',
  'phenomenes.verdictFavorable':    'FAVORABLE',
  'phenomenes.verdictAInvestiguer': 'À INVESTIGUER',
  'phenomenes.mvtTropFaible':       'Mouvement trop faible pour décomposer',
  'phenomenes.decompoNonSignif':    'Décomposition non significative (contributions instables)',
  'phenomenes.nonSignificatif':     'non significatif',
  'phenomenes.contributionParRegion':'Contribution par région :',

  // ── Phénomènes GROUPE B (`#805`) — Δ=0 FR sauf 3 ★ (Essayez, sans « V4.13 ») ─
  // B.5 lib/format.ts (traducteur trFr par défaut → clés SANS interpolation)
  'phenomenes.apparition':          'apparition',
  'phenomenes.verbStreak':          'streak',
  'phenomenes.verbAccel':           'accélération',
  'phenomenes.verbZscore':          'anomalie statistique',
  'phenomenes.verbRecord':          'record',
  'phenomenes.verbYoy':             'écart annuel',
  'phenomenes.sur':                 'sur',
  'phenomenes.semAbbr':             'sem.',
  // B.3 HistoirePanel + ConcurrentPanel
  'phenomenes.scoreConcentration':  '· score de concentration {{score}}',
  'phenomenes.pasAssezConcur':      'Pas assez de données concurrentes sur ce périmètre',
  'phenomenes.aucuneDecompoMarque': 'Aucune décomposition par marque n\'est disponible pour cette semaine. Essayez un autre périmètre (région / national).',
  'phenomenes.drillConcurrent':     'Drill concurrent',
  'phenomenes.perimetre':           'Périmètre',
  'phenomenes.voirNational':        'voir national',
  'phenomenes.pdmSamsungPeri':      'PDM Samsung — {{periscope}}',
  'phenomenes.volumeSamsungMarche': 'Volume Samsung {{a}} → {{b}} · Volume marché {{c}} → {{d}}',
  'phenomenes.mvtPdmParMarque':     'Mouvement de PDM par marque',
  'phenomenes.concentrationSomme':  'Concentration {{label}} — top1 = {{pct}} % de la somme',
  'phenomenes.concentre':           'Concentré',
  'phenomenes.diffus':              'Diffus',
  'phenomenes.contributeursMvt':    'Contributeurs au mouvement',
  'phenomenes.prennentParts':       'qui prennent des parts à Samsung',
  'phenomenes.perdentAuProfit':     'qui en perdent au profit de Samsung',
  'phenomenes.poussentMvt':         'qui poussent le mouvement',
  'phenomenes.compensateurs':       'Compensateurs',
  'phenomenes.perdentAussi':        'qui perdent aussi des parts',
  'phenomenes.gagnentEgalement':    'qui en gagnent également',
  'phenomenes.sensInverseMarque':   'qui vont en sens inverse',
  'phenomenes.voirMarquesPlus_one':     'voir {{count}} marque de plus',
  'phenomenes.voirMarquesPlus_other':     'voir {{count}} marques de plus',
  'phenomenes.replierSimple':       'replier',
  'phenomenes.countMarques_one':        '{{count}} marque',
  'phenomenes.countMarques_other':        '{{count}} marques',
  'phenomenes.nouvelEntrant':       'nouvel entrant',
  'phenomenes.sorti':               'sorti',
  'phenomenes.contribReelle':       'Contribution réelle : {{pct}} (effet-mix résiduel)',
  'phenomenes.contribution':        'Contribution : {{pct}}',
  'phenomenes.labelNatifPct':       '{{label}} : {{val}} ({{pct}})',
  'phenomenes.valeurClampee':       'Valeur réelle {{pct}} clampée visuellement à ±100 % (effet-mix)',
  // B.4 DrillLevelBlock + WaterfallChart + AxisToggle
  'phenomenes.niveauWilayaMin':     'wilaya',
  'phenomenes.niveauMarqueMin':     'marque',
  'phenomenes.decompositionPar':    'Décomposition par {{niveau}}',
  'phenomenes.contexteAmont':       '· contexte {{niveau}} amont',
  'phenomenes.calculDecompo':       'Calcul de la décomposition…',
  'phenomenes.calculEchec':         '⚠ Calcul en échec',
  'phenomenes.ventilationPasCalculee': 'La ventilation {{niveau}} n\'a pas pu être calculée. {{msg}}',
  'phenomenes.ventilationSansObjet':'Ventilation sans objet',
  'phenomenes.indicateurPasDecompo':'Cet indicateur ne se décompose pas au niveau {{niveau}}. {{msg}}',
  'phenomenes.sansDonnees':         'Sans données cette semaine',
  'phenomenes.aucuneDonneeVentiler':'Aucune donnée à ventiler au niveau {{niveau}} sur la période comparée.',
  'phenomenes.aucunContributeur':   'Aucun contributeur',
  'phenomenes.aucuneDecompoNiveau': 'Aucune décomposition disponible au niveau {{niveau}}.',
  'phenomenes.autres':              'Autres',
  'phenomenes.partNette':           'part nette {{v}} %',
  'phenomenes.noteCompensations':   '⚡ Certains contributeurs vont en sens inverse du Δ national (compensations — décompo Bennet 1er ordre, attendu).',
  'phenomenes.axeInvestigation':    'Axe d\'investigation',
  'phenomenes.geo':                 'Géo',
  'phenomenes.surfaceDrill':        'Phénomènes — drill {{niveau}}',

  // ── Phénomènes GROUPE C (`#806`) — Δ=0 FR sauf 1 ★ (mémo « Liste V1 » retiré) ─
  // C.1 tableau PDV (PdvList)
  'phenomenes.aucuneContribPdv':    'Aucune contribution PDV pour ce contexte.',
  'phenomenes.sourceVsMoyenne':     'vs moyenne {{n}} sem.',
  'phenomenes.effetAggrave':        'Aggrave',
  'phenomenes.effetAmeliore':       'Améliore',
  'phenomenes.grpAggravent':        'Aggravent 🔴',
  'phenomenes.grpAmeliorent':       'Améliorent 🟢',
  'phenomenes.grpMouvements':       'Mouvements',
  'phenomenes.deltaLabel':          'Δ :',
  'phenomenes.thCodePos':           'Code POS',
  'phenomenes.thEnseigne':          'Enseigne',
  'phenomenes.thEffet':             'Effet',
  'phenomenes.thEcart':             'Écart',
  'phenomenes.thPartMouvement':     'Part du mouvement',
  'phenomenes.thPartNette':         'Part nette (technique)',
  'phenomenes.ligneGroupePdv':      '{{titre}} · {{n}} PDV',
  'phenomenes.autresPdvPlafond':    'Autres PDV (au-delà du plafond)',
  // C.2 pop-up plein écran (PhenomenaDrillModal)
  'phenomenes.wilayaCap':           'Wilaya',
  'phenomenes.phenomeneBreadcrumb': 'Phénomène',
  'phenomenes.enNappe':             '· en nappe',
  'phenomenes.investigationPhenomene': 'Investigation du phénomène',
  'phenomenes.canalLabel':          'canal : {{canal}}',
  'phenomenes.drillNonDispo':       'Drill non disponible pour ce KPI',
  'phenomenes.kpiNonDecomposable':  'Le KPI {{kpi}} n\'est pas décomposable par cascade géographique.',

  // ── Chrome partagé (`#806`) — composants inter-modules ─────────────────────
  'commun.modalPleinEcran':         'Modal plein écran',
  'commun.erreurChargement':        'Erreur de chargement',
  'commun.erreurSurvenue':          'Une erreur est survenue.',

  // ── Toggle de langue ───────────────────────────────────────────────────────
  'langue.fr':                  'FR',
  'langue.en':                  'EN',
  'langue.titre':               'Langue de l’interface',

  // ── Mini-labels KPI des widgets (sidebar) — RÉELLEMENT rendus ──────────────
  'widget.sfm.tauxVisite':      'Taux visite',
  'widget.sfm.dispersion':      'Dispersion',
  'widget.sfm.conformite':      'Conformité',
  'widget.da.qualite':          'Qualité',
  'widget.da.preuves':          'Preuves',
  'widget.da.ecartQP':          'Écart Q–P',
  'widget.mxrm.pdmVol':         'PDM Vol.',
  'widget.mxrm.ihs':            'IHS',

  // ── Labels KPI (23 — K15 retiré, K20-K23 supprimés) ────────────────────────
  //   ⚠ `#949` — le titre disait « 24 » ; la section compte 23 clés : 28 − K15 −
  //   K20…K23 = 23. Le compte était faux dans le commentaire, pas dans les clés.
  //   ⚠ SSOT ÉCRAN utilisateur. Le moteur (kpiMeta.ts / catalog.ts) garde ses
  //   labels POV-Samsung — réconciliation par code à trancher (K06 diverge).
  'kpi.K01':                    'PDM Volume',
  'kpi.K02':                    'PDM Valeur',
  'kpi.K03':                    'Stock Share',
  'kpi.K04':                    'ND',
  'kpi.K05':                    'WD',
  'kpi.K06':                    'Part de linéaire',
  'kpi.K07':                    'IHS',
  'kpi.K08':                    'Volume ventes',
  'kpi.K09':                    'Chiffre d’affaires',
  'kpi.K10':                    'ASP',
  'kpi.K11':                    'Stock moyen',
  // ⚠⚠ `#858` P4-A — **LE CATALOGUE SE CORRIGE, IL N'ÉCRASE PAS UNE VÉRITÉ.**
  //    *Unifier sur `kpi.*` aurait rendu « Stock-out PDV » — précisément le
  //    libellé ambigu que le `#345` avait REFUSÉ après mesure : `K12` compte les
  //    PDV ayant AU MOINS UN SKU en rupture, pas les PDV en rupture totale ; et
  //    `K13` compte des occurrences PDV×SKU×semaine, pas des semaines. Le mot du
  //    module était le plus vrai : c'est LUI qui monte au catalogue.*
  'kpi.K12':                    'PDV avec ≥1 SKU en rupture',
  'kpi.K13':                    'Occurrences de rupture (PDV × SKU × sem.)',
  'kpi.K14':                    'Rotation stock',
  'kpi.K16':                    'Couverture PDV',
  'kpi.K17':                    'Fréquence de visite',
  'kpi.K18':                    'Anomalies détectées',
  'kpi.K19':                    'Taux d’exclusion DQ',
  'kpi.K24':                    'PDM 1er concurrent',
  'kpi.K25':                    'Écart vs 1er concurrent',
  'kpi.K26':                    'Marques actives',
  'kpi.K27':                    'ASP concurrents',
  'kpi.K28':                    'Perte de distribution',

  // ── `#949` — DESCRIPTIONS KPI (22 ; K13 et K15 hors écran) ────────────────
  //   Validées par Hkm « telles quelles », mot pour mot. Une chaîne = une clé.
  'kpi.K01.description':         'Part de marché Samsung en unités vendues. Hausse favorable.',
  'kpi.K02.description':         'Part de marché Samsung en valeur (CA). Hausse favorable.',
  'kpi.K03.description':         'Part de marché Samsung en unités en stock. Hausse favorable.',
  'kpi.K04.description':         'Part des PDV qui distribuent Samsung. Hausse favorable.',
  'kpi.K05.description':         'Distribution pondérée par les ventes des PDV. Hausse favorable.',
  'kpi.K06.description':         'Part de linéaire (facings) Samsung. Hausse favorable.',
  'kpi.K07.description':         'Part Samsung du stock et des facings en magasin. Hausse favorable.',
  'kpi.K08.description':         'Unités Samsung vendues. Hausse favorable.',
  'kpi.K09.description':         'Chiffre d\'affaires Samsung. Hausse favorable.',
  'kpi.K10.description':         'Prix de vente moyen Samsung. Ni bon ni mauvais en soi : à investiguer.',
  'kpi.K11.description':         'Stock moyen Samsung par PDV. Hausse favorable.',
  'kpi.K12.description':         'Nombre de PDV avec au moins un SKU Samsung en rupture. Hausse défavorable.',
  'kpi.K14.description':         'Ventes rapportées au stock moyen. Hausse favorable.',
  'kpi.K16.description':         'Part des PDV routés effectivement visités. Hausse favorable.',
  'kpi.K17.description':         'Nombre de visites par PDV routé. Hausse favorable.',
  'kpi.K18.description':         'Nombre d\'anomalies critiques relevées. Hausse défavorable.',
  'kpi.K19.description':         'Part des relevés exclus par le contrôle qualité. Hausse défavorable.',
  'kpi.K24.description':         'Part de marché du premier concurrent, nommé. Hausse défavorable.',
  'kpi.K25.description':         'Écart de part de marché entre Samsung et le premier concurrent, nommé. Hausse favorable.',
  'kpi.K26.description':         'Nombre de marques présentes sur le marché. Ni bon ni mauvais en soi : à investiguer.',
  'kpi.K27.description':         'Prix de vente moyen des concurrents. Ni bon ni mauvais en soi : à investiguer.',
  'kpi.K28.description':         'PDV qui cessent de tenir Samsung, confirmé sur deux semaines. Toujours défavorable.',
  // ⚠ K06 EST la part de linéaire (SSOT §17 famille VISIBILITE = K06 Shelf Share,
  //   K07 IHS ; catalog.ts mesure total_shelf_facings). Pas de clé shelfShare
  //   séparée. « Disponibilité » (Availability) est un TERME de section (S7), pas
  //   un KPI — aucun code ne calcule stock>0 OU shelf>0 (mesuré #797).
  // gloses longues (infobulle / export) — sigles seuls à l'écran, long en infobulle
  'kpi.K04.long':               'Distribution numérique',
  'kpi.K05.long':               'Distribution pondérée',
  'kpi.K07.long':               'In-House Share',
  'kpi.K10.long':               'Prix moyen de vente',

  // ── Statuts / canaux / circuits ────────────────────────────────────────────
  'canal.retail':               'Retail',
  'canal.wholesale':            'Wholesale',
  'circuit.piCi':               'PI/CI',
  'ampleur.majeur':             'Majeur',
  'ampleur.modere':             'Modéré',
  'ampleur.mineur':             'Mineur',
  'statut.termine':             'Terminé',
  'statut.publiee':             'Publiée',
  'statut.nonPubliee':          'Non publiée',

  // ── Marque vs Marque (Brand vs Brand) — GROUPE A : en-tête, filtres, Leadership,
  //   face-à-face, Radar, Segments, états, Mapping (`#810`). FR = T0 `#809`
  //   byte-pour-byte (Δ=0) SAUF ★1 (H1) / ★2 (vouvoiement). Invariants #800
  //   (SKU, Retail, Wholesale, chips $, ND, WD, IHS, ASP, Stock Share, %, —, ★, °,
  //   vs, Ratio, Shelf, Radar, Premium leader…) restent LITTÉRAUX, hors catalogue.
  'bvb.ancre':                  'Ancre : {{w}} · {{c}}',
  'bvb.tousCanaux':             'tous canaux',
  'bvb.periode':                'Période',
  'bvb.periode8sem':            '8 sem',
  'bvb.periode12sem':           '12 sem',
  'bvb.periodeTout':            'Tout',
  'bvb.affichage':              'Affichage',
  'bvb.groupes':                'Groupes',
  'bvb.marquesCompteur':        'Marques ({{sel}}/{{m}})',
  'bvb.retirer':                'Retirer {{code}}',
  'bvb.ajouter':                'ajouter',
  'bvb.aucuneMarqueDispo':      'Aucune marque disponible',
  'bvb.dejaSelectionne':        '{{x}} déjà sélectionné',
  'bvb.maxMarques':             'Maximum {{m}} marques',
  'bvb.leadershipTitre':        'Leadership par indicateur',
  'bvb.kpiWd':                  'WD · pondérée',
  'bvb.kpiShelf':               'Shelf · linéaire',
  'bvb.kpiIhs':                 'IHS · occupation',
  'bvb.nbSku':                  'Nb SKU',
  'bvb.aucuneDonnee':           '(aucune donnée)',
  'bvb.faceTitre':              'Comparaison face-à-face',
  'bvb.leaderEn':               'leader en ',
  'bvb.vertGras':               'vert gras',
  'bvb.metriqueSansPendant':    ' métrique sans pendant',
  'bvb.sansPendantTooltip':     'Métrique sans pendant — identique dans les deux modes',
  'bvb.basculeUnite':           'Bascule d\'unité : % ou Valeur',
  'bvb.grpPartsMarche':         'PARTS DE MARCHÉ',
  'bvb.grpDistribution':        'DISTRIBUTION & PRÉSENCE',
  'bvb.grpCommercial':          'COMMERCIAL',
  'bvb.grpPortefeuille':        'PORTEFEUILLE',
  'bvb.colPdmVol':              'PDM Vol',
  'bvb.colPdmVal':              'PDM Val',
  'bvb.sansPendant':            'Sans pendant',
  'bvb.marqueClient':           'Marque client',
  'bvb.totalSelection':         'Total (sélection)',
  'bvb.piedTable_one':              '{{count}} marque · {{c}} indicateurs · {{f}} familles',
  'bvb.piedTable_other':              '{{count}} marques · {{c}} indicateurs · {{f}} familles',
  'bvb.segmentsTitre':          'Segments de prix',
  'bvb.aucunSegmentVente':      'aucun segment de vente',
  'bvb.erreurComparaison':      'Erreur : impossible de charger la comparaison.',
  'bvb.selectionnezMarque':     'Sélectionnez au moins une marque.',
  'bvb.mappingTitre':           'Mapping stratégique',
  'bvb.mappingDesc':            'X : ASP ($) · Y : ventes (unités) · taille : CA · bornes : ASP moyen marché ({{asp}}) × médiane des ventes ({{med}})',
  'bvb.mappingVide':            'Aucune marque avec ventes et ASP > 0.',
  'bvb.quadPremiumConcentre':   'Premium concentré',
  'bvb.quadVolumeEntree':       'Volume · entrée',
  'bvb.quadEntreeFaible':       'Entrée · faible volume',
  'bvb.medianeVentes':          'Médiane ventes {{v}}',
  'bvb.aspMarche':              'ASP marché {{n}}$',
  'bvb.mappingAlerte':          '⚠ Toutes les marques sélectionnées tombent dans le même quadrant ({{q}}) — la segmentation ASP marché × médiane ventes ne discrimine pas ici. Élargir la sélection ou choisir une autre borne.',

  // ── GROUPE B1 : Lentilles, Heatmap, Bataille PDV (`#810`) ──
  'bvb.lentilleEchelle':        'Échelle partagée sur les 3 cartes · ±{{max}} pt (max de la sélection). Prix payé : sur la carte aux plus faibles écarts, les barres paraissent courtes — c\'est ce qui rend la comparaison honnête d\'une carte à l\'autre.',
  'bvb.lentilleTitreA':         'Occupation linéaire vs part VALEUR',
  'bvb.lentilleTitreB':         'Occupation linéaire vs part VOLUME',
  'bvb.lentilleTitreC':         'Qualité du réseau',
  'bvb.lentillePrefixe':        'Shelf Share − PDM ',
  'bvb.lentilleSousA2':         ', en points. Au-dessus de zéro : la marque SUR-occupe le linéaire par rapport à sa part du CA. En dessous : elle SOUS-occupe.',
  'bvb.lentilleSousB2':         ', en points. Au-dessus de zéro : plus de linéaire que sa part des UNITÉS vendues. L\'écart avec la lecture VALEUR trahit le positionnement prix.',
  'bvb.lentilleSousC':          'Écart au neutre WD ÷ ND, en points. Au-dessus de zéro : présente dans les points de vente qui PÈSENT. En dessous : surtout dans les petits. Ratio brut affiché en valeur d\'appui.',
  'bvb.heatmapTitre':           'Heatmap PDM × segment de prix',
  'bvb.heatmapSous':            'Part de marché de chaque marque DANS chaque tranche (ventes marque ÷ ventes marché du segment × 100). La longueur remplie de chaque cellule suit la PDM (jauge horizontale, 0-100 %).',
  'bvb.heatmapBandeau':         'Δ par cellule non servi — pastille de variation omise volontairement.',
  'bvb.heatmapErreur':          '⚠ Totaux marché par segment non disponibles — heatmap non calculable.',
  'bvb.pdvBatailleTitre':       'Bataille PDV — conquête · contesté · défendu',
  'bvb.pdvBatailleVide':        'Sélectionner au moins deux marques pour comparer leurs empreintes PDV.',
  'bvb.pdvBatailleSous':        'Empreinte comparée sur le parc actif de la fenêtre',
  'bvb.uPdv':                   '{{u}} PDV',
  'bvb.limiteParCarte':         ' · limité à {{n}} PDV par carte.',
  'bvb.reference':              'Référence :',
  'bvb.concurrent':             'Concurrent :',
  'bvb.conquete':               'Conquête',
  'bvb.conqueteSous1':          ' y est, ',
  'bvb.conqueteSous2':          ' non. À prendre.',
  'bvb.volumeConcurrent':       'Volume concurrent',
  'bvb.conteste':               'Contesté',
  'bvb.contesteSous':           'Les deux marques sont présentes. Arbitrer le linéaire.',
  'bvb.defendu':                'Défendu',
  'bvb.defenduSous':            ' seule y est. À protéger.',
  'bvb.votreVolume':            'Votre volume',
  'bvb.bilanPrefixe':           'Bilan de la fenêtre :',
  'bvb.bilanConquete':          ' conquête · ',
  'bvb.bilanConteste':          ' contesté · ',
  'bvb.bilanDefendu':           ' défendu · ',
  'bvb.bilanAucun':             ' aucun des deux = ',
  'bvb.bilanBouclage':          ' (bouclage strict).',
  'bvb.pdvCouverturePre':       'PDV · ',
  'bvb.pdvCouverturePost':      ' de la couverture',
  'bvb.pdvSurCouverture':       '{{c}} sur {{u}} PDV de la couverture',
  'bvb.volumesTotaux':          'Volumes totaux',
  'bvb.volumeTotal':            'Volume total',
  'bvb.moyParPdv':              'Moy. / PDV',
  'bvb.aucunPdvStatut':         'Aucun PDV dans ce statut',
  'bvb.wilayaPrincipale':       'Wilaya principale (nb PDV)',
  // ── GROUPE B2 : Dépendance SKU, dossiers 360 (chrome), Bataille géographique, Momentum (`#810`) ──
  'bvb.depTitre':                'Dépendance SKU — concentration & angle d\'attaque',
  'bvb.depVide':                 'Sélectionner au moins deux marques pour comparer leurs portefeuilles.',
  'bvb.depSous':                 'Combien de références portent la marque, et sur quel modèle attaquer.',
  'bvb.grainSousPrefixe':        'Grain : ',
  'bvb.grainMotModele':          'modèle',
  'bvb.grainLabel':              'Grain :',
  'bvb.angleAttaque':            'Angle d\'attaque',
  'bvb.prixMedian':              'prix médian',
  'bvb.faceA':                   'face à',
  'bvb.dossiersTitre':           'Dossiers SKU 360',
  'bvb.dossiersComplement':      ' — contenu intégral du tiroir, pour les deux modèles du duel',
  'bvb.aucunRival':              'Aucun rival apparié — pas de dossier à afficher.',
  'bvb.surCeTerrain':            ' sur ce terrain',
  'bvb.modelesTolerance':        ' — {{n}} {{mot}} dans la tolérance',
  'bvb.aucunModeleTolerance':    'Aucun modèle de {{ref}} n\'entre dans ±{{n}} % du prix du n°1 concurrent. Pas d\'appariement possible sur cette lentille prix.',
  'bvb.piedModeles':             'Δ prix = écart au prix médian du n°1 concurrent. Barre = volume relatif au mieux placé.',
  'bvb.pointsDeVente':           'Points de vente',
  'bvb.pdvQuiVendent':           ' — qui vendent le plus {{cible}}',
  'bvb.leNumeroUn':              'le n°1',
  'bvb.affichesSur':             '{{n}} affichés sur {{m}}',
  'bvb.plafond':                 ' · plafond {{t}}',
  'bvb.aucunPdvVend':            'Aucun PDV ne vend ce modèle sur la fenêtre.',
  'bvb.cibleMot':                'cible',
  'bvb.colVolPdv':               'Vol. PDV',
  'bvb.colCaPdv':                'CA PDV',
  'bvb.notreCote':               'Notre côté',
  'commun.concurrent':          'Concurrent',
  'bvb.portesPar':               'portés par',
  'bvb.no1Grain':                ' · n°1 {{kind}}',
  'bvb.verbePorte':              'porte',
  'bvb.verbePortent':            'portent',
  'bvb.moitieVentes':            ' {{kindP}} {{verbe}} la moitié des ventes',
  'bvb.auTotal':                 ' · {{total}} au total · {{vol}} u',
  'bvb.geoTitre':                'Bataille géographique',
  'bvb.neutre':                  'neutre',
  'bvb.geoTooltipPdm':           ' · PDM {{v}}',
  'bvb.pdmParRegion':            'PDM par région',
  'bvb.wilayasDisputees':        'Wilayas les plus disputées',
  'bvb.wilayasDisputeesSous':    '{{marque}} n\'est pas leader, ou l\'écart avec le n°2 est inférieur à 10 pts.',
  'bvb.aucuneWilayaDisputee':    'Aucune wilaya disputée (la marque cliente mène partout avec > 10 pts d\'écart).',
  'bvb.momentumTitre':           'Momentum · PDM {{m}}',
  'bvb.retirerFiltreWilaya':     'Retirer le filtre wilaya',
  'bvb.momentumSousScope':       'Évolution PDM dans la wilaya {{scope}} · 3 mois + 8 semaines',
  'bvb.momentumSousStd':         '3 mois + 8 semaines · axe télescopique (écart assumé vs standard 2Y+3M+6W)',
  'bvb.metriqueMomentum':        'Métrique momentum',
  'bvb.uMois':                   'mois',
  'bvb.uSem':                    'sem',

  // ── Comparateur SKU (`#813`). FR = T0 `#812` byte-exact SAUF ★ (glossaire
  //   appliqué à l'anglais qui traînait dans le FR ; ND partout, plus de DN).
  //   Invariants #800 (SKU, IHS, ND, WD, ASP, RAM, ROM, YTD, USD, DZD, $, —, ·) littéraux.
  'comparateur.periodeAnalyse':        'Période d\'analyse',
  'comparateur.predefinie':            'Prédéfinie',
  'comparateur.personnalisee':         'Personnalisée',
  'comparateur.periodePredefinie':     'Période prédéfinie',
  'comparateur.dateDebut':             'Date de début',
  'comparateur.dateFin':               'Date de fin',
  'comparateur.ytd':                   'Depuis début d\'année (YTD)',
  'comparateur.vider':                 '↺ vider',
  'comparateur.slotsComparaison':      'Slots de comparaison',
  'comparateur.recherchePrix':         'Recherche par prix',
  'comparateur.effacerResultats':      'Effacer les résultats et réactiver les slots',
  'comparateur.effacer':               '× effacer',
  'comparateur.erreurRecherche':       'Erreur lors de la recherche.',
  'comparateur.selectionnezJusqua':    'Sélectionnez jusqu\'à {{n}} SKUs',
  'comparateur.choisissez':            'Choisissez une marque et un SKU dans chaque slot à gauche',
  'comparateur.comparaison':           'Comparaison',
  'comparateur.specifications':        'Spécifications',
  'comparateur.metriques':             'Métriques',
  'commun.ramGo':                 'RAM (Go)',
  'commun.romGo':                 'ROM (Go)',
  'comparateur.ecranPo':               'Écran (po)',
  'comparateur.cameraMp':              'Caméra (MP)',
  'comparateur.radarSpecs':            'Radar — Spécifications',
  'comparateur.radarMetriques':        'Radar — Métriques business',
  'comparateur.specsTechniques':       'Spécifications techniques',
  'comparateur.metriquesBusiness':     'Métriques business',
  'comparateur.marqueSlot':            'Marque slot {{n}}',
  'comparateur.effacerSlot':           'Effacer slot {{n}}',
  'comparateur.tiretMarque':           '— Marque —',
  'commun.aucuneMarque':          'Aucune marque',
  'comparateur.aucunSku':              'Aucun SKU',
  'comparateur.dejaSelectionne':       '(déjà sélectionné)',
  'comparateur.rechercherSku':         'Rechercher un SKU…',
  'comparateur.resultatsSkuSlot':      'Résultats SKU slot {{n}}',
  'comparateur.retirerDuSlot':         'Retirer {{code}} du slot {{n}}',
  'comparateur.aucunSkuTrouve':        'Aucun SKU trouvé',
  'comparateur.prix':                  'Prix',
  'comparateur.vSegment':              'V.Segment',
  'comparateur.partSeg':               'Part Seg',
  'comparateur.vitrine':               'Vitrine',
  'comparateur.selectRadar':           'Sélectionnez ≥ 2 SKUs pour comparaison radar',
  'comparateur.legendeSerie':          '{{marque}} : {{code}}',
  'commun.ecran':                 'Écran',
  'comparateur.maxSkuSel':             'Max 3 SKU sélectionnés',
  'comparateur.aucunePhoto':           'Aucune photo',
  'comparateur.categorie':             'Catégorie',
  'commun.statut':                'Statut',
  'commun.actif':                 'Actif',
  'commun.inactif':               'Inactif',
  'comparateur.taillePo':              'Taille (po)',
  'comparateur.selectSpecs':           'Sélectionnez jusqu\'à 3 SKUs pour comparer les spécifications',
  'comparateur.utilisezSlots':         'Utilisez les slots ci-dessus pour choisir les SKUs à comparer.',
  'comparateur.segmentPrix':           'Segment prix',
  'comparateur.ventesTotal':           'Ventes total',
  'comparateur.partSegPoint':          'Part seg.',
  'comparateur.stockTotal':            'Stock total',
  'comparateur.vitrineTotal':          'Vitrine total',
  'comparateur.selectMetriques':       'Sélectionnez au moins 1 SKU pour voir les métriques.',
  'comparateur.reseau':                'Réseau',
  'comparateur.prixUsd':               'Prix USD',
  'comparateur.prixDzd':               'Prix DZD',
  'comparateur.rechercheConcurrentielle': 'Recherche concurrentielle par prix',
  'comparateur.trouvezSkus':           'Trouvez les SKUs concurrents dans une fourchette de prix donnée',
  'comparateur.erreurReessayer':       'Erreur lors de la recherche. Veuillez réessayer.',
  'comparateur.chercherMarque':        'Chercher une marque…',
  'comparateur.chercherModele':        'Chercher un modèle…',
  'comparateur.manuel':                'Manuel',
  'comparateur.devise':                'Devise',
  'comparateur.fourchette':            'Fourchette',
  'comparateur.prixMoyen':             'Prix moyen : {{p}} USD',
  'comparateur.prixCurrency':          'Prix ({{currency}})',
  'comparateur.aucunResultat':         'Aucun résultat trouvé',
  'comparateur.skuTrouve_one':             '→ {{count}} SKU trouvé',
  'comparateur.skuTrouve_other':             '→ {{count}} SKU trouvés',
  'comparateur.derniereSemaine':       'Dernière semaine',
  'comparateur.dern4sem':              '4 dern. sem.',
  'comparateur.dern8sem':              '8 dern. sem.',
  'comparateur.mois':                  'Mois',
  'comparateur.aucunConcurrent':       'Aucun SKU concurrent trouvé dans cette fourchette',
  'comparateur.essayezElargir':        '{{range}} · Essayez d\'élargir la fourchette.',
  'comparateur.tousMasques':           'Tous les SKUs masqués',
  'comparateur.reafficherTous':        'Réafficher tous ({{n}})',
  'comparateur.lancerComparaison':     'Lancer la comparaison ({{n}}/{{max}})',
  'comparateur.concurrentsTrouves':    'Concurrents trouvés',
  'comparateur.remplirSlots':          'Remplir les slots avec les SKUs cochés',
  'comparateur.selectAuMoins':         'Sélectionnez au moins {{n}} SKU',
  'comparateur.performanceMarche':     'Performance marché',
  'comparateur.stockMoy':              'Stock moy.',
  'comparateur.vitrineMoy':            'Vitrine moy.',
  'comparateur.exclure':               'Exclure {{marque}}',

  // ── `#817` · COHABITATION (Officiel vs PI/CI) + commun.* partages ───────────
  'commun.fermer':                    'Fermer',
  'commun.pdvMasquerListe':           'Masquer la liste',
  'commun.pdvListe':                  'Liste ({{n}} PDV)',
  'commun.pdvListeTronquee':          'Liste ({{n}} PDV sur {{total}})',
  'commun.pdvTronqueeDebut':          '⚠ Liste tronquée — {{n}} lignes affichées sur',
  'commun.pdvTronqueeFin':            'points de vente.',
  'commun.pdvNom':                    'Nom',
  'commun.pdvAucun':                  'Aucun point de vente',
  'commun.mois.1':                    'janv.',
  'commun.mois.2':                    'févr.',
  'commun.mois.3':                    'mars',
  'commun.mois.4':                    'avr.',
  'commun.mois.5':                    'mai',
  'commun.mois.6':                    'juin',
  'commun.mois.7':                    'juil.',
  'commun.mois.8':                    'août',
  'commun.mois.9':                    'sept.',
  'commun.mois.10':                   'oct.',
  'commun.mois.11':                   'nov.',
  'commun.mois.12':                   'déc.',
  'cohabitation.h1':                  'Cohabitation officiel / PI/CI',
  'cohabitation.enCours':             'cohabitations en cours',
  'cohabitation.erreurCharger':       'Impossible de charger les cohabitations.',
  'cohabitation.indispo':             'Les cohabitations ne sont pas disponibles pour l\'instant.',
  'cohabitation.indispoSous':         'Ce n\'est ni un chargement, ni une panne : aucune donnée n\'a été reçue.',
  'cohabitation.aucuneMarque':        'Aucune marque n’a de référence relevée dans les deux circuits sur ce canal.',
  'cohabitation.marqueIndispo':       'Les cohabitations {{marque}} ne sont pas disponibles pour l\'instant.',
  'cohabitation.marquesConcernees':   'Marques concernées — {{n}}',
  'cohabitation.skuEnCohabitation':   '{{marque}} — {{n}} SKU en cohabitation',
  'cohabitation.active':              'active',
  'cohabitation.actives':             'actives',
  'cohabitation.archivee':            'archivée',
  'cohabitation.archivees':           'archivées',
  'cohabitation.masquerArchive':      'Masquer l’archive',
  'cohabitation.archiveN':            'Archive — {{n}}',
  'cohabitation.critereDebut':        'Active : les deux circuits ont',
  'cohabitation.critereVendu':        'vendu',
  'cohabitation.critereVenduTitre':   'Vendu, pas présent : du stock qui ne bouge plus n’est pas une cohabitation.',
  // ⚠ `#943` — la phrase disait l'ANCIENNE règle (« ont vendu ces n dernières
  //   semaines ») : elle dit maintenant la même semaine, à l'écran comme au moteur.
  'cohabitation.critereFin':          'la même semaine, au moins une fois ces {{n}} dernières semaines',
  // ⚠ `#943` — une carte montre la semaine courante ; quand la coexistence n'y
  //   est pas prouvée, elle dit laquelle la prouve. Pas de zéros sans raison.
  'cohabitation.prouveeEn':           'prouvée en {{semaine}}',
  'cohabitation.videTitre':           'Aucune cohabitation {{marque}} en cours.',
  'cohabitation.videArchivees':       'cohabitations terminées sont conservées à l’archive, avec leur issue.',
  'cohabitation.videArchivee':        'cohabitation terminée est conservée à l’archive, avec son issue.',
  'cohabitation.voirArchive':         'Voir l’archive',
  'cohabitation.videSansArchive':     'Aucune référence {{marque}} n’a été relevée à la fois en circuit officiel et en PI/CI sur la fenêtre.',
  'cohabitation.comparerDansLeTemps': 'Comparer {{sku}} dans le temps',
  'cohabitation.cohabitation':        'cohabitation',
  'cohabitation.dejaEtablie':         'déjà établie au début de nos données',
  'cohabitation.depuis':              'cohabitation depuis',
  'cohabitation.premierPici':         'le PI/CI',
  'cohabitation.premierOfficiel':     'l\'officiel',
  'cohabitation.etaitLaEnPremier':    'était là en premier',
  'cohabitation.memeSemaine':         'les deux circuits apparus la même semaine',
  'cohabitation.officielMinuscule':   'officiel',
  'cohabitation.nc':                  'n/c',
  'cohabitation.ncTitre':             'pas de semaine précédente comparable',
  'cohabitation.volume':              'Volume',
  'cohabitation.lineaire':            'Linéaire',
  'cohabitation.tiroirAria':          'Comparaison des deux circuits',
  'cohabitation.tiroirSousTitre':     'Les deux circuits dans le temps ·',
  'cohabitation.erreurSeries':        'Impossible de charger les séries.',
  'cohabitation.axeTelescopique':     'Axe télescopique — 2 ans · 3 mois · 6 semaines.',
  'cohabitation.donneesDepuis':       '⚠ Données disponibles depuis',
  'cohabitation.donneesDepuisFin':    ': les périodes antérieures sont absentes, pas nulles.',
  'cohabitation.fluxSemaine':         'Les mesures de flux sont ramenées à la SEMAINE à tous les grains, sinon un point « mois » vaudrait quatre points « semaine ».',
  'cohabitation.gCa':                 'Chiffre d\'affaires',
  'cohabitation.gDispo':              'Disponibilité',
  'cohabitation.uSem':                'u / sem',
  'cohabitation.usdSem':              'USD / sem',
  'cohabitation.grainAnnee':          'année',
  'cohabitation.grainMois':           'mois',
  'cohabitation.partagePdv':          'Partage des points de vente',
  'cohabitation.universSku':          'univers du SKU',
  'cohabitation.neBouclentPas':       '⚠ NE BOUCLENT PAS.',
  'cohabitation.piciSeul':            'PI/CI seul',
  'cohabitation.lesDeux':             'les deux',
  'cohabitation.officielSeul':        'officiel seul',
  'cohabitation.ventesPici':          'Ventes PI/CI',
  'cohabitation.ventesSku':           'Ventes du SKU',
  'cohabitation.ventesOfficiel':      'Ventes officiel',
  'commun.aucunPdv':            'Aucun PDV',
  'cohabitation.listePdvAria':        '{{part}} — liste des points de vente',
  'cohabitation.toutes':              'Toutes ({{n}})',
  'cohabitation.noteA1':              '⚠ La',
  'cohabitation.noteA2':              'fin',
  'cohabitation.noteA3':              'n\'est pas un événement relevé, c\'est une',
  'cohabitation.noteA4':              'déduction',
  'cohabitation.noteB':               'la dernière semaine où les deux circuits ont vendu, prononcée par quatre semaines sans vente de l\'un d\'eux.',
  'cohabitation.noteC1':              '« — » signale qu\'ils n\'ont',
  'cohabitation.noteC2':              'jamais vendu la même semaine',
  'cohabitation.noteD':               'Le classement se recalcule à chaque lecture : si un circuit éteint se remet à vendre, la cohabitation ressort d\'ici et redevient une carte.',
  'cohabitation.colDebut':            'Début',
  'cohabitation.colFin':              'Fin',
  'cohabitation.colDuree':            'Durée',
  'cohabitation.colPremierArrive':    'Premier arrivé',
  'cohabitation.colRestait':          'Restait à la fin',
  'cohabitation.colVolDebut':         'Volumes au début',
  'cohabitation.colVolFin':           'Volumes à la fin',
  'cohabitation.ouvrirDetail':        'Ouvrir le détail comparatif',
  'cohabitation.semNb':               '{{n}} sem',
  'cohabitation.personne':            'personne',
  'cohabitation.surfaceFiche':        'Cohabitation — fiche SKU',
  'cohabitation.surfaceArchive':      'Cohabitation — archive',
  'cohabitation.surfaceEnsemble':     'Cohabitation — vue d\'ensemble',

  // ── `#819` · LA FONTE — un doublon (même FR **et** même EN) = UNE clé `commun.*` ────
  // ⚠ `#819` — **DEUX CHAÎNES VOISINES, DEUX CLÉS, ET C'EST VOULU.**
  //   *`commun.erreurChargement` (sans point final, EN `Loading error`) est une
  //   ÉTIQUETTE ; celle-ci, avec son point, est une PHRASE d'état — EN `Failed to
  //   load.`. Un caractère les sépare : ce n'est PAS un doublon (le doublon, c'est
  //   même FR **et** même EN), et les fondre changerait deux écrans validés.*
  'commun.echecChargement':              'Erreur de chargement.',
  'commun.canal':                        'Canal',
  'commun.district':                     'District',
  'commun.last4sem':                     '4 dernières semaines',
  'commun.last8sem':                     '8 dernières semaines',
  'commun.last3mois':                    '3 derniers mois',
  'commun.last6mois':                    '6 derniers mois',
  'commun.last12mois':                   '12 derniers mois',

  'commun.marques':                      'Marques',
  'commun.pdv':                          'PDV',

  // ── `#819` · GEO-DONNÉES (la carte des wilayas) ─────────────────────────
  'geo.colorerPar':                   'Colorer la carte par {{kpi}}',
  'geo.canaux':               'Canaux',
  'geo.pdvActifs':            '{{n}} PDV actifs',
  'geo.pdvActifsTitre':       'PDV distincts ayant au moins une observation sur la période affichée, pour le canal sélectionné',
  'geo.algerie':              'Algérie',
  'geo.nombrePdv':            'Nombre de PDV',
  'geo.horsScopeSud':         'Hors scope (SUD)',
  'geo.ndGlose':              'Distribution numérique',
  'geo.tipVolumePdm':         'Volume : {{v}} · PDM : {{p}}%',
  'geo.tipVolume':            'Volume : {{v}}',
  'geo.tipPdvDb':             'PDV DB : {{n}}',
  'geo.tipPdvRouting':        'PDV Routing : {{n}}',
  'geo.tipKpiValeur':         '{{kpi}} : {{valeur}}',
  'geo.tipRegionPdv':         '{{region}} · {{n}} PDV',
  'geo.tipPdv':               '{{n}} PDV',
  'geo.cliquerSelectionner':  'Cliquer pour sélectionner',
  'geo.derniereVisite':       'Dernière visite',
  'geo.vsPrec':               'vs préc.',
  'geo.titrePdv':             '{{wilaya}} · {{region}} · PDV',
  'geo.parSeg':               'Par seg.',
  'geo.nonEvalueAbbr':        'n/é',
  'geo.nonEvalues':           'Non évalués — observés sur la période, sans segment attribué',
  'geo.imageSkuIndispo':      'Image SKU indisponible',
  'geo.erreurTop3':           'Chargement Top-3 SKU impossible.',
  'geo.wilayaZoomPdv':        'Wilaya (zoom PDV)',
  'geo.donneesIndispo':       'données indisponibles',
  'geo.vsRegional':           'vs Régional',
  'geo.contribNationale':     '{{prefix}}Contribution Nationale',
  'geo.contribRegionale':     '{{prefix}}Contribution Régionale',
  'geo.nbPdvMarque':          'Nb PDV marque',
  'geo.chargementDuel':       'Chargement duel…',
  'geo.chargementCarte':      'Chargement de la carte',
  'geo.surfaceCarte':         'La carte',
  'geo.surfaceCadrans':       'Cadrans wilaya',
  'geo.surfaceAnalyse':       'Analyse de zone',
  'geo.surfaceDuel':          'Comparaison wilayas',

  // ── `#820` · les 16 libellés que l'ÉCRAN a trouvés (Hkm) ─────────────────
  'commun.kpis':           'KPI',
  'geo.retirerWilaya':                'Retirer wilaya {{side}}',
  'geo.selectionnez':      'Sélectionnez',
  'geo.quitter':           'Quitter',
  'geo.cliquez2Wilayas_one':   'Cliquez 2 wilayas — {{count}}/2 sélectionnée',
  'geo.cliquez2Wilayas_other':   'Cliquez 2 wilayas — {{count}}/2 sélectionnées',
  'geo.erreurWilayas':     'Erreur de chargement des données wilayas.',
  'geo.erreurAnalytique':  'Erreur de chargement des données analytiques.',
  'geo.classementPdm':     '{{zone}} — PDM {{marque}}',
  'geo.titreKpis':         '{{wilaya}} · {{region}} · KPI',
  'geo.voirPdv':           'Voir PDV',
  'geo.topPdv':            'Top PDV',
  'geo.affichageLimite':   'Affichage limité à 2 000 PDV — affinez les filtres.',
  'geo.aucuneVenteSku':    'Aucune vente SKU sur la période.',
  'commun.erreur':       'Erreur',

  // ── `#821` · restes du rejeu `#820` (Marché) ──────────────────────────
  'marche.brand360.groupe':       'Groupe',
  'marche.ws.samsungU':           '{{marque}} U.',
  'marche.s9.erreurTableauPrix':  'Erreur de chargement du tableau des prix.',
  'marche.echelleCommune':        '— échelle commune',
  'marche.aucuneDonneeAfficher':  'Aucune donnée à afficher.',

  // ── `#824` · FLASH INFO (lecture + saisie) ────────────────────────────
  'flashinfo.creer':                     'Créer',
  'flashinfo.erreurChargement':          'Erreur lors du chargement des Flash Info.',
  'flashinfo.aucuneActivite':            'Aucune activité Flash Info',
  'flashinfo.aucuneActiviteSous':        'Modifiez les filtres ou revenez plus tard.',
  'commun.pagePrecedente':            'Page précédente',
  'commun.pageSuivante':              'Page suivante',
  'flashinfo.bandeauVide':               'Aucune activité sur les 7 derniers jours',
  'flashinfo.aLaUne':                    'À la une',
  'flashinfo.aucunePhoto':               'aucune photo',
  'flashinfo.toutesRegions':             'Toutes les régions',
  'flashinfo.rechercherPlaceholder':     'Rechercher…',
  'flashinfo.typeActivite':              'Type d\'activité',
  'flashinfo.tousTypes':                 'Tous types',
  'flashinfo.canalCible':                'Canal cible',
  'commun.tousCanaux':                'Tous canaux',
  'flashinfo.dateDebut':                 'Date début',
  'flashinfo.dateFin':                   'Date fin',
  'flashinfo.reinitialiserFiltres':      'Réinitialiser filtres',
  'commun.retour':                    'Retour',
  'flashinfo.modifier':                  'Modifier',
  'flashinfo.archiver':                  'Archiver',
  'flashinfo.archivage':                 'Archivage…',
  'commun.supprimer':                 'Supprimer',
  'flashinfo.wilayasCiblees':            'Wilayas ciblées',
  'flashinfo.publieLe':                  'Publié le {{date}}',
  'flashinfo.parAuteur':                 '· par {{auteur}}',
  'flashinfo.modaleArchiverTitre':       'Archiver cette Flash Info ?',
  'flashinfo.archivageDefinitif':        'L’archivage est définitif.',
  'flashinfo.archivageSuite':            'La fiche quittera le bandeau pour rejoindre les archives, et',
  'flashinfo.archivageGras':             'ne pourra plus être republiée',
  'flashinfo.archivageFin':              'une fois archivée, sa seule sortie est la suppression.',
  'commun.annuler':                   'Annuler',
  'flashinfo.archiverDefinitivement':    'Archiver définitivement',
  'flashinfo.echecArchivage':            'L\'archivage a échoué — {{cause}}. La publication est toujours publiée.',
  'flashinfo.modaleSupprimerTitre':      'Supprimer cette Flash Info',
  'flashinfo.purgeAvant':                'Cette action supprimera définitivement la fiche',
  'flashinfo.purgeApres':                'ainsi que toutes ses photos.',
  'flashinfo.purgeIrrecuperable':        'Il sera impossible de la récupérer.',
  'flashinfo.echecSuppression':          'La suppression a échoué — {{cause}}. La publication est toujours présente.',
  'flashinfo.type.LAUNCH':               'Lancement produit',
  'flashinfo.type.BUNDLE':               'Bundle / Pack',
  'flashinfo.type.TOMBOLA':              'Tombola',
  'flashinfo.type.POSTERS':              'Pose d\'affiches',
  'flashinfo.type.FLYERS':               'Distribution flyers',
  'flashinfo.type.PLV_INSTALL':          'Installation PLV',
  'flashinfo.type.EVENT':                'Événement',
  'flashinfo.type.PRICE_CHANGE':         'Changement de prix',
  'flashinfo.type.OOS_COMPETITOR':       'Rupture concurrent',
  'flashinfo.type.NEW_REFERENCING':      'Nouveau référencement',
  'flashinfo.type.FLASH_PROMO':          'Promo flash',
  'flashinfo.type.OTHER':                'Autre',
  'flashinfo.canal.distributor':         'Distributeur',
  'flashinfo.canal.wholesaler':          'Wholesale',
  'flashinfo.canal.retailer':            'Retail',
  'flashinfo.canal.consumer':            'Consommateur',
  'flashinfo.retourFeed':                'Retour au feed',
  'flashinfo.nouvelleActivite':          'Nouvelle activité Flash Info',
  'flashinfo.modifierActivite':          'Modifier l\'activité Flash Info',
  'flashinfo.champsAvant':               'Champs',
  'flashinfo.champsApres':               'obligatoires',
  'flashinfo.skusConcernes':             'SKUs concernés',
  'flashinfo.titre':                     'Titre',
  'flashinfo.mecanisme':                 'Mécanisme commercial',
  'flashinfo.regionCible':               'Région cible',
  'flashinfo.merchCible':                'Merchandiser cible',
  'flashinfo.pdvConcerne':               'PDV concerné',
  'flashinfo.optionnel':                 '(optionnel)',
  'flashinfo.retourMarche':              'Retour marché',
  'flashinfo.optionnelMax1000':          '(optionnel, max 1000)',
  'flashinfo.phTitre':                   'Titre de l\'activité',
  'flashinfo.phDescription':             'Description détaillée de l\'activité',
  'flashinfo.phMecanisme':               'ex. Achat de 2 SKU = -10% sur le 3e',
  'flashinfo.phRetourMarche':            'Réception terrain, retour des gérants…',
  'flashinfo.optSelectionner':           '— Sélectionner —',
  'flashinfo.optToutesRegions':          '— Toutes régions —',
  'flashinfo.optAucun':                  '— Aucun —',
  'flashinfo.optAucunPdv':               '— Aucun PDV —',
  'flashinfo.chargementWilayas':         'Chargement wilayas…',
  'flashinfo.sauvegarderDraft':          'Sauvegarder DRAFT',
  'commun.publier':                   'Publier',
  'commun.sauvegarder':               'Sauvegarder',
  'flashinfo.erreurChargementActivite':  'Impossible de charger l\'activité. Vérifiez l\'ID et réessayez.',
  'flashinfo.versionIntacte':            '. La version précédente est intacte.',
  'flashinfo.cascadeMerchPdv':           'Merchandiser et PDV ont été retirés : ils dépendaient de la région précédente.',
  'flashinfo.cascadePdv':                'Le PDV choisi a été retiré : il ne dépend pas de ce merchandiser.',
  'flashinfo.aucunPdvMerch':             'Aucun PDV de ce merchandiser dans cette région',
  'flashinfo.erreurUploadPhotos':        'Erreur upload photos',
  'flashinfo.uploadPartiel':             'Activité créée. Upload photos partiel : {{msg}}',
  'flashinfo.erreurSuppressionPhoto':    'Erreur suppression photo',
  'flashinfo.erreurUploadNouvelles':     'Erreur upload nouvelles photos',
  'flashinfo.serveurARepondu':           'le serveur a répondu {{status}}',
  'flashinfo.causeInconnue':             'cause inconnue (aucun message du serveur)',
  'flashinfo.dureeActivite':             'Durée d\'activité',
  'flashinfo.uniteJours':                'Jours',
  'flashinfo.uniteSemaines':             'Semaines',
  'flashinfo.uniteMois':                 'Mois',
  'flashinfo.nombre':                    'Nombre',
  'commun.unite':                     'Unité',
  'flashinfo.photosN':                   'Photos ({{n}}/{{max}})',
  'flashinfo.photosVide':                'Aucune photo — cliquez pour ajouter (JPEG/PNG/WebP ≤ 2 Mo)',
  'flashinfo.photosMax':                 'Maximum {{max}} photos. Vous avez déjà {{n}} photo(s).',
  'flashinfo.photoFormat':               'Format non accepté : {{f}}. Utilisez JPEG, PNG ou WebP.',
  'flashinfo.photoLourde':               'Photo trop lourde : {{f}}. Maximum 2 Mo.',
  'flashinfo.choisirPhotos':             'Choisir des photos',
  'flashinfo.skuChoisirMarque':          '— Sélectionnez d\'abord une marque —',
  'flashinfo.apercuDirect':              'Aperçu en direct',
  'flashinfo.phMarque':                  'Marque…',
  'flashinfo.phTitreCourt':              'Titre…',
  'flashinfo.surfaceBandeau':            'Flash Info — bandeau',
  'flashinfo.surfaceFiche':              'Flash Info — fiche',
  'flashinfo.surfaceVisionneuse':        'Flash Info — visionneuse',
  'flashinfo.filNouvelleActivite':       'Nouvelle activité',
  'flashinfo.filEdition':                'Édition',

  // ── `#825` · ce que le détecteur ne voyait pas (props, tables) ────────
  'flashinfo.optionnelMax500':   '(optionnel, max 500)',
  'flashinfo.choisirSku':        '— Choisir un SKU —',
  'flashinfo.nSelectionnees_one':    '({{count}} sélectionnée)',
  'flashinfo.nSelectionnees_other':    '({{count}} sélectionnées)',
  'flashinfo.dateFinCalculee':   'Date fin calculée :',
  'flashinfo.statut.BROUILLON':  'BROUILLON',
  'flashinfo.statut.PUBLIEE':    'PUBLIEE',
  'flashinfo.statut.ARCHIVEE':   'ARCHIVEE',
  'flashinfo.dureeJour':         'jour',
  'flashinfo.dureeJours':        'jours',
  'flashinfo.dureeSemaines':     'semaines',
  'flashinfo.dureeMois':         'mois',

  // ── `#825` · ce que le détecteur CORRIGÉ a trouvé (props, aria, accolades) ─
  'flashinfo.optionnelN':               '(optionnel, {{n}})',
  'flashinfo.tousSkusSelectionnes':     '— Tous les SKUs sélectionnés —',
  'flashinfo.aucunSkuMarque':           '— Aucun SKU pour cette marque —',
  'flashinfo.photoPrecedente':          'Photo précédente',
  'flashinfo.photoSuivante':            'Photo suivante',
  'flashinfo.activiteIntrouvable':      'Activité introuvable ou accès refusé.',
  'flashinfo.heriteeMerchPdv':          'Le merchandiser et le PDV enregistrés ne correspondent plus à cette région — la sélection date d avant un changement de tournée. Elle est conservée telle quelle.',
  'flashinfo.heriteeMerch':             'Le merchandiser enregistré ne correspond plus à cette région — la sélection date d avant un changement de tournée. Elle est conservée telle quelle.',
  'flashinfo.heriteePdv':               'Le PDV enregistré n est plus desservi par ce merchandiser — la sélection date d avant un changement de tournée. Elle est conservée telle quelle.',

  // ── `#827` · SFM Team Performance (T0 `#826`) + restes `#825` ──────────
  'perf.titreMerchs':                'Merchandisers — valeurs W · feux 4 sem',
  'perf.titreEvolution':             'Évolution équipe — {{n}} sem',
  'perf.titreHeatmap':               'Jours travaillés',
  'perf.titreAlertes':               'Alertes {{canal}}',
  'perf.titreClusters':              'Fraude tournée · clusters « même point, plusieurs PDV »',
  'perf.titreDispersionEquipe':      'Dispersion GPS · Équipe',
  'perf.titreDispersionMerch':       'Dispersion GPS · {{merch}}',
  'perf.titreConformite':            'Conformité visite',
  'perf.effacer':                    'Effacer',
  'perf.rougeCouverture':            'Rouge couverture',
  'perf.rougeQualite':               'Rouge qualité',
  'perf.visitesRealisees':           'Visites réalisées',
  'perf.couvertureRouting':          'Couverture routing',
  'perf.nPdvHebdo':                  '{{n}} / {{m}} PDV hebdo',
  'perf.lignesFigees':               'Lignes figées',
  'perf.pasSemainePrecedente':       'pas de semaine précédente',
  'perf.lignesCollectees':           'Lignes collectées',
  'perf.skusDistincts':              'SKUs distincts',
  'perf.ventesCollectees':           'Ventes collectées',
  'perf.ratioDollarLigne':           'Ratio $/ligne',
  'perf.visites':                    'Visites',
  'perf.txVisite':                   'Tx visite',
  'perf.couvPct':                    'Couv. %',
  'perf.lignes':                     'Lignes',
  'perf.figeesPct':                  'Figées %',
  'perf.ventesDollar':               'Ventes ($)',
  'perf.ventesVol':                  'Ventes (Vol)',
  'perf.aucunMerchFiltre':           'Aucun merch ne matche le filtre statut. Cliquez sur une puce pour la retirer.',
  'perf.aucunMerchPeriode':          'Aucun merchandiser sur la période.',
  'perf.aucunMerchandiser':          'Aucun merchandiser.',
  'perf.aucunMerchFiltreCourt':      'Aucun merch pour le filtre statut.',
  'perf.echecListeMerchs':           'Impossible de charger la liste des merchandisers.',
  'perf.aucune':                     'aucune',
  'perf.pdvFermes':                  'PDV fermés',
  'perf.aucunMerchCanal':            'Aucun merch sur ce canal.',
  'perf.echecAlertes':               'Erreur de chargement des alertes.',
  'perf.actConforme':                'conforme',
  'perf.actInvestiguer':             'investiguer',
  'perf.actVisiteTerrain':           'visite terrain superviseur',
  'perf.actSanction':                'sanction disciplinaire',
  'perf.actInsuffisant':             'donnée insuffisante — NON conforme',
  'perf.couverture':                 'Couverture',
  'perf.grisCouverture':             'aucun PDV planifié au routing cette semaine',
  'perf.lignesCollecteesEcart':      'Lignes collectées (écart vs équipe)',
  'perf.grisMoyenne':                'moyenne équipe non calculable',
  'perf.figees':                     'Figées',
  'perf.lignesFigeesVentes':         'Lignes figées (avec ventes)',
  'perf.grisAucuneLigne':            'aucune ligne collectée cette semaine',
  'perf.visitesCourtes':             'Visites courtes',
  'perf.visitesMoins15':             'Visites de moins de 15 min',
  'commun.nVisite':                    '{{n}} visite',
  'commun.nVisites':                   '{{n}} visites',
  'perf.grisNetTime':                'durée de visite (net_time) non collectée sur ce merch',
  'perf.premiereVisite':             '1ʳᵉ visite',
  'perf.heurePremiereVisite':        'Heure de 1ʳᵉ visite',
  'perf.grisParser':                 'importée avant le fix parser #325',
  'perf.pdvTrouvesFermes':           'PDV trouvés fermés',
  'perf.grisFermeture':              'donnée de fermeture non disponible',
  'perf.pdvNonVisite':               'PDV non visité',
  'perf.pdvRoutingNonVisites':       'PDV du routing non visités depuis 3+ semaines',
  'perf.grisAucunPdvRouting':        'aucun PDV au routing de ce merch',
  'perf.tempsJour':                  'Temps/jour',
  'perf.tempsTravailJour':           'Temps de travail par jour (bilatéral)',
  'perf.ventesCollecteesEcart':      'Ventes collectées (écart vs équipe)',
  'perf.apresHeure':                 'après {{h}}h{{mm}}',
  'perf.loOuHi':                     '{{lo}} ou {{hi}}',
  'perf.infoQualite':                'qualité = min(figées, temps) · #WS# neutralisés · clic → fiche',
  'perf.infoTauxVisite':             'Taux de visite = Visites / Routing cible',
  'perf.infoCouvertureW':            'Couverture W = PDV routing hebdo visités / PDV planifiés hebdo (#323, borné 100%)',
  'perf.infoFigees':                 'Lignes figées / lignes totales — <10 % perf · >20 % alerte',
  'perf.infoVolumeVentes':           'Volume ventes — unités',
  'perf.infoGrilleTemporelle':       'Grille temporelle = observations.week_iso (étiquette rawdata {{marque}}, aligné Marché). Tous les KPIs — visites, lignes, CA — vivent sur la même fenêtre.',
  'perf.infoMoyenneMerch':           'Moyenne par merch (canal courant, #WS# exclus) — situe ce merch vs ses pairs',
  'perf.semainesPartielles':         'Semaines partielles : ',
  'perf.semainePartielle':           'Semaine partielle — {{n}} jour(s) ouvré(s) présent(s) sur 5',
  'perf.feuxProvisoiresInfo':        'Feux provisoires — {{n}} semaine(s) partielle(s) sur la fenêtre : ',
  'perf.alertesSansDonnee':          '{{n}} alerte(s) sans donnée — non évaluables',
  'perf.pdvFermesContexte':          'PDV fermés (contexte — pas une alerte)',
  'perf.voirDetail':                 'Voir le détail de {{merch}}',
  'perf.seuilProvisoire':            '⚠ seuil provisoire',
  'perf.leg10Visites':               '≥ 10 visites',
  'perf.leg59Visites':               '5 – 9 visites',
  'perf.leg14Visites':               '1 – 4 visites',
  'perf.leg0Visite':                 '0 visite',
  'perf.nMerchXJours':               '{{n}} merch × {{m}} j.',
  'perf.nMerchsXJours':              '{{n}} merchs × {{m}} j.',
  'perf.nJoursXMerchs':              '{{n}} j. × {{m}} merchs',
  'perf.derniereSemaineIncomplete':  '⚠ dernière semaine incomplète · imports jusqu\'au {{date}}',
  'perf.importsJusquau':             'imports jusqu\'au {{date}}',
  'perf.celluleVisite':              '{{merch}} · {{date}} · {{n}} visite',
  'perf.celluleVisites':             '{{merch}} · {{date}} · {{n}} visites',
  'perf.celluleZero':                '{{merch}} · {{date}} · 0 visite',
  'perf.aucuneActivite':             'Aucune activité sur la période.',
  'perf.echecHeatmap':               'Impossible de charger la heatmap.',
  'perf.ceMerch':                    'Ce merch',
  'perf.moyenneEquipe':              'Moyenne équipe',
  'perf.nPartielle':                 '{{n}} partielle',
  'perf.nPartielles':                '{{n}} partielles',
  'perf.suffixePartielle':           ' · {{n}}/5 jours (partielle)',
  'perf.ventesUsd':                  'Ventes (USD)',
  'perf.echecSerie':                 'Impossible de charger la série.',
  'perf.beginPdv':                   'begin (position au scan) ↔ PDV (référentiel)',
  'perf.nGeolocalises':              ' géolocalisés',
  'perf.visitesSansScan':            ' visités sans scan',
  'perf.nonVisites':                 ' non visités',
  'perf.pdvNonCoord':                ' PDV non coord',
  'perf.semaineIso':                 'semaine {{w}}',
  'perf.mediane':                    'Médiane',
  'perf.moy1km':                     'Moy. <1km',
  'perf.conformeFin50':              'Conforme fin ≤ 50 m',
  'perf.conforme50100':              'Conforme 50-100 m',
  'perf.limite100250':               'Limite 100-250 m',
  'perf.suspect250500':              'Suspect 250-500 m',
  'perf.aberrant500':                'Aberrant 500 m-5 km',
  'perf.aucuneCoordGps':             'Aucune coord GPS exploitable sur la fenêtre. Vérifiez la propagation coord PDV.',
  'perf.dispersionIndispo':          'Dispersion GPS indisponible',
  'perf.nVisitesAnalysables':        ' visites analysables',
  'perf.conformesSeuil':             'Conformes ≤ {{n}} m ',
  'perf.deplacees':                  'Déplacées ',
  'perf.horsGraphe':                 'Hors graphe (> 1 km) ',
  'perf.ecartBeginEnd':              'Écart begin↔end',
  'perf.dureeMin':                   'durée (min)',
  'perf.ecartDuree':                 'écart = {{m}} m · durée = {{min}} min',
  'perf.conformeSeuilN':             'Conforme ≤ {{n}} m ({{c}})',
  'perf.deplaceeSeuil':              'Déplacée > {{n}} m — 1 km',
  'perf.horsGrapheN':                '> 1 km : {{n}} hors graphe',
  'perf.conformiteIndispo':          'Conformité begin↔end indisponible',
  'perf.nMerchsGrille':              '{{n}} merchs · grille {{a}}m × {{b}}m',
  'perf.pdvEnCluster':               'PDV en cluster',
  'perf.maxCluster':                 'Max / cluster',
  'perf.totalPdv':                   'Total PDV',
  'perf.aucunMerchFenetre':          'Aucun merch sur cette fenêtre',
  'perf.clustersIndispo':            'Clusters GPS indisponibles',
  'perf.feuxProvisoires':            'Feux provisoires',
  'perf.nJours5':                    '{{n}}/5 jours',
  'perf.top3Lignes':                 'Top 3 lignes',
  'perf.bottom3Lignes':              'Bottom 3 lignes',
  'perf.top3VentesDollar':           'Top 3 ventes ($)',
  'perf.bottom3VentesDollar':        'Bottom 3 ventes ($)',
  'perf.top3VentesVol':              'Top 3 ventes (vol)',
  'perf.bottom3VentesVol':           'Bottom 3 ventes (vol)',
  'perf.ficheMerch':                 'Fiche merch {{code}}',
  'perf.ficheIndispo':               'Fiche indisponible',
  'perf.echecFiche':                 'Impossible de charger la fiche de ce merchandiser.',
  'perf.wsEnAttente':                'WS en attente',
  'perf.ficheStatut4Sem':            'Fiche merch — statut 4 sem glissantes',
  'perf.wholesaleEnAttente':         'Wholesale en attente',
  'perf.pct4Sem':                    '{{x}} % (4 sem)',
  'perf.donneeIndispo':              'donnée indispo',
  'perf.figeesTemps4Sem':            'figées {{x}} % · temps {{y}} min (4 sem)',
  'perf.visite':                     'Visite',
  'perf.voirIdentite':               'Voir identité',
  'perf.alertes':                    'Alertes',
  'perf.aucuneAlerteActive':         'Aucune alerte active',
  'perf.provisoire':                 'provisoire',
  'perf.donneeInsuffisante':         'donnée insuffisante',
  'perf.contexte':                   'Contexte · ',
  'perf.cetteSemaine':               ' cette semaine.',
  'perf.aucuneAlerteEvaluee':        'Aucune alerte évaluée pour {{merch}} sur cette semaine (merch sans activité ?).',
  'perf.tempsMoyen':                 'Temps moyen',
  'perf.ecartGpsMoy':                'Écart GPS moy.',
  'perf.conformiteGps':              'Conformité GPS',
  'perf.min15Perf':                  '≥15 min perf acceptée',
  'perf.visitePdv':                  'visite → PDV',
  'perf.routingCible':               'Routing cible',
  'perf.clustersBegin':              'Regroupements begin (~50m) · fraude tournée potentielle',
  'perf.grilleCellules':             'Cellules de grille où ≥2 PDV distincts partagent le même begin',
  'perf.centreZone':                 'centre de zone (~50 m) : ({{lat}}, {{lon}})',
  'perf.infoCentreCellule':          'Centre de la cellule de grille (~50 m de côté) où les begin GPS ont été regroupés — pas une position GPS individuelle réelle.',
  'perf.aucunRegroupement':          'Aucun regroupement suspect détecté sur la fenêtre.',
  'perf.conformiteBeginEnd':         'Conformité visite · begin↔end',
  'perf.fermeSurPlace':              'Le merch a-t-il fermé la visite sur place ? (seuil {{n}} m)',
  'perf.conforme':                   ' conforme',
  'perf.nDeplacee':                  '{{n}}/{{m}} déplacée',
  'perf.nDeplacees':                 '{{n}}/{{m}} déplacées',
  'perf.toutesVisitesSurPlace':      'Toutes ses {{n}} visites analysables terminées sur place.',
  'perf.aucuneVisiteAnalysable':     'Aucune visite analysable sur la fenêtre.',
  'perf.conformiteVisitesIndispo':   'Conformité visites indisponible',
  'perf.resolutionIdentite':         'Résolution identité',
  'perf.phase4J':                    'Fonctionnalité disponible Phase 4.J',
  'perf.texteResolution':            'La résolution d\'identité (endpoint /merch/identity/',
  'perf.texteResolutionFin':         ') est planifiée dans le BACKLOG 4.D.4-bis. Cette modale affichera le formulaire motif + audit log READ_MERCH_IDENTITY lors de son activation.',
  'perf.surfEvolution':              'Performance — Évolution équipe',
  'perf.surfHeatmap':                'Performance — Jours travaillés',
  'perf.surfAlertes':                'Performance — Alertes équipe',
  'perf.surfMerchs':                 'Performance — Merchandisers',
  'perf.surfDispersion':             'Performance — Dispersion GPS',
  'perf.surfConformite':             'Performance — Conformité GPS',
  'perf.surfClusters':               'Performance — Clusters de fraude',
  'perf.surfFiche':                  'Performance — Fiche merchandiser',
  'perf.nivConforme':                'Conforme',
  'perf.nivRoutingTenu':             'Routing hebdo tenu',
  'perf.nivAInvestiguer':            'À investiguer',
  'perf.nivGrave':                   'Grave',
  'perf.nivVisiteTerrain':           'Visite terrain superviseur',
  'perf.nivCritique':                'Critique',
  'perf.nivSanction':                'Sanction disciplinaire',
  'perf.nivDonneeIndispo':           'Donnée indisponible',
  'perf.notif.FIRST_VISIT_LATE':     '1ʳᵉ visite tardive',
  'perf.notif.GPS_DISPERSION':       'Dispersion GPS',
  'perf.notif.POS_UNVISITED':        'PDV non visités 3+ semaines',
  'perf.notif.TIME_PER_DAY':         'Temps de travail par jour',
  'perf.notif.sufVsEquipe':          'vs équipe',
  'perf.notif.sufHorsSeuil':         'des visites hors seuil',
  'perf.notif.sufDuRouting':         'du routing',
  'marche.chargementLibelle':        'Chargement',
  'marche.derniereSemImportee':      'Dernière sem. importée',
  'marche.topWilayas':               'Top wilayas',
  'flashinfo.choisirMerch':          'Choisir un merchandiser',
  'flashinfo.merchSansPdv':          'Ce merchandiser ne dessert aucun PDV',

  // ── `#834` · Exactitude des données (T0 `#830`) ───────────────────────
  'da.echecKpiOps':            'Impossible de charger les KPI opérationnels.',
  'da.fenetre':                'Fenêtre {{w1}} → {{w2}} · ',
  'da.chargementMin':          'chargement…',
  'da.echecDaV2':              'Impossible de charger Data Accuracy V2.',
  'da.nRelevesPropres':        '{{n}} / {{m}} relevés propres',
  'da.nPts':                   '{{n}} pts',
  'da.diagnostic':             'diagnostic',
  'da.formule':                'Formule : Qualité − Preuves, en points.',
  'da.nVisitesProuvees':       '{{n}} / {{m}} visites prouvées',
  'da.histoIndispo':           'Historique indisponible',
  'da.histoAucuneSemaine':     ' — aucune semaine mesurée jusqu\'à {{w}}. Les semaines antérieures n\'ont pas encore été chargées ou calculées.',
  'da.histoDeuxCourbes':       'Historique — les deux courbes',
  'da.nSemaine':               '{{n}} semaine',
  'da.nSemaines':              '{{n}} semaines',
  'da.ariaHistorique':         'Historique Qualité et Preuves',
  'da.nVisitesEcart':          '{{n}} visites · écart {{p}} pts',
  'da.trouPreuve':             'D\'où vient le trou de preuve',
  'da.prouvees':               'Prouvées',
  'da.hintProuvees':           '= Scan RFID + PDV géolocalisé + GPS ≤ 50 m + Photo façade',
  'da.echecMerch':             'Échec merch',
  'da.hintEchecMerch':         '= Scan RFID + PDV géolocalisé + (GPS > 50 m OU Photo façade absente)',
  'da.preuveImpossibleScan':   'Preuve impossible — scan',
  'da.hintScan':               '= Pas de scan RFID',
  'da.preuveImpossibleCoord':  'Preuve impossible — coord.',
  'da.hintCoord':              '= Scan RFID + PDV sans coord GPS en base',
  'da.raterQualite':           'Ce qui fait rater la qualité',
  'da.ctrlFraicheurCourt':     'Fraîcheur (relevé recopié)',
  'da.ctrlPhotoIntCourt':      'Photo intérieure absente',
  'da.ctrlLignesCourt':        'Lignes sous l’habitude',
  'da.ctrlSkuCourt':           'SKU sous l’habitude',
  'da.ctrlVentesCourt':        'Ventes hors bande',
  'da.ctrlTempsCourt':         'Temps de visite < 15 min',
  'da.echecEntonnoirs':        'Impossible de charger les entonnoirs.',
  'da.chargementEntonnoirs':   'Chargement des entonnoirs…',
  'da.qualiteMaj':             'QUALITÉ',
  'da.contenuReleve':          'le contenu du relevé',
  'da.chaineReelle':           'chaîne réelle',
  'da.sixControles':           'les 6 contrôles PASS strict',
  'da.egalQualite':            '= Qualité',
  'da.indulgentNaPass':        'indulgent, NA→PASS',
  'da.preuveMaj':              'PREUVE',
  'da.presenceProuvee':        'la présence prouvée du merch',
  'da.relevesHorsPlan':        'Relevés effectués sur des PDV hors tournée planifiée — comptés dans Data Proof, hors entonnoir.',
  'da.nHorsPlan':              '+ ',
  'da.horsPlanApres':          ' hors plan',
  'da.egalPreuves':            '= Preuves',
  'da.vsRelevesEffectues':     'vs relevés effectués',
  'da.vsPlanifie':             'vs Planifié',
  'da.vueCanalisee':           'Vue canalisée ({{canal}}) — le canal appartient au relevé. La marche « Planifié » est masquée : le routing n\'a pas de canal. Un merch mixte apparaît dans les deux vues avec ses relevés respectifs — la somme retail + wholesale dépasse le total.',
  'da.cumulEntonnoir':         'Cumul entonnoir',
  'da.reelIndep':              'Réel indép.',
  'da.deplusCumul':            '+{{n}} de plus que l\'entonnoir cumulé',
  'da.infoCumul':              'Cumul entonnoir : chaque marche filtre les survivants de la précédente. C\'est la logique séquentielle propre à l\'entonnoir.',
  'da.pctDepart':              '% du départ (cumul)',
  'da.ref':                    'ref.',
  'da.infoIndulgent':          'Indulgent indépendant : ce contrôle seul, sur TOUS les relevés. Les non-mesurables (photo/temps manquants) comptent comme réussites (règle Data Quality V2).',
  'da.pctPasEnFaute':          '% pas en faute',
  'da.infoReel':               'Réel indépendant : ce contrôle seul, sur les relevés effectivement mesurés (le signal existe). Recoupe à 100 % avec le panneau « Ce qui fait rater la qualité ». L\'écart avec « % pas en faute » révèle les non-mesurables — photos manquantes faute de scan.',
  'da.pctReelSignal':          '% réel du signal',
  'da.passFailNa':             'PASS {{a}} · FAIL {{b}} · NA {{c}}',
  'da.ariaEntonnoir':          'Entonnoir {{titre}}',
  'da.perteChip':              '−{{n}} · {{p}} pts',
  'da.perteDelta':             '− {{n}}  ·  Δ {{p}}',
  'da.pointBloquant':          'POINT BLOQUANT · {{label}}',
  'da.piedEntonnoir':          'Pied d\'entonnoir',
  'da.fenetreW':               'Fenêtre {{w}}',
  'da.fenetreW1W2':            'Fenêtre {{w1}} → {{w2}}',
  'da.echecIndex':             'Impossible de charger l\'index.',
  'da.titreMerchVisites':      '{{merch}} · {{n}} visites',
  'da.suffixeFaible':          ' (< {{seuil}})',
  'da.suffixeMixte':           ' · MIXTE — ce merch produit des relevés dans les 2 canaux ; ce qui s\'affiche ici est sa part sur {{canal}}, pas toute son activité',
  'da.ariaMerchMixte':         'Merch mixte : produit des relevés dans les 2 canaux',
  'da.visitesNonRattachees':   'visites non rattachées à un merch — comptées dans la vue nationale, non imputées.',
  'da.nM':                     '{{n}} m',
  'da.nKm':                    '{{n}} km',
  'da.nVisitesFenetre':        '{{n}} visites · {{w1}} → {{w2}}',
  'da.histoDeuxScores':        'Historique des deux scores',
  'da.uneSeuleSemaine':        'Une seule semaine mesurée pour ce merchandiser ({{w}}) — il n\'y a pas encore d\'évolution à tracer.',
  'da.sesDefaillances':        'Ses défaillances de preuve',
  'da.gpsTropLoin':            'GPS trop loin seul',
  'da.gpsPhotoAbsente':        'GPS + photo absente',
  'da.photoAbsenteSeule':      'Photo absente seule',
  'da.visitesAControler':      'Visites à contrôler',
  'da.nAControler':            '{{n}} à contrôler',
  'da.distance':               'Distance',
  'da.motif':                  'Motif',
  'da.nVisiteAberrante':       'visite avec distance aberrante (> {{k}} km) — inversion de signe / hémisphère, isolée, non imputée au merch.',
  'da.nVisitesAberrantes':     'visites avec distance aberrante (> {{k}} km) — inversion de signe / hémisphère, isolées, non imputées au merch.',
  'da.svgQualite':             '{{w}} — Qualité {{x}}',
  'da.svgPreuves':             '{{w}} — Preuves {{x}}',
  'da.surfPilotage':           'Data Accuracy — pilotage',
  'da.surfHistorique':         'Data Accuracy — historique',
  'da.surfEntonnoirQualite':   'Data Accuracy — entonnoir qualite',
  'da.surfEntonnoirPreuve':    'Data Accuracy — entonnoir preuve',
  'da.surfFiche':              'Data Accuracy — fiche merchandiser',
  'da.etape.VISITE':           'Visité',
  'da.etape.GPS50':            'GPS ≤ 50 m',
  'da.etape.PHOTO_EXT':        'Photo ext. (façade)',
  'da.etape.PLANIFIE':         'Planifié (réf.)',
  'da.etape.PHOTO_INT':        'Photo int. (linéaire)',
  'da.etape.TEMPS':            'Temps > 15 min',
  'da.etape.FRAICHEUR':        'Fraîcheur',
  'da.etape.BACKCHECK':        'Backcheck',
  'da.digestAucuneVisite':     'aucune visite évaluable',
  'da.digestQualite':          'Qualité {{x}}',
  'da.digestPremiereSemaine':  ' (première semaine suivie)',

  // ── `#834` · phrases à STYLE INTERNE, coupées autour (`#817`) ─────────
  'da.seuilAvant':                 'Seuil : ≥ 90 % ',
  'da.vert':                       'vert',
  'da.seuilMilieu':                ' · < 90 % ',
  'da.rouge':                      'rouge',
  'da.ratioAvant':                 'Ratio calculé sur ',
  'da.ratioEm':                    'tous les relevés effectués',
  'da.ratioApres':                 ' (au plan + hors plan). Les non-prouvés se ventilent entre le merch, l\'app SFMT et Orbicore.',

  // ── `#834` · phrases à GRAS INTERNE, coupées autour (`#817`) ──────────
  'da.nomTemps':                   'Temps de visite',
  'da.resteTemps':                 ' — durée nette > 15 min',
  'da.resteFraicheur':             ' — < 20 % de lignes recopiées',
  'da.nomPhotoInt':                'Photo intérieure',
  'da.restePhotoInt':              ' — linéaire joint si scan',
  'da.nomLignesSku':               'Lignes / SKU',
  'da.resteLignesSku':             ' — ≥ 60 % de la médiane 8 dernières visites',
  'da.resteVentes':                ' — entre 30 % et 300 % de la médiane',
  'da.ecartPrefixe':               '· Écart ',
  'da.motFaible':                  'faible',
  'da.resteFaible':                ' : les deux scores bougent ensemble.',
  'da.motLarge':                   'large',
  'da.resteLarge':                 ' : contenu correct mais preuve absente → chaîne de preuve à corriger (GPS/photo), pas les merchs.',
  'da.resteGps':                   ' — distance PDV↔scan ≤ 50 m',
  'da.nomPhotoExt':                'Photo extérieure',
  'da.restePhotoExt':              ' — façade jointe à la visite',

  // ── `#837` A1 · Labo — la coquille et les 4 modes ───────────────────────
  'lab.mode.tendanceDesc':       'Évolution d\'un KPI sur une période.',
  'lab.mode.classement':         'Classement',
  'lab.mode.classementDesc':     'Top N marques, SKUs ou PDVs.',
  'lab.mode.croise':             'Tableau croisé',
  'lab.mode.croiseDesc':         'Builder drag & drop.',
  'lab.badgeAvance':             'Avancé',
  'lab.mode.exportDesc':         'Extraction brute vers Excel.',
  'lab.requetesSauvegardees':    'Requêtes sauvegardées',
  'lab.hintTendance':            'Suivez l\'évolution d\'un KPI dans le temps : volumes, parts de marché, prix moyen, distribution. Choisissez une période de 4 semaines à 12 mois, comparez 1 à 10 marques, identifiez les tendances.',
  'lab.hintClassement':          'Identifiez le top des marques, SKUs ou PDV selon un critère métier (CA, volume, présence). Le tri révèle qui pèse vraiment, et l\'écart entre le leader et ses challengers sur la période choisie.',
  'lab.hintCroise':              'Construisez votre vue en glissant-déposant lignes, colonnes et valeurs (marque × région × KPI). Maximum de flexibilité pour explorer des croisements libres, sans contrainte de format.',
  'lab.hintExport':              'Extrayez les données brutes filtrées directement vers Excel. Pour analyse externe, archive ou partage. Conserve toutes les colonnes sources sans agrégation.',
  'lab.accesRefuse':             'Accès refusé',
  'lab.desktopSeul':             'Labo Libre service est disponible uniquement sur desktop (min. 1024px).',
  'lab.aucunKpiSupporte':        'Aucun KPI sélectionné n\'est pris en charge pour ce type d\'analyse. Choisissez au moins un KPI disponible (non grisé).',
  'lab.normAbsolu':              'Absolu',
  'lab.modifierEtape':           'Modifier : {{question}}',

  // ── `#837` — presets de periode, forme LONGUE (le Labo et le Marche partagent le jeu) ───

  // ── `#837` A1 · Labo — le classement, les marques, les zones ────────────
  'lab.qEntite':                  'Quelle entité classer ?',
  'lab.qKpi':                     'Par quel KPI ?',
  'lab.qPeriode':                 'Quelle période ?',
  'lab.qTopCanalSens':            'Top N · Canal · Sens',
  'lab.qCanalSens':               'Canal · Sens',
  'lab.qPerimetre':               'Périmètre (marque · zones)',
  'lab.qNormLancer':              'Normalisation · Lancer',
  'lab.afficherTopN':             'Afficher le Top N',
  'lab.sens':                     'Sens',
  'lab.marques':                  'Marque(s)',
  'lab.zones':                    'Zones (régions · wilayas)',
  'lab.normalisation':            'Normalisation',
  'lab.ciblePinglee':             'Cible épinglée (optionnel)',
  'lab.epinglezM':                'Épinglez un {{entite}} précis pour voir son rang, même si elle tombe hors du Top {{n}}.',
  'lab.epinglezF':                'Épinglez une {{entite}} précise pour voir son rang, même si elle tombe hors du Top {{n}}.',
  'lab.rechercherEntite':         'Rechercher {{entite}}…',
  'lab.aucuneCible':              'Aucune cible épinglée',
  'lab.resumeToutesRegions':      'Toutes régions · {{canal}} · {{sens}}',
  'lab.resumeTopN':               'Top {{n}} · {{canal}} · {{sens}}',
  'lab.marqueDefaut':             '{{marque}} (défaut)',
  'lab.nMarque':                  '{{n}} marque',
  'lab.nMarques':                 '{{n}} marques',
  'lab.nZone':                    '{{n}} zone',
  'lab.nZones':                   '{{n}} zones',
  'lab.mesure':                   'Mesure :',
  'lab.teteEtAutre':              '{{tete}} + … + {{n}} autre',
  'lab.teteEtAutres':             '{{tete}} + … + {{n}} autres',
  'lab.compteurMarques':          '({{n}} marques)',
  'lab.marquesVideMarche':        'marque(s) — vide = marché total',
  'lab.regionIndispo':            'Région non disponible',
  'lab.configIncomplete':         '· Configuration incomplète ou invalide — corrigez les étapes signalées.',
  'lab.kpiIncompatible':          '· KPI incompatible avec l\'entité {{entite}} — choisissez-en un autre.',
  'lab.capMarques10':             '10 marques max — laissez vide pour top 7 auto.',
  'lab.capRetirezEn':             '{{msg}} (retirez-en {{n}})',
  'lab.deuxKpisUneMarque':        '2 KPIs = 1 marque max — retirez un KPI pour comparer plusieurs marques',
  'lab.plusieursMarquesUnKpi':    'Plusieurs marques = 1 KPI max — choisissez 1 marque pour comparer 2 KPIs',
  'lab.blocageCardinalite':       '2 KPIs + plusieurs marques : choisissez 1 marque OU retirez un KPI',

  // ── `#837` A1 · Labo — les motifs de gating (le hook nomme, l'ecran ecrit) ───
  'lab.gateKpiRequis':          'KPI requis',
  'lab.gateKpiNonRangeable':    'KPI {{kpi}} non rangeable V1',
  'lab.gateNonAdditif':         'KPI non additif — normalisation par PDV non applicable',
  'lab.gateFeuille':            'Entité {{entite}} est une feuille — normalisation par PDV exige une entité agrégat (wilaya / région / marque / merch)',

  // ── `#837` A2 · Labo — le flux Tendance ─────────────────────────────────
  'lab.qKpisAnalyser':        'Quels KPIs analyser ?',
  'lab.qCanal':               'Quel canal ?',
  'lab.qMarques':             'Quelles marques ?',
  'lab.qRegions':             'Quelles régions ?',
  'lab.transsionGroupees':    'Transsion (marques groupées)',

  // ── `#837` A1 · Labo — zones, wilayas, periode ──────────────────────────
  'lab.wilayas':                  'Wilayas',
  'lab.regionEstEntite':          'L\'entité classée EST la région — pas de filtre région (périmètre national).',
  'lab.wilayaEstEntite':          'L\'entité classée EST le wilaya — pas de filtre wilaya (périmètre = régions seules ou national).',
  'lab.pasDeFiltreWilaya':        'Pas de filtre wilaya pour cette entité.',
  'lab.filtrerWilayas':           'Filtrer wilayas…',
  'lab.aucunWilaya':              'Aucun wilaya — affinez la recherche.',
  'lab.rechercherWilaya':         'Rechercher une wilaya…',
  'lab.touteLaRegion':            'Toute la région',
  'lab.aucuneWilayaCochee':       'Aucune wilaya cochée — la requête porte sur toute la région.',
  'lab.requeteReduiteWilaya':     'La requête se réduit à {{n}} wilaya.',
  'lab.requeteReduiteWilayas':    'La requête se réduit à {{n}} wilayas.',
  'lab.periodeRapide':            'Rapide',
  'lab.du':                       'Du',
  'lab.au':                       'Au',

  // ── `#837` A1 ★ · Labo — LE jeu KPI unique (long) et ses formes courtes ───
  'lab.fam.financier':            'Financier',
  'lab.fam.taux':                 'Taux & Parts de marché',
  'lab.fam.comptage':             'Comptage',
  'lab.fam.comparaisons':         'Comparaisons relatives (dormant — V4.5x)',
  'lab.kpi.vitrineMoyenne':       'Vitrine moyenne',
  'lab.kpi.pdmCategorie':         'PDM catégorie',
  'lab.kpi.profondeurGamme':      'Profondeur de gamme',
  'lab.kpi.vsTotalMarche':        'vs Total marché',
  'lab.kpi.vsPeriodeN1':          'vs Période N-1',
  'lab.unite.unitesPdv':          'unités/PDV',
  'lab.unite.skuPdv':             'SKU/PDV',
  'lab.kpiCourt.caDzd':           'CA (DZD)',
  'lab.kpiCourt.volumeVentes':    'Volume ventes (unités)',
  'lab.kpiCourt.stockMoyen':      'Stock moyen (unités)',
  'lab.kpiCourt.ihsMarque':       'IHS {{marque}} (%)',

  // ── `#837` A2 · Labo — le tableau croise et son catalogue de dimensions ───
  'lab.pivot.filtres':            'Filtres',
  'commun.lignes':             'Lignes',
  // `#954` — l'unité de K13 (`suffix: 'lignes'`), en minuscule : « +173 lignes ».
  'commun.lignesMin':          'lignes',
  'lab.pivot.colonnes':           'Colonnes',
  'lab.pivot.valeurs':            'Valeurs',
  'lab.pivot.glissezDim':         'Glissez des dimensions…',
  'lab.pivot.glissezKpi':         'Glissez des KPIs…',
  'lab.pivot.dimDispo':           'Dimensions disponibles',
  'lab.pivot.toutesPlacees':      'Toutes placées ✓',
  'lab.pivot.kpiDispo':           'KPIs disponibles',
  'lab.pivot.partExigeSujet':     'Ce KPI de part exige un sujet : glissez Marque en Filtres OU en Ligne/Colonne.',
  'lab.pivot.valeursAuMoins1':    'La zone Valeurs doit contenir au moins 1 KPI (glissez un KPI, pas une dimension).',
  'lab.pivot.ajoutezKpi':         'Ajoutez au moins 1 KPI dans la zone Valeurs.',
  'lab.pivot.partSansSujet':      'Un KPI de part est en Valeurs sans sujet. Glissez Marque en Filtres OU en Lignes/Colonnes — une part sans marque n\'a pas de sens.',
  'lab.pivot.scopeReduit1':       'Scope réduit : {{liste}} a été retiré (incompatible avec la nouvelle période).',
  'lab.pivot.scopeReduitN':       'Scope réduit : {{liste}} ont été retirés (incompatible avec la nouvelle période).',
  'lab.pivot.deplacer':           'Déplacer',
  'lab.pivot.retirer':            'Retirer {{label}}',
  'lab.dim.geographie':           'Géographie',
  'lab.dim.produit':              'Produit',
  'lab.dim.modeleSku':            'Modèle/SKU',
  'lab.dim.canalVente':           'Canal de vente',
  'lab.dim.temporel':             'Temporel',
  'lab.dim.annee':                'Année',
  'lab.dim.pointDeVente':         'Point de vente',
  'lab.dim.canalRefPdv':          'Canal référentiel PDV',
  'lab.dim.segmentation':         'Segmentation',
  'lab.dim.segmentPdv':           'Segment PDV (A–D)',
  'lab.kpiSelectionnerAu1':       'Sélectionner ≥1',
  'lab.kpiNSel':                  '{{n}} sél.',
  'lab.kpiNSelMax':               '{{n}} sél. / {{max}}',
  'lab.kpiBientot':               'Bientôt disponible',
  'lab.kpiIncompatEntite':        'Incompatible avec l\'entité sélectionnée',

  // ── `#837` B1 · Labo — extraction, hub, requetes sauvegardees ───────────
  'lab.presetsPerso':           'Presets / perso',
  'lab.batchsPrecis':           'Batchs précis',
  'lab.skuModele':              'SKU / Modèle',
  'lab.erreurComptage':         'Erreur de comptage — vérifier les filtres.',
  'lab.depassePlafond':         ' lignes — dépasse le plafond {{max}}, affinez les filtres.',
  'lab.aucuneLigne':            'Aucune ligne dans le périmètre — rien à exporter.',
  'lab.generationXlsx':         '⏳ Génération XLSX en cours…',
  'lab.telechargerExcel':       '📥 Télécharger l\'Excel',
  'lab.telechargerExcelN':      '📥 Télécharger l\'Excel ({{n}} lignes)',
  'lab.echecTelechargement':    'Échec du téléchargement — voir la console.',
  'lab.telecharge':             '✓ Téléchargé : {{fichier}} ({{n}} lignes)',
  'lab.rechercherSku':          'Rechercher un modèle SKU…',
  'lab.chargementSegments':     'Chargement des segments…',
  'lab.rechercherBatch':        'Rechercher un batch (semaine)…',
  'lab.aucunBatch':             'Aucun batch sélectionné',
  'lab.batchLigne':             '{{semaine}} — {{date}} ({{n}} lignes)',
  'lab.accesRefuseVue':         'Accès refusé (lab.view requis)',
  'lab.hubRequetes':            'Hub Requêtes',
  'lab.hubSousTitre':           'Gérez vos requêtes sauvegardées et les templates Orbicore',
  'lab.permSaveRequise':        'Permission lab.save_query requise',
  'lab.permShareRequise':       'Permission lab.share_query requise',
  'lab.sauverCourante':         'Sauvegarder la requête courante du Composer',
  'lab.plusSauverCourante':     '+ Sauvegarder requête courante',
  'lab.retourComposer':         '← Composer',
  'lab.mesRecentes':            'Mes requêtes récentes',
  'lab.aucuneRequete':          'Aucune requête sauvegardée',
  'lab.affichageSur':           'Affichage : 6 / {{n}}',
  'lab.erreurChargement':       'Erreur de chargement — {{msg}}',
  'lab.aucuneRequetePoint':     'Aucune requête sauvegardée.',
  'lab.construireRequete':      'Construire une requête',
  'lab.partageesAvecMoi':       'Partagées avec moi',
  'lab.aucunePartagee':         'Aucune requête partagée',
  'lab.erreurMsg':              'Erreur — {{msg}}',
  'lab.rienPartageAvecVous':    'Aucune requête n\'a été partagée avec vous.',
  'lab.visibilitePrivee':       'Privée',
  'lab.visibilitePartagee':     'Partagée',
  'lab.visibiliteOrga':         'Organisation',
  'lab.executeN':               'Exécuté {{n}}×',
  'lab.dernier':                'Dernier : {{date}}',
  'lab.charger':                'Charger',
  'lab.dupliquer':              'Dupliquer',
  'lab.renommer':               'Renommer',
  'lab.partager':               'Partager',
  'lab.utiliser':               'Utiliser',
  'lab.copier':                 'Copier',
  'lab.dupliquerPerso':         'Dupliquer comme requête perso',
  'lab.sauverRequete':          'Sauvegarder la requête',
  'lab.renommerRequete':        'Renommer la requête',
  'lab.exempleNom':             'ex. Sell-out {{marque}} Retail CENTRE ytd',
  'lab.descOptionnelle':        'Description optionnelle…',
  'lab.tagsAide':               '(séparés par des virgules, max 10)',
  'lab.exempleTags':            'retail, sell-out, {{marque}}',
  'commun.sauvegardeEnCours':      'Sauvegarde…',
  'commun.enregistrer':            'Enregistrer',
  'lab.partagerRequete':        'Partager la requête',
  'lab.visibilite':             'Visibilité',
  'lab.idsUtilisateurs':        'IDs utilisateurs',
  'lab.uuidsAide':              '(UUIDs séparés par virgule ou espace)',
  'lab.aucunUuid':              'Aucun UUID valide détecté.',
  'lab.nUtilisateur':           '{{n}} utilisateur sélectionné',
  'lab.nUtilisateurs':          '{{n}} utilisateurs sélectionnés',
  'lab.miseAJour':              'Mise à jour…',
  'lab.visPriveeLongue':        'Privée (moi uniquement)',
  'lab.visPartageeLongue':      'Partagée (utilisateurs choisis)',
  'lab.visOrgaLongue':          'Toute l\'organisation',
  'lab.supprimerRequete':       'Supprimer la requête',
  'commun.suppressionEnCours':     'Suppression…',
  'lab.confirmSupprPrefix':     'Supprimer ',
  'lab.confirmSupprSuffix':     ' ? Cette action est réversible via l’administration.',

  // ── `#837` B2 · Labo — resultats, rendus, surfaces ──────────────────────
  'lab.typeAnalyse':                 'Type d\'analyse',
  'lab.formatSortie':                'Format de sortie',
  'lab.lieux':                       'Lieux',
  'lab.executionEnCours':            'Exécution en cours…',
  'lab.erreurMoteur':                'Le moteur SSL a rencontré une erreur inattendue lors de l\'exécution de cette requête.',
  'lab.erreurMoteurHint':            'Cette combinaison de KPI, période et cible n\'est pas encore supportée. Détail technique en console navigateur (F12).',
  'lab.configurezRequete':           'Configurez votre requête pour voir l\'aperçu ici',
  'lab.aucunResultatCriteres':       'Aucun résultat trouvé pour ces critères.',
  'lab.nLigne':                      '{{n}} ligne',
  'lab.nLignes':                     '{{n}} lignes',
  'lab.aucunResultat':               'Aucun résultat.',
  'lab.aucunResultatRequete':        'Aucun résultat pour cette requête.',
  'lab.aucunClassement':             'Aucun classement disponible pour ces critères.',
  'lab.donneesInsuffisantes':        'Données insuffisantes',
  'lab.aucuneValeurPeriode':         'Aucune valeur sur la période sélectionnée. Vérifiez la période ou les filtres actifs.',
  'lab.aucuneValeurPeriodeCourt':    'Aucune valeur sur la période sélectionnée.',
  'lab.verifiezPeriode':             'Vérifiez la période ou les filtres actifs.',
  'lab.estimation':                  'Estimation…',
  'lab.erreurEstimation':            'Erreur d\'estimation',
  'lab.zeroEstime':                  '⚠ 0 résultat estimé — vérifiez vos filtres',
  'lab.nLigneEstimee':               '~{{n}} ligne estimée',
  'lab.nLignesEstimees':             '~{{n}} lignes estimées',
  'lab.executionCourte':             'Exécution…',
  'lab.lancerAnalyse':               'Lancer l\'analyse',
  'lab.comboMax2':                   '2 KPIs maximum en mode combo, désélectionnez-en {{n}}.',
  'lab.comboUneMarque':              'Le mode combo compare 2 indicateurs sur une seule marque. Sélectionnez une marque unique.',
  'lab.totalPopulation':             'Total population :',
  'lab.referenceMarche':             'Référence marché :',
  'lab.baseEcartMarche':             '(base Δ marché)',
  'lab.colRang':                     'Rang',
  'lab.colEntite':                   'Entité',
  'lab.colPctPop':                   '% pop.',
  'lab.colCumul':                    'Cumul %',
  'lab.colEcartLeader':              'Δ leader',
  'lab.colEcartMarche':              'Δ marché',
  'lab.epinglee':                    'Épinglée',
  'lab.chartCourbe':                 'Courbe',
  'lab.chartBarres':                 'Barres',
  'lab.chartListe':                  'Liste',
  'lab.chartTableau':                'Tableau',
  'lab.chartDoubleAxe':              'Double axe',
  'lab.affichage':                   'Affichage :',
  'lab.typeGraphique':               'Type de graphique',
  'lab.auto':                        'Auto ({{type}})',
  'lab.resultat':                    'Résultat',
  'lab.rechercherResultats':         'Rechercher dans les résultats…',
  'lab.filtreeSur':                  ' (filtrée sur "{{recherche}}")',
  'lab.grandTotal':                  'Grand total :',
  'lab.surfListe':                   'Lab — liste',
  'lab.surfCroise':                  'Lab — tableau croisé',
  'lab.surfCroise2D':                'Lab — tableau croisé 2D',
  'lab.surfTableau':                 'Lab — tableau',
  'lab.surfClassement':              'Lab — classement',

  // ── `#837` B2 · Labo — pivot rendu, panneau de filtres, graphe ──────────
  'lab.selectionValeurs':       'Sélection des valeurs',
  'lab.application':            'Application…',
  'lab.filtreBientot':          'Filtre sur cette dimension disponible prochainement (grande cardinalité — sélecteur avec recherche).',
  'lab.erreurValeurs':          'Erreur de chargement des valeurs.',
  'lab.aucuneValeurDispo':      'Aucune valeur disponible.',
  'lab.rechercherDimension':    'Rechercher {{dimension}}…',
  'lab.moyenne':                'Moyenne',
  'lab.totalPool':              'Total (pool)',
  'lab.totalDistinct':          'Total (distinct)',
  'lab.badgeEnCours':           ' (en cours)',
  'lab.tableauPivot':           'Tableau pivot',
  'lab.suffixeBarres':          ' · {{kpi}} (barres) · {{kpi2}} (ligne)',

  // ── `#837` back · Labo — les 6 messages anglais et les 4 codes nus ──────

  // ── `#839` T1 · CHROME — la coquille que tous les modules traversent ────
  'commun.rechercher':            'Rechercher',
  'commun.rechercheGlobale':      'Recherche globale (Cmd+K)',
  'commun.rechercherQuoi':        'Rechercher un PDV, marque, module…',
  'commun.rechercheEnCours':      'Recherche en cours…',
  'commun.recherchePoints':       'Recherche…',
  'commun.accesRapide':           'Accès rapide',
  'commun.aucunResultatPour':     'Aucun résultat pour « {{recherche}} »',
  'commun.naviguer':              'naviguer',
  'commun.selectionnerTouche':    'sélectionner',
  'commun.toucheEchap':           'Échap',
  'commun.fermerMinuscule':       'fermer',
  'commun.modele':                'Modèle',
  'commun.barreNavigation':       'Barre de navigation',
  'commun.filAriane':             'Fil d\'Ariane',
  'commun.selectionnerModule':    'Sélectionner un module',
  'commun.menuUtilisateur':       'Menu utilisateur',
  'commun.widgetsNavigation':     'Widgets navigation',
  'commun.accueilDashboard':      'MX SFMT Dashboard — Accueil',
  'commun.reconciliation':        'Réconciliation',
  'commun.seDeconnecter':         'Se déconnecter',
  'theme.modeClair':              'Mode clair',
  'theme.modeSombre':             'Mode sombre',
  'commun.quitterPleinEcran':     'Quitter le plein écran',
  'commun.pleinEcran':            'Plein écran',
  'commun.precedent':             'Précédent',
  'commun.suivant':               'Suivant',
  'commun.paginationSur':         '{{a}}–{{b}} sur {{total}}',
  'commun.aucuneDonneeDispo':     'Aucune donnée disponible.',
  'commun.erreurReact':           '⚠ Erreur React capturée',

  // ── `#839` T1 · les routes sans nom au catalogue (FR inchangé, EN ajouté) ───
  'nav.marcheRetailWholesale':    'Marché Retail / Wholesale',
  'nav.catalogueFacteurs':        'Catalogue facteurs',
  'nav.assistantImport':          'Assistant import',
  'nav.validationSkus':           'Validation SKUs',
  'nav.validationPdvs':           'Validation PDVs',
  'nav.validationMerchs':         'Validation Merchs',
  'nav.validationBrands':         'Validation marques',
  'nav.utilisateurs':             'Utilisateurs',
  'nav.monitoringSysteme':        'Monitoring système',
  'nav.alertesSysteme':           'Alertes système',
  'nav.pilotageQualite':          'Pilotage Qualité',
  'nav.phenomenesMarche':         'Phénomènes Marché',

  // ── `#839` T1 · deux cles de plus, mesurees sur pieces ──────────────────
  'nav.modules':             'Modules',
  'commun.nNotifNonLues':    '{{n}} notification(s) non lue(s)',

  // ── `#839` T1 · la marque cliente dans le chrome ────────────────────────
  'commun.marqueClientePays':    '{{marque}} Algeria',

  // ── `#840` T2 · AUTH — les trois pages ──────────────────────────────────
  'auth.connexion':                   'Connexion',
  'auth.adresseEmail':                'Adresse e-mail',
  'auth.connexionEnCours':            'Connexion…',
  'auth.seConnecter':                 'Se connecter',
  'auth.emailInvalide':               'Adresse e-mail invalide',
  'auth.motDePasseRequis':            'Mot de passe requis',
  'auth.identifiantsIncorrects':      'Email ou mot de passe incorrect.',
  'auth.tropDeTentatives':            'Trop de tentatives, réessayez dans 15 minutes.',
  'audit.stats.titre':                'Statistiques d’usage',
  'audit.stats.connexions':           'Connexions par jour',
  'audit.stats.ecrans':               'Actions les plus fréquentes',
  'audit.stats.derniers':             'Dernier accès par utilisateur',
  'audit.stats.vide':                 'Aucune donnée sur la période.',
  'auth.exempleEmail':                'votre@email.com',
  'auth.sessionJetonRejoue':          'Un jeton de session déjà utilisé a été présenté. Par précaution, votre session a été fermée. Si vous ne vous reconnaissez pas dans cette action, prévenez un administrateur.',
  'auth.sessionDureeMax':             'Votre session a atteint sa durée maximale et s\'est fermée. Ce n’est pas une panne : reconnectez-vous pour continuer.',
  'auth.sessionInactivite':           'Votre session s\'est fermée après 30 minutes sans activité. Ce n’est pas une panne : reconnectez-vous pour continuer.',
  'auth.verification2Etapes':         'Vérification en deux étapes',
  'auth.codeAuthentification':        'Code d\'authentification',
  'auth.codeSecours':                 'Code de secours',
  'auth.verificationEnCours':         'Vérification…',
  'auth.valider':                     'Valider',
  'auth.telephoneIndispo':            'Téléphone indisponible ? Utiliser un code de secours',
  'auth.revenirCodeAppli':            'Revenir au code de l\'application',
  'auth.retourConnexion':             'Retour à la connexion',
  'auth.saisissezCode6':              'Saisissez le code à 6 chiffres de votre application d\'authentification.',
  'auth.saisissezSecours':            'Saisissez l\'un des codes de secours reçus lors de l\'activation.',
  'auth.erreurSaisieCode6':           'Saisissez un code à 6 chiffres.',
  'auth.erreurSaisieSecours':         'Saisissez un code de secours complet (format XXXXX-XXXXX).',
  'auth.codeRefuseTotp':              'Ce code n\'a pas été accepté. Vérifiez qu\'il s\'agit du code à 6 chiffres affiché en ce moment par votre application — il change toutes les 30 secondes.',
  'auth.codeRefuseSecours':           'Ce code de secours n\'a pas été accepté. Vérifiez que vous le recopiez en entier (format XXXXX-XXXXX) et qu\'il n\'a pas déjà servi : chaque code ne fonctionne qu\'une fois.',
  'auth.secoursUneFoisDebut':         '⚠ Un code de secours ne fonctionne ',
  'auth.secoursUneFoisGras':          'qu\'une seule fois',
  'auth.secoursUneFoisFin':           ' : une fois accepté, il est consommé définitivement. Vous en aviez huit, et ils ne peuvent pas être régénérés.',
  'auth.configSecondFacteur':         'Configuration du second facteur',
  'auth.secondFacteurActive':         'Second facteur activé',
  'auth.copies':                      'Copiés',
  'auth.copierLesCodes':              'Copier les codes',
  'auth.jaiNoteMesCodes':             'J\'ai noté mes codes — continuer',
  'auth.secondFacteurDejaConfig':     'Second facteur déjà configuré',
  'auth.compteDejaSecondFacteur':     'Ce compte porte déjà un second facteur. Connectez-vous avec le code de votre application, ou avec un code de secours si vous ne l\'avez plus.',
  'auth.saisirUnCode':                'Saisir un code',
  'auth.compteExigeSecondFacteur':    'Ce compte exige un second facteur',
  'auth.ajoutezLaCle':                'Ajoutez la clé ci-dessous dans votre application d\'authentification, puis saisissez le code qu\'elle affiche. Vous n\'aurez accès à aucune donnée avant.',
  'auth.preparation':                 'Préparation…',
  'auth.cleASaisir':                  'Clé à saisir dans l\'application',
  'auth.codeAfficheAppli':            'Code affiché par l\'application',
  'auth.activerSecondFacteur':        'Activer le second facteur',
  'auth.impossiblePreparer':          'Impossible de préparer le second facteur.',
  'auth.erreurSaisieCode6Appli':      'Saisissez le code à 6 chiffres affiché par votre application.',
  'auth.codeRefuseEnrol':             'Ce code n\'a pas été accepté. Vérifiez qu\'il s\'agit du code affiché en ce moment par votre application — il change toutes les 30 secondes.',
  'auth.notezCesCodes':               '⚠ Notez ces {{n}} codes maintenant — ils ne seront plus jamais affichés.',
  'auth.chacunUneFoisDebut':          'Chacun ne fonctionne ',
  'auth.chacunUneFoisFin':            ', et ils ne peuvent pas être régénérés. Ils sont votre seul recours si vous perdez votre téléphone. Recharger cette page ne les remontrera pas.',
  // ── `#1086` · le QR d'enrôlement (décision Hkm, clôt l'arbitrage du `#548`) ──
  'auth.scannezLeQr':                 'Scannez ce code avec votre application d\'authentification.',
  'auth.qrIndisponible':              'Le code visuel n\'a pas pu s\'afficher — utilisez la clé ci-dessous, elle fait exactement la même chose.',
  'auth.ouSaisieManuelle':            'Ou saisie manuelle',
  // ⚠ `#1086` — la phrase disait « aucun QR n'est généré ici ». Il y en a un.
  'auth.saisieManuelleDebut':         'Si vous préférez, votre application peut aussi ingérer l\'adresse ',
  // ── `#1089` · le panneau d'un lot en échec dit le chemin qui marche ──
  'import.echecTitre':                 'Import en échec',
  'import.echecConsigneSupprimer':     'Une nouvelle tentative sur cette semaine sera refusée tant que ce lot existe. Supprimez-le d’abord (Historique des imports → Supprimer), puis relancez l’import.',
  'import.echecConsigneDemander':      'Une nouvelle tentative sur cette semaine sera refusée tant que ce lot existe. Demandez à un Director ou un Project manager de le supprimer, puis relancez l’import.',

  // ── `#840` T2 ★2 · AUTH — ce que le SERVEUR nomme, l'écran l'écrit ──────

  // ── `#841` T3-A · IMPORT — l'assistant hebdomadaire ─────────────────────
  'import.assistantTitre':             'Assistant d\'import hebdomadaire',
  'import.assistantSous':              'Importez les données de la semaine en 3 étapes guidées.',
  'import.tauxChange':                 'Taux de change',
  'import.fxHebdo':                    'FX rate hebdomadaire',
  'import.fichiersSource':             'Fichiers source',
  'import.rawdataActivite':            'Rawdata + activité',
  'import.validationLancement':        'Validation & lancement',
  'import.fxPourSemaine':              'FX rate DA/USD pour la semaine à importer',
  'import.semaineIso':                 'Semaine ISO',
  'import.tauxConversionOfficiel':     'Taux de conversion officiel',
  'import.contexteSemaine':            'Contexte de la semaine',
  'import.commentairesDivers':         'Commentaires divers',
  'import.noteAbsence':                'Note d\'absence',
  'import.seraJointRH':                'Sera joint au commentaire du batch comme contexte RH.',
  'import.exSemaine':                  'ex : 2026-W18',
  'import.exTaux':                     'ex : 135.5',
  'import.remarquesTaux':              'Remarques sur ce taux de change…',
  'import.absencesConges':             'Absences, congés, événements terrain cette semaine…',
  'import.validerTaux':                'Valider le taux',
  'import.erreurTaux':                 'Erreur lors de l\'enregistrement du taux.',
  'import.rawdataRetail':              '1. Rawdata Retail',
  'import.rawdataWholesale':           '2. Rawdata Wholesale',
  'import.rapportActivite':            '3. Rapport d\'activité',
  'import.requis':                     'Requis',
  'import.optionnel':                  'Optionnel',
  'import.descRetail':                 '23 colonnes · PDV / SKU / Semaine / Marque',
  'import.descWholesale':              '23 colonnes · ancrées sur REGION · channel = wholesale',
  'import.descActivite':               'Visites terrain · PDV Code / Visit Date',
  'import.labelRetail':                'Rawdata Retail',
  'import.labelWholesale':             'Rawdata Wholesale',
  'import.labelActivite':              'Rapport d\'activité',
  'import.lignesValidesRejetees':      '{{v}} lignes valides · {{r}} rejetées',
  'import.importe':                    'Importé',
  'import.erreurImportRawdata':        'Erreur lors de l\'import rawdata',
  'import.erreurImportWholesale':      'Erreur lors de l\'import wholesale',
  'import.erreurImportActivite':       'Erreur lors de l\'import activité',
  'import.colonnesRequises':           'Colonnes requises : ',
  'import.formatAttendu':              'Format attendu : 23 colonnes canoniques ancrées sur ',
  'import.retourEtape1':               '← Retour étape 1',
  'import.retourEtape2':               '← Retour étape 2',
  'import.continuerReconciliation':    'Continuer vers réconciliation',
  'import.recommencerDebut':           'Recommencer depuis le début',
  'import.validationCroisee':          'Validation croisée rawdata × activité',
  'import.lignesValides':              'lignes valides',
  'import.nonUploade':                 'non uploadé',
  'import.activite':                   'Activité',
  'import.reconciliationExplique':     'La réconciliation croise les deux fichiers pour vérifier la cohérence des visites. Le rapport sera accessible immédiatement après le lancement.',
  'import.phaseEnvoi':                 'envoi…',
  'import.phaseCalcul':                'calcul en cours…',
  'import.phaseFile':                  'job mis en file, en attente du worker…',
  'import.reconEnCoursPhase':          'Réconciliation en cours… ({{phase}})',
  'import.jobArrierePlanDebut':        'Le job tourne en arrière-plan (BullMQ). Ne fermez pas l\'onglet — la page rapport s\'ouvrira automatiquement quand le statut passera à ',
  'import.jobArrierePlanMilieu':       '. Volume traité : ',
  'import.jobArrierePlanFin':          ' lignes rawdata.',
  'import.batchFailedDebut':           'Le batch est en statut ',
  'import.batchFailedFin':             ' — vous pouvez relancer le bouton « Lancer la réconciliation » ci-dessous, ou consulter les logs côté serveur pour identifier la cause.',
  'import.workerEchoue':               'Le worker de réconciliation a échoué.',
  'import.erreurLancementRecon':       'Erreur lors du lancement de la réconciliation.',
  'import.postNonEnqueue':             'Le POST n\'a pas pu être enqueué (Redis indisponible ?). Vérifiez l\'état du serveur, puis relancez.',
  'import.envoi':                      'Envoi…',
  'import.reconEnCours':               'Réconciliation en cours…',
  'import.relancerReconciliation':     'Relancer la réconciliation',
  'import.lancerReconciliation':       'Lancer la réconciliation',
  'import.glissezFichier':             'Glissez un fichier ici ou cliquez pour parcourir',
  'import.retirer':                    'Retirer',
  'import.zoneDepot':                  'Zone de dépôt pour {{label}}',
  'import.envoiEnCours':               'Envoi en cours',
  'import.retirerFichier':             'Retirer le fichier sélectionné',
  'import.etapesWizard':               'Étapes du wizard',

  // ── `#841` T3-B · IMPORT — historique, statuts, publication ─────────────
  'import.historiqueTitre':         'Historique des imports',
  'import.historiqueSous':          'Tous les batches d\'import hebdomadaires',
  'import.nouvelImport':            'Nouvel import',
  'import.reinitFiltres':           'Réinitialiser les filtres',
  'import.impossibleCharger':       'Impossible de charger l\'historique.',
  'import.aucunBatch':              'Aucun batch trouvé.',
  'import.essayezFiltres':          'Essayez de modifier les filtres.',
  'import.tousStatuts':             'Tous les statuts',
  'commun.filtrerStatut':           'Filtrer par statut',
  'import.filtrerSemaine':          'Filtrer par semaine ISO',
  'import.phSemaineIso':            'Semaine ISO (ex : 2025-W18)',
  'commun.enAttente':               'En attente',
  'import.stPartiel':               'Partiel',
  'import.stCharge':                'Chargé',
  'import.stValidation':            'Validation',
  'import.stEnRevue':               'En revue',
  'import.stApprouve':              'Approuvé',
  'import.stApprouveForce':         'Approuvé (forcé)',
  'import.stEchec':                 'Échec',
  'import.stFinalisePartiel':       'Finalisé (partiel)',
  'import.stRejete':                'Rejeté',
  'import.colLignesRD':             'Lignes RD',
  'import.colLignesWHS':            'Lignes WHS',
  'import.colLignesACT':            'Lignes ACT',
  'import.colCreeLe':               'Créé le',
  'import.colPublication':          'Publication',
  'commun.action':               'Action',
  'import.titreTauxSaisi':          'Taux de change DA / USD saisi au wizard',
  'import.titreFichierRD':          'Fichier RAWDATA retail',
  'import.titreLignesRD':           'Lignes valides retenues du fichier RAWDATA retail',
  'import.titreFichierWHS':         'Fichier RAWDATA wholesale',
  'import.titreLignesWHS':          'Lignes valides retenues du fichier RAWDATA wholesale',
  'import.titreFichierACT':         'Fichier Activity Report',
  'import.titreLignesACT':          'Lignes valides retenues du fichier Activity Report',
  'import.nonImporte':              'non importé',
  'import.voirRapport':             'Voir le rapport de réconciliation',
  'import.rapportFleche':           'Rapport →',
  'import.supprimerTraces':         'Supprimer toutes les traces de cet import',
  'import.suppressionEnCours':      'Suppression en cours…',
  'import.supprimerCetImport':      'Supprimer cet import',
  'import.donneesDeCetImport':      'données de cet import',
  'import.supprimerImportSem':      'Supprimer l\'import {{semaine}}',
  'import.unResultat':              '{{n}} résultat',
  'import.nResultats':              '{{n}} résultats',
  'import.depublier':               'Dépublier',
  'import.nonPubliee':              '○ non publiée',
  'import.publicationEnCours':      'Publication…',
  'import.nonFinalisee':            'non finalisée',
  'import.depublicationEnCours':    'Dépublication…',
  'import.stATerminer':             'Import à terminer',
  'import.terminer':                'Terminer',
  'import.terminerEnCours':         'Finalisation…',
  'import.terminerErreur':          'Échec de la finalisation',
  'import.echecPublication':        'Échec de la publication.',
  'import.echecDepublication':      'Échec de la dépublication.',
  'import.publieeLe':               '● publiée le {{date}}',
  'import.publieeLePar':            '● publiée le {{date}} · {{role}}',
  'import.statutPubliable':         'Statut « {{statut}} » — publiable à partir de {{liste}}',
  'import.depublierSemaine':        'Dépublier {{semaine}}',
  'import.visibiliteMarque':        'Visibilité {{marque}} : une semaine non publiée n\'existe pas pour le client',
  'import.neVerraPlusGras':         '{{marque}} ne verra plus cette semaine',
  'import.neVerraPlusFin':          ' — ni ses chiffres, ni son existence. La semaine reste intacte chez Orbicore et peut être republiée à tout moment.',

  // ── `#841` T3-C · IMPORT — rejets, purge, hooks ─────────────────────────
  'import.verdictSeuil':                 '⛔ Semaine NON publiée — {{pct}} % de lignes rejetées dépasse le seuil de {{seuil}} %.',
  'import.autresErreurs':                '…et {{n}} autres erreurs ({{exemple}})',
  'import.regleColonne':                 '{{n}}× {{regle}} sur colonne {{colonne}}',
  'import.avecExemples':                 '{{base}} (ex. {{exemples}})',
  'import.nLignesRejetees':              '⚠ {{n}} lignes rejetées',
  'import.nLignesRejeteesMotifs':        '⚠ {{n}} lignes rejetées — motifs :',
  'import.motConfirmation':              'SUPPRIMER',
  'import.actionIrreversible':           '⚠️ Action irréversible',
  'import.supprimeraTousDebut':          'Cette action supprimera ',
  'import.supprimeraTousGras':           'TOUS',
  'import.supprimeraTousFin':            ' les {{label}} ainsi que toutes les données associées (cascade). Il sera impossible de les récupérer.',
  'import.pourConfirmerDebut':           'Pour confirmer, tapez ',
  'import.pourConfirmerFin':             ' ci-dessous :',
  'import.supprimerTousLes':             'Supprimer tous les {{label}}',
  'import.purgeDebut':                   'Cette action supprime ',
  'import.purgeToutes':                  'TOUTES',
  'import.purgeMilieu1':                 ' les traces de l\'import ',
  'import.purgeMilieu2':                 ' : ',
  'import.purgeObservations':            '{{n}} observations',
  'import.purgeFin':                     ', trust scores, anomalies, fichiers en staging et le rapport de réconciliation.',
  'import.purgeArrierePlan':             'La suppression s\'exécute en arrière-plan et peut prendre ~1 min. Irréversible.',
  'import.aucunBatchAVider':             'Aucun batch à vider.',
  'import.aucunIdBatch':                 'Aucun identifiant de batch rawdata disponible',
  'import.suppressionEchoue':            'La suppression a échoué.',
  'import.jobSuppressionIntrouvable':    'Job de suppression introuvable (worker indisponible ?).',
  'import.suppression10min':             'Suppression toujours en cours après 10 min — voir les logs serveur. Le job continue en arrière-plan.',
  'import.publicationEchoue':            'La publication a échoué.',
  'import.depublicationEchoue':          'La dépublication a échoué.',
  'import.introuvable':                  '{{resource}} introuvable',
  'import.suppressionContrainte':        'La suppression a échoué (contrainte de données).',

  // ── `#842` T4-A · ADMIN — parametres, configuration, utilisateurs ───────
  'admin.aucuneSection':               'Aucune section accessible.',
  'admin.compte':                      'Compte',
  'admin.nomComplet':                  'Nom complet',
  'admin.roleS':                       'Rôle(s)',
  'admin.nonConnecte':                 'Utilisateur non connecté.',
  'admin.listesDiffusion':             'Listes de diffusion du rapport email',
  'admin.listesIndispo':               'Listes indisponibles.',
  'admin.aideListeComplete':           'Version COMPLÈTE — toutes les sources, SFM Perf et Data Accuracy compris.',
  'admin.aideListeFiltree':            'Version FILTRÉE — le marché, jamais les alertes internes sur l’équipe.',
  'admin.aucunDestinataire':           'aucun destinataire — cet envoi ne partira pas',
  'admin.unDestinataire':              '{{n}} destinataire',
  'admin.nDestinataires':              '{{n}} destinataires',
  'admin.uneAdresseParLigne':          'une adresse par ligne',
  'admin.listeNom':                    'Liste {{nom}}',
  'admin.enregistre':                  'Enregistré',
  'admin.enregistrementImpossible':    'enregistrement impossible',
  'admin.configSysteme':               'Configuration système',
  'admin.seuilsLimites':               'Seuils et limites — modifiables par SA/AO uniquement',
  'admin.configErreur':                'Impossible de charger la configuration. Réessayez.',
  'admin.aucuneEntreeConfig':          'Aucune entrée de configuration.',
  'admin.misAJourLe':                  'Mis à jour le',
  'admin.modSysteme':                  'Système',
  'admin.modJournalAudit':             'Journal d\'audit',
  'admin.gestionUtilisateurs':         'Gestion utilisateurs',
  'admin.nouvelUtilisateur':           'Nouvel utilisateur',
  'admin.usersErreur':                 'Impossible de charger les utilisateurs. Réessayez.',
  'admin.aucunUtilisateur':            'Aucun utilisateur trouvé.',
  'admin.comptesPlateforme':           'Comptes utilisateurs de la plateforme',
  'admin.unUtilisateur':               '{{n}} utilisateur',
  'admin.nUtilisateurs':               '{{n}} utilisateurs',
  'admin.precedentFleche':             '← Précédent',
  'commun.suivantFleche':               'Suivant →',
  'admin.editer':                      'Éditer',
  'admin.aucun':                       'Aucun',
  'admin.rechercherEmailNom':          'Rechercher email / nom',
  'admin.rechercherUtilisateur':       'Rechercher un utilisateur',
  'admin.tousStatuts':                 'Tous statuts',
  'admin.stSuspendu':                  'Suspendu',
  'admin.stDesactive':                 'Désactivé',
  'admin.role.SUPER_ADMIN':            'Orbicore – Director',
  'admin.role.ADMIN_ORBICORE':         'Orbicore – PM',
  'admin.role.CP_JUNIOR':              'Orbicore – Junior PM',
  'admin.role.ANALYST_ORBICORE':       'Orbicore – Data analyst',
  'admin.role.MANAGER_ORBICORE':       'Orbicore – Controller',
  'admin.role.SUPPORT_ORBICORE':        'Orbicore – Support', // `#1091`
  'admin.editerUtilisateur':           'Éditer l\'utilisateur',
  'admin.statutActuel':                'Statut actuel :',
  'admin.nouveauStatut':               'Nouveau statut',
  'admin.inchange':                    '— Inchangé —',
  'admin.desactiveIrreversible':       'Ce compte est désactivé de façon irréversible.',
  'admin.erreurMaj':                   'Erreur lors de la mise à jour. Réessayez.',
  'admin.enregistrementEnCours':       'Enregistrement…',
  'admin.rolesTitre':                  'Rôles',
  'admin.chargementRoles':             'Chargement des rôles…',
  'admin.rolesIntrouvables':           'Impossible de charger les rôles de ce compte.',
  'admin.aucunRoleAttribue':           'Aucun rôle attribué.',
  'admin.retirerRole':                 'Retirer le rôle {{role}}',
  'admin.retraitRefuse':               'Retrait refusé.',
  'admin.roleAAjouter':                'Rôle à ajouter',
  'admin.tousRolesAttribues':          '— Tous les rôles sont attribués —',
  'admin.choisirRole':                 '— Choisir un rôle —',
  'commun.ajouter':                     'Ajouter',
  'admin.ajoutEnCours':                'Ajout…',
  'admin.attributionRefusee':          'Attribution refusée.',
  'admin.secondFacteur':               'Second facteur (2FA)',
  'admin.etatActuel':                  'État actuel : ',
  'admin.configureParUtilisateur':     'configuré par l’utilisateur',
  'admin.nonConfigure':                'non configuré',
  'admin.exigerSecondFacteur':         'Exiger un second facteur',
  'admin.exigerSecondFacteur2fa':      'Exiger un second facteur (2FA)',
  'admin.decocherNEffacePas':          'Décocher n\'efface pas le second facteur déjà configuré : le compte continuera de le réclamer à la connexion.',
  'admin.devraConfigurer':             'Le compte devra le configurer à sa prochaine connexion, sans accès aux données avant.',
  'admin.compteBloqueDebut':           'Compte bloqué ? Effacer son second facteur lui rend l’accès. Il devra le reconfigurer si l’exigence reste posée. ',
  'admin.codesSecoursInvalides':       'Ses codes de secours restants seront invalidés.',
  'admin.reinitSecondFacteur':         'Réinitialiser le second facteur',
  'admin.reinitEnCours':               'Réinitialisation…',
  'admin.secondFacteurEfface':         'Second facteur effacé',
  'admin.codesInvalides':              ' — {{n}} code(s) de secours invalidé(s).',
  'admin.peutSeReconnecter':           ' Le compte peut se reconnecter.',
  'admin.reinit2faEchouee':            'La réinitialisation du second facteur a échoué.',
  'commun.motDePasse':                  'Mot de passe',
  'admin.motDePasseTemporaire':        'Mot de passe temporaire',
  'admin.afficheUneSeuleFois':         '(affiché une seule fois)',
  'admin.sessionsRevoquees':           ' — toutes les sessions de ce compte ont été révoquées.',
  'admin.copierMotDePasseTemp':        'Copier le mot de passe temporaire',
  'admin.copierMotDePasse':            'Copier le mot de passe',
  'admin.communiquerSecurise':         'Communiquer ce mot de passe à l’utilisateur de manière sécurisée.',
  'admin.reinitMotDePasse':            'Réinitialiser le mot de passe',
  'admin.generationEnCours':           'Génération…',
  'admin.reinitRefusee':               'Réinitialisation refusée.',
  'admin.creerUtilisateur':            'Créer un utilisateur',
  'admin.utilisateurCree':             'Utilisateur créé avec succès',
  'admin.termine':                     'Terminé',
  'admin.roleInitial':                 'Rôle initial',
  'admin.sansRole':                    '— Sans rôle —',
  'admin.premiereConnexion2fa':        'À sa première connexion, ce compte devra configurer une application d\'authentification. Il n\'aura accès à aucune donnée avant de l\'avoir fait.',
  'admin.erreurCreation':              'Erreur lors de la création. Réessayez.',
  'admin.creationEnCours':             'Création…',
  'admin.phPrenomNom':                 'Prénom Nom',

  // ── `#842` T4-B · ADMIN — calibrage P1 et seuils P9 ─────────────────────
  'admin.calibrageAmpleur':            'Calibrage des seuils d\'ampleur',
  'admin.phenomenesP1':                'Phénomènes P1 — par KPI · ',
  'admin.editable':                    'éditable',
  'admin.lectureSeule':                'lecture seule',
  'admin.semaineReference':            ' · semaine de référence ',
  'admin.calibrageNatifTitre':         'Calibrage en valeurs natives',
  'admin.calibrageNatif1':             ' — vous saisissez des seuils dans l\'unité du KPI (pt, $, PDV …). Le système en dérive le seuil ',
  'admin.calibrageNatifRelatif':       'relatif (%)',
  'admin.calibrageNatif2':             ' sur la base nationale de la semaine de calibrage, qui reste figée — c\'est ce ',
  'admin.calibrageNatifMotRelatif':    'relatif',
  'admin.calibrageNatif3':             ' que le moteur de phénomènes utilise pour classer.',
  'admin.deriveTitre':                 'Dérive',
  'admin.derive1':                     ' — au fil des semaines, la base nationale bouge. Si elle s’écarte de plus de ',
  'admin.derive2':                     ' de la base figée, un badge ambré propose un ',
  'admin.deriveRecalage':              'recalage',
  'admin.derive3':                     ' pour réaligner le relatif sans changer la cible métier en natif.',
  'admin.lectureSeulePerm1':           'Lecture seule — la permission ',
  'admin.lectureSeulePerm2':           ' est requise pour éditer.',
  'admin.seuilsErreur':                'Impossible de charger les seuils.',
  'admin.aucunSeuilSeede':             'Aucun seuil seedé — exécutez le seed phenomena.',
  'admin.colNature':                   'Nature',
  // `#949` — « Description » n'existait nulle part au catalogue : clé neuve.
  'admin.colDescription':              'Description',
  // `#949` — le bandeau dit ce que l'écran règle VRAIMENT (`#946`).
  'admin.calibrageDetection':          'Un phénomène naît d\'un mouvement inhabituel de la valeur nationale du KPI (record, série, écart à la moyenne, accélération, an sur an). Les seuils ci-dessous ne décident pas de sa naissance : ils fixent à partir de quel écart il est modéré, puis majeur.',
  'admin.colMajeurNatif':              'Majeur (natif)',
  'admin.colModereNatif':              'Modéré (natif)',
  'admin.colBaseDerive':               'Base & dérive',
  'admin.colEtat':                     'État',
  'admin.calculEnEchec':               '⚠ calcul en échec',
  'admin.calculEnEchecTitre':          'Le calcul de cet indicateur a ÉCHOUÉ — ce n\'est pas une absence de données, c\'est un défaut à corriger.',
  'admin.sansDonneesSemaine':          'sans données cette semaine',
  'admin.sansDonneesTitre':            'La source de cet indicateur ne couvre pas cette semaine — il n\'y a rien à calculer. Ce n\'est pas un défaut.',
  'admin.baseActuelle':                'base actuelle ',
  'admin.pasDeReference':              'pas de référence (mode relatif legacy)',
  'admin.refCourt':                    'réf. ',
  'admin.actuelleCourt':               'actuelle ',
  'admin.deriveValeur':                'dérive {{valeur}}',
  'admin.deriveAmbreTitre':            'Dérive ≥ 20 % : la base a notablement bougé depuis la calibration, recale recommandé.',
  'admin.deriveSousSeuil':             'Dérive sous le seuil de tolérance.',
  'admin.calibreNatifLe':              'calibré natif le {{date}}',
  'admin.relatifLegacy':               'relatif legacy',
  'admin.defaut':                      'défaut',
  'admin.ordreSeuils':                 'majeur > modéré > 0',
  'admin.calibrageImpossibleTitre':    'Calibrage natif impossible : base nationale indisponible cette semaine.',
  'admin.calibrageImpossibleEdit':     'Calibrage natif impossible cette semaine — base nationale indisponible.',
  'admin.baseIndisponible422':         'Base nationale indisponible cette semaine — calibrage natif impossible.',
  'admin.echecCode':                   'Échec ({{code}})',
  'admin.reseau':                      'réseau',
  'admin.pctRelatif':                  '{{v}} % relatif',
  'admin.natifReconstruitTitre':       'Natif reconstruit depuis le seuil relatif × base courante (pas de référence figée).',
  'admin.natifNonCalculableTitre':     'Base nationale indisponible cette semaine — natif non calculable. Le moteur classe sur ce % relatif.',
  'admin.recaler':                     'Recaler',
  'admin.recalerKpi':                  'Recaler {{kpi}}',
  'admin.recalerKpiDerive':            'Recaler {{kpi}} — la base a dérivé de {{derive}}.',
  'admin.recalerKpiBase':              'Recaler {{kpi}} sur la base de la semaine courante.',
  'admin.recalerKpiTitre':             'Recaler {{kpi}} sur la semaine courante',
  'admin.editerKpi':                   'Éditer {{kpi}}',
  'admin.recalLigne1':                 'Le seuil natif reste inchangé : {{majeur}} (majeur) / {{modere}} (modéré).',
  'admin.recalLigne2':                 'La base de référence{{semaine}}est remplacée par la base actuelle ({{base}}). Le seuil RELATIF consommé par le moteur de classement est recalculé à partir d’elle.{{derive}}',
  'admin.recalSemaineDu':              ' — celle du {{semaine}} — ',
  'admin.recalDeriveActuelle':         ' Dérive actuelle : {{derive}}.',
  'admin.recalLigne3':                 'L\'écriture est immédiate et ne touche que {{kpi}}. Les {{n}} autres seuils sont inchangés.',
  'admin.seuilsP9':                    'Seuils d\'alertes P9 — Performance',
  'admin.p9SousTitre':                 '9 alertes × 2 canaux · ',
  'admin.aucunSeuilP9':                'Aucun seuil — exécutez le seed perf_alert_thresholds.',
  'commun.alerte':                   'Alerte',
  'admin.colDir':                      'Dir.',
  'admin.colJaune':                    '🟡 Jaune',
  'admin.colOrange':                   '🟠 Orange',
  'admin.colRouge':                    '🔴 Rouge',
  'admin.colCouvMin':                  'Couv. min %',
  'admin.colModifie':                  'Modifié',
  'admin.gpsDesactivee':               'désactivée — activer après chargement coords référence',
  'admin.erreurReseau':                'Erreur réseau',
  'admin.p9.COVERAGE':                 'Couverture routing (%)',
  'admin.p9.LINES_COLLECTED':          'Lignes collectées (écart rel. équipe %)',
  'admin.p9.FROZEN':                   'Lignes figées (%)',
  'admin.p9.SHORT_VISITS':             'Visites < 15 min (nombre)',
  'admin.p9.FIRST_VISIT_LATE':         '1ʳᵉ visite après 9h30 (heure)',
  'admin.p9.POS_CLOSED':               'PDV fermés (nombre)',
  'admin.p9.POS_UNVISITED':            'PDV non visité 3+ sem (%)',
  'admin.p9.TIME_PER_DAY':             'Temps/jour (bilatéral, min)',
  'admin.p9.SALES_COLLECTED':          'Collecte ventes (écart rel. équipe %)',
  'admin.p9.GPS_DISPERSION':           'Dispersion GPS begin-PDV (%)',
  'admin.unite.PCT':                   '%',
  'admin.unite.DELTA_REL':             'écart rel.',
  'admin.unite.MINUTES':               'min',
  'admin.unite.TIME':                  'heure',
  'admin.unite.COUNT':                 'nombre',
  'admin.dir.BAS':                     'Bas',
  'admin.dir.HAUT':                    'Haut',
  'admin.dir.COUNT':                   'Nombre',
  'admin.dir.HEURE':                   'Heure',
  'admin.dir.BILAT':                   'Bilatéral',

  // ── `#842` T4-C · ADMIN — segmentation et journaux d'audit ──────────────
  'admin.reglesSegmentation':         'Règles de segmentation',
  'admin.uneRegle':                   '{{n}} règle',
  'admin.nRegles':                    '{{n}} règles',
  'admin.regleActiveNom':             ' · Active : {{nom}}',
  'admin.voirCatalogue':              'Voir catalogue',
  'admin.recalculer':                 'Recalculer',
  'admin.creerRegle':                 'Créer règle',
  'admin.aucuneRegle':                'Aucune règle',
  'admin.creezPremiereRegle':         'Créez votre première règle de segmentation.',
  'admin.erreurChargementRegles':     'Erreur lors du chargement des règles.',
  'admin.unFacteur':                  '{{n}} facteur',
  'admin.nFacteurs':                  '{{n}} facteurs',
  'admin.activeeLe':                  ' · activée {{date}}',
  'admin.simuler':                    'Simuler',
  'admin.activer':                    'Activer',
  'admin.confirmer':                  'Confirmer',
  'admin.regleActivee':               'Règle "{{nom}}" activée.',
  'admin.regleSupprimee':             'Règle "{{nom}}" supprimée.',
  'admin.recalculNonLance':           'Le recalcul n\'a pas été lancé — {{cause}}. Les segments actuels sont inchangés.',
  'admin.recalculLance':              'Recalcul lancé — en attente du résultat…',
  'admin.segmentsRecalcules':         'Segments recalculés',
  'admin.nPdvSegmentes':              '{{n}} PDV segmentés',
  'admin.ontChangeSegment':           ' · {{n}} ont changé de segment',
  'admin.recalculTermine':            'Recalcul terminé — {{combien}}{{bouge}}.',
  'admin.recalculAgregatsPerimes':    'Recalcul terminé — {{combien}}. ⚠ Les agrégats n’ont pas pu être rafraîchis : les écrans PDM et Shelf Share afficheront l’ancienne répartition par segment jusqu’au prochain rafraîchissement.',
  'admin.recalculEchoue':             'Le recalcul a ÉCHOUÉ — {{cause}}.',
  'admin.causeNonRenseignee':         'cause non renseignée par le serveur',
  'admin.recalculInconnu':            'Recalcul lancé, mais toujours sans réponse. Recharge la page dans un moment pour voir le résultat.',
  'admin.confActiverRegle':           'Activer la règle « {{nom}} »',
  'admin.confDejaActive':             '« {{nom}} » est déjà la règle active. L’activer à nouveau relance un calcul complet.',
  'admin.confSeraRemplacee':          'La règle « {{nom}} », active aujourd’hui, sera désactivée et remplacée.',
  'admin.confPremierCalcul':          'Aucune règle n\'est active aujourd\'hui : ce sera le PREMIER calcul de segmentation.',
  'admin.confLigneEffet':             'Tous les PDV du référentiel seront segmentés : leur segment courant, l\'historique de segmentation et les anomalies de données insuffisantes seront écrits.',
  'admin.confActivationSurPlace':     'L\'activation calcule sur place : la page reste en attente jusqu\'au bout, elle n\'attend pas le prochain import.',
  'admin.confRelancerCalcul':         'Relancer le calcul',
  'admin.confActiverSegmenter':       'Activer et segmenter',
  'admin.confRecalculTitre':          'Recalculer tous les segments',
  'admin.confRegleReappliquee':       'La règle active « {{nom}} » sera réappliquée. Aucune règle n\'est modifiée, aucune n\'est activée ni désactivée.',
  'admin.confRecalculTacheFond':      'Le recalcul part en tâche de fond. La page attend le relevé du serveur et affiche s\'il a réussi — « lancé » n\'est pas un résultat.',
  'admin.regleBrouillon':             'Brouillon',
  'admin.regleActive':                'Active',
  'admin.regleArchivee':              'Archivée',
  'admin.creerUneRegle':              'Créer une règle',
  'admin.modifierLaRegle':            'Modifier la règle',
  'admin.nomRegle':                   'Nom règle',
  'admin.exRegle':                    'ex. 2026_{{marque}}_Q2',
  'admin.descriptionRegle':           'Description règle',
  'admin.dslJsonRegle':               'DSL JSON règle',
  'admin.enregistrerRegle':           'Enregistrer règle',
  'admin.catalogueFacteurs':          'Catalogue des facteurs',
  'admin.regles':                     'Règles',
  'admin.unFacteurV1':                '{{n}} facteur V1 disponible',
  'admin.nFacteursV1':                '{{n}} facteurs V1 disponibles',
  'admin.catalogueLectureSeule1':     'Catalogue en lecture seule — 5 facteurs V1 actifs. Le facteur ',
  'admin.catalogueLectureSeule2':     ' (Phase 5.I) sera activé lors de la prochaine vague.',
  'admin.defautV1':                   'Défaut V1',
  'admin.normalisation':              'Normalisation : ',
  'admin.normIdentite':               'Identité (mono-facteur)',
  'admin.normMinMax':                 'Min-max',
  'admin.plageBrute':                 'Plage brute : ',
  'admin.erreurCatalogue':            'Erreur lors du chargement du catalogue.',
  'admin.resultatsSimulation':        'Résultats simulation',
  'admin.stables':                    'Stables',
  'admin.promus':                     'Promus',
  'admin.retrogrades':                'Rétrogradés',
  'commun.nonEvalues':                 'Non évalués',
  'admin.nonEvaluesExplique':         ' : aucune observation sur la période — le moteur n’a pas pu les classer. Ce n’est pas un mauvais segment, c’est une donnée absente. Ils sont comptés dans le total pour que les parts fassent 100 %.',
  'admin.simSansRegle1':              'Aucune règle n’est active : il n’y a pas d’état précédent à comparer. La simulation ci-dessous est donc un ',
  'admin.simPremierCalcul':           'premier calcul',
  'admin.simSansRegle2':              ', pas une évolution.',
  'admin.seuilTransitions':           '⚠ Seuil de transitions dépassé (>30%) — vérifiez les facteurs.',
  'admin.simNouvelleRegle':           'Simulation (nouvelle règle)',
  'admin.regleActiveActuelle':        'Règle active actuelle',
  'admin.matriceTransition':          'Matrice de transition (lignes=avant, colonnes=après)',
  'admin.calculeEn':                  'Calculé en {{ms}}ms',
  'admin.depuisCache':                ' · depuis cache',
  'admin.auditSousTitre':             'Qui a fait quoi, quand — journal complet, lecture seule.',
  'admin.auditErreur':                'Impossible de charger les audit logs. Vérifiez votre connexion et réessayez.',
  'admin.colDate':                    'Date',
  'admin.colActeur':                  'Acteur',
  'commun.cible':                   'Cible',
  'admin.systemeAnonyme':             'Système / anonyme',
  'admin.chargementLogs':             'Chargement des logs…',
  'admin.aucunAuditLog':              'Aucun audit log trouvé pour ces critères.',
  'admin.uneEntree':                  '{{n}} entrée',
  'admin.nEntrees':                   '{{n}} entrées',
  'admin.precCourt':                  '‹ Préc',
  'admin.suivCourt':                  'Suiv ›',
  'admin.auditSucces':                'Succès',
  'admin.auditEchec':                 'Échec',
  'admin.auditRefuse':                'Refusé',
  'admin.filtresAudit':               'Filtres audit logs',
  'admin.tousLesActeurs':             'Tous les acteurs',
  'admin.filtrerActeur':              'Filtrer par acteur',
  'admin.filtrerNature':              'Filtrer par nature',
  'admin.filtrerAction':              'Filtrer par action',
  'admin.depuisDate':                 'Depuis (date)',
  'admin.jusquaDate':                 'Jusqu\'à (date)',
  'admin.refus':                      'Refus',
  'admin.refusSeulement':             'Refus seulement',
  'admin.phCodeAction':               'Code d\'action précis (ex : LOGIN, PURGED…)',
  'admin.nature.toutes':              'Toutes natures',
  'admin.nature.connexion':           'Connexions',
  'admin.nature.compte':              'Comptes & droits',
  'admin.nature.suppression':         'Suppressions & purges',
  'admin.nature.reglage':             'Réglages & seuils',
  'admin.nature.import':              'Imports & réconciliation',
  'admin.nature.export':              'Exports & Lab',
  'admin.nature.consultation':        'Consultations',

  // ── `#845` T5-A · REFERENTIELS — les parcs et l'import en masse ─────────
  'ref.colRegion':                   'RÉGION',
  'ref.colWilaya':                   'WILAYA',
  'ref.colDistrict':                 'DISTRICT',
  'ref.colMerch':                    'MERCH',
  'ref.colCodePdv':                  'CODE PDV',
  'ref.colCanal':                    'CANAL',
  'ref.colNomPdv':                   'NOM PDV',
  'ref.colSegment':                  'SEGMENT',
  'ref.colStatut':                   'STATUT',
  'ref.modifiezFiltres':             'Modifiez les filtres.',
  'ref.lignesParPage':               'Lignes par page :',
  'ref.pdvDroitsRefuses':            'Vous n\'avez pas les droits pour consulter les PDV.',
  'ref.pdvReseau':                   'Impossible de charger les PDV (réseau indisponible).',
  'ref.pdvErreur':                   'Erreur lors du chargement des PDV.',
  'ref.reessayerPdv':                'Réessayer chargement PDV',
  'ref.pagePrecedentePdv':           'Page précédente PDV',
  'ref.pageSuivantePdv':             'Page suivante PDV',
  'ref.pageN':                       'Page {{n}}',
  'ref.plageSurTotal':               '{{debut}}–{{fin}} sur {{total}}',
  'ref.nonEvalueTitre':              'Non évalué — aucune observation sur la période',
  'ref.parcPdv':                     'Parc PDV',
  'ref.actifs':                      'Actifs',
  'ref.inactifs':                    'Inactifs',
  'ref.parRegion':                   'Par région',
  'ref.parCanal':                    'Par canal',
  'ref.parSegmentPdv':               'Par segment PDV',
  'ref.parMarque':                   'Par marque',
  'ref.parSegmentPrix':              'Par segment prix',
  'ref.statsPortee':                 '⚠ Statistiques sur le référentiel entier — la liste ci-dessous est filtrée : {{n}} sur {{total}}.',
  'ref.skusErreur':                  'Erreur lors du chargement des SKUs.',
  'ref.pagePrecedenteSku':           'Page précédente SKU',
  'ref.pageSuivanteSku':             'Page suivante SKU',
  'ref.colCode':                     'Code',
  'ref.colLibelle':                  'Libellé',
  'ref.colBatterie':                 'Batterie (mAh)',
  'ref.colTaille':                   'Taille (")',
  'ref.colArriere':                  'Arrière (MP)',
  'ref.toutesWilayas':               'Toutes wilayas',
  'ref.rechercherPdv':               'Rechercher PDV',
  'ref.phCodeOuNom':                 'Code ou nom…',
  'ref.reinitFiltresPdv':            'Réinitialiser filtres PDV',
  'ref.actifsUniquement':            'Actifs uniquement',
  'ref.rechercherSku':               'Rechercher SKU',
  'ref.phCodeOuLibelle':             'Code ou libellé…',
  'ref.marqueSku':                   'Marque SKU',
  'ref.skusActifsUniquement':        'SKU actifs uniquement',
  'ref.reinitFiltresSku':            'Réinitialiser filtres SKU',
  'ref.nonSegmente':                 'Non segmenté',
  'ref.segmentCourant':              'Segment courant',
  'ref.merchandiser':                'Merchandiser',
  'ref.premiereReference':           '1ère référence',
  'ref.photoSku':                    'Photo SKU',
  'ref.fond':                        'Fond',
  'ref.texte':                       'Texte',
  'ref.echecSauvegarde':             'Échec sauvegarde',
  'ref.echecType':                   'Échec type',
  'ref.type':                        'Type',
  'ref.client':                      'Client',
  'ref.rechercherMarque':            'Rechercher marque',
  'ref.phNomOuCode':                 'Nom ou code…',
  'ref.basculerClientConcurrent':    'Basculer client / concurrent',
  'ref.fermerConfigurateur':         'Fermer configurateur couleur',
  'ref.marquesErreur':               'Erreur lors du chargement des marques.',
  'ref.template':                    'Template',
  'ref.uploadEnCours':               'Upload en cours…',
  'ref.importTermine':               'Import terminé',
  'ref.erreurUpload':                'Erreur lors de l\'upload. Vérifiez le format et la taille.',
  'ref.remplacer':                   'Remplacer',
  'ref.erreurSuppression':           'Erreur lors de la suppression.',
  'ref.choisirPhotoSku':             'Choisir une photo SKU',

  // ── `#845` T5-A bis · REFERENTIELS — les tuiles d'en-tete de page ───────
  'ref.skuAuReferentiel':        'SKU au référentiel',
  'ref.pdvAuReferentiel':        'PDV au référentiel',
  'ref.marquesAuReferentiel':    'Marques au référentiel',
  'ref.couvertureWilayas':       'Couverture wilayas',

  // ── `#845` T5-A ter · le mot Segment seul (aria du filtre) ──────────────
  'ref.colSegmentMot':    'Segment',

  // ── `#845` T5-A quater · l'import en masse, le detail ───────────────────
  'ref.importLabel':          'Import {{quoi}} (Excel)',
  'ref.colonnes':             'Colonnes : ',
  'ref.choisirFichier':       'Choisir un fichier .xlsx',
  'ref.lignesTraitees':       'lignes traitées : ',
  'ref.nCreees':              '{{n}} créées',
  'ref.nMisesAJour':          '{{n}} mises à jour',
  'ref.uneLigneIgnoree':      '{{n}} ligne ignorée',
  'ref.nLignesIgnorees':      '{{n}} lignes ignorées',
  'ref.detailsUneLigne':      '▸ Détails ({{n}} ligne)',
  'ref.detailsNLignes':       '▸ Détails ({{n}} lignes)',
  'ref.etNAutresIgnorees':    '… et {{n}} autres lignes ignorées',

  // ── `#845` T5-A quinquies · marques et fiche PDV, le detail ─────────────
  'ref.sauvegarde':     'Sauvegardé',

  // ── `#845` T5-A sexies · la photo SKU (SkuPhotoUploader, VIVANT) ────────
  'ref.choisirPhoto':    'Choisir une photo (JPEG / PNG / WebP ≤ 5 Mo)',

  // ── `#845` T5-B · REFERENTIELS — les pages `pending-*` et les orphelins ───
  'ref.pendingResolu':              '✓ Résolu → {{cible}}',
  'ref.pendingCree':                '✓ Créé ({{cible}})',
  'ref.pendingIgnore':              'Ignoré',
  'ref.exporterCsv':                'Exporter CSV',
  'ref.actualiser':                 'Actualiser',
  'ref.masquerResolues':            'Masquer résolues',
  'ref.masquerResolus':             'Masquer résolus',
  'ref.telechargerCsvPrerempli':    'Télécharger un CSV pré-rempli pour bulk-import',
  'ref.skusAttenteErreur':          'Impossible de charger les SKUs en attente.',
  'ref.aucunSkuAttente':            'Aucun SKU en attente de validation.',
  'ref.modeleBrut':                 'Modèle brut',
  'ref.marqueBrute':                'Marque brute',
  'ref.marquesDetectees':           'Ces marques ont été détectées dans les fichiers rawdata mais ne correspondent à aucune marque référentielle.',
  'ref.aucuneMarqueAttente':        'Aucune marque en attente de validation.',
  'ref.marquesAttenteErreur':       'Impossible de charger les marques en attente.',
  'ref.verifierMarquesCreees':      'Vérifier si des marques ont été créées depuis le dernier import',
  'ref.zeroPii':                    'Protection des données personnelles — ZÉRO PII',
  'ref.aucunNomNiEmail':            'Aucun nom ni email n\'est stocké ici',
  'ref.merchsAttenteErreur':        'Impossible de charger les agents en attente.',
  'ref.aucunAgentInconnu':          'Aucun agent inconnu en attente.',
  'ref.idAnonyme':                  'ID anonyme',
  'ref.pdvObserve':                 'PDV observé',
  'ref.premiereVisite':             'Première visite',
  'ref.telechargerCsvAgents':       'Télécharger un CSV listant les agents inconnus pour résolution RH',
  'ref.pdvsAttenteErreur':          'Impossible de charger les PDVs en attente.',
  'ref.aucunPdvAttente':            'Aucun PDV en attente de validation.',
  'ref.donneeDisponible':           'Donnée disponible — pré-remplie dans le CSV',
  'ref.donneeManquante':            'Donnée manquante — à compléter dans le CSV',
  'ref.disponible':                 'disponible',
  'ref.manquant':                   'manquant',
  'ref.colCodePdvMin':              'Code PDV',
  'ref.colSource':                  'Source',
  'ref.telechargerCsvPdv':          'Télécharger un CSV pré-rempli pour bulk-import PDV',
  'ref.toutAccepter':               'Tout accepter',
  'ref.toutRejeter':                'Tout rejeter',
  'ref.aucunOrphelin':              'Aucun orphelin rawdata en attente ✓',
  'ref.aucunPdvOuvert':             'Aucun PDV ouvert en attente ✓',
  'ref.aucunPdvFerme':              'Aucun PDV fermé.',
  'ref.autoAccepte':                'Auto-accepté',
  'ref.colDateVisite':              'DATE VISITE',
  'ref.colLignesRd':                'LIGNES RD',
  'ref.colVentesVol':               'VENTES VOL.',
  'ref.colActions':                 'ACTIONS',
  'ref.accepter':                   'Accepter',
  'ref.rejeter':                    'Rejeter',
  'ref.aucunImportEnCours':         'Aucun import en cours',
  'ref.importezPourAcceder':        'Importez et réconciliez un fichier pour accéder à cette page.',
  'ref.rfidRawdata':                'RFID / Rawdata',
  'ref.batchStatutFin':             ' — statut ',
  'ref.pdvRawdataSansRfid':         'PDV présents dans rawdata, absents dans les activités RFID',
  'ref.pdvRfidSansRawdata':         'PDV présents dans les activités RFID, absents dans rawdata',

  // ── `#845` T5-B bis · le gabarit des quatre pages `pending-*` ───────────
  'ref.pendingSku.titre':                  'SKUs à valider',
  'ref.pendingMarque.titre':               'Marques à valider',
  'ref.pendingPdv.titre':                  'PDVs à valider',
  'ref.pendingSku.enAttente':              '{{n}} SKU en attente de résolution',
  'ref.pendingSku.nEnAttente':             '{{n}} SKUs en attente de résolution',
  'ref.pendingSku.tousResolus':            'Tous les SKUs sont résolus',
  'ref.pendingSku.autoResolu':             '✓ {{n}} SKU auto-résolu — {{reste}} restant.',
  'ref.pendingSku.autoResolus':            '✓ {{n}} SKUs auto-résolus — {{reste}} restants.',
  'ref.pendingSku.aucuneResolution':       'Aucune nouvelle résolution — {{n}} SKUs en attente.',
  'ref.pendingSku.total':                  '{{n}} SKUs au total — triés par volume de lignes concernées',
  'ref.pendingSku.detectes':               'Ces modèles (',
  'ref.pendingSku.detectesFin':            ') ont été détectés dans les fichiers rawdata mais ne correspondent à aucun SKU référentiel. Les lignes de staging ont été ',
  'ref.pendingSku.workflow':               ' cliquez ',
  'ref.pendingSku.workflowSpecs':          ' → complétez les specs (RAM, ROM, prix…) → importez via ',
  'ref.pendingSku.refSkus':                'Référentiels → SKUs',
  'ref.pendingMarque.enAttente':           '{{n}} marque en attente de résolution',
  'ref.pendingMarque.nEnAttente':          '{{n}} marques en attente de résolution',
  'ref.pendingMarque.toutesResolues':      'Toutes les marques sont résolues',
  'ref.pendingMarque.autoResolue':         '✓ {{n}} marque auto-résolue — {{reste}} restante.',
  'ref.pendingMarque.autoResolues':        '✓ {{n}} marques auto-résolues — {{reste}} restantes.',
  'ref.pendingMarque.aucuneResolution':    'Aucune nouvelle résolution — {{n}} marques en attente.',
  'ref.pendingMarque.total':               '{{n}} marques au total — triées par volume de lignes concernées',
  'ref.pendingMarque.refMarques':          'Référentiels → Marques',
  'ref.voirToutes':                        'Voir toutes',
  'ref.pendingAgent.autoResolu':           '✓ {{n}} agent auto-résolu — {{reste}} restant.',
  'ref.pendingAgent.autoResolus':          '✓ {{n}} agents auto-résolus — {{reste}} restants.',
  'ref.pendingAgent.aucuneResolution':     'Aucune nouvelle résolution — {{n}} agents en attente.',
  'ref.pendingAgent.total':                '{{n}} agents au total — triés par volume d\'activité',
  'ref.pendingPdv.enAttente':              '{{n}} PDV en attente de résolution',
  'ref.pendingPdv.nEnAttente':             '{{n}} PDVs en attente de résolution',
  'ref.pendingPdv.tousResolus':            'Tous les PDVs sont résolus',
  'ref.pendingPdv.autoResolu':             '✓ {{n}} PDV auto-résolu — {{reste}} restant.',
  'ref.pendingPdv.autoResolus':            '✓ {{n}} PDVs auto-résolus — {{reste}} restants.',
  'ref.pendingPdv.aucuneResolution':       'Aucune nouvelle résolution — {{n}} PDVs en attente.',
  'ref.pendingPdv.total':                  '{{n}} PDVs au total — triés par volume de lignes concernées',
  'ref.voirTous':                          'Voir tous',
  'ref.acceptees':                         'acceptées',
  'ref.workflowGras':                      'Workflow :',
  'ref.workflowActualiser':                ' → cliquez ',
  'ref.workflowMarquer':                   ' pour marquer les entrées résolues.',

  // ── `#845` T5-B ter · le workflow des marques ───────────────────────────
  'ref.pendingMarque.workflowColonnes':    ' → complétez les colonnes cluster et is_client_brand → importez via ',

  // ── `#845` T5-B quater · la phrase de staging ───────────────────────────
  'ref.lignesStagingAcceptees':    ' Les lignes de staging ont été ',

  // ── `#845` T5-B quinquies · les agents en attente (#065 : la formulation EST la garde) ───
  'ref.pendingAgent.titre2':               'Merchandisers à identifier',
  'ref.pendingAgent.inconnu':              '{{n}} agent inconnu en attente',
  'ref.pendingAgent.inconnus':             '{{n}} agents inconnus en attente',
  'ref.pendingAgent.tousIdentifies':       'Tous les agents sont identifiés',
  'ref.piiEntrees':                        'Ces entrées représentent des agents dont le nom/email n\'a pas été trouvé dans',
  'ref.piiFin':                            ' — le code affiché est un hash SHA256 non réversible.',
  'ref.pendingAgent.workflowUtilisez':     ' utilisez ',
  'ref.pendingAgent.workflowPlus':         ' + ',
  'ref.pendingAgent.workflowLocaliser':    ' pour localiser l\'agent via les registres RH → créez l\'entrée ',
  'ref.pendingAgent.workflowVia':          ' via ',
  'ref.refMerchandisers':                  'Référentiels → Merchandisers',

  // ── `#845` T5-B sexies · les PDV en attente ─────────────────────────────
  'ref.pendingPdv.detectes':          'Ces codes PDV ont été détectés dans les fichiers rawdata ou activity report mais ne correspondent à aucun PDV référentiel. Les colonnes ',
  'ref.pendingPdv.vertes':            'vertes',
  'ref.pendingPdv.prerempliesCsv':    ' sont pré-remplies dans le CSV ; ',
  'ref.pendingPdv.rouges':            'rouges',
  'ref.pendingPdv.aCompleter':        ' sont à compléter par l\'admin.',
  'ref.etape2':                       'Compléter les colonnes manquantes (🔴)',
  'ref.etape3':                       'Importer via Référentiels > PDVs',
  'ref.rapportRfid':                  'Rapport RFID — activity_report',
  'ref.rawdataFichier':               'Rawdata — fichier rawdata',

  // ── `#845` T5-B septies · les orphelins RFID / Rawdata ──────────────────
  'ref.colTempsMin':       'TEMPS (min)',
  'ref.colCodeMerch':      'CODE MERCH',
  'ref.pdvFermesAutoN':    'PDV fermés — collecte non attendue, auto-acceptés ({{n}})',

  // ── `#845` T5-B octies · les derniers restes ────────────────────────────
  'ref.puisCliquer':     ' → puis cliquer ',
  'ref.erreurImport':    'Erreur lors de l\'import.',

  // ── `#847` TROU6 · les 31 sites du sixieme trou ─────────────────────────
  'marche.nav.s1':               'S1 Vue',
  'marche.nav.s2':               'S2 PDM',
  'marche.nav.s3':               'S3 Régions',
  'marche.nav.s4':               'S4 Segments',
  'marche.nav.s5':               'S5 Shelf',
  'commun.segment.premium':      'Premium',
  'commun.segment.strong':       'Strong',
  'commun.segment.standard':     'Standard',
  'commun.segment.faible':       'Faible',
  'commun.segment.A':            'Segment A',
  'commun.segment.B':            'Segment B',
  'commun.segment.C':            'Segment C',
  'commun.segment.D':            'Segment D',

  // ── `#847` T5-BIS · les neuf fichiers de la famille voisine ─────────────
  'ref.effectif':                     'Effectif',
  'ref.donneesPseudonymisees':        'données pseudonymisées',
  'ref.aucunMerchBloc':               'Aucun merchandiser dans ce bloc avec les filtres courants.',
  'ref.rechercherMerch':              'Rechercher merchandiser',
  'ref.phCode':                       'Code…',
  'ref.canalMerch':                   'Canal merchandiser',
  'ref.wilayaMerch':                  'Wilaya merchandiser',
  'ref.aucunMerch':                   'Aucun merchandiser',
  'ref.pagePrecedenteMerch':          'Page précédente merchandisers',
  'ref.pageSuivanteMerch':            'Page suivante merchandisers',
  'ref.retailTiret':                  'Retail — ',
  'ref.merchsErreur':                 'Erreur lors du chargement des merchandisers.',
  'ref.routingsErreur':               'Erreur lors du chargement des routings.',
  'ref.aucuneAffectation':            'Aucune affectation',
  'ref.colPdvCode':                   'PDV CODE',
  'ref.colPdvNom':                    'PDV NOM',
  'ref.colJour':                      'JOUR',
  'ref.filtrerCodeMerch':             'Filtrer par code merchandiser',
  'ref.phCodeMerch':                  'Code merch…',
  'ref.pagePrecedenteRoutings':       'Page précédente routings',
  'ref.pageSuivanteRoutings':         'Page suivante routings',
  'ref.parJour':                      'Par jour',
  'ref.modifiezFiltre':               'Modifiez le filtre.',
  'ref.groupeMarque':                 'Groupe marque',

  // ── `#847` T5-BIS bis · le reste des merchandisers ──────────────────────
  'ref.aucunMerchReferentiel':    'Aucun merchandiser enregistré dans le référentiel.',

  // ── `#847` T5-BIS ter · routings et fiche SKU ───────────────────────────
  'ref.importezRoutings':    'Importez un fichier routings.',

  // ── `#847` T5-BIS quater · la validation des merchs ─────────────────────

  // ── `#847` T5-BIS sexies · le bouton resoudre ───────────────────────────

  // ── `#848` T6 · NOTIFICATIONS — les trois composeurs, la page, la cloche ───
  'commun.source.PHENOMENA':        'Phénomènes',
  'commun.source.FLASH_INFO':       'Flash Info',
  'commun.source.SFM_PERF':         'SFM Perf',
  'commun.source.DATA_ACCURACY':    'Data Accuracy',
  'commun.source.PICI':             'PI/CI',
  'commun.source.MX_RM_FLOW':       'MX RM Flow',
  'notifs.pici.nouvelle':           'Nouvelle cohabitation',
  'notifs.pici.enCours':            'Cohabitation en cours',
  'notifs.pici.retour':             'Cohabitation de retour',
  'notifs.depuis':                  ', depuis {{semaine}}',
  'notifs.officiel':                'officiel {{n}} u.',
  'notifs.pici':                    'PI/CI {{n}} u.',
  'notifs.mxrm.top5':               'Top 5 SKU {{semaine}} : ',
  'notifs.mxrm.marquePdmVol':       '{{marque}} {{semaine}} : PDM vol ',
  'notifs.mxrm.val':                ' · val ',
  'notifs.mxrm.stock':              ' · stock ',
  'notifs.mxrm.ihs':                ' · IHS ',
  'notifs.mxrm.officiel':           ' — officiel ',
  'notifs.mxrm.ci':                 ' · CI ',
  'notifs.mxrm.marche':             'Marché {{semaine}} : ',
  'notifs.mxrm.titreTop5':          'Top SKU — {{semaine}} · {{n}} modèles',
  'notifs.mxrm.titreMarque':        '{{marque}} — {{semaine}}',
  'notifs.mxrm.titreMarche':        'Marché — {{semaine}}',
  'notifs.mxrm.libPdmVol':          'PDM vol',
  'notifs.mxrm.libPdmVal':          'PDM val',
  'notifs.mxrm.libStock':           'Stock',
  'notifs.orange':                  'orange',
  'notifs.jaune':                   'jaune',
  'notifs.natureNouvelle':          'nouvelle',
  'notifs.natureEnCours':           'en cours',
  'notifs.natureRetour':            'retour',
  'notifs.sfmRecalibre':            'Seuils SFM recalibrés — {{n}} alerte(s) recalculée(s)',
  'notifs.marquerNonLue':           'Marquer comme non lue',
  'notifs.marquerLue':              'Marquer comme lue',
  'notifs.lueCliquer':              'Lue — cliquer pour marquer non lue',
  'notifs.nonLueCliquer':           'Non lue — cliquer pour marquer lue',
  'notifs.lue':                     'Lue',
  'notifs.nonLue':                  'Non lue',
  'notifs.toutesSources':           '← Toutes les sources',
  'notifs.aucune':                  'Aucune notification pour l\'instant.',
  'notifs.centreErreur':            'Le centre n’a pas pu être chargé.',
  'notifs.tronquee':                '⚠ {{n}} notification(s) comptée(s) mais non affichée(s) — la liste est tronquée.',
  'notifs.totalNonLues':            '{{total}} notification(s) · {{n}} non lue(s)',
  'notifs.uneNonLue':               '{{n}} non lue',
  'notifs.nNonLues':                '{{n}} non lues',
  'notifs.detectePar':              'Détecté par {{n}} méthodes : {{liste}}',
  'notifs.envoyerRapport':          'Envoyer le rapport',
  'notifs.lectureListes':           'Lecture des listes…',
  'notifs.listesVides':             'Les deux listes sont vides — cet envoi ne partira pas.',
  'notifs.envoiLance':              'Envoi lancé',
  'notifs.comptesListes':           'Orbicore : ',
  'notifs.comptesMarque':           ' · {{marque}} : ',
  'notifs.smtpIncomplet':           'Envoi impossible : la configuration SMTP est incomplète ({{liste}}).',
  'notifs.etatIndisponible':        'État d\'envoi indisponible.',
  'notifs.envoiImpossible':         'Envoi impossible.',

  // ── `#848` T6 bis · le libelle du dialogue de confirmation ──────────────
  'notifs.confirmerEnvoi':    'Confirmer l’envoi du rapport',

  // ── `#849` T7 · WELCOME — le bento, niveau 1 ────────────────────────────
  'welcome.hero':                     '{{marque}} Algeria · MX SFMT Dashboard',
  'welcome.baseline':                 'Analysez. Comparez. Décidez.',
  'welcome.pdvSuivis':                'PDV suivis',
  'welcome.pdvRouting':               'PDV routing',
  'welcome.skuCatalogues':            'SKU catalogués',
  'welcome.carteTitre':               'Marché par wilaya · Nord Algérie · {{marque}} Focus',
  'welcome.carteErreur':              'Erreur de chargement de la carte.',
  'welcome.aucuneDonneeRegionale':    'Aucune donnée régionale disponible.',
  'welcome.colorerCarte':             'Colorer la carte par {{kpi}}',
  'welcome.top5Sku':                  'Top 5 SKU · ventes semaine',
  'welcome.aucuneVenteMarque':        'Aucune vente {{marque}} cette semaine.',
  'welcome.classementMarques':        'Classement marques · PDM volume',
  'welcome.aucuneDonneeMarche':       'Aucune donnée marché cette semaine.',
  'nav.mxRmFlow':                     'MX RM Flow',
  'nav.sfmPerf.sous':                 'Terrain & visites',
  'nav.marche.sous':                  'Retail & Wholesale',

  // ── `#852` §0-BIS · les coquilles de page Référentiels ──────────────────
  'ref.routings.titre':        'Référentiels — Routings',
  'ref.routings.sous':         'Affectations merchandiser × PDV × jour de visite (1=lundi, 7=dimanche).',
  // ⚠ `#933` — Hkm nomme le bouton lui-même : « tu les nommes Export Base ».
  //   Ce n'est pas « Exporter » : le mot dit QUE C'EST LA BASE qui sort, pas
  //   la page affichée — c'est la décision ➀ du `#930` écrite sur le bouton.
  'ref.exportBase':            'Export Base',
  'ref.affectations':          'Affectations',
  'ref.pdvCouverts':           'PDV couverts',
  'ref.merchAuReferentiel':    'Merchandisers au référentiel',
  'commun.supprimerTout':      'Supprimer tout',
  'ref.type.brands':           'marques',
  'ref.type.merchandisers':    'merchandisers',
  'ref.type.pdvs':             'PDVs',
  'ref.type.routings':         'routings',
  'ref.type.skus':             'SKUs',
  'ref.colValidFrom':          'valid_from (optionnel, AAAA-MM-JJ)',

  // ── `#853` · les régions — UNE valeur fixe est un LIBELLÉ, pas une donnée ───
  'commun.region.centre':      'Centre',
  'commun.region.est':         'Est',
  'commun.region.ouest':       'Ouest',
  'commun.region.sud':         'Sud',
  'commun.region.nord':        'Nord',
  'commun.region.national':    'National',
  'lab.axesDetectes':          'Axes détectés : lignes=',
  'lab.axesColonnes':          ' · colonnes=',
  'lab.axesValeur':            ' · valeur=',

  // ── `#858` P4-A · le suffixe de marque est un GABARIT, pas une concaténation ───
  'commun.kpiMarque':    '{{kpi}} {{marque}}',


  // ── `#861` P4-B §1 · les gabarits du récit (recit.ts) ─────────────────────────
  'phenomenes.recit.G1':       '{{sujet}}{{qui}} {{verbe}} : {{ecart}} — {{jugement}}',
  'phenomenes.recit.G2':       '{{sujet}}{{qui}} {{verbe}} — {{jugement}}',
  'phenomenes.recit.qui':      ' ({{marque}})',
  'phenomenes.recit.G3':       'Le déplacement des poids ajoute {{ecart}} qui n\'appartient à {{aucun}} {{niveau}}.',
  'phenomenes.recit.G4':       '{{libelle}} {{verbe}} {{ecart}}',
  'phenomenes.recit.G5':       '{{a}} et {{b}} {{verbe}} {{ecart}}',
  'phenomenes.recit.G6':       '{{n}} autres {{verbe}} {{ecart}}',
  'phenomenes.recit.G7':       'Mouvement concentré : {{tete}} porte {{n}} % du mouvement ({{ecart}}){{suite}}',
  'phenomenes.recit.G7suite':  ' — {{membres}}.',
  'phenomenes.recit.G8':       'Mouvement concentré, sans porteur identifié.',
  'phenomenes.recit.G9':       ' {{n}} {{niveau}} {{aller}} en sens inverse et {{efface}} {{ecart}}.',
  'phenomenes.recit.G10':      'Mouvement diffus : {{unSeul}} {{niveau}} y participe ({{n}} %).',
  'phenomenes.recit.G11':      'Mouvement diffus : {{n}} {{niveau}} se partagent le mouvement ({{parts}}) — {{aucunExplique}}.',
  'phenomenes.recit.G12':      'Mouvement diffus : {{n}} {{niveau}} y participent, {{troisPremiers}} ne pèsent que {{p}} % — {{aucunExplique}}.',

  // ── `#861` P4-B §1 · jugements et verbes de direction ─────────────────────────
  'phenomenes.jugement.favorable':    'favorable',
  'phenomenes.jugement.defavorable':  'défavorable',
  'phenomenes.jugement.neutre':       'à investiguer',
  'phenomenes.direction.up':          'en hausse',
  'phenomenes.direction.down':        'en baisse',
  'phenomenes.direction.autre':       'bouge',

  // ── `#861` P4-B §1 · verbes de membre — i18next `_one` / `_other` ─────────────
  'phenomenes.membre.coPorte_one':    'co-porte',
  'phenomenes.membre.coPorte_other':  'co-portent',
  'phenomenes.membre.efface_one':     'efface',
  'phenomenes.membre.efface_other':   'effacent',
  'phenomenes.recit.aller_one':       'va',
  'phenomenes.recit.aller_other':     'vont',

  // ── `#861` P4-B §1 ★ · les niveaux — `PDV` est INVARIABLE en français ─────────

  // ── `#861` P4-B §1 ★ · les formes accordées en GENRE ──────────────────────────
  'phenomenes.accord.aucun.f':          'aucune',
  'phenomenes.accord.aucun.m':          'aucun',
  'phenomenes.accord.unSeul.f':         'une seule',
  'phenomenes.accord.unSeul.m':         'un seul',
  'phenomenes.accord.troisPremiers.f':  'les trois premières',
  'phenomenes.accord.troisPremiers.m':  'les trois premiers',
  'phenomenes.accord.aucunExplique.f':  'aucune ne l\'explique à elle seule',
  'phenomenes.accord.aucunExplique.m':  'aucun ne l\'explique à lui seul',

  // ── `#861` P4-B §2 · les 23 sujets de phrase (article compris) ────────────────
  'phenomenes.sujet.K01':  'la PDM volume',
  'phenomenes.sujet.K02':  'la PDM valeur',
  'phenomenes.sujet.K03':  'la PDM stock',
  'phenomenes.sujet.K04':  'la ND',
  'phenomenes.sujet.K05':  'la WD',
  'phenomenes.sujet.K06':  'la part de linéaire',
  'phenomenes.sujet.K07':  'l\'IHS',
  'phenomenes.sujet.K08':  'les ventes en volume',
  'phenomenes.sujet.K09':  'le chiffre d\'affaires',
  'phenomenes.sujet.K10':  'l\'ASP',
  'phenomenes.sujet.K11':  'le stock en magasin',
  'phenomenes.sujet.K12':  'les magasins en rupture',
  'phenomenes.sujet.K13':  'les ruptures accumulées',
  'phenomenes.sujet.K14':  'la rotation des stocks',
  'phenomenes.sujet.K16':  'la couverture des tournées',
  'phenomenes.sujet.K17':  'la fréquence des visites',
  'phenomenes.sujet.K18':  'les anomalies critiques',
  'phenomenes.sujet.K19':  'les relevés écartés pour qualité',
  'phenomenes.sujet.K24':  'la PDM du concurrent le plus fort',
  'phenomenes.sujet.K25':  'l\'écart avec le premier concurrent',
  'phenomenes.sujet.K26':  'le nombre de marques présentes',
  'phenomenes.sujet.K27':  'l\'ASP des concurrents',
  'phenomenes.sujet.K28':  'les magasins perdus',

  // ── `#861` P4-B §2 · le drapeau de pluriel, PAR LANGUE ────────────────────────
  'phenomenes.sujetPluriel.K01':  'non',
  'phenomenes.sujetPluriel.K02':  'non',
  'phenomenes.sujetPluriel.K03':  'non',
  'phenomenes.sujetPluriel.K04':  'non',
  'phenomenes.sujetPluriel.K05':  'non',
  'phenomenes.sujetPluriel.K06':  'non',
  'phenomenes.sujetPluriel.K07':  'non',
  'phenomenes.sujetPluriel.K08':  'oui',
  'phenomenes.sujetPluriel.K09':  'non',
  'phenomenes.sujetPluriel.K10':  'non',
  'phenomenes.sujetPluriel.K11':  'non',
  'phenomenes.sujetPluriel.K12':  'oui',
  'phenomenes.sujetPluriel.K13':  'oui',
  'phenomenes.sujetPluriel.K14':  'non',
  'phenomenes.sujetPluriel.K16':  'non',
  'phenomenes.sujetPluriel.K17':  'non',
  'phenomenes.sujetPluriel.K18':  'oui',
  'phenomenes.sujetPluriel.K19':  'oui',
  'phenomenes.sujetPluriel.K24':  'non',
  'phenomenes.sujetPluriel.K25':  'non',
  'phenomenes.sujetPluriel.K26':  'non',
  'phenomenes.sujetPluriel.K27':  'non',
  'phenomenes.sujetPluriel.K28':  'oui',

  // ── `#861` P4-B §2 · les 69 verbes des fiches ─────────────────────────────────

  // ── `#861` P4-B §2 · les sujets a entite (K24, K25) ───────────────────────────
  'phenomenes.sujetEntite.K24':  'la PDM de {{c}}, premier concurrent,',
  'phenomenes.sujetEntite.K25':  'l\'écart avec {{c}}',

  // ── `#861` P4-B §2 · les patrons — `record` fusionne ses deux branches sans fenetre 
  'phenomenes.patron.recordHaut':         ', au plus haut niveau observé depuis {{f}} semaines',
  'phenomenes.patron.recordMarque':       ', au niveau le plus marqué observé depuis {{f}} semaines',
  'phenomenes.patron.recordSansFenetre':  ', au niveau le plus marqué observé',
  'phenomenes.patron.streak':             ', sans interruption depuis {{n}} semaines',
  'phenomenes.patron.streakContinu':      ', de façon continue',
  'phenomenes.patron.acceleration':       ', et le mouvement s\'accélère',
  'phenomenes.patron.zscorePluriel':      ', nettement au-delà de leur niveau habituel',
  'phenomenes.patron.zscoreSingulier':    ', nettement au-delà de son niveau habituel',
  'phenomenes.patron.yoy':                ', par rapport à la même période l\'an dernier',
  'phenomenes.phraseKpi':                 '{{nom}} {{verbe}}{{forme}}{{chiffre}}.',
  'phenomenes.phraseChiffre':             ' ({{signe}}{{n}} %)',

  // ── `#861` P4-B · LE VOCABULAIRE DU MOUVEMENT — `#819` : quatre KPI
  //    disaient « gagne du terrain », c'est UN mot, pas quatre. ─────────────
  'phenomenes.mouvement.gagneDuTerrain':         'gagne du terrain',
  'phenomenes.mouvement.recule':                 'recule',
  'phenomenes.mouvement.seMaintient':            'se maintient',
  'phenomenes.mouvement.sEtend':                 's\'étend',
  'phenomenes.mouvement.seReduit':               'se réduit',
  'phenomenes.mouvement.resteStable':            'reste stable',
  'phenomenes.mouvement.progresse':              'progresse',
  'phenomenes.mouvement.regresse':               'régresse',
  'phenomenes.mouvement.progressent':            'progressent',
  'phenomenes.mouvement.reculent':               'reculent',
  'phenomenes.mouvement.seMaintiennent':         'se maintiennent',
  'phenomenes.mouvement.reculeFalls':            'recule',
  'phenomenes.mouvement.monte':                  'monte',
  'phenomenes.mouvement.baisse':                 'baisse',
  'phenomenes.mouvement.seReconstitue':          'se reconstitue',
  'phenomenes.mouvement.sAmenuise':              's\'amenuise',
  'phenomenes.mouvement.seResorbent':            'se résorbent',
  'phenomenes.mouvement.seMultiplient':          'se multiplient',
  'phenomenes.mouvement.restentAuMemeNiveau':    'restent au même niveau',
  'phenomenes.mouvement.sAggravent':             's\'aggravent',
  'phenomenes.mouvement.sAccelere':              's\'accélère',
  'phenomenes.mouvement.ralentit':               'ralentit',
  'phenomenes.mouvement.sAmeliore':              's\'améliore',
  'phenomenes.mouvement.seDegrade':              'se dégrade',
  'phenomenes.mouvement.augmente':               'augmente',
  'phenomenes.mouvement.diminue':                'diminue',
  'phenomenes.mouvement.diminuent':              'diminuent',
  'phenomenes.mouvement.augmentent':             'augmentent',
  'phenomenes.mouvement.seCreuse':               'se creuse',
  'phenomenes.mouvement.seReduitNarrows':        'se réduit',
  'phenomenes.mouvement.seReduisent':            'se réduisent',
  'phenomenes.mouvement.sAccumulent':            's\'accumulent',

  // ── `#861` P4-B · les 4 mots de niveau qui MANQUAIENT (les autres sont reutilises) 
  'phenomenes.niveauRegionsMin':  'régions',
  'phenomenes.niveauWilayasMin':  'wilayas',
  'phenomenes.niveauCanalMin':    'canal',
  'phenomenes.niveauCanauxMin':   'canaux',

  // ── `#863` §1 · Brand 360 — les deux chaines qui manquaient reellement (#802 groupe C) 
  'marche.brand360.pdmPct':     'PDM {{pct}}',
  'marche.brand360.pctMarque':  '{{pct}} marque',

  // ── `#863` §3 · AlertNarrative — les 4 recits et le repli (valides Hkm 09/09) ─
  'alerte.recit.le':       'Le ',
  'alerte.recit.pic':      ', un pic ',
  'alerte.recit.dq1':      ' d\'anomalies Data Quality a été détecté. ',
  'alerte.recit.dq2':      'Le trust score moyen a chuté significativement ',
  'alerte.recit.dq3':      'sur la période concernée. Une investigation immédiate est recommandée pour identifier les sources d\'erreurs et préserver la fiabilité des données Marché.',
  'alerte.recit.trust1':   ', une chute du trust score a été enregistrée (sévérité : ',
  'alerte.recit.trust2':   'Les observations concernées présentent des anomalies sur le GPS matching et/ou le scan RFID. ',
  'alerte.recit.trust3':   'Une revue terrain des merchandisers impactés est nécessaire pour rétablir la qualité de collecte.',
  'alerte.recit.export1':  ', une tentative d\'export massif de données a été détectée (sévérité : ',
  'alerte.recit.export2':  'Ce type d\'événement peut indiquer une exfiltration non autorisée. ',
  'alerte.recit.export3':  'Vérifier les logs d\'accès et notifier l\'équipe sécurité si nécessaire.',
  'alerte.recit.burst1':   ', un burst de tentatives de connexion échouées a été détecté (sévérité : ',
  'alerte.recit.burst2':   'Ce pattern peut indiquer une attaque par force brute. ',
  'alerte.recit.burst3':   'Vérifier les IPs sources et envisager un blocage temporaire si le pattern persiste.',
  'alerte.recit.repli1':   'Alerte {{type}} (sévérité : ',
  'alerte.recit.repli2':   ') déclenchée le {{date}}. Consulter les détails techniques ci-dessous.',

  // ── `#866` P4-C1 · `commun.srv.*` — UNE CLE PAR TEXTE, pas par code (#819) ────
  'commun.srv.AUTH_REQUIRED':                         'Authentification requise.',
  'commun.srv.INSUFFICIENT_PERMISSIONS':              'Permission requise : {{permission}}',
  'commun.srv.TOO_MANY_REQUESTS':                     'Trop de requêtes.',
  'commun.srv.VALIDATION_ERROR':                      'Requête invalide.',
  'commun.srv.INTERNAL_ERROR':                        'Une erreur interne est survenue. Réessayez.',
  'commun.srv.SERVICE_UNAVAILABLE':                   'Service temporairement indisponible. Réessayez dans quelques instants.',
  'commun.srv.NOT_FOUND':                             'Introuvable.',
  'commun.srv.FORBIDDEN':                             'Action non autorisée.',
  'commun.srv.RATE_LIMITED':                          'Quota atteint ({{limite}}/h). Réessayez plus tard.',
  'commun.srv.LAB_INVALID_REQUEST':                   'Requête Lab invalide.',
  'commun.srv.LAB_QUERY_NAME_DUPLICATE':              'La requête « {{nom}} » existe déjà.',
  'commun.srv.LAB_PDV_SEARCH_REQUIRED':               'Recherche (≥ 2 caractères) ou filtre cascade requis pour les PDV.',
  'commun.srv.LAB_INCOMPATIBLE_KPI_TYPE':             'KPI {{kpi}} incompatible avec ce type d’analyse.',
  'commun.srv.LAB_INVALID_OUTPUT_FOR_TYPE':           'Format de sortie incompatible avec ce type d’analyse.',
  'commun.srv.LAB_DATES_ORDER':                       'dates.from doit être ≤ dates.to',
  'commun.srv.LAB_INVALID_FILTERS':                   'Filtres invalides',
  'commun.srv.LAB_EXPORT_STREAM_FAILED':              'Échec du flux d’export',
  'commun.srv.PIVOT_BRAND_AXIS_AND_FILTER_CONFLICT':  'Marque est à la fois en dimension et en filtre — le filtre marque est ignoré.',
  'commun.srv.RANKING_KPI_SINGLE_REQUIRED':           'Le classement exige exactement 1 KPI de type métrique.',
  'commun.srv.RANKING_KPI_NOT_ALLOWED':               'KPI {{kpi}} non classable.',
  'commun.srv.RANKING_KPI_ENTITY_SCOPE':              'KPI {{kpi}} n’accepte pas cette entité.',
  'commun.srv.RANKING_RATIO_PDV_UNSUPPORTED':         '{{kpi}} au grain PDV non supporté (présence binaire).',
  'commun.srv.RANKING_NORM_NONFLUX':                  'Normalisation par PDV : KPI additif requis ({{kpi}} ne l’est pas).',
  'commun.srv.RANKING_NORM_REQUIRES_AGGREGATE':       'Normalisation par PDV : entité agrégat requise (wilaya, région, marque, merch).',
  'commun.srv.MISSING_FILE':                          'Aucun fichier reçu.',
  'commun.srv.EMPTY_FILE':                            'Le fichier ne contient aucune ligne de données.',
  'commun.srv.INVALID_CONTENT_TYPE':                  'Format d’envoi invalide.',
  'commun.srv.FILE_TOO_LARGE':                        'Fichier trop volumineux (max {{max}} Mo).',
  'commun.srv.INVALID_IMAGE_FORMAT':                  'Format d’image non accepté (JPEG, PNG, WebP).',
  'commun.srv.IMAGE_PROCESSING_FAILED':               'Le traitement de l’image a échoué.',
  'commun.srv.FI_PHOTO_MAX_REACHED':                  'Nombre maximum de photos atteint.',
  'commun.srv.FI_NOT_FOUND':                          'Flash Info introuvable.',
  'commun.srv.FI_FORBIDDEN':                          'Action non autorisée sur cette Flash Info.',
  'commun.srv.FI_INVALID_TRANSITION':                 'Transition de statut impossible.',
  'commun.srv.DEFAULT_CATEGORY_MISSING':              'Catégorie par défaut manquante — seed des référentiels requis.',
  'commun.srv.INVALID_PARAMS':                    'Paramètres invalides.',
  'commun.srv.GEO_WILAYA_NOT_FOUND':                  'Wilaya introuvable.',
  'commun.srv.GEO_UNKNOWN_BRAND':                     'Marque inconnue : {{code}}.',
  'commun.srv.SMTP_NON_CONFIGURE':                    'Envoi impossible : configuration SMTP incomplète ({{liste}}).',
  'commun.srv.AUCUN_DESTINATAIRE':                    'Les deux listes sont vides.',
  'commun.srv.ACCOUNT_INACTIVE':                      'Compte inactif',
  'commun.srv.ACCOUNT_LOCKED':                        'Compte temporairement verrouillé. Réessayez plus tard.',
  'commun.srv.AUTH_2FA_ALREADY':                      'Un second facteur est déjà configuré sur ce compte.',
  'commun.srv.AUTH_2FA_PREPARE_FIRST':                'Commencez par préparer le second facteur.',
  'commun.srv.AUTH_CODE_6_DIGITS':                    'Code à 6 chiffres attendu.',
  'commun.srv.AUTH_CODE_INVALID':                     'Code invalide.',
  'commun.srv.AUTH_INVALID_CREDENTIALS':              'Identifiants incorrects',
  'commun.srv.AUTH_SESSION_INVALID':                  'Session invalide',
  'commun.srv.AUTH_TOTP_INVALID':                     'Code TOTP invalide ou expiré',
  'commun.srv.AUTH_TOTP_REQUIRED':                    'totp_code ou backup_code requis.',
  'commun.srv.SESSION_EXPIRED':                       'Session invalide ou expirée',

  // ── `#868` P4-D1 · les categories de la BASE (codes du seed, pas ceux supposes) 
  'commun.categorie.PHONE':          'Téléphonie',
  'commun.categorie.APPLIANCE':      'Électroménager',
  'commun.categorie.SMARTPHONE':     'Smartphone',
  'commun.categorie.FEATURE_PHONE':  'Téléphone Classique',
  'commun.terrain':                  'Terrain',

  // ── `#869` P4-D1 bis · les 7 templates du Labo (valides Hkm 10/09) ────────────
  'lab.template.pdm_volume.nom':             'Évolution PDM par marque (mensuel)',
  'lab.template.pdm_volume.desc':            'PDM volume de toutes marques sur les 12 derniers mois, granularité mensuelle. Sortie graphique linéaire + Excel.',
  'lab.template.cond_stock.nom':             'Liste PDV avec stock manquant',
  'lab.template.cond_stock.desc':            'Filtre conditionnel stock = 0 sur SKUs prioritaires {{marque}}. Identifie les PDV en rupture sur les 4 dernières semaines.',
  'lab.template.sales_volume_units.nom':     'Comparaison ventes 2 SKUs',
  'lab.template.sales_volume_units.desc':    'Comparaison ventes volume entre 2 SKUs {{marque}} avec enrichissement % segment et % marché sur les 8 dernières semaines.',
  'lab.template.rawdata_export.nom':         'Export rawdata consolidé 6 mois',
  'lab.template.rawdata_export.desc':        'Export rawdata agrégé catégorie I, granularité hebdomadaire, sur les 6 derniers mois. Requiert permission rawdata.export_aggregated.',
  'lab.template.sales_value_dzd.nom':        'Top 10 PDV par ventes {{marque}}',
  'lab.template.sales_value_dzd.desc':       'Classement des 10 meilleurs PDV par ventes {{marque}} (valeur DZD) sur les 4 dernières semaines.',
  'lab.template.cond_visit_frequency.nom':   'Fréquence visites < 1/sem',
  'lab.template.cond_visit_frequency.desc':  'Identifie les PDV visités moins d\\\'une fois par semaine sur les 8 dernières semaines. Aide à optimiser les plans de routing.',
  'lab.template.ihs_samsung.nom':            'IHS {{marque}} > 50% par wilaya',
  'lab.template.ihs_samsung.desc':           'Carte de chaleur IHS {{marque}} par wilaya sur les 4 dernières semaines. Permet d\\\'identifier les zones à fort indice habitat {{marque}} (IHS ≥ 50%).',

  // ── #871 reprise Labo : le compteur, l'agregat, la mention de filtre ──────────
  'lab.chart.lignes_one':      '{{count}} ligne · {{ms}} ms',
  'lab.chart.lignes_other':    '{{count}} lignes · {{ms}} ms',
  'lab.export.lignesVendues':  'Lignes vendues uniquement (ventes > 0)',

  // ── #871 (5) les 5 facteurs de segmentation (valides Hkm 10/09) ───────────────
  'admin.facteur.weekly_sales_vs_regional_avg.nom':   'Ventes hebdo vs moyenne régionale',
  'admin.facteur.weekly_sales_vs_regional_avg.desc':  'Ratio ventes hebdomadaires PDV / moyenne régionale (rolling 4 semaines). Facteur mono par défaut V1 — normalisation identité.',
  'admin.facteur.stock_avg_4w.nom':                   'Stock moyen 4 semaines',
  'admin.facteur.stock_avg_4w.desc':                  'Stock moyen en unités sur les 4 semaines glissantes. Normalisation min-max inter-PDV.',
  'admin.facteur.visit_frequency_per_month.nom':      'Fréquence de visite par mois',
  'admin.facteur.visit_frequency_per_month.desc':     'Nombre moyen de visites merchandiseurs par mois (rolling 90 jours). Normalisation min-max inter-PDV.',
  'admin.facteur.samsung_shelf_share_avg.nom':        'Shelf share moyen {{marque}}',
  'admin.facteur.samsung_shelf_share_avg.desc':       'Part de linéaire {{marque}} en rayon (facings {{marque}} / total facings), moyenne rolling 4 semaines. Normalisation min-max inter-PDV.',
  'admin.facteur.samsung_availability_rate.nom':      'Disponibilité {{marque}} (taux de présence)',
  'admin.facteur.samsung_availability_rate.desc':     'Taux de présence produits {{marque}} en stock (visites avec stock > 0 / total visites), rolling 4 semaines. Normalisation min-max inter-PDV.',

  // ── #873 les 4 formats de sortie du Labo (codes en base, #817 (1)) ────────────
  'lab.format.liste':    'liste',
  'lab.format.tableau':  'tableau',
  'lab.format.graph':    'graph',
  'lab.format.excel':    'excel',

  // ══════════════════════════════════════════════════════════════════════
  // `#874` §2 — **LA DETTE `#819` TOMBE À ZÉRO.** Vingt-trois libellés que
  //   deux modules ou plus servaient chacun de leur côté vivent désormais
  //   en `commun.*` : on réutilise, on ne recrée pas.
  // ══════════════════════════════════════════════════════════════════════
  'commun.ca': 'CA',
  'commun.dispoAbbr': 'Dispo',
  'commun.dispo': 'Dispo',
  'commun.valeur': 'Valeur',
  'commun.mesureVolume': 'Volume',
  'commun.lectureStrat': 'Lecture stratégique',
  'commun.vueEnsemble': 'Vue d\'ensemble',
  'commun.quatreSem': '4 sem',
  'commun.semaine': 'Semaine',
  'commun.ventes': 'Ventes',
  'commun.officiel': 'Officiel',
  'commun.semaineMin': 'semaine',
  'commun.batterie': 'Batterie',
  'commun.camera': 'Caméra',
  'commun.pdmAbbr': 'PDM',
  'commun.pdmValAbbr': 'PDM Val.',
  'commun.tendance': 'Tendance',
  'commun.regionMin': 'région',
  'commun.aucunModele': 'Aucun modèle',
  'commun.nSem': '{{n}} sem.',
  'commun.unites': 'unités',
  'commun.tous': 'Tous',
  'commun.partMarche': 'Part de marché',

  // ── #878 les 4 derniers codes serveur (table Hkm 10/09) ───────────────────────
  'commun.srv.DELETE_ALREADY_RUNNING':      'Une suppression est déjà en cours.',
  'commun.srv.DIMENSION_CONTEXT_MISMATCH':  'Dimension incompatible avec le contexte du phénomène.',
  'commun.srv.REASON_REQUIRED':             'Un motif est requis pour cette action.',
  'commun.srv.SCOPE_MISMATCH':              'Hors de votre périmètre.',
  // ── `#1089` · les refus d'import disent leurs faits (plus jamais « HTTP 422 » nu) ──
  'commun.srv.INVALID_TEMPLATE':                         'Le fichier ne suit pas le modèle attendu.',
  'commun.srv.INVALID_TEMPLATE_ENTETE_ABSENTE':          'Le fichier ne suit pas le modèle attendu : l’en-tête « {{ancre}} » est introuvable.',
  'commun.srv.INVALID_TEMPLATE_ENTETES_MANQUANTS':       'Le fichier ne suit pas le modèle attendu : {{trouves}} colonnes sur {{attendus}} — la colonne {{position}} « {{attendu}} » manque.',
  'commun.srv.INVALID_TEMPLATE_ENTETE_DIFFERENTE':       'Le fichier ne suit pas le modèle attendu : colonne {{position}}, attendu « {{attendu}} », trouvé « {{trouve}} ».',
  'commun.srv.INVALID_TEMPLATE_COLONNES':                'Le fichier ne suit pas le modèle attendu : {{attendues}} colonnes attendues, {{trouvees}} trouvées.',
  'commun.srv.INVALID_TEMPLATE_COLONNES_OBLIGATOIRES':   'Le fichier ne suit pas le modèle attendu : colonnes obligatoires manquantes — {{manquantes}}.',
  'commun.srv.INVALID_TEMPLATE_SEMAINE_INDEDUCTIBLE':    'Impossible de déduire la semaine du fichier : {{colonnes}} absente(s) ou vide(s).',
  'commun.srv.BATCH_ALREADY_FINALIZED':                  'Un import existe déjà pour {{semaine}} (statut {{statut}}, fichier {{fichier}}). Supprimez-le d’abord dans l’historique des imports, puis relancez.',
  'commun.srv.WEEK_MISMATCH':                            'Le fichier contient la semaine {{semaineFichier}}, vous avez choisi {{semaineChoisie}}. Vérifiez le fichier ou la semaine de l’assistant.',
  'commun.srv.ERREUR_ENVOI':                             'Le serveur a refusé le fichier (statut {{statut}}, code {{code}}).',

  // ── #878 les 19 motifs de rejet d'import — 17 textes (table Hkm 10/09) ────────
  'import.rejet.pdvManquant':             'Code PDV manquant (champ obligatoire)',
  'import.rejet.dateManquante':           'Date de visite manquante (champ obligatoire)',
  'import.rejet.timeOutAvantTimeIn':      'time_out ({{out}}) est antérieure à time_in ({{in}})',
  'import.rejet.tempsNegatif':            'Temps passé négatif ({{n}} min) — sera ignoré',
  'import.rejet.tempsExcessif':           'Temps passé excessif ({{n}} min > 240) — sera ignoré',
  'import.rejet.latitudeInvalide':        'Latitude begin invalide : {{v}} (doit être ∈ [-90, 90])',
  'import.rejet.longitudeInvalide':       'Longitude begin invalide : {{v}} (doit être ∈ [-180, 180])',
  'import.rejet.gpsEloigne':              'GPS trop éloigné du PDV de référence : {{m}}m (bucket: {{bucket}})',
  'import.rejet.sansPhoto':               'PDV ouvert mais aucune photo fournie (interior ni exterior)',
  'import.rejet.skuNonResolvable':        'SKU non résolvable « {{model}} » (marque={{brand}}, RAM={{ram}}, ROM={{rom}}) — sera conservé en PENDING_REPLAY à la finalisation.',
  'import.rejet.skuManquant':             'Code SKU (modèle) manquant (champ obligatoire)',
  'import.rejet.doublonInterBatch':       'Doublon inter-batch : observations pour ({{pdv}}, {{date}}) déjà présentes',
  'import.rejet.ventesSuperieuresStock':  'Ventes 7j ({{sales}}) > stock × 7 ({{stock7}}) — incohérence possible',
  'import.rejet.prixNegatif':             'Prix de vente ≤ 0 ({{p}} DZD) — incohérence qualité à investiguer',
  'import.rejet.vitrineNegative':         'Quantité vitrine négative non autorisée',
  'import.rejet.productTypeNonStandard':  'product_type \'{{v}}\' non standard — valeur attendue : OFFICIEL ou PI-CI',
  'import.rejet.doublonExact':            'Doublon exact dans le fichier : ligne identique à une ligne précédente (row {{i}})',

  // ── #878 les 2 gabarits d'anomaly_flags (table Hkm 10/09) ─────────────────────
  'admin.anomalie.SKU_UNKNOWN':                 'SKU non résolu après cascade code→composite→name — modèle : « {{model}} »',
  'admin.anomalie.CHANNEL_MISMATCH_MERCH_PDV':  'Merch {{merch}} sur PDV {{pdv}} — violation canal',

  // ── #878 les 23 descriptions config_display (table Hkm 10/09) ─────────────────
  'admin.config.marche.brand_visibility_threshold_pct.desc':  'Seuil de visibilité des marques (% PDM) — marques en-dessous → bucket Others (DECISIONS #077)',
  'admin.config.marche.weekly_window_weeks.desc':             'Fenêtre temporelle weekly : périodes dans cette fenêtre → mv_agg_weekly (DECISIONS #064)',
  'admin.config.system.export_max_rows.desc':                 'Plafond de lignes d\'un export — au-delà, la sortie est TRONQUÉE (pas refusée)',
  'admin.config.system.export_daily_limit.desc':              'Exports Self Service Lab autorisés par jour et par utilisateur',
  'admin.config.sku.inactivity_threshold_weeks.desc':         'Seuil d\'inactivité SKU en semaines — SKU sans observation depuis N semaines → is_active=false (syncSkuActivation)',
  'admin.config.import.reject_threshold_pct.desc':            'Seuil de rejet d\'import (%) : au-delà, la semaine échoue et n\'est PAS publiée (validateRawdata)',
  'admin.config.system.lab_runs_per_hour.desc':               'Exécutions Self Service Lab autorisées par heure et par utilisateur (checkRunQuota)',
  'admin.config.audit.retention_ordinary_days.desc':          'Rétention du trail ordinaire (jours) — au-delà, la ligne est oubliée (appliquerRetention)',
  'admin.config.audit.retention_security_days.desc':          'Rétention du signal de sécurité (jours) : refus, connexions, suppressions — conservés plus longtemps',
  'admin.config.session.idle_timeout_minutes.desc':           'Inactivité (minutes) avant fermeture automatique d\'une session — le plafond absolu reste au-dessus',
  'admin.config.session.absolute_max_hours.desc':             'Durée maximale (heures) d\'une session depuis son ouverture — au-delà, reconnexion obligatoire',
  'admin.config.phenomena.zscore_window_weeks.desc':          'Z-score — fenetre glissante (semaines) du calcul moyenne/ecart-type',
  'admin.config.phenomena.zscore_min_points.desc':            'Z-score — points minimaux pour calculer un z (sinon pas de detection)',
  'admin.config.phenomena.zscore_gate_z.desc':                'Z-score — SEUIL D EXISTENCE : sous |z| <= ce seuil, aucun phenomene',
  'admin.config.phenomena.zscore_band_eleve_z.desc':          'Z-score — bande ELEVEE (aussi le cutoff FRANC/LIMITE)',
  'admin.config.phenomena.zscore_band_critique_z.desc':       'Z-score — bande CRITIQUE',
  'admin.config.phenomena.accel_ref_window_weeks.desc':       'Acceleration — fenetre de reference (nb de deltas moyennes)',
  'admin.config.phenomena.accel_ratio_signal.desc':           'Acceleration — SEUIL D EXISTENCE : sous ratio <= ce seuil, aucun phenomene',
  'admin.config.phenomena.accel_ratio_eleve.desc':            'Acceleration — bande ELEVEE (aussi le cutoff FRANC/LIMITE)',
  'admin.config.phenomena.accel_ratio_critique.desc':         'Acceleration — bande CRITIQUE',
  'admin.config.phenomena.streak_signal_weeks.desc':          'Streak — SEUIL D EXISTENCE : sous cette longueur, aucun phenomene',
  'admin.config.phenomena.streak_eleve_weeks.desc':           'Streak — bande ELEVEE (aussi le cutoff FRANC/LIMITE)',
  'admin.config.phenomena.streak_critique_weeks.desc':        'Streak — bande CRITIQUE',

  // ── #881 les 9 en-tetes du rapport e-mail que le catalogue n'avait pas ────────
  'email.entete.codeMerch':        'Code merch',
  'email.entete.niveau':           'Niveau',
  'email.entete.partVal':          'Part val.',
  'email.entete.partVol':          'Part vol.',
  'email.entete.marcheEntier':     'Marché entier',
  'email.entete.gravite':          'Gravité',
  'email.entete.qualiteCollecte':  'Qualité de la collecte',
  'email.entete.tauxQualite':      'Taux de qualité',
  'email.bloc.graphiques':         'Graphiques',

  // ── #882 les libelles de ligne et les phrases de comptage de l'e-mail ─────────
  'email.entete.tauxPreuves':    'Taux de preuves',
  'email.compte.majeurs':        'majeurs',
  'email.compte.rouges':         'rouges',
  'email.compte.cohabitation':   'cohabitation {{marque}}',
  'email.compte.cohabitations':  'cohabitations {{marque}}',
  'email.compte.cetteSemaine':   'cette semaine',
  'email.compte.auDashboard':    'au dashboard',
  'email.compte.autres_one':     '{{count}} autre',
  'email.compte.autres_other':   '{{count}} autres',
  'email.piedDePage':            'Rapport automatique du Centre de notifications — Orbicore. Les merchandisers sont désignés par leur code.',

  // ── #885 le titre d'evolution du tiroir merch (demande Hkm 10/09) ─────────────
  'perf.titreEvolutionMerch':  'Évolution merchandiseur — {{n}} sem',

  // ── #886 le classeur des problemes d'un merchandiseur (5 feuilles + resume) ───
  'perf.pb.resume':             'Résumé',
  'perf.pb.nonCouverts':        'Routing non couvert',
  'perf.pb.nonVisites':         'Non visités 3+ sem',
  'perf.pb.colDateVisite':      'Date de visite',
  'perf.pb.colJourRouting':     'Jour de routing',
  'perf.pb.colSemainePrec':     'Semaine précédente',
  'perf.pb.colDureeNette':      'Durée nette (min)',
  'perf.pb.colMerch':           'Merchandiseur',
  'perf.pb.colProbleme':        'Problème',
  'perf.pb.colPourcent':        'Part',
  'perf.pb.clustersGps':        'Clusters GPS',
  'perf.pb.conformiteGps':      'Conformité begin↔end',
  'perf.pb.colNumCluster':      'N° cluster',
  'perf.pb.colCentreLat':       'Centre lat.',
  'perf.pb.colCentreLon':       'Centre lon.',
  'perf.pb.colEcartM':          'Écart (m)',
  'perf.pb.colSeuilM':          'Seuil (m)',
  'perf.pb.colDetail':          'Précision',
  'perf.pb.photoIntAbsente':    'Sans photo intérieure',
  'perf.pb.photoExtAbsente':    'Sans photo extérieure',
  'perf.pb.gpsTropLoin':        'Visités à plus de 50 m',
  'perf.pb.colDistanceSeuil':   'Distance (m) — seuil 50 m, source Data Accuracy',
  'perf.pb.colMotifEchec':      'Motif d\'échec',
  'perf.pb.motifPhoto':         'Photo',
  'perf.pb.motifGpsPhoto':      'GPS + photo',
  'perf.pb.detailClusters_one':      '{{count}} cluster',
  'perf.pb.detailClusters_other':    '{{count}} clusters',
  'perf.pb.detailAnalysables_one':   'sur {{count}} visite analysable',
  'perf.pb.detailAnalysables_other': 'sur {{count}} visites analysables',
  'perf.exportProblemes':       'Télécharger les problèmes',
} as const

/** ⚠ **Toute clé de `fr` DOIT exister ici** — sinon `en` ne compile pas. */
export const en: Record<keyof typeof fr, string> = {
  'nav.marche':                 'Market',
  'nav.phenomenes':             'Market Phenomena',
  'nav.sfmPerf':                'SFM Team Performance',
  'nav.geoData':                'Geo-Data',
  'nav.geoData.sous':           'Mapping & Metrics',
  'nav.brandVsBrand':           'Brand vs Brand',
  'nav.brandVsBrand.sous':      'Understand & Target',
  // ⚠ `#798` — INVARIANTS restants (FR=EN, validé Hkm 05/09) : MX RM Flow, SFM
  //   Team Performance, Flash Info, Brand 360, SKU 360. Brand vs Brand, Geo-Data,
  //   Self-Service Lab, Data Accuracy sont désormais TRADUITS en FR.
  'nav.cohabitation':           'Coexistence',
  'nav.cohabitation.sous':      'Official vs PI/CI',
  'nav.comparateur':            'SKU Comparator',
  'nav.comparateur.sous':       'Price & Specs',
  'nav.lab':                    'Self-Service Lab',
  'nav.lab.sous':               'Custom Reports',
  'nav.dataAccuracy':           'Data Accuracy',
  'nav.flashInfo':              'Flash Info',
  'nav.flashInfo.sous':         'Weekly Feeds',

  'nav.tableauDeBord':          'Dashboard',
  'nav.referentiels':           'Reference data',
  'nav.administration':         'Administration',
  'nav.historiqueImports':      'Import history',
  'nav.configuration':          'Configuration',
  'nav.segmentation':           'Segmentation',
  'nav.parametres':             'Settings',
  'nav.validation':             'Validation',
  'nav.monitoring':             'Monitoring',
  'nav.auditLogs':              'Audit logs',
  'nav.marketClusters':         'Market clusters',
  'nav.rfidRawdata':            'RFID / Rawdata',
  'nav.centreNotifications':    'Notification center',
  'nav.welcome':                'Home',
  'nav.importer':               'Import',
  'nav.performance':            'Performance',
  'nav.dataQuality':            'Data Quality',
  'nav.lancementsOfficiels':    'Official launches',

  'marche.s1':                  'Overview',
  'marche.s3':                  'Regions',
  'marche.s4':                  'Segments',
  'marche.s6':                  'Top Models',
  'marche.s8':                  'IHS',
  'marche.s9':                  'ASP',

  'marche.entete.semaines':         'Weeks…',
  'marche.entete.semainesIndispo':  'Weeks unavailable',
  'marche.entete.aucuneSemaine':    'No week imported',
  'marche.entete.compHebdoSeule':   'Comparison available in weekly view only (12 wks)',
  'marche.entete.exportPpt':        'Export → PowerPoint',
  'marche.entete.rapportEnCours':   'Generating report…',
  'marche.ppt.bilanSuccesTitre':    'Report generated',
  'marche.ppt.bilanSuccesCorps':    '{{n}} captures, file downloaded.',
  'marche.ppt.bilanEchecTitre':     'Generation interrupted',
  'marche.ppt.bilanEchecCorps':     'Capture {{rang}}/{{total}} — {{surface}} — {{cause}}',

  'marche.carte.nonDisponible':     'Not available',
  'marche.carte.aucuneVenteUnivers':'No sales in the visited universe',
  'marche.carte.pcs':               'PCS',
  'marche.carte.moyAgg':            ' · avg {{x}}/wk · {{n}} wks',
  'marche.carte.refIndispo':        'No reference for this comparison mode',
  'marche.carte.na':                'N/A',
  'marche.carte.visitesPos':        'PoS visits',
  'marche.carte.vsRouting':         '{{pct}} vs Routing',
  'marche.carte.ventesUnites':      'Sales (units)',
  'marche.carte.caUsd':             'Turnover (USD)',
  'marche.carte.ci':                'CI',
  'marche.carte.ciInflow':          'CI Inflow',
  'commun.reessayer':         'Retry',
  'marche.carte.erreurSyntheseSamsung': 'Failed to load the Samsung summary.',
  'marche.carte.erreurSyntheseMarche':  'Failed to load the market summary.',

  'commun.region':           'Region',
  'marche.filtre.national':         'National',
  'marche.filtre.wilaya':           'Wilaya',
  'marche.filtre.toutes':           'All',
  'marche.filtre.segPrix':          'Price seg',
  'commun.chargement':              'Loading…',
  'marche.filtre.indisponible':     'Unavailable',
  'commun.tousSegments':     'All segments',
  'marche.filtre.segPdv':           'PoS seg',
  'marche.filtre.marque':           'Brand',
  'marche.filtre.toutesMarques':    'All brands',
  'marche.filtre.personnalise':     'Custom',
  'marche.filtre.appliquer':        'Apply',
  'marche.filtre.derniereSem':      'Latest wk',
  'marche.filtre.derniereSemW':     'Latest wk ({{wk}})',
  'marche.filtre.prof4':            '4 wks',
  'marche.filtre.prof8':            '8 wks',
  'marche.filtre.prof3m':           '3 mo',
  'marche.filtre.prof6m':           '6 mo',
  'marche.filtre.prof12m':          '12 mo',
  'marche.filtre.ytd':              'Year to date',
  'marche.filtre.profYtd':          'YTD',

  'marche.s1.partsMarche':          'Market share — by brand',
  'marche.s1.aucunResultat':        'No result for this filter',
  'marche.s1.partVolPct':           'Vol share %',
  'marche.s1.partValPct':           'Val share %',
  'marche.s1.unites':               'Units',
  'marche.s1.varPdmVol':            'Market share change (Volume) vs previous week (points)',
  'marche.s1.varPdmVal':            'Market share change (Value) vs previous week (points)',
  'marche.s1.ouvrirMarque':         'Open brand file',
  'marche.s1.ouvrirModele':         'Open model file',
  'marche.s1.erreurParts':          'Failed to load market shares.',
  'commun.volume':               'Volume',
  'marche.s1.classerUnites':        'Rank and measure in units sold',
  'marche.s1.classerCa':            'Rank and measure in turnover (USD)',
  'marche.s1.modeles':              'Models',
  'marche.s1.erreurTop5':           'Failed to load Top 5 SKUs.',
  'marche.s1.aucuneVentePerimetre': 'No sales in this scope.',
  'marche.s1.modeleNonId':          'Unidentified model',
  'marche.s1.moyHebdoExcl':         'Weekly average of {{refs}} — {{ancre}} excluded from its own reference.',
  'marche.s1.moyHebdo':             'Weekly average of {{refs}}.',
  'marche.s1.ttW1':                 'Comparison with the previous week.',
  'marche.s1.tt4w':                 'Average of the 3 PREVIOUS weeks — the displayed week is excluded from its own reference (total window: 4 weeks).',
  'marche.s1.ttSwly':               'Comparison with the same week last year.',

  'marche.s2.poidsRegion':          'Region weight',
  'marche.s2.poidsNational':        'National weight',
  'marche.s2.footnote':             '{{n}} wilayas · week {{week}} · {{m}} brands + Others',
  'marche.s2.titreSlide19':         'Market share by wilaya (slide 19)',
  'marche.s2.sousSemaine':          'week {{week}}',
  'marche.s2.erreurMatrice':        'Failed to load the matrix.',
  'marche.s2.pasMatrice':           'No brand × wilaya matrix for the current scope.',
  'marche.s2.titreMvS':             'Market vs Samsung',
  'marche.s2.recordLow12':          '▼ 12w low',
  'marche.s2.recordLow26':          '▼ 26w low',
  'marche.s2.recordLow52':          '▼ 52w low',
  'marche.s2.recordHigh12':         '▲ 12w high',
  'marche.s2.recordHigh26':         '▲ 26w high',
  'marche.s2.recordHigh52':         '▲ 52w high',
  'marche.s2.erreurTitre':          'Failed to load ({{titre}}).',
  'marche.s2.pasSerieVol':          'No volume series for the current scope.',
  'marche.s2.pasSerieVal':          'No value series for the current scope.',
  'marche.s2.pdmVolQty':            'MS Qty',
  'marche.s2.pdmValAmt':            'MS Value',

  'marche.s3.erreurPoids':          'Failed to load regional weight.',
  'marche.s3.aucuneDonnee':         'No data',
  'marche.s3.aucunPoids':           'No regional weight for the current scope.',
  'marche.s3.titreVol':             'Volume — weight % by week',
  'marche.s3.titreVal':             'Value — weight % by week',

  'marche.s4.segSelectionne':       '● {{code}} selected',
  'marche.s4.erreurSegments':       'Failed to load price segments.',
  'marche.s4.aucuneDonneeSeg':      'No segment data',
  'marche.s4.aucunSegment':         'No price segment for the selected period and channel.',
  'marche.s4.volumeQty':            'Volume (QTY)',
  'marche.s4.valeurAmt':            'Value (AMT)',
  'marche.s4.detailSegPre':         'Segment detail',
  'marche.s4.detailSegPost':        '— Top models',
  'marche.s4.fermer':               'Close ✕',
  'marche.s4.erreurL2':             'Failed to load L2.',
  'marche.s4.aucunSku':             'No SKU for this segment.',
  'marche.s4.msParMarque':          'M/S by brand in the band (vol.)',
  'marche.s4.aucuneDonneeTele':     'No telescopic data',
  'marche.s4.aucuneEvolution':      'No trend available for the selected period and channel.',
  'marche.s4.evolutionTranche':     'Trend by price band',
  'marche.s4.tranche':              'Band',

  'marche.s5.titreBrand':           'Shelf Share by brand',
  'marche.s5.pasShelfBrand':        'No shelf share for this scope.',
  'marche.s5.legende':              'Σ brands + Others = 100 %. Visibility threshold 3 %. ★ = client brand.',
  'marche.s5.titreEvo':             'Shelf Share trend by brand — telescopic axis',
  'marche.s5.impossibleEvo':        'Failed to load shelf share trend.',
  'marche.s5.titreSkuMother':       'Shelf Share by model — top 20',
  'marche.s5.titreSkuSku':          'Shelf Share by SKU — top 20',
  'marche.s5.selBadge':             '● {{label}} selected — click to deselect',
  'marche.s5.impossibleData':       'Failed to load data.',
  'marche.s5.pasHistorique':        'No history for this scope.',
  'marche.s5.impossibleTopSku':     'Failed to load top SKU.',
  'marche.s5.pasSku':               'No SKU for this scope.',
  'marche.s5.legSkuA':              'Size = market shelf (denominator unchanged by drill). Colour = brand. 2 shares per tile: ',
  'marche.s5.legSkuB':              ' (vs total market) and ',
  'marche.s5.legSkuC':              ' (vs its brand). Stock = sum of observations at the anchor week. “—” if not provided.',

  'marche.nav.s6':                  'S6 Top Models',
  'marche.nav.s7':                  'S7 Availability',
  'marche.nav.s8':                  'S8 IHS',
  'marche.nav.s9':                  'S9 ASP',
  'marche.nav.index':               'Index',
  'marche.nav.sommaire':            'Surfaces summary',
  'marche.export.exporterDonnees':  'Export data',
  'marche.export.exporter':         'Export',
  'marche.export.enCours':          'Export…',
  'marche.export.echecDroits':      'Export refused: this account is not allowed to export this surface.',
  'marche.export.echec':            'Export failed — {{motif}}',
  'marche.export.format':           'Export format',
  'marche.export.reduireSurface':   'Shrink {{surface}}',
  'marche.export.agrandirSurface':  'Enlarge {{surface}}',
  'marche.export.aucuneDonneePeriode': 'No data for the period.',
  'marche.export.reduire':          'Shrink',
  'marche.export.deplier':          'Expand',
  'marche.export.replier':          'Collapse',
  'marche.ws.clustersWholesale':    'Wholesale Clusters',
  'marche.ws.totalSamsung':         'Total: {{n}} Samsung units',
  'marche.ws.tousClusters':         '← All clusters',
  'marche.ws.pdmParCluster':        'MS by cluster',
  'marche.ws.partSamsung':          'Samsung market share by Wholesale Market Cluster',
  'marche.ws.aucuneDonnee':         'No wholesale data for the period.',
  'marche.ws.source':               'Source: {{source}}',
  'marche.ws.pdmSamsung':           'Samsung MS',
  'marche.ws.clusterCodePdv':       '{{code}} · {{n}} PoS',
  'marche.ws.clusterId':            'Cluster #{{id}}',
  'marche.ws.pdmSamsungLabel':      'Samsung MS: ',
  'marche.ws.unitesLabel':          'Units: ',
  'marche.ws.caLabel':              'Turnover: ',
  'marche.ws.fermerDetail':         'Close cluster detail',
  'marche.ws.aucunPdv':             'No PoS in this cluster for the period.',
  'marche.ws.pdmPct':               'MS %',
  'marche.ws.comparatif':           'MS Qty comparison by channel',
  'marche.ws.aucuneComparative':    'No comparative data for the period.',
  'marche.ws.sourcePeriode':        'Source: {{source}} · Period: {{period}}',

  'marche.s6.marqCompact':          'Br.',
  'marche.s6.subtitle':             'Market: {{n}} units · Σ MS top {{top}} = {{v}}%',
  'marche.s6.titre':                'Top {{top}} Models — sell-out',
  'marche.s6.erreurTop':            'Failed to load Top Models.',
  'marche.s6.aucunSkuSamsung':      'No Samsung SKU in the top for the current scope.',
  'marche.s6.aucunModele':          'No model for the current scope.',

  // ── Marché GROUPE C — S7 Dispo (`#802`) ────────────────────────────────────
  'marche.s7.pdvMaille':            '{{n}} PoS (scope)',
  'marche.s7.pdvMarche':            '{{n}} PoS (market)',
  'marche.s7.echantillonFaible':    '⚠ {{n}} PoS — small sample',
  'marche.s7.titreNdWd':            'ND / WD — model level',
  'marche.s7.filtreBarre':          'Filtered by the global bar',
  'marche.s7.snapTitre':            'Availability (bars) · ND and WD (lines) — top 15',
  'marche.s7.erreurDispo':          'Failed to load model availability.',
  'marche.s7.aucunModeleMaille':    'No model for the current scope.',
  'marche.s7.dispoPct':             'Avail. %',
  'marche.s7.evoDispo':             'Availability trend',
  'marche.s7.evoNd':                'ND trend',
  'marche.s7.evoWd':                'WD trend',
  'marche.s7.titreSemaines':        '{{titre}} — {{n}} weeks',
  'marche.s7.mesure':               'Measure',
  'marche.s7.wdPasAbsolu':          'WD has no absolute value: its numerator sums sales, not points of sale.',
  'marche.s7.valeurAbsolue':        'Absolute value',
  'marche.s7.erreurEvo':            'Failed to load trend.',
  'marche.s7.aucuneEvoMaille':      'No trend for the current scope.',

  // ── Marché GROUPE C — S8 IHS (`#802`) ──────────────────────────────────────
  'marche.s8.titreIhsModele':       'IHS by model — combinable scope',
  'marche.s8.erreurIhsModele':      'Failed to load model IHS.',
  'marche.s8.aucunIhsModeleMaille': 'No model IHS for the current scope.',
  'marche.s8.tooltipIhsMarche':     'Market IHS: {{v}} %',
  'marche.s8.tooltipPartMarque':    '{{brand}} share: {{v}} %',
  'marche.s8.erreurEvoIhs':         'Failed to load IHS trend.',
  'marche.s8.aucuneEvoIhsMaille':   'No IHS trend for the scope.',
  'marche.s8.evoIhsSemaines':       'IHS trend — {{n}} weeks',
  'marche.s8.titreDonut':           'IHS by brand — distribution share',
  'marche.s8.totalIhs':             'Total IHS: {{v}}',
  'marche.s8.erreurIhsMarque':      'Failed to load brand IHS.',
  'marche.s8.aucunIhsPerimetre':    'No IHS for the current scope.',
  'marche.s8.titreTele':            'IHS trend by brand — telescopic axis',
  'marche.s8.erreurEvoIhsImpossible': 'Unable to load IHS trend.',
  'marche.s8.aucunHistoIhs':        'No IHS history for this scope.',

  // ── Marché GROUPE C — S9 ASP (`#802`) ──────────────────────────────────────
  'marche.s9.exclureMarques':       'Exclude brands',
  'marche.s9.exclues':              'Excluded ({{n}})',
  'marche.s9.filtreMarques':        'Brand filter',
  'commun.reinitialiser':        'Reset',
  'marche.s9.titreAspTele':         'ASP trend · telescopic axis',
  'marche.s9.titreAspTeleDepth':    'ASP trend · telescopic axis ({{y}} yrs + {{m}} mos + {{w}} wks)',
  'marche.s9.erreurAsp':            'Load error (ASP).',
  'marche.s9.aucuneSerieAsp':       'No ASP series for the current scope.',
  'marche.s9.retourMarques':        'Back to brand view',
  'marche.s9.grainAgregation':      'Aggregation level (Mother/SKU module toggle)',
  'marche.s9.erreurAspSku':         'Load error (ASP SKU).',
  'marche.s9.aucuneSerieAspSku':    'No ASP SKU series for this brand in the current scope.',
  'marche.s9.retour':               '← Back',
  'marche.s9.grain':                'Level {{g}}',
  'marche.s9.titreAspTopSku':       'ASP trend · {{n}} Top SKU',
  'marche.s9.titreAsp':             'ASP trend',
  'marche.s9.deltaUsd':             'Change in dollars vs the previous week.',
  'marche.s9.deltaPct':             'Change in percentage vs the previous week.',
  'marche.s9.aucunSkuPerimetre':    'No SKU in this scope.',
  'marche.s9.ouvrirFicheSku':       '{{sku}} — open the SKU 360 file',
  'marche.s9.rankingAsp':           'Ranking ASP — telescopic axis',
  'marche.s9.rankingAspSku':        'Ranking ASP SKU — 3M+8W axis',

  // ── Marché GROUPE C — Pop-up SKU 360 (`#802`) ──────────────────────────────
  'marche.sku360.dossierModele':    'Model file',
  'marche.sku360.aucunDossier':     'No file for this model',
  'marche.sku360.rienAfficher':     'Nothing to show for the selected period and channel.',
  'marche.sku360.modeleIndispo':    'Model unavailable',
  'marche.sku360.impossibleCharger':'Unable to load this model file.',
  'marche.sku360.dossierIndispo':   'Model file unavailable.',
  'marche.sku360.skuDeclinaison':   'SKU · variant',
  'marche.sku360.modeleAgrege':     'Aggregated model · {{n}} SKU',
  'marche.sku360.ramStock':         'RAM/Stor.',
  'marche.sku360.ramValue':         '{{ram}} / {{rom}} GB',
  'marche.sku360.configVendue':     'Best-selling config',
  'marche.sku360.declinaisons':     '{{n}} variants',
  'marche.sku360.perfCommerciale':  'Commercial performance',
  'marche.sku360.ventesSellout':    'Sell-out sales',
  'marche.sku360.uneSem':           '1 wk',
  'marche.sku360.marge':            'Margin ({{suf}})',
  'marche.sku360.margeIndispo':     'Margin unavailable: no {{canal}} reading in the window. A gap is not invented — empty, never zero.',
  'marche.sku360.presenceTerrain':  'Field presence',
  'marche.sku360.stockOuShelf':     'stock or shelf',
  'marche.sku360.subPdv':           '{{a}} / {{b}} PoS',
  'marche.sku360.rangMarche':       '{{ord}} market rank',
  'marche.sku360.repartitionRegionale': 'Regional split',
  'marche.sku360.ventesModelePct':  '% of model sales',
  'marche.sku360.trajectoireVentes':'Sales trajectory',
  'marche.sku360.erreurTrajectoire':'Failed to load trajectory.',
  'marche.sku360.aucuneDonnee8Sem': 'No data over the last 8 weeks.',
  'marche.sku360.historiquePartiel':'partial history ({{n}}/8 wks)',
  'marche.sku360.dn':               'ND',
  'marche.sku360.vsDn':             '{{delta}} vs ND',
  'marche.sku360.wdPondere':        'Weighted WD',
  'marche.sku360.sourcingContribution': 'Sourcing & Contribution',
  'marche.sku360.piciOfficiel':     'PI/CI vs Official',

  // ── Marché GROUPE C — Pop-up Brand 360 (`#802`) ────────────────────────────
  'marche.brand360.dossierMarque':  'Brand file',
  'marche.brand360.marqueIndispo':  'Brand unavailable',
  'marche.brand360.impossibleCharger': 'Unable to load this brand file.',
  'marche.brand360.aucunDossier':   'No file for this brand',
  'marche.brand360.section1':       '1 · Identity & Portfolio',
  'marche.brand360.section2':       '2 · Commercial',
  'marche.brand360.section3':       '3 · Distribution & Trend',
  'marche.brand360.topModeles':     'Top brand models',
  'marche.brand360.aucunModeleVendu': 'No model sold in the period.',
  'marche.brand360.ouvrirModele':   'Open model file',
  'marche.brand360.repartitionSegments': 'Price segment split',
  'marche.brand360.intraMarque':    'intra-brand',
  'marche.brand360.pctVentesMarque':'{{region}} · {{pct}} % of brand sales',
  'marche.brand360.repartitionRegions': 'Region split',
  'marche.brand360.presenceCartes': 'Presence (5 cards)',
  'marche.brand360.distributionGeo':'Geographic distribution',
  'marche.brand360.aucuneDonneeRegionale': 'No regional data.',
  'marche.brand360.trajectoireTele':'Telescopic trajectory · {{week}}',
  'marche.brand360.aucunPointTrajectoire': 'No trajectory point available.',
  'marche.brand360.aucuneRepartitionSegment': 'No segment split (brand with no sales in the period).',
  'marche.brand360.aucuneVenteRepartir': 'No sales to distribute.',
  'marche.brand360.legendeTrajectoire': '{{n}} points · bars = sales · line = MS Qty %',

  // ── Rapport PPT Marché (`#802`) — rendu au back via traduire+langueCourante ─
  // ⚠ `#1055` B2 — rapport EN ANGLAIS : identiques à l'entrée FR.
  'ppt.titreRapport':               'Market Report — MX RM Flow',
  'ppt.sousTitre':                  'SFMT Dashboard · Samsung Algeria',
  'ppt.pied':                       'SFMT · Orbicore · Confidential',
  'ppt.gardeLigne2':                'MX RM Presentation Week {{week}}',
  'ppt.gardeLigne3':                'SFMT Dashboard – SAMSUNG Algeria',
  'ppt.ctxRegion':                  'Region {{r}}',
  'ppt.ctxRegions':                 'Regions {{list}}',
  'ppt.ctxFicheSku':                'SKU 360 file — rank {{n}}',
  'ppt.ctxSegment':                 'Segment {{label}} $',
  'ppt.ctxCohabEnsemble':           'Coexistence {{marque}} — official vs PI/CI',
  'ppt.ctxCohabTemps':              'Coexistence — both channels over time',

  // ── Phénomènes GROUPE A (`#804`) ───────────────────────────────────────────
  'phenomenes.titrePage':           'Market Phenomena',
  'phenomenes.rbac':                'You do not have the rights to view market phenomena.',
  'phenomenes.vueVivante':          'LIVE VIEW',
  'phenomenes.vueVivanteSuffixe':   '· {{brand}} phenomena computed on the fly (not persisted, no Acknowledge/Resolve tracking).',
  'phenomenes.chargement':          'Loading phenomena',
  'phenomenes.aucunMarche':         'No market phenomenon this week',
  'phenomenes.aucunTerrain':        'No field phenomenon this week',
  'phenomenes.calme':               'Analysed — no signal detected this week (quiet market).',
  'phenomenes.pasDispo':            'Analysis not yet available',
  'phenomenes.echoue':              'This week\'s analysis failed{{msg}} — retry at next import.',
  'phenomenes.pasTourne':           'This week\'s analysis has not run yet — come back after the next import.',
  'phenomenes.detectionPartielle':  'Partial detection: some indicators could not be computed{{detail}}. Re-run detection to complete.',
  'phenomenes.lectureEchec':        'Reading phenomena failed — please retry.',
  'phenomenes.perimee':             'Analysis outdated — a newer import exists',
  'phenomenes.perimeeMsg':          'This week\'s detection predates the last import{{detail}}. What is shown was computed on a history that has changed since — it is not "nothing to report". Re-run detection ("Re-run detection" button) for this week.',
  'phenomenes.importDu':            'import of {{date}}',
  'phenomenes.analyseDu':           ', analysis of {{date}})',
  'phenomenes.histInsuffisant':     'Insufficient history',
  'phenomenes.histInsuffisantMsg':  'Analysed, but {{n}} pattern(s) remain dormant for lack of depth (e.g. year-on-year) — no anomaly, just not enough weeks in the database.',
  'phenomenes.aucunMajeur':         'No major phenomenon this week',
  'phenomenes.canalOverviewAria':   'Overview channel',
  'phenomenes.tousIndispo':         '"All" mode unavailable for a third-party brand (computed per channel).',
  'phenomenes.marqueFocusAria':     'Overview focus brand',
  'phenomenes.marcheTotal':         'Total market (Samsung ★)',
  'phenomenes.semaineDeuxPoints':   'Week:',
  'phenomenes.ouvrirLectureStrat':  'Open Strategic reading',
  'phenomenes.ouvrirPositionnement':'Open Brand positioning',
  'phenomenes.positionnement':      'Brand positioning',
  'phenomenes.configurerSeuils':    'Configure thresholds',
  'phenomenes.seuils':              'Thresholds',
  'phenomenes.contexteEvenementiel':'Event context',
  'phenomenes.sensFavorable':       'Favourable',
  'phenomenes.sensDefavorable':     'Unfavourable',
  'phenomenes.sensNeutre':          'Neutral',
  'phenomenes.confirme':            'Confirmed',
  'phenomenes.aConfirmer':          'To confirm',
  'phenomenes.relanceTitle':        'Re-runs detection on all weeks present — useful after loading an earlier week',
  'phenomenes.relancePending':      'Re-running…',
  'phenomenes.relancer':            'Re-run detection',
  'phenomenes.cockpit':             'Weekly cockpit · {{sem}} · {{n}} {{noun}}',
  'phenomenes.phenomeneSing':       'phenomenon',
  'phenomenes.phenomenePlur':       'phenomena',
  'phenomenes.aucunCetteSemaine':   'No phenomenon this week',
  'phenomenes.cockpitVide':         'The Overview cockpit will show aggregates once a detection is recorded.',
  'phenomenes.total':               'Total',
  'phenomenes.majeurs':             'Major',
  'phenomenes.menaces':             'Threats',
  'phenomenes.opportunites':        'Opportunities',
  'phenomenes.confirmes':           'Confirmed',
  'phenomenes.neutres':             'Neutral',
  'phenomenes.parSens':             'By direction',
  'phenomenes.parPattern':          'By pattern',
  'phenomenes.parFamilleKpi':       'By KPI family',
  'phenomenes.record':              'Record',
  'phenomenes.acceleration':        'Acceleration',
  'phenomenes.ecartStatistique':    'Statistical gap',
  'phenomenes.glissementAnnuel':    'Year-on-year',
  'phenomenes.familleDistribution': 'Distribution',
  'phenomenes.familleVisibilite':   'Shelf visibility',
  'phenomenes.familleVolumesPrix':  'Volumes & prices',
  'phenomenes.familleStock':        'Stock & stock-outs',
  'phenomenes.familleConcurrence':  'Competition',
  'phenomenes.familleActivites':    'Field activities',
  'phenomenes.patternsDetection':   'Detection patterns',
  'phenomenes.clicDeplier':         'Click = expand',
  'phenomenes.patAccelerations':    'ACCELERATIONS',
  'phenomenes.subPics12':           '12-wk peaks',
  'phenomenes.subSeries':           'Lasting streaks',
  'phenomenes.subVariations':       'Sharp swings',
  'phenomenes.subAnomalies':        'Stat. anomalies',
  'phenomenes.subEcarts':           'Yearly gaps',
  'phenomenes.indispoHistorique':   'Unavailable — history < 52 weeks',
  'phenomenes.replier':             '↑ collapse',
  'phenomenes.plusAutres':          '+ {{n}} more',
  'phenomenes.moins52':             '< 52 wks',
  'phenomenes.aucunSignal':         'no signal',
  'phenomenes.investigationDispo':  'Investigation available',
  'phenomenes.investigation':       'Investigation',
  'phenomenes.detecteParPatterns':  'Detected by {{n}} patterns → stronger reliability: {{signatures}}',
  'phenomenes.vsMoyenneSem':        'vs {{n}}-wk average:',
  'phenomenes.vsValue':             'vs {{value}}',
  'phenomenes.moyenneSemParen':     '({{n}}-wk average)',
  'phenomenes.surfaceSeverite':     'Phenomena — {{niveau}}',
  'phenomenes.niveauMajeur':        'major',
  'phenomenes.niveauModere':        'moderate',
  'phenomenes.niveauMineur':        'minor',
  'phenomenes.thRef':               'Ref.',
  'phenomenes.verdictDefavorable':  'UNFAVOURABLE',
  'phenomenes.verdictFavorable':    'FAVOURABLE',
  'phenomenes.verdictAInvestiguer': 'TO INVESTIGATE',
  'phenomenes.mvtTropFaible':       'Movement too small to break down',
  'phenomenes.decompoNonSignif':    'Non-significant breakdown (unstable contributions)',
  'phenomenes.nonSignificatif':     'non-significant',
  'phenomenes.contributionParRegion':'Contribution by region:',

  // ── Phénomènes GROUPE B (`#805`) ───────────────────────────────────────────
  'phenomenes.apparition':          'appearance',
  'phenomenes.verbStreak':          'streak',
  'phenomenes.verbAccel':           'acceleration',
  'phenomenes.verbZscore':          'statistical anomaly',
  'phenomenes.verbRecord':          'record',
  'phenomenes.verbYoy':             'yearly gap',
  'phenomenes.sur':                 'over',
  'phenomenes.semAbbr':             'wks',
  'phenomenes.scoreConcentration':  '· concentration score {{score}}',
  'phenomenes.pasAssezConcur':      'Not enough competitor data for this scope',
  'phenomenes.aucuneDecompoMarque': 'No brand breakdown available for this week. Try another scope (region / national).',
  'phenomenes.drillConcurrent':     'Competitor drill',
  'phenomenes.perimetre':           'Scope',
  'phenomenes.voirNational':        'see national',
  'phenomenes.pdmSamsungPeri':      'Samsung MS — {{periscope}}',
  'phenomenes.volumeSamsungMarche': 'Samsung volume {{a}} → {{b}} · Market volume {{c}} → {{d}}',
  'phenomenes.mvtPdmParMarque':     'MS movement by brand',
  'phenomenes.concentrationSomme':  'Concentration {{label}} — top1 = {{pct}} % of the sum',
  'phenomenes.concentre':           'Concentrated',
  'phenomenes.diffus':              'Diffuse',
  'phenomenes.contributeursMvt':    'Contributors to the movement',
  'phenomenes.prennentParts':       'taking share from Samsung',
  'phenomenes.perdentAuProfit':     'losing share to Samsung',
  'phenomenes.poussentMvt':         'driving the movement',
  'phenomenes.compensateurs':       'Offsetters',
  'phenomenes.perdentAussi':        'also losing share',
  'phenomenes.gagnentEgalement':    'also gaining share',
  'phenomenes.sensInverseMarque':   'moving the other way',
  'phenomenes.voirMarquesPlus_one':     'see {{count}} more brand',
  'phenomenes.voirMarquesPlus_other':     'see {{count}} more brands',
  'phenomenes.replierSimple':       'collapse',
  'phenomenes.countMarques_one':        '{{count}} brand',
  'phenomenes.countMarques_other':        '{{count}} brands',
  'phenomenes.nouvelEntrant':       'new entrant',
  'phenomenes.sorti':               'exited',
  'phenomenes.contribReelle':       'Actual contribution: {{pct}} (residual mix effect)',
  'phenomenes.contribution':        'Contribution: {{pct}}',
  'phenomenes.labelNatifPct':       '{{label}}: {{val}} ({{pct}})',
  'phenomenes.valeurClampee':       'Actual value {{pct}} visually clamped to ±100 % (mix effect)',
  'phenomenes.niveauWilayaMin':     'wilaya',
  'phenomenes.niveauMarqueMin':     'brand',
  'phenomenes.decompositionPar':    'Breakdown by {{niveau}}',
  'phenomenes.contexteAmont':       '· upstream {{niveau}} context',
  'phenomenes.calculDecompo':       'Computing breakdown…',
  'phenomenes.calculEchec':         '⚠ Computation failed',
  'phenomenes.ventilationPasCalculee': 'The {{niveau}} breakdown could not be computed. {{msg}}',
  'phenomenes.ventilationSansObjet':'Breakdown not applicable',
  'phenomenes.indicateurPasDecompo':'This indicator does not break down at {{niveau}} level. {{msg}}',
  'phenomenes.sansDonnees':         'No data this week',
  'phenomenes.aucuneDonneeVentiler':'No data to break down at {{niveau}} level over the compared period.',
  'phenomenes.aucunContributeur':   'No contributor',
  'phenomenes.aucuneDecompoNiveau': 'No breakdown available at {{niveau}} level.',
  'phenomenes.autres':              'Others',
  'phenomenes.partNette':           'net share {{v}} %',
  'phenomenes.noteCompensations':   '⚡ Some contributors move against the national Δ (offsets — 1st-order Bennet decomposition, expected).',
  'phenomenes.axeInvestigation':    'Investigation axis',
  'phenomenes.geo':                 'Geo',
  'phenomenes.surfaceDrill':        'Phenomena — {{niveau}} drill',

  // ── Phénomènes GROUPE C (`#806`) ───────────────────────────────────────────
  'phenomenes.aucuneContribPdv':    'No PoS contribution for this context.',
  'phenomenes.sourceVsMoyenne':     'vs {{n}}-wk average',
  'phenomenes.effetAggrave':        'Worsens',
  'phenomenes.effetAmeliore':       'Improves',
  'phenomenes.grpAggravent':        'Worsening 🔴',
  'phenomenes.grpAmeliorent':       'Improving 🟢',
  'phenomenes.grpMouvements':       'Movements',
  'phenomenes.deltaLabel':          'Δ:',
  'phenomenes.thCodePos':           'PoS code',
  'phenomenes.thEnseigne':          'Banner',
  'phenomenes.thEffet':             'Effect',
  'phenomenes.thEcart':             'Gap',
  'phenomenes.thPartMouvement':     'Share of movement',
  'phenomenes.thPartNette':         'Net share (technical)',
  'phenomenes.ligneGroupePdv':      '{{titre}} · {{n}} PoS',
  'phenomenes.autresPdvPlafond':    'Other PoS (beyond the cap)',
  'phenomenes.wilayaCap':           'Wilaya',
  'phenomenes.phenomeneBreadcrumb': 'Phenomenon',
  'phenomenes.enNappe':             '· flat view',
  'phenomenes.investigationPhenomene': 'Phenomenon investigation',
  'phenomenes.canalLabel':          'channel: {{canal}}',
  'phenomenes.drillNonDispo':       'Drill not available for this KPI',
  'phenomenes.kpiNonDecomposable':  'KPI {{kpi}} cannot be broken down by geographic cascade.',

  // ── Chrome partagé (`#806`) ────────────────────────────────────────────────
  'commun.modalPleinEcran':         'Full-screen modal',
  'commun.erreurChargement':        'Loading error',
  'commun.erreurSurvenue':          'An error occurred.',

  'langue.fr':                  'FR',
  'langue.en':                  'EN',
  'langue.titre':               'Interface language',

  'widget.sfm.tauxVisite':      'Visit rate',
  'widget.sfm.dispersion':      'Dispersion',
  'widget.sfm.conformite':      'Compliance',
  'widget.da.qualite':          'Quality',
  'widget.da.preuves':          'Proofs',
  'widget.da.ecartQP':          'Q–P gap',
  'widget.mxrm.pdmVol':         'MS Qty',
  'widget.mxrm.ihs':            'IHS',

  'kpi.K01':                    'MS Qty',
  'kpi.K02':                    'MS Value',
  'kpi.K03':                    'Stock Share',
  'kpi.K04':                    'ND',
  'kpi.K05':                    'WD',
  'kpi.K06':                    'Shelf Share',
  'kpi.K07':                    'IHS',
  'kpi.K08':                    'Sales Volume',
  'kpi.K09':                    'Turnover',
  'kpi.K10':                    'ASP',
  'kpi.K11':                    'Avg Stock',
  'kpi.K12':                    'PoS with ≥1 SKU out of stock',
  'kpi.K13':                    'Out-of-stock occurrences (PoS × SKU × wk)',
  'kpi.K14':                    'Stock Rotation',
  'kpi.K16':                    'PoS coverage',
  'kpi.K17':                    'Visit frequency',
  'kpi.K18':                    'Detected anomalies',
  'kpi.K19':                    'DQ exclusion rate',
  'kpi.K24':                    'Top competitor MS',
  'kpi.K25':                    'Gap vs top competitor',
  'kpi.K26':                    'Active brands',
  'kpi.K27':                    'Competitor ASP',
  'kpi.K28':                    'Distribution loss',

  'kpi.K01.description':         'Samsung market share in units sold. Rise is favourable.',
  'kpi.K02.description':         'Samsung market share in value (turnover). Rise is favourable.',
  'kpi.K03.description':         'Samsung share of units in stock. Rise is favourable.',
  'kpi.K04.description':         'Share of PoS carrying Samsung. Rise is favourable.',
  'kpi.K05.description':         'Distribution weighted by PoS sales. Rise is favourable.',
  'kpi.K06.description':         'Samsung shelf share (facings). Rise is favourable.',
  'kpi.K07.description':         'Samsung share of in-store stock and facings. Rise is favourable.',
  'kpi.K08.description':         'Samsung units sold. Rise is favourable.',
  'kpi.K09.description':         'Samsung turnover. Rise is favourable.',
  'kpi.K10.description':         'Samsung average selling price. Neither good nor bad in itself: to investigate.',
  'kpi.K11.description':         'Average Samsung stock per PoS. Rise is favourable.',
  'kpi.K12.description':         'Number of PoS with at least one Samsung SKU out of stock. Rise is unfavourable.',
  'kpi.K14.description':         'Sales relative to average stock. Rise is favourable.',
  'kpi.K16.description':         'Share of routed PoS actually visited. Rise is favourable.',
  'kpi.K17.description':         'Visits per routed PoS. Rise is favourable.',
  'kpi.K18.description':         'Number of critical anomalies flagged. Rise is unfavourable.',
  'kpi.K19.description':         'Share of readings excluded by data quality. Rise is unfavourable.',
  'kpi.K24.description':         'Market share of the top competitor, named. Rise is unfavourable.',
  'kpi.K25.description':         'Market-share gap between Samsung and the top competitor, named. Rise is favourable.',
  'kpi.K26.description':         'Number of brands active on the market. Neither good nor bad in itself: to investigate.',
  'kpi.K27.description':         'Competitors\' average selling price. Neither good nor bad in itself: to investigate.',
  'kpi.K28.description':         'PoS that stop carrying Samsung, confirmed over two weeks. Always unfavourable.',
  'kpi.K04.long':               'Numeric Distribution',
  'kpi.K05.long':               'Weighted Distribution',
  'kpi.K07.long':               'In-House Share',
  'kpi.K10.long':               'Average Selling Price',

  'canal.retail':               'Retail',
  'canal.wholesale':            'Wholesale',
  'circuit.piCi':               'PI/CI',
  'ampleur.majeur':             'Major',
  'ampleur.modere':             'Moderate',
  'ampleur.mineur':             'Minor',
  'statut.termine':             'Completed',
  'statut.publiee':             'Published',
  'statut.nonPubliee':          'Not published',

  // ── Brand vs Brand — GROUPE A (`#810`) ──
  'bvb.ancre':                  'Anchor: {{w}} · {{c}}',
  'bvb.tousCanaux':             'all channels',
  'bvb.periode':                'Period',
  'bvb.periode8sem':            '8 wks',
  'bvb.periode12sem':           '12 wks',
  'bvb.periodeTout':            'All',
  'bvb.affichage':              'Display',
  'bvb.groupes':                'Groups',
  'bvb.marquesCompteur':        'Brands ({{sel}}/{{m}})',
  'bvb.retirer':                'Remove {{code}}',
  'bvb.ajouter':                'add',
  'bvb.aucuneMarqueDispo':      'No brand available',
  'bvb.dejaSelectionne':        '{{x}} already selected',
  'bvb.maxMarques':             'Maximum {{m}} brands',
  'bvb.leadershipTitre':        'Leadership by indicator',
  'bvb.kpiWd':                  'WD · weighted',
  'bvb.kpiShelf':               'Shelf · facings',
  'bvb.kpiIhs':                 'IHS · occupancy',
  'bvb.nbSku':                  'SKU count',
  'bvb.aucuneDonnee':           '(no data)',
  'bvb.faceTitre':              'Head-to-head comparison',
  'bvb.leaderEn':               'leader in ',
  'bvb.vertGras':               'bold green',
  'bvb.metriqueSansPendant':    ' metric without counterpart',
  'bvb.sansPendantTooltip':     'Metric without counterpart — identical in both modes',
  'bvb.basculeUnite':           'Unit toggle: % or Value',
  'bvb.grpPartsMarche':         'MARKET SHARE',
  'bvb.grpDistribution':        'DISTRIBUTION & PRESENCE',
  'bvb.grpCommercial':          'SALES',
  'bvb.grpPortefeuille':        'PORTFOLIO',
  'bvb.colPdmVol':              'MS Qty',
  'bvb.colPdmVal':              'MS Value',
  'bvb.sansPendant':            'Without counterpart',
  'bvb.marqueClient':           'Client brand',
  'bvb.totalSelection':         'Total (selection)',
  'bvb.piedTable_one':              '{{count}} brand · {{c}} indicators · {{f}} families',
  'bvb.piedTable_other':              '{{count}} brands · {{c}} indicators · {{f}} families',
  'bvb.segmentsTitre':          'Price segments',
  'bvb.aucunSegmentVente':      'no sales segment',
  'bvb.erreurComparaison':      'Error: failed to load the comparison.',
  'bvb.selectionnezMarque':     'Select at least one brand.',
  'bvb.mappingTitre':           'Strategic mapping',
  'bvb.mappingDesc':            'X: ASP ($) · Y: sales (units) · size: turnover · bounds: market avg ASP ({{asp}}) × median sales ({{med}})',
  'bvb.mappingVide':            'No brand with sales and ASP > 0.',
  'bvb.quadPremiumConcentre':   'Premium niche',
  'bvb.quadVolumeEntree':       'Volume · entry',
  'bvb.quadEntreeFaible':       'Entry · low volume',
  'bvb.medianeVentes':          'Median sales {{v}}',
  'bvb.aspMarche':              'Market ASP {{n}}$',
  'bvb.mappingAlerte':          '⚠ All selected brands fall in the same quadrant ({{q}}) — the market ASP × median sales segmentation does not discriminate here. Widen the selection or choose another bound.',

  // ── GROUPE B1 (`#810`) ──
  'bvb.lentilleEchelle':        'Shared scale across the 3 cards · ±{{max}} pt (selection max). The price: on the card with the smallest gaps, bars look short — that is what keeps the comparison honest from one card to the next.',
  'bvb.lentilleTitreA':         'Shelf occupancy vs VALUE share',
  'bvb.lentilleTitreB':         'Shelf occupancy vs VOLUME share',
  'bvb.lentilleTitreC':         'Network quality',
  'bvb.lentillePrefixe':        'Shelf Share − MS ',
  'bvb.lentilleSousA2':         ', in points. Above zero: the brand OVER-occupies the shelf relative to its turnover share. Below: it UNDER-occupies.',
  'bvb.lentilleSousB2':         ', in points. Above zero: more shelf than its share of UNITS sold. The gap with the VALUE reading reveals price positioning.',
  'bvb.lentilleSousC':          'Gap to neutral WD ÷ ND, in points. Above zero: present in the points of sale that WEIGH. Below: mostly in small ones. Raw ratio shown as support value.',
  'bvb.heatmapTitre':           'MS × price segment heatmap',
  'bvb.heatmapSous':            'Each brand\'s market share WITHIN each band (brand sales ÷ segment market sales × 100). The filled length of each cell follows MS (horizontal gauge, 0-100 %).',
  'bvb.heatmapBandeau':         'Δ per cell not served — variation dot deliberately omitted.',
  'bvb.heatmapErreur':          '⚠ Market totals by segment unavailable — heatmap cannot be computed.',
  'bvb.pdvBatailleTitre':       'PoS battle — conquest · contested · defended',
  'bvb.pdvBatailleVide':        'Select at least two brands to compare their PoS footprints.',
  'bvb.pdvBatailleSous':        'Compared footprint on the window\'s active base',
  'bvb.uPdv':                   '{{u}} PoS',
  'bvb.limiteParCarte':         ' · capped at {{n}} PoS per card.',
  'bvb.reference':              'Reference:',
  'bvb.concurrent':             'Competitor:',
  'bvb.conquete':               'Conquest',
  'bvb.conqueteSous1':          ' is there, ',
  'bvb.conqueteSous2':          ' is not. To take.',
  'bvb.volumeConcurrent':       'Competitor volume',
  'bvb.conteste':               'Contested',
  'bvb.contesteSous':           'Both brands are present. Arbitrate the shelf.',
  'bvb.defendu':                'Defended',
  'bvb.defenduSous':            ' alone is there. To protect.',
  'bvb.votreVolume':            'Your volume',
  'bvb.bilanPrefixe':           'Window summary:',
  'bvb.bilanConquete':          ' conquest · ',
  'bvb.bilanConteste':          ' contested · ',
  'bvb.bilanDefendu':           ' defended · ',
  'bvb.bilanAucun':             ' neither = ',
  'bvb.bilanBouclage':          ' (strict reconciliation).',
  'bvb.pdvCouverturePre':       'PoS · ',
  'bvb.pdvCouverturePost':      ' of coverage',
  'bvb.pdvSurCouverture':       '{{c}} of {{u}} covered PoS',
  'bvb.volumesTotaux':          'Total volumes',
  'bvb.volumeTotal':            'Total volume',
  'bvb.moyParPdv':              'Avg / PoS',
  'bvb.aucunPdvStatut':         'No PoS in this status',
  'bvb.wilayaPrincipale':       'Main wilaya (PoS count)',
  // ── GROUPE B2 (`#810`) ──
  'bvb.depTitre':                'SKU dependency — concentration & angle of attack',
  'bvb.depVide':                 'Select at least two brands to compare their portfolios.',
  'bvb.depSous':                 'How many references carry the brand, and which model to attack.',
  'bvb.grainSousPrefixe':        'Grain: ',
  'bvb.grainMotModele':          'model',
  'bvb.grainLabel':              'Grain:',
  'bvb.angleAttaque':            'Angle of attack',
  'bvb.prixMedian':              'median price',
  'bvb.faceA':                   'against',
  'bvb.dossiersTitre':           'SKU 360 files',
  'bvb.dossiersComplement':      ' — full drawer content, for both models of the duel',
  'bvb.aucunRival':              'No matched rival — no file to show.',
  'bvb.surCeTerrain':            ' on this ground',
  'bvb.modelesTolerance':        ' — {{n}} {{mot}} within tolerance',
  'bvb.aucunModeleTolerance':    'No {{ref}} model falls within ±{{n}} % of the top competitor\'s price. No matching possible on this price lens.',
  'bvb.piedModeles':             'Δ price = gap to the top competitor\'s median price. Bar = volume relative to the best placed.',
  'bvb.pointsDeVente':           'Points of sale',
  'bvb.pdvQuiVendent':           ' — selling the most {{cible}}',
  'bvb.leNumeroUn':              'the no.1',
  'bvb.affichesSur':             '{{n}} shown of {{m}}',
  'bvb.plafond':                 ' · cap {{t}}',
  'bvb.aucunPdvVend':            'No PoS sells this model in the window.',
  'bvb.cibleMot':                'target',
  'bvb.colVolPdv':               'PoS vol.',
  'bvb.colCaPdv':                'PoS turnover',
  'bvb.notreCote':               'Our side',
  'commun.concurrent':          'Competitor',
  'bvb.portesPar':               'carried by',
  'bvb.no1Grain':                ' · no.1 {{kind}}',
  'bvb.verbePorte':              'carries',
  'bvb.verbePortent':            'carry',
  'bvb.moitieVentes':            ' {{kindP}} {{verbe}} half the sales',
  'bvb.auTotal':                 ' · {{total}} in total · {{vol}} u.',
  'bvb.geoTitre':                'Geographic battle',
  'bvb.neutre':                  'neutral',
  'bvb.geoTooltipPdm':           ' · MS {{v}}',
  'bvb.pdmParRegion':            'MS by region',
  'bvb.wilayasDisputees':        'Most contested wilayas',
  'bvb.wilayasDisputeesSous':    '{{marque}} is not leader, or the gap with no.2 is under 10 pts.',
  'bvb.aucuneWilayaDisputee':    'No contested wilaya (the client brand leads everywhere by > 10 pts).',
  'bvb.momentumTitre':           'Momentum · MS {{m}}',
  'bvb.retirerFiltreWilaya':     'Remove wilaya filter',
  'bvb.momentumSousScope':       'MS trend in wilaya {{scope}} · 3 months + 8 weeks',
  'bvb.momentumSousStd':         '3 months + 8 weeks · telescopic axis (assumed deviation vs standard 2Y+3M+6W)',
  'bvb.metriqueMomentum':        'Momentum metric',
  'bvb.uMois':                   'mo',
  'bvb.uSem':                    'wk',

  // ── Comparateur SKU (`#813`) ──
  'comparateur.periodeAnalyse':        'Analysis period',
  'comparateur.predefinie':            'Preset',
  'comparateur.personnalisee':         'Custom',
  'comparateur.periodePredefinie':     'Preset period',
  'comparateur.dateDebut':             'Start date',
  'comparateur.dateFin':               'End date',
  'comparateur.ytd':                   'Year to date (YTD)',
  'comparateur.vider':                 '↺ clear',
  'comparateur.slotsComparaison':      'Comparison slots',
  'comparateur.recherchePrix':         'Price search',
  'comparateur.effacerResultats':      'Clear results and re-enable slots',
  'comparateur.effacer':               '× clear',
  'comparateur.erreurRecherche':       'Search error.',
  'comparateur.selectionnezJusqua':    'Select up to {{n}} SKUs',
  'comparateur.choisissez':            'Choose a brand and a SKU in each slot on the left',
  'comparateur.comparaison':           'Comparison',
  'comparateur.specifications':        'Specifications',
  'comparateur.metriques':             'Metrics',
  'commun.ramGo':                 'RAM (GB)',
  'commun.romGo':                 'ROM (GB)',
  'comparateur.ecranPo':               'Screen (in)',
  'comparateur.cameraMp':              'Camera (MP)',
  'comparateur.radarSpecs':            'Radar — Specifications',
  'comparateur.radarMetriques':        'Radar — Business metrics',
  'comparateur.specsTechniques':       'Technical specifications',
  'comparateur.metriquesBusiness':     'Business metrics',
  'comparateur.marqueSlot':            'Brand slot {{n}}',
  'comparateur.effacerSlot':           'Clear slot {{n}}',
  'comparateur.tiretMarque':           '— Brand —',
  'commun.aucuneMarque':          'No brand',
  'comparateur.aucunSku':              'No SKU',
  'comparateur.dejaSelectionne':       '(already selected)',
  'comparateur.rechercherSku':         'Search a SKU…',
  'comparateur.resultatsSkuSlot':      'SKU results slot {{n}}',
  'comparateur.retirerDuSlot':         'Remove {{code}} from slot {{n}}',
  'comparateur.aucunSkuTrouve':        'No SKU found',
  'comparateur.prix':                  'Price',
  'comparateur.vSegment':              'Seg. sales',
  'comparateur.partSeg':               'Seg. share',
  'comparateur.vitrine':               'Display',
  'comparateur.selectRadar':           'Select ≥ 2 SKUs for radar comparison',
  'comparateur.legendeSerie':          '{{marque}}: {{code}}',
  'commun.ecran':                 'Screen',
  'comparateur.maxSkuSel':             'Max 3 SKUs selected',
  'comparateur.aucunePhoto':           'No photo',
  'comparateur.categorie':             'Category',
  'commun.statut':                'Status',
  'commun.actif':                 'Active',
  'commun.inactif':               'Inactive',
  'comparateur.taillePo':              'Size (in)',
  'comparateur.selectSpecs':           'Select up to 3 SKUs to compare specifications',
  'comparateur.utilisezSlots':         'Use the slots above to choose the SKUs to compare.',
  'comparateur.segmentPrix':           'Price segment',
  'comparateur.ventesTotal':           'Total sales',
  'comparateur.partSegPoint':          'Seg. share',
  'comparateur.stockTotal':            'Total stock',
  'comparateur.vitrineTotal':          'Total display',
  'comparateur.selectMetriques':       'Select at least 1 SKU to see the metrics.',
  'comparateur.reseau':                'Network',
  'comparateur.prixUsd':               'Price USD',
  'comparateur.prixDzd':               'Price DZD',
  'comparateur.rechercheConcurrentielle': 'Competitive price search',
  'comparateur.trouvezSkus':           'Find competitor SKUs within a given price range',
  'comparateur.erreurReessayer':       'Search error. Please try again.',
  'comparateur.chercherMarque':        'Search a brand…',
  'comparateur.chercherModele':        'Search a model…',
  'comparateur.manuel':                'Manual',
  'comparateur.devise':                'Currency',
  'comparateur.fourchette':            'Range',
  'comparateur.prixMoyen':             'Avg price: {{p}} USD',
  'comparateur.prixCurrency':          'Price ({{currency}})',
  'comparateur.aucunResultat':         'No result found',
  'comparateur.skuTrouve_one':             '→ {{count}} SKU found',
  'comparateur.skuTrouve_other':             '→ {{count}} SKUs found',
  'comparateur.derniereSemaine':       'Last week',
  'comparateur.dern4sem':              'Last 4 wks',
  'comparateur.dern8sem':              'Last 8 wks',
  'comparateur.mois':                  'Month',
  'comparateur.aucunConcurrent':       'No competitor SKU found in this range',
  'comparateur.essayezElargir':        '{{range}} · Try widening the range.',
  'comparateur.tousMasques':           'All SKUs hidden',
  'comparateur.reafficherTous':        'Show all ({{n}})',
  'comparateur.lancerComparaison':     'Start comparison ({{n}}/{{max}})',
  'comparateur.concurrentsTrouves':    'Competitors found',
  'comparateur.remplirSlots':          'Fill the slots with the checked SKUs',
  'comparateur.selectAuMoins':         'Select at least {{n}} SKU',
  'comparateur.performanceMarche':     'Market performance',
  'comparateur.stockMoy':              'Avg stock',
  'comparateur.vitrineMoy':            'Avg display',
  'comparateur.exclure':               'Exclude {{marque}}',

  // ── `#817` · COHABITATION (Officiel vs PI/CI) + commun.* partages ───────────
  'commun.fermer':                    'Close',
  'commun.pdvMasquerListe':           'Hide list',
  'commun.pdvListe':                  'List ({{n}} PoS)',
  'commun.pdvListeTronquee':          'List ({{n}} PoS of {{total}})',
  'commun.pdvTronqueeDebut':          '⚠ Truncated list — {{n}} rows shown of',
  'commun.pdvTronqueeFin':            'points of sale.',
  'commun.pdvNom':                    'Name',
  'commun.pdvAucun':                  'No point of sale',
  'commun.mois.1':                    'Jan',
  'commun.mois.2':                    'Feb',
  'commun.mois.3':                    'Mar',
  'commun.mois.4':                    'Apr',
  'commun.mois.5':                    'May',
  'commun.mois.6':                    'Jun',
  'commun.mois.7':                    'Jul',
  'commun.mois.8':                    'Aug',
  'commun.mois.9':                    'Sep',
  'commun.mois.10':                   'Oct',
  'commun.mois.11':                   'Nov',
  'commun.mois.12':                   'Dec',
  'cohabitation.h1':                  'Official / PI/CI coexistence',
  'cohabitation.enCours':             'ongoing coexistences',
  'cohabitation.erreurCharger':       'Failed to load coexistences.',
  'cohabitation.indispo':             'Coexistences are not available for now.',
  'cohabitation.indispoSous':         'It is neither loading nor a failure: no data was received.',
  'cohabitation.aucuneMarque':        'No brand has a reference recorded in both channels on this channel.',
  'cohabitation.marqueIndispo':       '{{marque}} coexistences are not available for now.',
  'cohabitation.marquesConcernees':   'Brands concerned — {{n}}',
  'cohabitation.skuEnCohabitation':   '{{marque}} — {{n}} SKU in coexistence',
  'cohabitation.active':              'active',
  'cohabitation.actives':             'active',
  'cohabitation.archivee':            'archived',
  'cohabitation.archivees':           'archived',
  'cohabitation.masquerArchive':      'Hide archive',
  'cohabitation.archiveN':            'Archive — {{n}}',
  'cohabitation.critereDebut':        'Active: both channels have',
  'cohabitation.critereVendu':        'sold',
  'cohabitation.critereVenduTitre':   'Sold, not present: stock that no longer moves is not a coexistence.',
  'cohabitation.critereFin':          'in the same week, at least once in the last {{n}} weeks',
  'cohabitation.prouveeEn':           'proven in {{semaine}}',
  'cohabitation.videTitre':           'No ongoing {{marque}} coexistence.',
  'cohabitation.videArchivees':       'ended coexistences are kept in the archive, with their outcome.',
  'cohabitation.videArchivee':        'ended coexistence is kept in the archive, with its outcome.',
  'cohabitation.voirArchive':         'See archive',
  'cohabitation.videSansArchive':     'No {{marque}} reference was recorded in both the official channel and PI/CI over the window.',
  'cohabitation.comparerDansLeTemps': 'Compare {{sku}} over time',
  'cohabitation.cohabitation':        'coexistence',
  'cohabitation.dejaEtablie':         'already established at the start of our data',
  'cohabitation.depuis':              'coexistence since',
  'cohabitation.premierPici':         'PI/CI',
  'cohabitation.premierOfficiel':     'the official channel',
  'cohabitation.etaitLaEnPremier':    'was there first',
  'cohabitation.memeSemaine':         'both channels appeared the same week',
  'cohabitation.officielMinuscule':   'official',
  'cohabitation.nc':                  'n/a',
  'cohabitation.ncTitre':             'no comparable previous week',
  'cohabitation.volume':              'Volume',
  'cohabitation.lineaire':            'Shelf',
  'cohabitation.tiroirAria':          'Comparison of both channels',
  'cohabitation.tiroirSousTitre':     'Both channels over time ·',
  'cohabitation.erreurSeries':        'Failed to load series.',
  'cohabitation.axeTelescopique':     'Telescopic axis — 2 years · 3 months · 6 weeks.',
  'cohabitation.donneesDepuis':       '⚠ Data available since',
  'cohabitation.donneesDepuisFin':    ': earlier periods are absent, not zero.',
  'cohabitation.fluxSemaine':         'Flow measures are brought back to the WEEK at every grain, otherwise a "month" point would be worth four "week" points.',
  'cohabitation.gCa':                 'Turnover',
  'cohabitation.gDispo':              'Availability',
  'cohabitation.uSem':                'u / wk',
  'cohabitation.usdSem':              'USD / wk',
  'cohabitation.grainAnnee':          'year',
  'cohabitation.grainMois':           'month',
  'cohabitation.partagePdv':          'Points of sale split',
  'cohabitation.universSku':          'SKU universe',
  'cohabitation.neBouclentPas':       '⚠ DO NOT RECONCILE.',
  'cohabitation.piciSeul':            'PI/CI only',
  'cohabitation.lesDeux':             'both',
  'cohabitation.officielSeul':        'official only',
  'cohabitation.ventesPici':          'PI/CI sales',
  'cohabitation.ventesSku':           'SKU sales',
  'cohabitation.ventesOfficiel':      'Official sales',
  'commun.aucunPdv':            'No PoS',
  'cohabitation.listePdvAria':        '{{part}} — list of points of sale',
  'cohabitation.toutes':              'All ({{n}})',
  'cohabitation.noteA1':              '⚠ The',
  'cohabitation.noteA2':              'end',
  'cohabitation.noteA3':              'is not a recorded event, it is a',
  'cohabitation.noteA4':              'deduction',
  'cohabitation.noteB':               'the last week both channels sold, pronounced by four weeks without sales from one of them.',
  'cohabitation.noteC1':              '"—" means they',
  'cohabitation.noteC2':              'never sold the same week',
  'cohabitation.noteD':               'The ranking is recomputed at each read: if an extinct channel sells again, the coexistence leaves here and becomes a card again.',
  'cohabitation.colDebut':            'Start',
  'cohabitation.colFin':              'End',
  'cohabitation.colDuree':            'Duration',
  'cohabitation.colPremierArrive':    'First arrived',
  'cohabitation.colRestait':          'Remained at the end',
  'cohabitation.colVolDebut':         'Volumes at start',
  'cohabitation.colVolFin':           'Volumes at end',
  'cohabitation.ouvrirDetail':        'Open comparative detail',
  'cohabitation.semNb':               '{{n}} wks',
  'cohabitation.personne':            'nobody',
  'cohabitation.surfaceFiche':        'Coexistence — SKU file',
  'cohabitation.surfaceArchive':      'Coexistence — archive',
  'cohabitation.surfaceEnsemble':     'Coexistence — overview',

  // ── `#819` · LA FONTE — un doublon (même FR **et** même EN) = UNE clé `commun.*` ────
  'commun.echecChargement':              'Failed to load.',
  'commun.canal':                        'Channel',
  'commun.district':                     'District',
  'commun.last4sem':                     'Last 4 weeks',
  'commun.last8sem':                     'Last 8 weeks',
  'commun.last3mois':                    'Last 3 months',
  'commun.last6mois':                    'Last 6 months',
  'commun.last12mois':                   'Last 12 months',

  'commun.marques':                      'Brands',
  'commun.pdv':                          'PoS',

  // ── `#819` · GEO-DONNÉES (la carte des wilayas) ─────────────────────────
  'geo.colorerPar':                   'Color the map by {{kpi}}',
  'geo.canaux':               'Channels',
  'geo.pdvActifs':            '{{n}} active PoS',
  'geo.pdvActifsTitre':       'Distinct PoS with at least one observation over the displayed period, for the selected channel',
  'geo.algerie':              'Algeria',
  'geo.nombrePdv':            'Number of PoS',
  'geo.horsScopeSud':         'Out of scope (SOUTH)',
  'geo.ndGlose':              'Numeric distribution',
  'geo.tipVolumePdm':         'Volume: {{v}} · MS: {{p}}%',
  'geo.tipVolume':            'Volume: {{v}}',
  'geo.tipPdvDb':             'PoS DB: {{n}}',
  'geo.tipPdvRouting':        'PoS Routing: {{n}}',
  'geo.tipKpiValeur':         '{{kpi}}: {{valeur}}',
  'geo.tipRegionPdv':         '{{region}} · {{n}} PoS',
  'geo.tipPdv':               '{{n}} PoS',
  'geo.cliquerSelectionner':  'Click to select',
  'geo.derniereVisite':       'Last visit',
  'geo.vsPrec':               'vs prev.',
  'geo.titrePdv':             '{{wilaya}} · {{region}} · PoS',
  'geo.parSeg':               'By seg.',
  'geo.nonEvalueAbbr':        'n/a',
  'geo.nonEvalues':           'Not assessed — observed over the period, no segment assigned',
  'geo.imageSkuIndispo':      'SKU image unavailable',
  'geo.erreurTop3':           'Failed to load Top-3 SKU.',
  'geo.wilayaZoomPdv':        'Wilaya (PoS zoom)',
  'geo.donneesIndispo':       'data unavailable',
  'geo.vsRegional':           'vs Regional',
  'geo.contribNationale':     '{{prefix}}National contribution',
  'geo.contribRegionale':     '{{prefix}}Regional contribution',
  'geo.nbPdvMarque':          'Brand PoS count',
  'geo.chargementDuel':       'Loading duel…',
  'geo.chargementCarte':      'Loading map',
  'geo.surfaceCarte':         'The map',
  'geo.surfaceCadrans':       'Wilaya dials',
  'geo.surfaceAnalyse':       'Zone analysis',
  'geo.surfaceDuel':          'Wilaya comparison',

  // ── `#820` · les 16 libellés que l'ÉCRAN a trouvés (Hkm) ─────────────────
  'commun.kpis':           'KPIs',
  'geo.retirerWilaya':                'Remove wilaya {{side}}',
  'geo.selectionnez':      'Select',
  'geo.quitter':           'Exit',
  'geo.cliquez2Wilayas_one':   'Click 2 wilayas — {{count}}/2 selected',
  'geo.cliquez2Wilayas_other':   'Click 2 wilayas — {{count}}/2 selected',
  'geo.erreurWilayas':     'Failed to load wilaya data.',
  'geo.erreurAnalytique':  'Failed to load analytics data.',
  'geo.classementPdm':     '{{zone}} — {{marque}} MS',
  'geo.titreKpis':         '{{wilaya}} · {{region}} · KPIs',
  'geo.voirPdv':           'View PoS',
  'geo.topPdv':            'Top PoS',
  'geo.affichageLimite':   'Display limited to 2,000 PoS — refine the filters.',
  'geo.aucuneVenteSku':    'No SKU sales over the period.',
  'commun.erreur':       'Error',

  // ── `#821` · restes du rejeu `#820` (Marché) ──────────────────────────
  'marche.brand360.groupe':       'Group',
  'marche.ws.samsungU':           '{{marque}} U.',
  'marche.s9.erreurTableauPrix':  'Failed to load price table.',
  'marche.echelleCommune':        '— shared scale',
  'marche.aucuneDonneeAfficher':  'No data to display.',

  // ── `#824` · FLASH INFO (lecture + saisie) ────────────────────────────
  'flashinfo.creer':                     'Create',
  'flashinfo.erreurChargement':          'Failed to load Flash Info.',
  'flashinfo.aucuneActivite':            'No Flash Info activity',
  'flashinfo.aucuneActiviteSous':        'Change the filters or come back later.',
  'commun.pagePrecedente':            'Previous page',
  'commun.pageSuivante':              'Next page',
  'flashinfo.bandeauVide':               'No activity in the last 7 days',
  'flashinfo.aLaUne':                    'Featured',
  'flashinfo.aucunePhoto':               'no photo',
  'flashinfo.toutesRegions':             'All regions',
  'flashinfo.rechercherPlaceholder':     'Search…',
  'flashinfo.typeActivite':              'Activity type',
  'flashinfo.tousTypes':                 'All types',
  'flashinfo.canalCible':                'Target channel',
  'commun.tousCanaux':                'All channels',
  'flashinfo.dateDebut':                 'Start date',
  'flashinfo.dateFin':                   'End date',
  'flashinfo.reinitialiserFiltres':      'Reset filters',
  'commun.retour':                    'Back',
  'flashinfo.modifier':                  'Edit',
  'flashinfo.archiver':                  'Archive',
  'flashinfo.archivage':                 'Archiving…',
  'commun.supprimer':                 'Delete',
  'flashinfo.wilayasCiblees':            'Targeted wilayas',
  'flashinfo.publieLe':                  'Published on {{date}}',
  'flashinfo.parAuteur':                 '· by {{auteur}}',
  'flashinfo.modaleArchiverTitre':       'Archive this Flash Info?',
  'flashinfo.archivageDefinitif':        'Archiving is permanent.',
  'flashinfo.archivageSuite':            'The card will leave the banner for the archives, and',
  'flashinfo.archivageGras':             'can no longer be republished',
  'flashinfo.archivageFin':              'once archived, its only way out is deletion.',
  'commun.annuler':                   'Cancel',
  'flashinfo.archiverDefinitivement':    'Archive permanently',
  'flashinfo.echecArchivage':            'Archiving failed — {{cause}}. The publication is still published.',
  'flashinfo.modaleSupprimerTitre':      'Delete this Flash Info',
  'flashinfo.purgeAvant':                'This action will permanently delete the card',
  'flashinfo.purgeApres':                'and all its photos.',
  'flashinfo.purgeIrrecuperable':        'It will be impossible to recover.',
  'flashinfo.echecSuppression':          'Deletion failed — {{cause}}. The publication is still present.',
  'flashinfo.type.LAUNCH':               'Product launch',
  'flashinfo.type.BUNDLE':               'Bundle / Pack',
  'flashinfo.type.TOMBOLA':              'Tombola',
  'flashinfo.type.POSTERS':              'Poster placement',
  'flashinfo.type.FLYERS':               'Flyer distribution',
  'flashinfo.type.PLV_INSTALL':          'POS display install',
  'flashinfo.type.EVENT':                'Event',
  'flashinfo.type.PRICE_CHANGE':         'Price change',
  'flashinfo.type.OOS_COMPETITOR':       'Competitor stock-out',
  'flashinfo.type.NEW_REFERENCING':      'New listing',
  'flashinfo.type.FLASH_PROMO':          'Flash promo',
  'flashinfo.type.OTHER':                'Other',
  'flashinfo.canal.distributor':         'Distributor',
  'flashinfo.canal.wholesaler':          'Wholesale',
  'flashinfo.canal.retailer':            'Retail',
  'flashinfo.canal.consumer':            'Consumer',
  'flashinfo.retourFeed':                'Back to feed',
  'flashinfo.nouvelleActivite':          'New Flash Info activity',
  'flashinfo.modifierActivite':          'Edit Flash Info activity',
  'flashinfo.champsAvant':               'Fields',
  'flashinfo.champsApres':               'required',
  'flashinfo.skusConcernes':             'SKUs concerned',
  'flashinfo.titre':                     'Title',
  'flashinfo.mecanisme':                 'Commercial mechanism',
  'flashinfo.regionCible':               'Target region',
  'flashinfo.merchCible':                'Target merchandiser',
  'flashinfo.pdvConcerne':               'PoS concerned',
  'flashinfo.optionnel':                 '(optional)',
  'flashinfo.retourMarche':              'Market feedback',
  'flashinfo.optionnelMax1000':          '(optional, max 1000)',
  'flashinfo.phTitre':                   'Activity title',
  'flashinfo.phDescription':             'Detailed activity description',
  'flashinfo.phMecanisme':               'e.g. Buy 2 SKUs = -10% on the 3rd',
  'flashinfo.phRetourMarche':            'Field reception, managers\' feedback…',
  'flashinfo.optSelectionner':           '— Select —',
  'flashinfo.optToutesRegions':          '— All regions —',
  'flashinfo.optAucun':                  '— None —',
  'flashinfo.optAucunPdv':               '— No PoS —',
  'flashinfo.chargementWilayas':         'Loading wilayas…',
  'flashinfo.sauvegarderDraft':          'Save DRAFT',
  'commun.publier':                   'Publish',
  'commun.sauvegarder':               'Save',
  'flashinfo.erreurChargementActivite':  'Failed to load the activity. Check the ID and retry.',
  'flashinfo.versionIntacte':            '. The previous version is intact.',
  'flashinfo.cascadeMerchPdv':           'Merchandiser and PoS were removed: they depended on the previous region.',
  'flashinfo.cascadePdv':                'The chosen PoS was removed: it does not depend on this merchandiser.',
  'flashinfo.aucunPdvMerch':             'No PoS of this merchandiser in this region',
  'flashinfo.erreurUploadPhotos':        'Photo upload error',
  'flashinfo.uploadPartiel':             'Activity created. Partial photo upload: {{msg}}',
  'flashinfo.erreurSuppressionPhoto':    'Photo deletion error',
  'flashinfo.erreurUploadNouvelles':     'New photos upload error',
  'flashinfo.serveurARepondu':           'the server responded {{status}}',
  'flashinfo.causeInconnue':             'unknown cause (no server message)',
  'flashinfo.dureeActivite':             'Activity duration',
  'flashinfo.uniteJours':                'Days',
  'flashinfo.uniteSemaines':             'Weeks',
  'flashinfo.uniteMois':                 'Months',
  'flashinfo.nombre':                    'Number',
  'commun.unite':                     'Unit',
  'flashinfo.photosN':                   'Photos ({{n}}/{{max}})',
  'flashinfo.photosVide':                'No photo — click to add (JPEG/PNG/WebP ≤ 2 MB)',
  'flashinfo.photosMax':                 'Maximum {{max}} photos. You already have {{n}} photo(s).',
  'flashinfo.photoFormat':               'Unsupported format: {{f}}. Use JPEG, PNG or WebP.',
  'flashinfo.photoLourde':               'Photo too large: {{f}}. Maximum 2 MB.',
  'flashinfo.choisirPhotos':             'Choose photos',
  'flashinfo.skuChoisirMarque':          '— Select a brand first —',
  'flashinfo.apercuDirect':              'Live preview',
  'flashinfo.phMarque':                  'Brand…',
  'flashinfo.phTitreCourt':              'Title…',
  'flashinfo.surfaceBandeau':            'Flash Info — banner',
  'flashinfo.surfaceFiche':              'Flash Info — card',
  'flashinfo.surfaceVisionneuse':        'Flash Info — viewer',
  'flashinfo.filNouvelleActivite':       'New activity',
  'flashinfo.filEdition':                'Edit',

  // ── `#825` · ce que le détecteur ne voyait pas (props, tables) ────────
  'flashinfo.optionnelMax500':   '(optional, max 500)',
  'flashinfo.choisirSku':        '— Choose a SKU —',
  'flashinfo.nSelectionnees_one':    '({{count}} selected)',
  'flashinfo.nSelectionnees_other':    '({{count}} selected)',
  'flashinfo.dateFinCalculee':   'Computed end date:',
  'flashinfo.statut.BROUILLON':  'DRAFT',
  'flashinfo.statut.PUBLIEE':    'PUBLISHED',
  'flashinfo.statut.ARCHIVEE':   'ARCHIVED',
  'flashinfo.dureeJour':         'day',
  'flashinfo.dureeJours':        'days',
  'flashinfo.dureeSemaines':     'weeks',
  'flashinfo.dureeMois':         'months',

  // ── `#825` · ce que le détecteur CORRIGÉ a trouvé (props, aria, accolades) ─
  'flashinfo.optionnelN':               '(optional, {{n}})',
  'flashinfo.tousSkusSelectionnes':     '— All SKUs selected —',
  'flashinfo.aucunSkuMarque':           '— No SKU for this brand —',
  'flashinfo.photoPrecedente':          'Previous photo',
  'flashinfo.photoSuivante':            'Next photo',
  'flashinfo.activiteIntrouvable':      'Activity not found or access denied.',
  'flashinfo.heriteeMerchPdv':          'The saved merchandiser and PDV no longer match this region — the selection predates a route change. It is kept as is.',
  'flashinfo.heriteeMerch':             'The saved merchandiser no longer matches this region — the selection predates a route change. It is kept as is.',
  'flashinfo.heriteePdv':               'The saved PDV is no longer served by this merchandiser — the selection predates a route change. It is kept as is.',

  // ── `#827` · SFM Team Performance (T0 `#826`) + restes `#825` ──────────
  'perf.titreMerchs':                'Merchandisers — W values · 4-wk lights',
  'perf.titreEvolution':             'Team trend — {{n}} wks',
  'perf.titreHeatmap':               'Working days heatmap',
  'perf.titreAlertes':               '{{canal}} alerts',
  'perf.titreClusters':              'Route fraud · clusters "same point, several PoS"',
  'perf.titreDispersionEquipe':      'GPS dispersion · Team',
  'perf.titreDispersionMerch':       'GPS dispersion · {{merch}}',
  'perf.titreConformite':            'Visit compliance',
  'perf.effacer':                    'Clear',
  'perf.rougeCouverture':            'Coverage red',
  'perf.rougeQualite':               'Quality red',
  'perf.visitesRealisees':           'Visits done',
  'perf.couvertureRouting':          'Routing coverage',
  'perf.nPdvHebdo':                  '{{n}} / {{m}} weekly PoS',
  'perf.lignesFigees':               'Frozen lines',
  'perf.pasSemainePrecedente':       'no previous week',
  'perf.lignesCollectees':           'Collected lines',
  'perf.skusDistincts':              'Distinct SKUs',
  'perf.ventesCollectees':           'Collected sales',
  'perf.ratioDollarLigne':           '$/line ratio',
  'perf.visites':                    'Visits',
  'perf.txVisite':                   'Visit rate',
  'perf.couvPct':                    'Cov. %',
  'perf.lignes':                     'Lines',
  'perf.figeesPct':                  'Frozen %',
  'perf.ventesDollar':               'Sales ($)',
  'perf.ventesVol':                  'Sales (Vol)',
  'perf.aucunMerchFiltre':           'No merch matches the status filter. Click a chip to remove it.',
  'perf.aucunMerchPeriode':          'No merchandiser over the period.',
  'perf.aucunMerchandiser':          'No merchandiser.',
  'perf.aucunMerchFiltreCourt':      'No merch for the status filter.',
  'perf.echecListeMerchs':           'Failed to load the merchandiser list.',
  'perf.aucune':                     'none',
  'perf.pdvFermes':                  'Closed PoS',
  'perf.aucunMerchCanal':            'No merch on this channel.',
  'perf.echecAlertes':               'Failed to load alerts.',
  'perf.actConforme':                'compliant',
  'perf.actInvestiguer':             'investigate',
  'perf.actVisiteTerrain':           'supervisor field visit',
  'perf.actSanction':                'disciplinary action',
  'perf.actInsuffisant':             'insufficient data — NOT compliant',
  'perf.couverture':                 'Coverage',
  'perf.grisCouverture':             'no PoS scheduled on routing this week',
  'perf.lignesCollecteesEcart':      'Collected lines (gap vs team)',
  'perf.grisMoyenne':                'team average not computable',
  'perf.figees':                     'Frozen',
  'perf.lignesFigeesVentes':         'Frozen lines (with sales)',
  'perf.grisAucuneLigne':            'no line collected this week',
  'perf.visitesCourtes':             'Short visits',
  'perf.visitesMoins15':             'Visits under 15 min',
  'commun.nVisite':                    '{{n}} visit',
  'commun.nVisites':                   '{{n}} visits',
  'perf.grisNetTime':                'visit duration (net_time) not collected for this merch',
  'perf.premiereVisite':             '1st visit',
  'perf.heurePremiereVisite':        '1st visit time',
  'perf.grisParser':                 'imported before parser fix #325',
  'perf.pdvTrouvesFermes':           'PoS found closed',
  'perf.grisFermeture':              'closure data unavailable',
  'perf.pdvNonVisite':               'Unvisited PoS',
  'perf.pdvRoutingNonVisites':       'Routing PoS unvisited for 3+ weeks',
  'perf.grisAucunPdvRouting':        'no PoS on this merch\'s routing',
  'perf.tempsJour':                  'Time/day',
  'perf.tempsTravailJour':           'Working time per day (two-sided)',
  'perf.ventesCollecteesEcart':      'Collected sales (gap vs team)',
  'perf.apresHeure':                 'after {{h}}:{{mm}}',
  'perf.loOuHi':                     '{{lo}} or {{hi}}',
  'perf.infoQualite':                'quality = min(frozen, time) · #WS# neutralised · click → file',
  'perf.infoTauxVisite':             'Visit rate = Visits / Target routing',
  'perf.infoCouvertureW':            'Coverage W = weekly routing PoS visited / weekly scheduled PoS (#323, capped 100%)',
  'perf.infoFigees':                 'Frozen lines / total lines — <10 % perf · >20 % alert',
  'perf.infoVolumeVentes':           'Sales volume — units',
  'perf.infoGrilleTemporelle':       'Time grid = observations.week_iso ({{marque}} rawdata label, aligned with Market). All KPIs — visits, lines, turnover — share the same window.',
  'perf.infoMoyenneMerch':           'Average per merch (current channel, #WS# excluded) — places this merch vs peers',
  'perf.semainesPartielles':         'Partial weeks: ',
  'perf.semainePartielle':           'Partial week — {{n}} working day(s) present of 5',
  'perf.feuxProvisoiresInfo':        'Provisional lights — {{n}} partial week(s) in the window: ',
  'perf.alertesSansDonnee':          '{{n}} alert(s) without data — not assessable',
  'perf.pdvFermesContexte':          'Closed PoS (context — not an alert)',
  'perf.voirDetail':                 'See {{merch}} details',
  'perf.seuilProvisoire':            '⚠ provisional threshold',
  'perf.leg10Visites':               '≥ 10 visits',
  'perf.leg59Visites':               '5 – 9 visits',
  'perf.leg14Visites':               '1 – 4 visits',
  'perf.leg0Visite':                 '0 visit',
  'perf.nMerchXJours':               '{{n}} merch × {{m}} d.',
  'perf.nMerchsXJours':              '{{n}} merchs × {{m}} d.',
  'perf.nJoursXMerchs':              '{{n}} d. × {{m}} merchs',
  'perf.derniereSemaineIncomplete':  '⚠ last week incomplete · imports up to {{date}}',
  'perf.importsJusquau':             'imports up to {{date}}',
  'perf.celluleVisite':              '{{merch}} · {{date}} · {{n}} visit',
  'perf.celluleVisites':             '{{merch}} · {{date}} · {{n}} visits',
  'perf.celluleZero':                '{{merch}} · {{date}} · 0 visit',
  'perf.aucuneActivite':             'No activity over the period.',
  'perf.echecHeatmap':               'Failed to load heatmap.',
  'perf.ceMerch':                    'This merch',
  'perf.moyenneEquipe':              'Team average',
  'perf.nPartielle':                 '{{n}} partial',
  'perf.nPartielles':                '{{n}} partial',
  'perf.suffixePartielle':           ' · {{n}}/5 days (partial)',
  'perf.ventesUsd':                  'Sales (USD)',
  'perf.echecSerie':                 'Failed to load series.',
  'perf.beginPdv':                   'begin (scan position) ↔ PoS (reference)',
  'perf.nGeolocalises':              ' geolocated',
  'perf.visitesSansScan':            ' visited without scan',
  'perf.nonVisites':                 ' not visited',
  'perf.pdvNonCoord':                ' PoS without coordinates',
  'perf.semaineIso':                 'week {{w}}',
  'perf.mediane':                    'Median',
  'perf.moy1km':                     'Avg <1km',
  'perf.conformeFin50':              'Compliant end ≤ 50 m',
  'perf.conforme50100':              'Compliant 50-100 m',
  'perf.limite100250':               'Borderline 100-250 m',
  'perf.suspect250500':              'Suspect 250-500 m',
  'perf.aberrant500':                'Outlier 500 m-5 km',
  'perf.aucuneCoordGps':             'No usable GPS coordinates in the window. Check PoS coordinate propagation.',
  'perf.dispersionIndispo':          'GPS dispersion unavailable',
  'perf.nVisitesAnalysables':        ' analysable visits',
  'perf.conformesSeuil':             'Compliant ≤ {{n}} m ',
  'perf.deplacees':                  'Moved ',
  'perf.horsGraphe':                 'Off-chart (> 1 km) ',
  'perf.ecartBeginEnd':              'begin↔end gap',
  'perf.dureeMin':                   'duration (min)',
  'perf.ecartDuree':                 'gap = {{m}} m · duration = {{min}} min',
  'perf.conformeSeuilN':             'Compliant ≤ {{n}} m ({{c}})',
  'perf.deplaceeSeuil':              'Moved > {{n}} m — 1 km',
  'perf.horsGrapheN':                '> 1 km: {{n}} off-chart',
  'perf.conformiteIndispo':          'begin↔end compliance unavailable',
  'perf.nMerchsGrille':              '{{n}} merchs · grid {{a}}m × {{b}}m',
  'perf.pdvEnCluster':               'PoS in cluster',
  'perf.maxCluster':                 'Max / cluster',
  'perf.totalPdv':                   'Total PoS',
  'perf.aucunMerchFenetre':          'No merch in this window',
  'perf.clustersIndispo':            'GPS clusters unavailable',
  'perf.feuxProvisoires':            'Provisional lights',
  'perf.nJours5':                    '{{n}}/5 days',
  'perf.top3Lignes':                 'Top 3 lines',
  'perf.bottom3Lignes':              'Bottom 3 lines',
  'perf.top3VentesDollar':           'Top 3 sales ($)',
  'perf.bottom3VentesDollar':        'Bottom 3 sales ($)',
  'perf.top3VentesVol':              'Top 3 sales (vol)',
  'perf.bottom3VentesVol':           'Bottom 3 sales (vol)',
  'perf.ficheMerch':                 'Merch file {{code}}',
  'perf.ficheIndispo':               'File unavailable',
  'perf.echecFiche':                 'Failed to load this merchandiser\'s file.',
  'perf.wsEnAttente':                'WS pending',
  'perf.ficheStatut4Sem':            'Merch file — 4-wk rolling status',
  'perf.wholesaleEnAttente':         'Wholesale pending',
  'perf.pct4Sem':                    '{{x}} % (4 wks)',
  'perf.donneeIndispo':              'data unavailable',
  'perf.figeesTemps4Sem':            'frozen {{x}} % · time {{y}} min (4 wks)',
  'perf.visite':                     'Visit',
  'perf.voirIdentite':               'View identity',
  'perf.alertes':                    'Alerts',
  'perf.aucuneAlerteActive':         'No active alert',
  'perf.provisoire':                 'provisional',
  'perf.donneeInsuffisante':         'insufficient data',
  'perf.contexte':                   'Context · ',
  'perf.cetteSemaine':               ' this week.',
  'perf.aucuneAlerteEvaluee':        'No alert assessed for {{merch}} this week (merch without activity?).',
  'perf.tempsMoyen':                 'Average time',
  'perf.ecartGpsMoy':                'Avg GPS gap',
  'perf.conformiteGps':              'GPS compliance',
  'perf.min15Perf':                  '≥15 min perf accepted',
  'perf.visitePdv':                  'visit → PoS',
  'perf.routingCible':               'Target routing',
  'perf.clustersBegin':              'begin clusters (~50m) · potential route fraud',
  'perf.grilleCellules':             'Grid cells where ≥2 distinct PoS share the same begin',
  'perf.centreZone':                 'zone center (~50 m): ({{lat}}, {{lon}})',
  'perf.infoCentreCellule':          'Center of the grid cell (~50 m side) where GPS begins were grouped — not an actual individual GPS position.',
  'perf.aucunRegroupement':          'No suspect cluster detected in the window.',
  'perf.conformiteBeginEnd':         'Visit compliance · begin↔end',
  'perf.fermeSurPlace':              'Did the merch close the visit on site? (threshold {{n}} m)',
  'perf.conforme':                   ' compliant',
  'perf.nDeplacee':                  '{{n}}/{{m}} moved',
  'perf.nDeplacees':                 '{{n}}/{{m}} moved',
  'perf.toutesVisitesSurPlace':      'All {{n}} analysable visits ended on site.',
  'perf.aucuneVisiteAnalysable':     'No analysable visit in the window.',
  'perf.conformiteVisitesIndispo':   'Visit compliance unavailable',
  'perf.resolutionIdentite':         'Identity resolution',
  'perf.phase4J':                    'Feature available in Phase 4.J',
  'perf.texteResolution':            'Identity resolution (endpoint /merch/identity/',
  'perf.texteResolutionFin':         ') is planned in BACKLOG 4.D.4-bis. This modal will show the reason form + READ_MERCH_IDENTITY audit log once enabled.',
  'perf.surfEvolution':              'Performance — Team trend',
  'perf.surfHeatmap':                'Performance — Working days',
  'perf.surfAlertes':                'Performance — Team alerts',
  'perf.surfMerchs':                 'Performance — Merchandisers',
  'perf.surfDispersion':             'Performance — GPS dispersion',
  'perf.surfConformite':             'Performance — GPS compliance',
  'perf.surfClusters':               'Performance — Fraud clusters',
  'perf.surfFiche':                  'Performance — Merchandiser file',
  'perf.nivConforme':                'Compliant',
  'perf.nivRoutingTenu':             'Weekly routing held',
  'perf.nivAInvestiguer':            'To investigate',
  'perf.nivGrave':                   'Serious',
  'perf.nivVisiteTerrain':           'Supervisor field visit',
  'perf.nivCritique':                'Critical',
  'perf.nivSanction':                'Disciplinary action',
  'perf.nivDonneeIndispo':           'Data unavailable',
  'perf.notif.FIRST_VISIT_LATE':     'Late 1st visit',
  'perf.notif.GPS_DISPERSION':       'GPS dispersion',
  'perf.notif.POS_UNVISITED':        'PoS unvisited for 3+ weeks',
  'perf.notif.TIME_PER_DAY':         'Working time per day',
  'perf.notif.sufVsEquipe':          'vs team',
  'perf.notif.sufHorsSeuil':         'of visits above threshold',
  'perf.notif.sufDuRouting':         'of routing',
  'marche.chargementLibelle':        'Loading',
  'marche.derniereSemImportee':      'Latest wk imported',
  'marche.topWilayas':               'Top wilayas',
  'flashinfo.choisirMerch':          'Choose a merchandiser',
  'flashinfo.merchSansPdv':          'This merchandiser serves no PoS',

  // ── `#834` · Exactitude des données (T0 `#830`) ───────────────────────
  'da.echecKpiOps':            'Failed to load operational KPIs.',
  'da.fenetre':                'Window {{w1}} → {{w2}} · ',
  'da.chargementMin':          'loading…',
  'da.echecDaV2':              'Failed to load Data Accuracy V2.',
  'da.nRelevesPropres':        '{{n}} / {{m}} clean readings',
  'da.nPts':                   '{{n}} pts',
  'da.diagnostic':             'diagnosis',
  'da.formule':                'Formula: Quality − Proofs, in points.',
  'da.nVisitesProuvees':       '{{n}} / {{m}} proven visits',
  'da.histoIndispo':           'History unavailable',
  'da.histoAucuneSemaine':     '— no week measured up to {{w}}. Earlier weeks have not been loaded or computed yet.',
  'da.histoDeuxCourbes':       'History — both curves',
  'da.nSemaine':               '{{n}} week',
  'da.nSemaines':              '{{n}} weeks',
  'da.ariaHistorique':         'Quality and Proofs history',
  'da.nVisitesEcart':          '{{n}} visits · gap {{p}} pts',
  'da.trouPreuve':             'Where the proof gap comes from',
  'da.prouvees':               'Proven',
  'da.hintProuvees':           '= RFID scan + geolocated PoS + GPS ≤ 50 m + façade photo',
  'da.echecMerch':             'Merch failure',
  'da.hintEchecMerch':         '= RFID scan + geolocated PoS + (GPS > 50 m OR façade photo missing)',
  'da.preuveImpossibleScan':   'Proof impossible — scan',
  'da.hintScan':               '= No RFID scan',
  'da.preuveImpossibleCoord':  'Proof impossible — coord.',
  'da.hintCoord':              '= RFID scan + PoS without GPS coordinates in database',
  'da.raterQualite':           'What fails quality',
  'da.ctrlFraicheurCourt':     'Freshness (copied reading)',
  'da.ctrlPhotoIntCourt':      'Interior photo missing',
  'da.ctrlLignesCourt':        'Lines below habit',
  'da.ctrlSkuCourt':           'SKU below habit',
  'da.ctrlVentesCourt':        'Sales out of band',
  'da.ctrlTempsCourt':         'Visit time < 15 min',
  'da.echecEntonnoirs':        'Failed to load funnels.',
  'da.chargementEntonnoirs':   'Loading funnels…',
  'da.qualiteMaj':             'QUALITY',
  'da.contenuReleve':          'the reading\'s content',
  'da.chaineReelle':           'actual chain',
  'da.sixControles':           'the 6 controls strict PASS',
  'da.egalQualite':            '= Quality',
  'da.indulgentNaPass':        'lenient, NA→PASS',
  'da.preuveMaj':              'PROOF',
  'da.presenceProuvee':        'the merch\'s proven presence',
  'da.relevesHorsPlan':        'Readings made on PoS outside the planned route — counted in Proofs, outside the funnel.',
  'da.nHorsPlan':              '+ ',
  'da.horsPlanApres':          ' unplanned',
  'da.egalPreuves':            '= Proofs',
  'da.vsRelevesEffectues':     'vs readings made',
  'da.vsPlanifie':             'vs Planned',
  'da.vueCanalisee':           'Channel view ({{canal}}) — the channel belongs to the reading. The "Planned" step is hidden: routing has no channel. A mixed merch appears in both views with its own readings — retail + wholesale adds up to more than the total.',
  'da.cumulEntonnoir':         'Funnel cumulative',
  'da.reelIndep':              'Actual indep.',
  'da.deplusCumul':            '+{{n}} more than the cumulative funnel',
  'da.infoCumul':              'Funnel cumulative: each step filters the previous step\'s survivors. That is the funnel\'s own sequential logic.',
  'da.pctDepart':              '% of start (cumulative)',
  'da.ref':                    'ref.',
  'da.infoIndulgent':          'Lenient independent: this control alone, on ALL readings. Non-measurable ones (missing photo/time) count as passes (Data Quality V2 rule).',
  'da.pctPasEnFaute':          '% not at fault',
  'da.infoReel':               'Actual independent: this control alone, on readings actually measured (the signal exists). Reconciles 100 % with the "What fails quality" panel. The gap with "% not at fault" reveals the non-measurable — photos missing for lack of scan.',
  'da.pctReelSignal':          '% actual signal',
  'da.passFailNa':             'PASS {{a}} · FAIL {{b}} · NA {{c}}',
  'da.ariaEntonnoir':          '{{titre}} funnel',
  'da.perteChip':              '−{{n}} · {{p}} pts',
  'da.perteDelta':             '− {{n}}  ·  Δ {{p}}',
  'da.pointBloquant':          'BLOCKING POINT · {{label}}',
  'da.piedEntonnoir':          'Funnel bottom',
  'da.fenetreW':               'Window {{w}}',
  'da.fenetreW1W2':            'Window {{w1}} → {{w2}}',
  'da.echecIndex':             'Failed to load the index.',
  'da.titreMerchVisites':      '{{merch}} · {{n}} visits',
  'da.suffixeFaible':          ' (< {{seuil}})',
  'da.suffixeMixte':           ' · MIXED — this merch produces readings in both channels; what is shown here is its share on {{canal}}, not its whole activity',
  'da.ariaMerchMixte':         'Mixed merch: produces readings in both channels',
  'da.visitesNonRattachees':   'visits not attached to a merch — counted in the national view, not attributed.',
  'da.nM':                     '{{n}} m',
  'da.nKm':                    '{{n}} km',
  'da.nVisitesFenetre':        '{{n}} visits · {{w1}} → {{w2}}',
  'da.histoDeuxScores':        'History of both scores',
  'da.uneSeuleSemaine':        'Only one week measured for this merchandiser ({{w}}) — no trend to draw yet.',
  'da.sesDefaillances':        'Its proof failures',
  'da.gpsTropLoin':            'GPS too far only',
  'da.gpsPhotoAbsente':        'GPS + photo missing',
  'da.photoAbsenteSeule':      'Photo missing only',
  'da.visitesAControler':      'Visits to check',
  'da.nAControler':            '{{n}} to check',
  'da.distance':               'Distance',
  'da.motif':                  'Reason',
  'da.nVisiteAberrante':       'visit with aberrant distance (> {{k}} km) — sign / hemisphere inversion, isolated, not attributed to the merch.',
  'da.nVisitesAberrantes':     'visits with aberrant distance (> {{k}} km) — sign / hemisphere inversion, isolated, not attributed to the merch.',
  'da.svgQualite':             '{{w}} — Quality {{x}}',
  'da.svgPreuves':             '{{w}} — Proofs {{x}}',
  'da.surfPilotage':           'Data Accuracy — steering',
  'da.surfHistorique':         'Data Accuracy — history',
  'da.surfEntonnoirQualite':   'Data Accuracy — quality funnel',
  'da.surfEntonnoirPreuve':    'Data Accuracy — proof funnel',
  'da.surfFiche':              'Data Accuracy — merchandiser file',
  'da.etape.VISITE':           'Visited',
  'da.etape.GPS50':            'GPS ≤ 50 m',
  'da.etape.PHOTO_EXT':        'Ext. photo (façade)',
  'da.etape.PLANIFIE':         'Planned (ref.)',
  'da.etape.PHOTO_INT':        'Int. photo (shelf)',
  'da.etape.TEMPS':            'Time > 15 min',
  'da.etape.FRAICHEUR':        'Freshness',
  'da.etape.BACKCHECK':        'Backcheck',
  'da.digestAucuneVisite':     'no assessable visit',
  'da.digestQualite':          'Quality {{x}}',
  'da.digestPremiereSemaine':  ' (first tracked week)',

  // ── `#834` · phrases à STYLE INTERNE, coupées autour (`#817`) ─────────
  'da.seuilAvant':                 'Threshold: ≥ 90 % ',
  'da.vert':                       'green',
  'da.seuilMilieu':                ' · < 90 % ',
  'da.rouge':                      'red',
  'da.ratioAvant':                 'Ratio computed on ',
  'da.ratioEm':                    'all readings made',
  'da.ratioApres':                 ' (planned + unplanned). Unproven ones split between the merch, the SFMT app and Orbicore.',

  // ── `#834` · phrases à GRAS INTERNE, coupées autour (`#817`) ──────────
  'da.nomTemps':                   'Visit time',
  'da.resteTemps':                 ' — net duration > 15 min',
  'da.resteFraicheur':             ' — < 20 % copied lines',
  'da.nomPhotoInt':                'Interior photo',
  'da.restePhotoInt':              ' — shelf attached if scanned',
  'da.nomLignesSku':               'Lines / SKU',
  'da.resteLignesSku':             ' — ≥ 60 % of the last 8 visits\' median',
  'da.resteVentes':                ' — between 30 % and 300 % of the median',
  'da.ecartPrefixe':               '· ',
  'da.motFaible':                  'Small gap',
  'da.resteFaible':                ': both scores move together.',
  'da.motLarge':                   'Large gap',
  'da.resteLarge':                 ': correct content but missing proof → fix the proof chain (GPS/photo), not the merchs.',
  'da.resteGps':                   ' — PoS↔scan distance ≤ 50 m',
  'da.nomPhotoExt':                'Exterior photo',
  'da.restePhotoExt':              ' — façade attached to the visit',

  // ── `#837` A1 · Labo — la coquille et les 4 modes ───────────────────────
  'lab.mode.tendanceDesc':       'A KPI\'s evolution over a period.',
  'lab.mode.classement':         'Ranking',
  'lab.mode.classementDesc':     'Top N brands, SKUs or PoS.',
  'lab.mode.croise':             'Pivot table',
  'lab.mode.croiseDesc':         'Drag & drop builder.',
  'lab.badgeAvance':             'Advanced',
  'lab.mode.exportDesc':         'Raw extraction to Excel.',
  'lab.requetesSauvegardees':    'Saved queries',
  'lab.hintTendance':            'Track a KPI over time: volumes, market shares, average price, distribution. Choose a period from 4 weeks to 12 months, compare 1 to 10 brands, spot the trends.',
  'lab.hintClassement':          'Identify the top brands, SKUs or PoS by a business criterion (turnover, volume, presence). The ranking shows who really weighs, and the gap between the leader and its challengers over the chosen period.',
  'lab.hintCroise':              'Build your view by dragging and dropping rows, columns and values (brand × region × KPI). Maximum flexibility to explore free cross-tabs, with no format constraint.',
  'lab.hintExport':              'Extract the filtered raw data straight to Excel. For external analysis, archiving or sharing. Keeps all source columns without aggregation.',
  'lab.accesRefuse':             'Access denied',
  'lab.desktopSeul':             'Self-Service Lab is only available on desktop (min. 1024px).',
  'lab.aucunKpiSupporte':        'No selected KPI is supported for this analysis type. Choose at least one available KPI (not greyed out).',
  'lab.normAbsolu':              'Absolute',
  'lab.modifierEtape':           'Edit: {{question}}',

  // ── `#837` — presets de periode, forme LONGUE (le Labo et le Marche partagent le jeu) ───

  // ── `#837` A1 · Labo — le classement, les marques, les zones ────────────
  'lab.qEntite':                  'Which entity to rank?',
  'lab.qKpi':                     'By which KPI?',
  'lab.qPeriode':                 'Which period?',
  'lab.qTopCanalSens':            'Top N · Channel · Direction',
  'lab.qCanalSens':               'Channel · Direction',
  'lab.qPerimetre':               'Scope (brand · zones)',
  'lab.qNormLancer':              'Normalisation · Run',
  'lab.afficherTopN':             'Show Top N',
  'lab.sens':                     'Direction',
  'lab.marques':                  'Brand(s)',
  'lab.zones':                    'Zones (regions · wilayas)',
  'lab.normalisation':            'Normalisation',
  'lab.ciblePinglee':             'Pinned target (optional)',
  'lab.epinglezM':                'Pin a specific {{entite}} to see its rank, even if it falls outside the Top {{n}}.',
  'lab.epinglezF':                'Pin a specific {{entite}} to see its rank, even if it falls outside the Top {{n}}.',
  'lab.rechercherEntite':         'Search {{entite}}…',
  'lab.aucuneCible':              'No pinned target',
  'lab.resumeToutesRegions':      'All regions · {{canal}} · {{sens}}',
  'lab.resumeTopN':               'Top {{n}} · {{canal}} · {{sens}}',
  'lab.marqueDefaut':             '{{marque}} (default)',
  'lab.nMarque':                  '{{n}} brand',
  'lab.nMarques':                 '{{n}} brands',
  'lab.nZone':                    '{{n}} zone',
  'lab.nZones':                   '{{n}} zones',
  'lab.mesure':                   'Measure:',
  'lab.teteEtAutre':              '{{tete}} + … + {{n}} other',
  'lab.teteEtAutres':             '{{tete}} + … + {{n}} others',
  'lab.compteurMarques':          '({{n}} brands)',
  'lab.marquesVideMarche':        'brand(s) — empty = total market',
  'lab.regionIndispo':            'Region unavailable',
  'lab.configIncomplete':         '· Incomplete or invalid configuration — fix the flagged steps.',
  'lab.kpiIncompatible':          '· KPI incompatible with entity {{entite}} — choose another one.',
  'lab.capMarques10':             '10 brands max — leave empty for auto top 7.',
  'lab.capRetirezEn':             '{{msg}} (remove {{n}})',
  'lab.deuxKpisUneMarque':        '2 KPIs = 1 brand max — remove a KPI to compare several brands',
  'lab.plusieursMarquesUnKpi':    'Several brands = 1 KPI max — choose 1 brand to compare 2 KPIs',
  'lab.blocageCardinalite':       '2 KPIs + several brands: choose 1 brand OR remove a KPI',

  // ── `#837` A1 · Labo — les motifs de gating (le hook nomme, l'ecran ecrit) ───
  'lab.gateKpiRequis':          'KPI required',
  'lab.gateKpiNonRangeable':    'KPI {{kpi}} not rankable V1',
  'lab.gateNonAdditif':         'Non-additive KPI — per-PoS normalisation not applicable',
  'lab.gateFeuille':            'Entity {{entite}} is a leaf — per-PoS normalisation requires an aggregate entity (wilaya / region / brand / merch)',

  // ── `#837` A2 · Labo — le flux Tendance ─────────────────────────────────
  'lab.qKpisAnalyser':        'Which KPIs to analyse?',
  'lab.qCanal':               'Which channel?',
  'lab.qMarques':             'Which brands?',
  'lab.qRegions':             'Which regions?',
  'lab.transsionGroupees':    'Transsion (grouped brands)',

  // ── `#837` A1 · Labo — zones, wilayas, periode ──────────────────────────
  'lab.wilayas':                  'Wilayas',
  'lab.regionEstEntite':          'The ranked entity IS the region — no region filter (national scope).',
  'lab.wilayaEstEntite':          'The ranked entity IS the wilaya — no wilaya filter (scope = regions only or national).',
  'lab.pasDeFiltreWilaya':        'No wilaya filter for this entity.',
  'lab.filtrerWilayas':           'Filter wilayas…',
  'lab.aucunWilaya':              'No wilaya — refine the search.',
  'lab.rechercherWilaya':         'Search a wilaya…',
  'lab.touteLaRegion':            'Whole region',
  'lab.aucuneWilayaCochee':       'No wilaya checked — the query covers the whole region.',
  'lab.requeteReduiteWilaya':     'The query narrows to {{n}} wilaya.',
  'lab.requeteReduiteWilayas':    'The query narrows to {{n}} wilayas.',
  'lab.periodeRapide':            'Quick',
  'lab.du':                       'From',
  'lab.au':                       'To',

  // ── `#837` A1 ★ · Labo — LE jeu KPI unique (long) et ses formes courtes ───
  'lab.fam.financier':            'Financial',
  'lab.fam.taux':                 'Rates & Market shares',
  'lab.fam.comptage':             'Count',
  'lab.fam.comparaisons':         'Relative comparisons (dormant — V4.5x)',
  'lab.kpi.vitrineMoyenne':       'Avg display',
  'lab.kpi.pdmCategorie':         'Category MS',
  'lab.kpi.profondeurGamme':      'Range depth',
  'lab.kpi.vsTotalMarche':        'vs Total market',
  'lab.kpi.vsPeriodeN1':          'vs Period N-1',
  'lab.unite.unitesPdv':          'units/PoS',
  'lab.unite.skuPdv':             'SKU/PoS',
  'lab.kpiCourt.caDzd':           'Turnover (DZD)',
  'lab.kpiCourt.volumeVentes':    'Sales Volume (units)',
  'lab.kpiCourt.stockMoyen':      'Avg Stock (units)',
  'lab.kpiCourt.ihsMarque':       'IHS {{marque}} (%)',

  // ── `#837` A2 · Labo — le tableau croise et son catalogue de dimensions ───
  'lab.pivot.filtres':            'Filters',
  'commun.lignes':             'Rows',
  'commun.lignesMin':          'rows',
  'lab.pivot.colonnes':           'Columns',
  'lab.pivot.valeurs':            'Values',
  'lab.pivot.glissezDim':         'Drag dimensions…',
  'lab.pivot.glissezKpi':         'Drag KPIs…',
  'lab.pivot.dimDispo':           'Available dimensions',
  'lab.pivot.toutesPlacees':      'All placed ✓',
  'lab.pivot.kpiDispo':           'Available KPIs',
  'lab.pivot.partExigeSujet':     'This share KPI needs a subject: drag Brand to Filters OR to Row/Column.',
  'lab.pivot.valeursAuMoins1':    'The Values zone must contain at least 1 KPI (drag a KPI, not a dimension).',
  'lab.pivot.ajoutezKpi':         'Add at least 1 KPI to the Values zone.',
  'lab.pivot.partSansSujet':      'A share KPI is in Values without a subject. Drag Brand to Filters OR to Rows/Columns — a share without a brand is meaningless.',
  'lab.pivot.scopeReduit1':       'Scope reduced: {{liste}} removed (incompatible with the new period).',
  'lab.pivot.scopeReduitN':       'Scope reduced: {{liste}} removed (incompatible with the new period).',
  'lab.pivot.deplacer':           'Move',
  'lab.pivot.retirer':            'Remove {{label}}',
  'lab.dim.geographie':           'Geography',
  'lab.dim.produit':              'Product',
  'lab.dim.modeleSku':            'Model/SKU',
  'lab.dim.canalVente':           'Sales channel',
  'lab.dim.temporel':             'Time',
  'lab.dim.annee':                'Year',
  'lab.dim.pointDeVente':         'Point of sale',
  'lab.dim.canalRefPdv':          'PoS reference channel',
  'lab.dim.segmentation':         'Segmentation',
  'lab.dim.segmentPdv':           'PoS segment (A–D)',
  'lab.kpiSelectionnerAu1':       'Select ≥1',
  'lab.kpiNSel':                  '{{n}} sel.',
  'lab.kpiNSelMax':               '{{n}} sel. / {{max}}',
  'lab.kpiBientot':               'Coming soon',
  'lab.kpiIncompatEntite':        'Incompatible with the selected entity',

  // ── `#837` B1 · Labo — extraction, hub, requetes sauvegardees ───────────
  'lab.presetsPerso':           'Presets / custom',
  'lab.batchsPrecis':           'Specific batches',
  'lab.skuModele':              'SKU / Model',
  'lab.erreurComptage':         'Count error — check the filters.',
  'lab.depassePlafond':         ' rows — exceeds the cap {{max}}, refine the filters.',
  'lab.aucuneLigne':            'No row in scope — nothing to export.',
  'lab.generationXlsx':         '⏳ Generating XLSX…',
  'lab.telechargerExcel':       '📥 Download the Excel',
  'lab.telechargerExcelN':      '📥 Download the Excel ({{n}} rows)',
  'lab.echecTelechargement':    'Download failed — see the console.',
  'lab.telecharge':             '✓ Downloaded: {{fichier}} ({{n}} rows)',
  'lab.rechercherSku':          'Search a SKU model…',
  'lab.chargementSegments':     'Loading segments…',
  'lab.rechercherBatch':        'Search a batch (week)…',
  'lab.aucunBatch':             'No batch selected',
  'lab.batchLigne':             '{{semaine}} — {{date}} ({{n}} rows)',
  'lab.accesRefuseVue':         'Access denied (lab.view required)',
  'lab.hubRequetes':            'Query Hub',
  'lab.hubSousTitre':           'Manage your saved queries and the Orbicore templates',
  'lab.permSaveRequise':        'Permission lab.save_query required',
  'lab.permShareRequise':       'Permission lab.share_query required',
  'lab.sauverCourante':         'Save the Composer\'s current query',
  'lab.plusSauverCourante':     '+ Save current query',
  'lab.retourComposer':         '← Composer',
  'lab.mesRecentes':            'My recent queries',
  'lab.aucuneRequete':          'No saved query',
  'lab.affichageSur':           'Showing: 6 / {{n}}',
  'lab.erreurChargement':       'Loading error — {{msg}}',
  'lab.aucuneRequetePoint':     'No saved query.',
  'lab.construireRequete':      'Build a query',
  'lab.partageesAvecMoi':       'Shared with me',
  'lab.aucunePartagee':         'No shared query',
  'lab.erreurMsg':              'Error — {{msg}}',
  'lab.rienPartageAvecVous':    'No query has been shared with you.',
  'lab.visibilitePrivee':       'Private',
  'lab.visibilitePartagee':     'Shared',
  'lab.visibiliteOrga':         'Organisation',
  'lab.executeN':               'Run {{n}}×',
  'lab.dernier':                'Last: {{date}}',
  'lab.charger':                'Load',
  'lab.dupliquer':              'Duplicate',
  'lab.renommer':               'Rename',
  'lab.partager':               'Share',
  'lab.utiliser':               'Use',
  'lab.copier':                 'Copy',
  'lab.dupliquerPerso':         'Duplicate as personal query',
  'lab.sauverRequete':          'Save query',
  'lab.renommerRequete':        'Rename query',
  'lab.exempleNom':             'e.g. Sell-out {{marque}} Retail CENTRE ytd',
  'lab.descOptionnelle':        'Optional description…',
  'lab.tagsAide':               '(comma-separated, max 10)',
  'lab.exempleTags':            'retail, sell-out, {{marque}}',
  'commun.sauvegardeEnCours':      'Saving…',
  'commun.enregistrer':            'Save',
  'lab.partagerRequete':        'Share query',
  'lab.visibilite':             'Visibility',
  'lab.idsUtilisateurs':        'User IDs',
  'lab.uuidsAide':              '(UUIDs separated by comma or space)',
  'lab.aucunUuid':              'No valid UUID detected.',
  'lab.nUtilisateur':           '{{n}} user selected',
  'lab.nUtilisateurs':          '{{n}} users selected',
  'lab.miseAJour':              'Updating…',
  'lab.visPriveeLongue':        'Private (me only)',
  'lab.visPartageeLongue':      'Shared (chosen users)',
  'lab.visOrgaLongue':          'Whole organisation',
  'lab.supprimerRequete':       'Delete query',
  'commun.suppressionEnCours':     'Deleting…',
  'lab.confirmSupprPrefix':     'Delete ',
  'lab.confirmSupprSuffix':     '? This action is reversible via administration.',

  // ── `#837` B2 · Labo — resultats, rendus, surfaces ──────────────────────
  'lab.typeAnalyse':                 'Analysis type',
  'lab.formatSortie':                'Output format',
  'lab.lieux':                       'Places',
  'lab.executionEnCours':            'Running…',
  'lab.erreurMoteur':                'The SSL engine hit an unexpected error while running this query.',
  'lab.erreurMoteurHint':            'This KPI, period and target combination is not supported yet. Technical detail in the browser console (F12).',
  'lab.configurezRequete':           'Configure your query to see the preview here',
  'lab.aucunResultatCriteres':       'No result found for these criteria.',
  'lab.nLigne':                      '{{n}} row',
  'lab.nLignes':                     '{{n}} rows',
  'lab.aucunResultat':               'No result.',
  'lab.aucunResultatRequete':        'No result for this query.',
  'lab.aucunClassement':             'No ranking available for these criteria.',
  'lab.donneesInsuffisantes':        'Insufficient data',
  'lab.aucuneValeurPeriode':         'No value over the selected period. Check the period or the active filters.',
  'lab.aucuneValeurPeriodeCourt':    'No value over the selected period.',
  'lab.verifiezPeriode':             'Check the period or the active filters.',
  'lab.estimation':                  'Estimating…',
  'lab.erreurEstimation':            'Estimation error',
  'lab.zeroEstime':                  '⚠ 0 estimated result — check your filters',
  'lab.nLigneEstimee':               '~{{n}} estimated row',
  'lab.nLignesEstimees':             '~{{n}} estimated rows',
  'lab.executionCourte':             'Running…',
  'lab.lancerAnalyse':               'Run the analysis',
  'lab.comboMax2':                   '2 KPIs max in combo mode, deselect {{n}}.',
  'lab.comboUneMarque':              'Combo mode compares 2 indicators on a single brand. Select one brand.',
  'lab.totalPopulation':             'Total population:',
  'lab.referenceMarche':             'Market reference:',
  'lab.baseEcartMarche':             '(Δ market base)',
  'lab.colRang':                     'Rank',
  'lab.colEntite':                   'Entity',
  'lab.colPctPop':                   '% pop.',
  'lab.colCumul':                    'Cum. %',
  'lab.colEcartLeader':              'Δ leader',
  'lab.colEcartMarche':              'Δ market',
  'lab.epinglee':                    'Pinned',
  'lab.chartCourbe':                 'Line',
  'lab.chartBarres':                 'Bars',
  'lab.chartListe':                  'List',
  'lab.chartTableau':                'Table',
  'lab.chartDoubleAxe':              'Dual axis',
  'lab.affichage':                   'Display:',
  'lab.typeGraphique':               'Chart type',
  'lab.auto':                        'Auto ({{type}})',
  'lab.resultat':                    'Result',
  'lab.rechercherResultats':         'Search in results…',
  'lab.filtreeSur':                  ' (filtered on "{{recherche}}")',
  'lab.grandTotal':                  'Grand total:',
  'lab.surfListe':                   'Lab — list',
  'lab.surfCroise':                  'Lab — pivot table',
  'lab.surfCroise2D':                'Lab — pivot table 2D',
  'lab.surfTableau':                 'Lab — table',
  'lab.surfClassement':              'Lab — ranking',

  // ── `#837` B2 · Labo — pivot rendu, panneau de filtres, graphe ──────────
  'lab.selectionValeurs':       'Value selection',
  'lab.application':            'Applying…',
  'lab.filtreBientot':          'Filter on this dimension coming soon (high cardinality — searchable selector).',
  'lab.erreurValeurs':          'Failed to load values.',
  'lab.aucuneValeurDispo':      'No value available.',
  'lab.rechercherDimension':    'Search {{dimension}}…',
  'lab.moyenne':                'Average',
  'lab.totalPool':              'Total (pool)',
  'lab.totalDistinct':          'Total (distinct)',
  'lab.badgeEnCours':           ' (in progress)',
  'lab.tableauPivot':           'Pivot table',
  'lab.suffixeBarres':          ' · {{kpi}} (bars) · {{kpi2}} (line)',

  // ── `#837` back · Labo — les 6 messages anglais et les 4 codes nus ──────

  // ── `#839` T1 · CHROME — la coquille que tous les modules traversent ────
  'commun.rechercher':            'Search',
  'commun.rechercheGlobale':      'Global search (Cmd+K)',
  'commun.rechercherQuoi':        'Search a PoS, brand, module…',
  'commun.rechercheEnCours':      'Searching…',
  'commun.recherchePoints':       'Search…',
  'commun.accesRapide':           'Quick access',
  'commun.aucunResultatPour':     'No result for "{{recherche}}"',
  'commun.naviguer':              'navigate',
  'commun.selectionnerTouche':    'select',
  'commun.toucheEchap':           'Esc',
  'commun.fermerMinuscule':       'close',
  'commun.modele':                'Model',
  'commun.barreNavigation':       'Navigation bar',
  'commun.filAriane':             'Breadcrumb',
  'commun.selectionnerModule':    'Select a module',
  'commun.menuUtilisateur':       'User menu',
  'commun.widgetsNavigation':     'Navigation widgets',
  'commun.accueilDashboard':      'MX SFMT Dashboard — Home',
  'commun.reconciliation':        'Reconciliation',
  'commun.seDeconnecter':         'Log out',
  'theme.modeClair':              'Light mode',
  'theme.modeSombre':             'Dark mode',
  'commun.quitterPleinEcran':     'Exit full screen',
  'commun.pleinEcran':            'Full screen',
  'commun.precedent':             'Previous',
  'commun.suivant':               'Next',
  'commun.paginationSur':         '{{a}}–{{b}} of {{total}}',
  'commun.aucuneDonneeDispo':     'No data available.',
  'commun.erreurReact':           '⚠ React error caught',

  // ── `#839` T1 · les routes sans nom au catalogue (FR inchangé, EN ajouté) ───
  'nav.marcheRetailWholesale':    'Retail / Wholesale Market',
  'nav.catalogueFacteurs':        'Factors catalogue',
  'nav.assistantImport':          'Import wizard',
  'nav.validationSkus':           'SKU validation',
  'nav.validationPdvs':           'PoS validation',
  'nav.validationMerchs':         'Merch validation',
  'nav.validationBrands':         'Brand validation',
  'nav.utilisateurs':             'Users',
  'nav.monitoringSysteme':        'System monitoring',
  'nav.alertesSysteme':           'System alerts',
  'nav.pilotageQualite':          'Quality steering',
  'nav.phenomenesMarche':         'Market phenomena',

  // ── `#839` T1 · deux cles de plus, mesurees sur pieces ──────────────────
  'nav.modules':             'Modules',
  'commun.nNotifNonLues':    '{{n}} unread notification(s)',

  // ── `#839` T1 · la marque cliente dans le chrome ────────────────────────
  'commun.marqueClientePays':    '{{marque}} Algeria',

  // ── `#840` T2 · AUTH — les trois pages ──────────────────────────────────
  'auth.connexion':                   'Sign in',
  'auth.adresseEmail':                'Email address',
  'auth.connexionEnCours':            'Signing in…',
  'auth.seConnecter':                 'Sign in',
  'auth.emailInvalide':               'Invalid email address',
  'auth.motDePasseRequis':            'Password required',
  'auth.identifiantsIncorrects':      'Incorrect email or password.',
  'auth.tropDeTentatives':            'Too many attempts, try again in 15 minutes.',
  'audit.stats.titre':                'Usage statistics',
  'audit.stats.connexions':           'Logins per day',
  'audit.stats.ecrans':               'Most frequent actions',
  'audit.stats.derniers':             'Last access per user',
  'audit.stats.vide':                 'No data for this period.',
  'auth.exempleEmail':                'your@email.com',
  'auth.sessionJetonRejoue':          'A session token already used was presented. As a precaution, your session was closed. If you do not recognise this action, notify an administrator.',
  'auth.sessionDureeMax':             'Your session reached its maximum duration and was closed. This is not a failure: sign in again to continue.',
  'auth.sessionInactivite':           'Your session was closed after 30 minutes without activity. This is not a failure: sign in again to continue.',
  'auth.verification2Etapes':         'Two-step verification',
  'auth.codeAuthentification':        'Authentication code',
  'auth.codeSecours':                 'Backup code',
  'auth.verificationEnCours':         'Verifying…',
  'auth.valider':                     'Confirm',
  'auth.telephoneIndispo':            'Phone unavailable? Use a backup code',
  'auth.revenirCodeAppli':            'Back to the app code',
  'auth.retourConnexion':             'Back to sign in',
  'auth.saisissezCode6':              'Enter the 6-digit code from your authenticator app.',
  'auth.saisissezSecours':            'Enter one of the backup codes received at activation.',
  'auth.erreurSaisieCode6':           'Enter a 6-digit code.',
  'auth.erreurSaisieSecours':         'Enter a full backup code (format XXXXX-XXXXX).',
  'auth.codeRefuseTotp':              'This code was not accepted. Check it is the 6-digit code currently shown by your app — it changes every 30 seconds.',
  'auth.codeRefuseSecours':           'This backup code was not accepted. Check you copy it in full (format XXXXX-XXXXX) and that it has not been used: each code works only once.',
  'auth.secoursUneFoisDebut':         '⚠ A backup code works ',
  'auth.secoursUneFoisGras':          'only once',
  'auth.secoursUneFoisFin':           ': once accepted, it is consumed for good. You had eight, and they cannot be regenerated.',
  'auth.configSecondFacteur':         'Second factor setup',
  'auth.secondFacteurActive':         'Second factor enabled',
  'auth.copies':                      'Copied',
  'auth.copierLesCodes':              'Copy the codes',
  'auth.jaiNoteMesCodes':             'I have saved my codes — continue',
  'auth.secondFacteurDejaConfig':     'Second factor already set up',
  'auth.compteDejaSecondFacteur':     'This account already has a second factor. Sign in with your app code, or with a backup code if you no longer have it.',
  'auth.saisirUnCode':                'Enter a code',
  'auth.compteExigeSecondFacteur':    'This account requires a second factor',
  'auth.ajoutezLaCle':                'Add the key below to your authenticator app, then enter the code it shows. You will have access to no data before that.',
  'auth.preparation':                 'Preparing…',
  'auth.cleASaisir':                  'Key to enter in the app',
  'auth.codeAfficheAppli':            'Code shown by the app',
  'auth.activerSecondFacteur':        'Enable the second factor',
  'auth.impossiblePreparer':          'Unable to prepare the second factor.',
  'auth.erreurSaisieCode6Appli':      'Enter the 6-digit code shown by your app.',
  'auth.codeRefuseEnrol':             'This code was not accepted. Check it is the code currently shown by your app — it changes every 30 seconds.',
  'auth.notezCesCodes':               '⚠ Write down these {{n}} codes now — they will never be shown again.',
  'auth.chacunUneFoisDebut':          'Each works ',
  'auth.chacunUneFoisFin':            ', and they cannot be regenerated. They are your only recourse if you lose your phone. Reloading this page will not show them again.',
  // ── `#1086` · the enrolment QR ──
  'auth.scannezLeQr':                 'Scan this code with your authenticator app.',
  'auth.qrIndisponible':              'The visual code could not be displayed — use the key below, it does exactly the same thing.',
  'auth.ouSaisieManuelle':            'Or enter it manually',
  'auth.saisieManuelleDebut':         'If you prefer, your app can also ingest the address ',
  // ── `#1089` · a failed batch says the path that works ──
  'import.echecTitre':                 'Import failed',
  'import.echecConsigneSupprimer':     'A new attempt on this week will be refused while this batch exists. Delete it first (Import history → Delete), then run the import again.',
  'import.echecConsigneDemander':      'A new attempt on this week will be refused while this batch exists. Ask a Director or a Project manager to delete it, then run the import again.',

  // ── `#840` T2 ★2 · AUTH — ce que le SERVEUR nomme, l'écran l'écrit ──────

  // ── `#841` T3-A · IMPORT — l'assistant hebdomadaire ─────────────────────
  'import.assistantTitre':             'Weekly import assistant',
  'import.assistantSous':              'Import the week\'s data in 3 guided steps.',
  'import.tauxChange':                 'Exchange rate',
  'import.fxHebdo':                    'Weekly FX rate',
  'import.fichiersSource':             'Source files',
  'import.rawdataActivite':            'Rawdata + activity',
  'import.validationLancement':        'Validation & launch',
  'import.fxPourSemaine':              'DA/USD FX rate for the week to import',
  'import.semaineIso':                 'ISO week',
  'import.tauxConversionOfficiel':     'Official conversion rate',
  'import.contexteSemaine':            'Week context',
  'import.commentairesDivers':         'Miscellaneous comments',
  'import.noteAbsence':                'Absence note',
  'import.seraJointRH':                'Will be attached to the batch comment as HR context.',
  'import.exSemaine':                  'e.g. 2026-W18',
  'import.exTaux':                     'e.g. 135.5',
  'import.remarquesTaux':              'Remarks on this exchange rate…',
  'import.absencesConges':             'Absences, leave, field events this week…',
  'import.validerTaux':                'Confirm the rate',
  'import.erreurTaux':                 'Error saving the rate.',
  'import.rawdataRetail':              '1. Retail rawdata',
  'import.rawdataWholesale':           '2. Wholesale rawdata',
  'import.rapportActivite':            '3. Activity report',
  'import.requis':                     'Required',
  'import.optionnel':                  'Optional',
  'import.descRetail':                 '23 columns · PoS / SKU / Week / Brand',
  'import.descWholesale':              '23 columns · anchored on REGION · channel = wholesale',
  'import.descActivite':               'Field visits · PDV Code / Visit Date',
  'import.labelRetail':                'Retail rawdata',
  'import.labelWholesale':             'Wholesale rawdata',
  'import.labelActivite':              'Activity report',
  'import.lignesValidesRejetees':      '{{v}} valid rows · {{r}} rejected',
  'import.importe':                    'Imported',
  'import.erreurImportRawdata':        'Rawdata import error',
  'import.erreurImportWholesale':      'Wholesale import error',
  'import.erreurImportActivite':       'Activity import error',
  'import.colonnesRequises':           'Required columns: ',
  'import.formatAttendu':              'Expected format: 23 canonical columns anchored on ',
  'import.retourEtape1':               '← Back to step 1',
  'import.retourEtape2':               '← Back to step 2',
  'import.continuerReconciliation':    'Continue to reconciliation',
  'import.recommencerDebut':           'Start over',
  'import.validationCroisee':          'Rawdata × activity cross-validation',
  'import.lignesValides':              'valid rows',
  'import.nonUploade':                 'not uploaded',
  'import.activite':                   'Activity',
  'import.reconciliationExplique':     'Reconciliation crosses both files to check visit consistency. The report will be available right after launch.',
  'import.phaseEnvoi':                 'sending…',
  'import.phaseCalcul':                'computing…',
  'import.phaseFile':                  'job queued, waiting for the worker…',
  'import.reconEnCoursPhase':          'Reconciliation running… ({{phase}})',
  'import.jobArrierePlanDebut':        'The job runs in the background (BullMQ). Do not close the tab — the report page will open automatically when the status reaches ',
  'import.jobArrierePlanMilieu':       '. Volume processed: ',
  'import.jobArrierePlanFin':          ' rawdata rows.',
  'import.batchFailedDebut':           'The batch is in ',
  'import.batchFailedFin':             ' status — you can restart with the "Launch reconciliation" button below, or check the server logs to identify the cause.',
  'import.workerEchoue':               'The reconciliation worker failed.',
  'import.erreurLancementRecon':       'Error launching reconciliation.',
  'import.postNonEnqueue':             'The POST could not be enqueued (Redis unavailable?). Check the server state, then restart.',
  'import.envoi':                      'Sending…',
  'import.reconEnCours':               'Reconciliation running…',
  'import.relancerReconciliation':     'Restart reconciliation',
  'import.lancerReconciliation':       'Launch reconciliation',
  'import.glissezFichier':             'Drop a file here or click to browse',
  'import.retirer':                    'Remove',
  'import.zoneDepot':                  'Drop zone for {{label}}',
  'import.envoiEnCours':               'Uploading',
  'import.retirerFichier':             'Remove the selected file',
  'import.etapesWizard':               'Wizard steps',

  // ── `#841` T3-B · IMPORT — historique, statuts, publication ─────────────
  'import.historiqueTitre':         'Import history',
  'import.historiqueSous':          'All weekly import batches',
  'import.nouvelImport':            'New import',
  'import.reinitFiltres':           'Reset filters',
  'import.impossibleCharger':       'Failed to load history.',
  'import.aucunBatch':              'No batch found.',
  'import.essayezFiltres':          'Try changing the filters.',
  'import.tousStatuts':             'All statuses',
  'commun.filtrerStatut':           'Filter by status',
  'import.filtrerSemaine':          'Filter by ISO week',
  'import.phSemaineIso':            'ISO week (e.g. 2025-W18)',
  'commun.enAttente':               'Pending',
  'import.stPartiel':               'Partial',
  'import.stCharge':                'Loaded',
  'import.stValidation':            'Validation',
  'import.stEnRevue':               'In review',
  'import.stApprouve':              'Approved',
  'import.stApprouveForce':         'Approved (forced)',
  'import.stEchec':                 'Failed',
  'import.stFinalisePartiel':       'Finalised (partial)',
  'import.stRejete':                'Rejected',
  'import.colLignesRD':             'RD rows',
  'import.colLignesWHS':            'WHS rows',
  'import.colLignesACT':            'ACT rows',
  'import.colCreeLe':               'Created on',
  'import.colPublication':          'Publication',
  'commun.action':               'Action',
  'import.titreTauxSaisi':          'DA / USD exchange rate entered in the wizard',
  'import.titreFichierRD':          'Retail RAWDATA file',
  'import.titreLignesRD':           'Valid rows kept from the retail RAWDATA file',
  'import.titreFichierWHS':         'Wholesale RAWDATA file',
  'import.titreLignesWHS':          'Valid rows kept from the wholesale RAWDATA file',
  'import.titreFichierACT':         'Activity Report file',
  'import.titreLignesACT':          'Valid rows kept from the Activity Report file',
  'import.nonImporte':              'not imported',
  'import.voirRapport':             'View the reconciliation report',
  'import.rapportFleche':           'Report →',
  'import.supprimerTraces':         'Delete all traces of this import',
  'import.suppressionEnCours':      'Deleting…',
  'import.supprimerCetImport':      'Delete this import',
  'import.donneesDeCetImport':      'data of this import',
  'import.supprimerImportSem':      'Delete the {{semaine}} import',
  'import.unResultat':              '{{n}} result',
  'import.nResultats':              '{{n}} results',
  'import.depublier':               'Unpublish',
  'import.nonPubliee':              '○ not published',
  'import.publicationEnCours':      'Publishing…',
  'import.nonFinalisee':            'not finalised',
  'import.depublicationEnCours':    'Unpublishing…',
  'import.stATerminer':             'Import to finalize',
  'import.terminer':                'Finalize',
  'import.terminerEnCours':         'Finalizing…',
  'import.terminerErreur':          'Finalization failed',
  'import.echecPublication':        'Publication failed.',
  'import.echecDepublication':      'Unpublication failed.',
  'import.publieeLe':               '● published on {{date}}',
  'import.publieeLePar':            '● published on {{date}} · {{role}}',
  'import.statutPubliable':         'Status "{{statut}}" — publishable from {{liste}}',
  'import.depublierSemaine':        'Unpublish {{semaine}}',
  'import.visibiliteMarque':        '{{marque}} visibility: an unpublished week does not exist for the client',
  'import.neVerraPlusGras':         '{{marque}} will no longer see this week',
  'import.neVerraPlusFin':          ' — neither its figures nor its existence. The week stays intact at Orbicore and can be republished at any time.',

  // ── `#841` T3-C · IMPORT — rejets, purge, hooks ─────────────────────────
  'import.verdictSeuil':                 '⛔ Week NOT published — {{pct}} % of rejected rows exceeds the {{seuil}} % threshold.',
  'import.autresErreurs':                '…and {{n}} other errors ({{exemple}})',
  'import.regleColonne':                 '{{n}}× {{regle}} on column {{colonne}}',
  'import.avecExemples':                 '{{base}} (e.g. {{exemples}})',
  'import.nLignesRejetees':              '⚠ {{n}} rejected rows',
  'import.nLignesRejeteesMotifs':        '⚠ {{n}} rejected rows — reasons:',
  'import.motConfirmation':              'DELETE',
  'import.actionIrreversible':           '⚠️ Irreversible action',
  'import.supprimeraTousDebut':          'This action will delete ',
  'import.supprimeraTousGras':           'ALL',
  'import.supprimeraTousFin':            ' the {{label}} as well as all associated data (cascade). It will be impossible to recover them.',
  'import.pourConfirmerDebut':           'To confirm, type ',
  'import.pourConfirmerFin':             ' below:',
  'import.supprimerTousLes':             'Delete all the {{label}}',
  'import.purgeDebut':                   'This action removes ',
  'import.purgeToutes':                  'ALL',
  'import.purgeMilieu1':                 ' traces of the import ',
  'import.purgeMilieu2':                 ': ',
  'import.purgeObservations':            '{{n}} observations',
  'import.purgeFin':                     ', trust scores, anomalies, staging files and the reconciliation report.',
  'import.purgeArrierePlan':             'Deletion runs in the background and may take ~1 min. Irreversible.',
  'import.aucunBatchAVider':             'No batch to purge.',
  'import.aucunIdBatch':                 'No rawdata batch ID available',
  'import.suppressionEchoue':            'Deletion failed.',
  'import.jobSuppressionIntrouvable':    'Deletion job not found (worker unavailable?).',
  'import.suppression10min':             'Deletion still running after 10 min — see the server logs. The job continues in the background.',
  'import.publicationEchoue':            'Publication failed.',
  'import.depublicationEchoue':          'Unpublication failed.',
  'import.introuvable':                  '{{resource}} not found',
  'import.suppressionContrainte':        'Deletion failed (data constraint).',

  // ── `#842` T4-A · ADMIN — parametres, configuration, utilisateurs ───────
  'admin.aucuneSection':               'No accessible section.',
  'admin.compte':                      'Account',
  'admin.nomComplet':                  'Full name',
  'admin.roleS':                       'Role(s)',
  'admin.nonConnecte':                 'Not signed in.',
  'admin.listesDiffusion':             'Email report distribution lists',
  'admin.listesIndispo':               'Lists unavailable.',
  'admin.aideListeComplete':           'FULL version — all sources, SFM Perf and Data Accuracy included.',
  'admin.aideListeFiltree':            'FILTERED version — the market, never the internal team alerts.',
  'admin.aucunDestinataire':           'no recipient — this send will not go out',
  'admin.unDestinataire':              '{{n}} recipient',
  'admin.nDestinataires':              '{{n}} recipients',
  'admin.uneAdresseParLigne':          'one address per line',
  'admin.listeNom':                    'List {{nom}}',
  'admin.enregistre':                  'Saved',
  'admin.enregistrementImpossible':    'save failed',
  'admin.configSysteme':               'System configuration',
  'admin.seuilsLimites':               'Thresholds and limits — editable by SA/AO only',
  'admin.configErreur':                'Failed to load configuration. Retry.',
  'admin.aucuneEntreeConfig':          'No configuration entry.',
  'admin.misAJourLe':                  'Updated on',
  'admin.modSysteme':                  'System',
  'admin.modJournalAudit':             'Audit log',
  'admin.gestionUtilisateurs':         'User management',
  'admin.nouvelUtilisateur':           'New user',
  'admin.usersErreur':                 'Failed to load users. Retry.',
  'admin.aucunUtilisateur':            'No user found.',
  'admin.comptesPlateforme':           'Platform user accounts',
  'admin.unUtilisateur':               '{{n}} user',
  'admin.nUtilisateurs':               '{{n}} users',
  'admin.precedentFleche':             '← Previous',
  'commun.suivantFleche':               'Next →',
  'admin.editer':                      'Edit',
  'admin.aucun':                       'None',
  'admin.rechercherEmailNom':          'Search email / name',
  'admin.rechercherUtilisateur':       'Search for a user',
  'admin.tousStatuts':                 'All statuses',
  'admin.stSuspendu':                  'Suspended',
  'admin.stDesactive':                 'Deactivated',
  'admin.role.SUPER_ADMIN':            'Orbicore – Director',
  'admin.role.ADMIN_ORBICORE':         'Orbicore – PM',
  'admin.role.CP_JUNIOR':              'Orbicore – Junior PM',
  'admin.role.ANALYST_ORBICORE':       'Orbicore – Data analyst',
  'admin.role.MANAGER_ORBICORE':       'Orbicore – Controller',
  'admin.role.SUPPORT_ORBICORE':        'Orbicore – Support', // `#1091`
  'admin.editerUtilisateur':           'Edit user',
  'admin.statutActuel':                'Current status:',
  'admin.nouveauStatut':               'New status',
  'admin.inchange':                    '— Unchanged —',
  'admin.desactiveIrreversible':       'This account is deactivated irreversibly.',
  'admin.erreurMaj':                   'Update failed. Retry.',
  'admin.enregistrementEnCours':       'Saving…',
  'admin.rolesTitre':                  'Roles',
  'admin.chargementRoles':             'Loading roles…',
  'admin.rolesIntrouvables':           'Failed to load this account’s roles.',
  'admin.aucunRoleAttribue':           'No role assigned.',
  'admin.retirerRole':                 'Remove the {{role}} role',
  'admin.retraitRefuse':               'Removal refused.',
  'admin.roleAAjouter':                'Role to add',
  'admin.tousRolesAttribues':          '— All roles are assigned —',
  'admin.choisirRole':                 '— Choose a role —',
  'commun.ajouter':                     'Add',
  'admin.ajoutEnCours':                'Adding…',
  'admin.attributionRefusee':          'Assignment refused.',
  'admin.secondFacteur':               'Second factor (2FA)',
  'admin.etatActuel':                  'Current state: ',
  'admin.configureParUtilisateur':     'set up by the user',
  'admin.nonConfigure':                'not set up',
  'admin.exigerSecondFacteur':         'Require a second factor',
  'admin.exigerSecondFacteur2fa':      'Require a second factor (2FA)',
  'admin.decocherNEffacePas':          'Unchecking does not erase the second factor already set up: the account will keep asking for it at sign-in.',
  'admin.devraConfigurer':             'The account will have to set it up at its next sign-in, with no access to data before.',
  'admin.compteBloqueDebut':           'Account locked? Erasing its second factor gives access back. It will have to set it up again if the requirement stays.',
  'admin.codesSecoursInvalides':       'Its remaining backup codes will be invalidated.',
  'admin.reinitSecondFacteur':         'Reset the second factor',
  'admin.reinitEnCours':               'Resetting…',
  'admin.secondFacteurEfface':         'Second factor erased',
  'admin.codesInvalides':              ' — {{n}} backup code(s) invalidated.',
  'admin.peutSeReconnecter':           ' The account can sign in again.',
  'admin.reinit2faEchouee':            'The second factor reset failed.',
  'commun.motDePasse':                  'Password',
  'admin.motDePasseTemporaire':        'Temporary password',
  'admin.afficheUneSeuleFois':         '(shown once)',
  'admin.sessionsRevoquees':           ' — all sessions of this account have been revoked.',
  'admin.copierMotDePasseTemp':        'Copy the temporary password',
  'admin.copierMotDePasse':            'Copy the password',
  'admin.communiquerSecurise':         'Communicate this password to the user securely.',
  'admin.reinitMotDePasse':            'Reset the password',
  'admin.generationEnCours':           'Generating…',
  'admin.reinitRefusee':               'Reset refused.',
  'admin.creerUtilisateur':            'Create a user',
  'admin.utilisateurCree':             'User created successfully',
  'admin.termine':                     'Done',
  'admin.roleInitial':                 'Initial role',
  'admin.sansRole':                    '— No role —',
  'admin.premiereConnexion2fa':        'At first sign-in, this account will have to set up an authenticator app. It will have no access to any data before doing so.',
  'admin.erreurCreation':              'Creation failed. Retry.',
  'admin.creationEnCours':             'Creating…',
  'admin.phPrenomNom':                 'First name Last name',

  // ── `#842` T4-B · ADMIN — calibrage P1 et seuils P9 ─────────────────────
  'admin.calibrageAmpleur':            'Magnitude threshold calibration',
  'admin.phenomenesP1':                'P1 phenomena — by KPI · ',
  'admin.editable':                    'editable',
  'admin.lectureSeule':                'read-only',
  'admin.semaineReference':            ' · reference week ',
  'admin.calibrageNatifTitre':         'Calibration in native values',
  'admin.calibrageNatif1':             ' — you enter thresholds in the KPI\'s unit (pt, $, PoS …). The system derives the ',
  'admin.calibrageNatifRelatif':       'relative (%)',
  'admin.calibrageNatif2':             ' threshold from the national base of the calibration week, which stays frozen — it is that ',
  'admin.calibrageNatifMotRelatif':    'relative',
  'admin.calibrageNatif3':             ' one the phenomena engine uses to classify.',
  'admin.deriveTitre':                 'Drift',
  'admin.derive1':                     ' — week after week, the national base moves. If it deviates by more than ',
  'admin.derive2':                     ' from the frozen base, an amber badge offers a ',
  'admin.deriveRecalage':              'recalibration',
  'admin.derive3':                     ' to realign the relative without changing the native business target.',
  'admin.lectureSeulePerm1':           'Read-only — the ',
  'admin.lectureSeulePerm2':           ' permission is required to edit.',
  'admin.seuilsErreur':                'Failed to load thresholds.',
  'admin.aucunSeuilSeede':             'No seeded threshold — run the phenomena seed.',
  'admin.colNature':                   'Nature',
  'admin.colDescription':              'Description',
  'admin.calibrageDetection':          'A phenomenon is born from an unusual move of the KPI\'s national value (record, streak, deviation from average, acceleration, year on year). The thresholds below do not decide its birth: they set the gap from which it is moderate, then major.',
  'admin.colMajeurNatif':              'Major (native)',
  'admin.colModereNatif':              'Moderate (native)',
  'admin.colBaseDerive':               'Base & drift',
  'admin.colEtat':                     'State',
  'admin.calculEnEchec':               '⚠ computation failed',
  'admin.calculEnEchecTitre':          'This indicator’s computation FAILED — this is not missing data, it is a defect to fix.',
  'admin.sansDonneesSemaine':          'no data this week',
  'admin.sansDonneesTitre':            'This indicator’s source does not cover this week — nothing to compute. This is not a defect.',
  'admin.baseActuelle':                'current base ',
  'admin.pasDeReference':              'no reference (legacy relative mode)',
  'admin.refCourt':                    'ref. ',
  'admin.actuelleCourt':               'current ',
  'admin.deriveValeur':                'drift {{valeur}}',
  'admin.deriveAmbreTitre':            'Drift ≥ 20 %: the base has moved notably since calibration, recalibration recommended.',
  'admin.deriveSousSeuil':             'Drift below the tolerance threshold.',
  'admin.calibreNatifLe':              'native calibrated on {{date}}',
  'admin.relatifLegacy':               'legacy relative',
  'admin.defaut':                      'default',
  'admin.ordreSeuils':                 'major > moderate > 0',
  'admin.calibrageImpossibleTitre':    'Native calibration impossible: national base unavailable this week.',
  'admin.calibrageImpossibleEdit':     'Native calibration impossible this week — national base unavailable.',
  'admin.baseIndisponible422':         'National base unavailable this week — native calibration impossible.',
  'admin.echecCode':                   'Failure ({{code}})',
  'admin.reseau':                      'network',
  'admin.pctRelatif':                  '{{v}} % relative',
  'admin.natifReconstruitTitre':       'Native rebuilt from relative threshold × current base (no frozen reference).',
  'admin.natifNonCalculableTitre':     'National base unavailable this week — native not computable. The engine classifies on this relative %.',
  'admin.recaler':                     'Recalibrate',
  'admin.recalerKpi':                  'Recalibrate {{kpi}}',
  'admin.recalerKpiDerive':            'Recalibrate {{kpi}} — the base drifted by {{derive}}.',
  'admin.recalerKpiBase':              'Recalibrate {{kpi}} on the current week’s base.',
  'admin.recalerKpiTitre':             'Recalibrate {{kpi}} on the current week',
  'admin.editerKpi':                   'Edit {{kpi}}',
  'admin.recalLigne1':                 'The native threshold stays unchanged: {{majeur}} (major) / {{modere}} (moderate).',
  'admin.recalLigne2':                 'The reference base{{semaine}}is replaced by the current base ({{base}}). The RELATIVE threshold used by the classification engine is recomputed from it.{{derive}}',
  'admin.recalSemaineDu':              ' — the one from {{semaine}} — ',
  'admin.recalDeriveActuelle':         ' Current drift: {{derive}}.',
  'admin.recalLigne3':                 'The write is immediate and only affects {{kpi}}. The other {{n}} thresholds are unchanged.',
  'admin.seuilsP9':                    'P9 alert thresholds — Performance',
  'admin.p9SousTitre':                 '9 alerts × 2 channels · ',
  'admin.aucunSeuilP9':                'No threshold — run the perf_alert_thresholds seed.',
  'commun.alerte':                   'Alert',
  'admin.colDir':                      'Dir.',
  'admin.colJaune':                    '🟡 Yellow',
  'admin.colOrange':                   '🟠 Orange',
  'admin.colRouge':                    '🔴 Red',
  'admin.colCouvMin':                  'Min cov. %',
  'admin.colModifie':                  'Modified',
  'admin.gpsDesactivee':               'disabled — enable after loading reference coordinates',
  'admin.erreurReseau':                'Network error',
  'admin.p9.COVERAGE':                 'Routing coverage (%)',
  'admin.p9.LINES_COLLECTED':          'Collected lines (team rel. gap %)',
  'admin.p9.FROZEN':                   'Frozen lines (%)',
  'admin.p9.SHORT_VISITS':             'Visits < 15 min (count)',
  'admin.p9.FIRST_VISIT_LATE':         '1st visit after 9:30 (time)',
  'admin.p9.POS_CLOSED':               'Closed PoS (count)',
  'admin.p9.POS_UNVISITED':            'PoS unvisited 3+ wks (%)',
  'admin.p9.TIME_PER_DAY':             'Time/day (two-sided, min)',
  'admin.p9.SALES_COLLECTED':          'Sales collection (team rel. gap %)',
  'admin.p9.GPS_DISPERSION':           'GPS dispersion begin-PoS (%)',
  'admin.unite.PCT':                   '%',
  'admin.unite.DELTA_REL':             'rel. gap',
  'admin.unite.MINUTES':               'min',
  'admin.unite.TIME':                  'time',
  'admin.unite.COUNT':                 'count',
  'admin.dir.BAS':                     'Low',
  'admin.dir.HAUT':                    'High',
  'admin.dir.COUNT':                   'Count',
  'admin.dir.HEURE':                   'Time',
  'admin.dir.BILAT':                   'Two-sided',

  // ── `#842` T4-C · ADMIN — segmentation et journaux d'audit ──────────────
  'admin.reglesSegmentation':         'Segmentation rules',
  'admin.uneRegle':                   '{{n}} rule',
  'admin.nRegles':                    '{{n}} rules',
  'admin.regleActiveNom':             ' · Active: {{nom}}',
  'admin.voirCatalogue':              'See catalogue',
  'admin.recalculer':                 'Recalculate',
  'admin.creerRegle':                 'Create rule',
  'admin.aucuneRegle':                'No rule',
  'admin.creezPremiereRegle':         'Create your first segmentation rule.',
  'admin.erreurChargementRegles':     'Error loading rules.',
  'admin.unFacteur':                  '{{n}} factor',
  'admin.nFacteurs':                  '{{n}} factors',
  'admin.activeeLe':                  ' · activated {{date}}',
  'admin.simuler':                    'Simulate',
  'admin.activer':                    'Activate',
  'admin.confirmer':                  'Confirm',
  'admin.regleActivee':               'Rule "{{nom}}" activated.',
  'admin.regleSupprimee':             'Rule "{{nom}}" deleted.',
  'admin.recalculNonLance':           'The recalculation was not started — {{cause}}. The current segments are unchanged.',
  'admin.recalculLance':              'Recalculation started — waiting for the result…',
  'admin.segmentsRecalcules':         'Segments recalculated',
  'admin.nPdvSegmentes':              '{{n}} PoS segmented',
  'admin.ontChangeSegment':           ' · {{n}} changed segment',
  'admin.recalculTermine':            'Recalculation finished — {{combien}}{{bouge}}.',
  'admin.recalculAgregatsPerimes':    'Recalculation finished — {{combien}}. ⚠ The aggregates could not be refreshed: the PDM and Shelf Share screens will show the previous per-segment split until the next refresh.',
  'admin.recalculEchoue':             'The recalculation FAILED — {{cause}}.',
  'admin.causeNonRenseignee':         'cause not provided by the server',
  'admin.recalculInconnu':            'Recalculation started, but still no answer. Reload the page in a moment to see the result.',
  'admin.confActiverRegle':           'Activate rule "{{nom}}"',
  'admin.confDejaActive':             '"{{nom}}" is already the active rule. Activating it again reruns a full computation.',
  'admin.confSeraRemplacee':          'The rule "{{nom}}", active today, will be deactivated and replaced.',
  'admin.confPremierCalcul':          'No rule is active today: this will be the FIRST segmentation computation.',
  'admin.confLigneEffet':             'All PoS of the reference base will be segmented: their current segment, the segmentation history and the insufficient-data anomalies will be written.',
  'admin.confActivationSurPlace':     'Activation computes in place: the page waits until the end, it does not wait for the next import.',
  'admin.confRelancerCalcul':         'Rerun the computation',
  'admin.confActiverSegmenter':       'Activate and segment',
  'admin.confRecalculTitre':          'Recalculate all segments',
  'admin.confRegleReappliquee':       'The active rule "{{nom}}" will be reapplied. No rule is modified, none is activated or deactivated.',
  'admin.confRecalculTacheFond':      'The recalculation runs in the background. The page waits for the server’s report and shows whether it succeeded — "started" is not a result.',
  'admin.regleBrouillon':             'Draft',
  'admin.regleActive':                'Active',
  'admin.regleArchivee':              'Archived',
  'admin.creerUneRegle':              'Create a rule',
  'admin.modifierLaRegle':            'Edit the rule',
  'admin.nomRegle':                   'Rule name',
  'admin.exRegle':                    'e.g. 2026_{{marque}}_Q2',
  'admin.descriptionRegle':           'Rule description',
  'admin.dslJsonRegle':               'Rule DSL JSON',
  'admin.enregistrerRegle':           'Save rule',
  'admin.catalogueFacteurs':          'Factor catalogue',
  'admin.regles':                     'Rules',
  'admin.unFacteurV1':                '{{n}} V1 factor available',
  'admin.nFacteursV1':                '{{n}} V1 factors available',
  'admin.catalogueLectureSeule1':     'Read-only catalogue — 5 active V1 factors. The factor ',
  'admin.catalogueLectureSeule2':     ' (Phase 5.I) will be activated in the next wave.',
  'admin.defautV1':                   'V1 default',
  'admin.normalisation':              'Normalisation: ',
  'admin.normIdentite':               'Identity (single factor)',
  'admin.normMinMax':                 'Min-max',
  'admin.plageBrute':                 'Raw range: ',
  'admin.erreurCatalogue':            'Error loading the catalogue.',
  'admin.resultatsSimulation':        'Simulation results',
  'admin.stables':                    'Stable',
  'admin.promus':                     'Promoted',
  'admin.retrogrades':                'Demoted',
  'commun.nonEvalues':                 'Not assessed',
  'admin.nonEvaluesExplique':         ': no observation over the period — the engine could not classify them. It is not a bad segment, it is missing data. They are counted in the total so that the shares add up to 100 %.',
  'admin.simSansRegle1':              'No rule is active: there is no previous state to compare. The simulation below is therefore a ',
  'admin.simPremierCalcul':           'first computation',
  'admin.simSansRegle2':              ', not an evolution.',
  'admin.seuilTransitions':           '⚠ Transition threshold exceeded (>30%) — check the factors.',
  'admin.simNouvelleRegle':           'Simulation (new rule)',
  'admin.regleActiveActuelle':        'Current active rule',
  'admin.matriceTransition':          'Transition matrix (rows=before, columns=after)',
  'admin.calculeEn':                  'Computed in {{ms}}ms',
  'admin.depuisCache':                ' · from cache',
  'admin.auditSousTitre':             'Who did what, when — full log, read-only.',
  'admin.auditErreur':                'Failed to load audit logs. Check your connection and retry.',
  'admin.colDate':                    'Date',
  'admin.colActeur':                  'Actor',
  'commun.cible':                   'Target',
  'admin.systemeAnonyme':             'System / anonymous',
  'admin.chargementLogs':             'Loading logs…',
  'admin.aucunAuditLog':              'No audit log found for these criteria.',
  'admin.uneEntree':                  '{{n}} entry',
  'admin.nEntrees':                   '{{n}} entries',
  'admin.precCourt':                  '‹ Prev',
  'admin.suivCourt':                  'Next ›',
  'admin.auditSucces':                'Success',
  'admin.auditEchec':                 'Failure',
  'admin.auditRefuse':                'Denied',
  'admin.filtresAudit':               'Audit log filters',
  'admin.tousLesActeurs':             'All actors',
  'admin.filtrerActeur':              'Filter by actor',
  'admin.filtrerNature':              'Filter by nature',
  'admin.filtrerAction':              'Filter by action',
  'admin.depuisDate':                 'From (date)',
  'admin.jusquaDate':                 'Until (date)',
  'admin.refus':                      'Denials',
  'admin.refusSeulement':             'Denials only',
  'admin.phCodeAction':               'Exact action code (e.g. LOGIN, PURGED…)',
  'admin.nature.toutes':              'All natures',
  'admin.nature.connexion':           'Sign-ins',
  'admin.nature.compte':              'Accounts & rights',
  'admin.nature.suppression':         'Deletions & purges',
  'admin.nature.reglage':             'Settings & thresholds',
  'admin.nature.import':              'Imports & reconciliation',
  'admin.nature.export':              'Exports & Lab',
  'admin.nature.consultation':        'Views',

  // ── `#845` T5-A · REFERENTIELS — les parcs et l'import en masse ─────────
  'ref.colRegion':                   'REGION',
  'ref.colWilaya':                   'WILAYA',
  'ref.colDistrict':                 'DISTRICT',
  'ref.colMerch':                    'MERCH',
  'ref.colCodePdv':                  'PoS CODE',
  'ref.colCanal':                    'CHANNEL',
  'ref.colNomPdv':                   'PoS NAME',
  'ref.colSegment':                  'SEGMENT',
  'ref.colStatut':                   'STATUS',
  'ref.modifiezFiltres':             'Change the filters.',
  'ref.lignesParPage':               'Rows per page:',
  'ref.pdvDroitsRefuses':            'You do not have the rights to view the PoS.',
  'ref.pdvReseau':                   'Failed to load the PoS (network unavailable).',
  'ref.pdvErreur':                   'Error loading the PoS.',
  'ref.reessayerPdv':                'Retry loading PoS',
  'ref.pagePrecedentePdv':           'Previous PoS page',
  'ref.pageSuivantePdv':             'Next PoS page',
  'ref.pageN':                       'Page {{n}}',
  'ref.plageSurTotal':               '{{debut}}–{{fin}} of {{total}}',
  'ref.nonEvalueTitre':              'Not assessed — no observation over the period',
  'ref.parcPdv':                     'PoS base',
  'ref.actifs':                      'Active',
  'ref.inactifs':                    'Inactive',
  'ref.parRegion':                   'By region',
  'ref.parCanal':                    'By channel',
  'ref.parSegmentPdv':               'By PoS segment',
  'ref.parMarque':                   'By brand',
  'ref.parSegmentPrix':              'By price segment',
  'ref.statsPortee':                 '⚠ Statistics on the whole reference base — the list below is filtered: {{n}} of {{total}}.',
  'ref.skusErreur':                  'Error loading SKUs.',
  'ref.pagePrecedenteSku':           'Previous SKU page',
  'ref.pageSuivanteSku':             'Next SKU page',
  'ref.colCode':                     'Code',
  'ref.colLibelle':                  'Label',
  'ref.colBatterie':                 'Battery (mAh)',
  'ref.colTaille':                   'Size (")',
  'ref.colArriere':                  'Rear (MP)',
  'ref.toutesWilayas':               'All wilayas',
  'ref.rechercherPdv':               'Search PoS',
  'ref.phCodeOuNom':                 'Code or name…',
  'ref.reinitFiltresPdv':            'Reset PoS filters',
  'ref.actifsUniquement':            'Active only',
  'ref.rechercherSku':               'Search SKU',
  'ref.phCodeOuLibelle':             'Code or label…',
  'ref.marqueSku':                   'SKU brand',
  'ref.skusActifsUniquement':        'Active SKUs only',
  'ref.reinitFiltresSku':            'Reset SKU filters',
  'ref.nonSegmente':                 'Not segmented',
  'ref.segmentCourant':              'Current segment',
  'ref.merchandiser':                'Merchandiser',
  'ref.premiereReference':           '1st reference',
  'ref.photoSku':                    'SKU photo',
  'ref.fond':                        'Background',
  'ref.texte':                       'Text',
  'ref.echecSauvegarde':             'Save failed',
  'ref.echecType':                   'Type failed',
  'ref.type':                        'Type',
  'ref.client':                      'Client',
  'ref.rechercherMarque':            'Search brand',
  'ref.phNomOuCode':                 'Name or code…',
  'ref.basculerClientConcurrent':    'Switch client / competitor',
  'ref.fermerConfigurateur':         'Close colour configurator',
  'ref.marquesErreur':               'Error loading brands.',
  'ref.template':                    'Template',
  'ref.uploadEnCours':               'Uploading…',
  'ref.importTermine':               'Import done',
  'ref.erreurUpload':                'Upload error. Check format and size.',
  'ref.remplacer':                   'Replace',
  'ref.erreurSuppression':           'Deletion error.',
  'ref.choisirPhotoSku':             'Choose an SKU photo',

  // ── `#845` T5-A bis · REFERENTIELS — les tuiles d'en-tete de page ───────
  'ref.skuAuReferentiel':        'SKUs in reference',
  'ref.pdvAuReferentiel':        'PoS in reference',
  'ref.marquesAuReferentiel':    'Brands in reference',
  'ref.couvertureWilayas':       'Wilaya coverage',

  // ── `#845` T5-A ter · le mot Segment seul (aria du filtre) ──────────────
  'ref.colSegmentMot':    'Segment',

  // ── `#845` T5-A quater · l'import en masse, le detail ───────────────────
  'ref.importLabel':          '{{quoi}} import (Excel)',
  'ref.colonnes':             'Columns: ',
  'ref.choisirFichier':       'Choose an .xlsx file',
  'ref.lignesTraitees':       'rows processed: ',
  'ref.nCreees':              '{{n}} created',
  'ref.nMisesAJour':          '{{n}} updated',
  'ref.uneLigneIgnoree':      '{{n}} row ignored',
  'ref.nLignesIgnorees':      '{{n}} rows ignored',
  'ref.detailsUneLigne':      '▸ Details ({{n}} row)',
  'ref.detailsNLignes':       '▸ Details ({{n}} rows)',
  'ref.etNAutresIgnorees':    '… and {{n}} other rows ignored',

  // ── `#845` T5-A quinquies · marques et fiche PDV, le detail ─────────────
  'ref.sauvegarde':     'Saved',

  // ── `#845` T5-A sexies · la photo SKU (SkuPhotoUploader, VIVANT) ────────
  'ref.choisirPhoto':    'Choose a photo (JPEG / PNG / WebP ≤ 5 MB)',

  // ── `#845` T5-B · REFERENTIELS — les pages `pending-*` et les orphelins ───
  'ref.pendingResolu':              '✓ Resolved → {{cible}}',
  'ref.pendingCree':                '✓ Created ({{cible}})',
  'ref.pendingIgnore':              'Ignored',
  'ref.exporterCsv':                'Export CSV',
  'ref.actualiser':                 'Refresh',
  'ref.masquerResolues':            'Hide resolved',
  'ref.masquerResolus':             'Hide resolved',
  'ref.telechargerCsvPrerempli':    'Download a pre-filled CSV for bulk import',
  'ref.skusAttenteErreur':          'Failed to load pending SKUs.',
  'ref.aucunSkuAttente':            'No SKU pending validation.',
  'ref.modeleBrut':                 'Raw model',
  'ref.marqueBrute':                'Raw brand',
  'ref.marquesDetectees':           'These brands were detected in the rawdata files but match no reference brand.',
  'ref.aucuneMarqueAttente':        'No brand pending validation.',
  'ref.marquesAttenteErreur':       'Failed to load pending brands.',
  'ref.verifierMarquesCreees':      'Check whether brands have been created since the last import',
  'ref.zeroPii':                    'Personal data protection — ZERO PII',
  'ref.aucunNomNiEmail':            'No name or email is stored here',
  'ref.merchsAttenteErreur':        'Failed to load pending agents.',
  'ref.aucunAgentInconnu':          'No unknown agent pending.',
  'ref.idAnonyme':                  'Anonymous ID',
  'ref.pdvObserve':                 'PoS observed',
  'ref.premiereVisite':             'First visit',
  'ref.telechargerCsvAgents':       'Download a CSV listing the unknown agents for HR resolution',
  'ref.pdvsAttenteErreur':          'Failed to load pending PoS.',
  'ref.aucunPdvAttente':            'No PoS pending validation.',
  'ref.donneeDisponible':           'Data available — pre-filled in the CSV',
  'ref.donneeManquante':            'Data missing — to complete in the CSV',
  'ref.disponible':                 'available',
  'ref.manquant':                   'missing',
  'ref.colCodePdvMin':              'PoS code',
  'ref.colSource':                  'Source',
  'ref.telechargerCsvPdv':          'Download a pre-filled CSV for PoS bulk import',
  'ref.toutAccepter':               'Accept all',
  'ref.toutRejeter':                'Reject all',
  'ref.aucunOrphelin':              'No pending rawdata orphan ✓',
  'ref.aucunPdvOuvert':             'No pending open PoS ✓',
  'ref.aucunPdvFerme':              'No closed PoS.',
  'ref.autoAccepte':                'Auto-accepted',
  'ref.colDateVisite':              'VISIT DATE',
  'ref.colLignesRd':                'RD ROWS',
  'ref.colVentesVol':               'SALES VOL.',
  'ref.colActions':                 'ACTIONS',
  'ref.accepter':                   'Accept',
  'ref.rejeter':                    'Reject',
  'ref.aucunImportEnCours':         'No import in progress',
  'ref.importezPourAcceder':        'Import and reconcile a file to access this page.',
  'ref.rfidRawdata':                'RFID / Rawdata',
  'ref.batchStatutFin':             ' — status ',
  'ref.pdvRawdataSansRfid':         'PoS present in rawdata, absent from RFID activities',
  'ref.pdvRfidSansRawdata':         'PoS present in RFID activities, absent from rawdata',

  // ── `#845` T5-B bis · le gabarit des quatre pages `pending-*` ───────────
  'ref.pendingSku.titre':                  'SKUs to validate',
  'ref.pendingMarque.titre':               'Brands to validate',
  'ref.pendingPdv.titre':                  'PoS to validate',
  'ref.pendingSku.enAttente':              '{{n}} SKU pending resolution',
  'ref.pendingSku.nEnAttente':             '{{n}} SKUs pending resolution',
  'ref.pendingSku.tousResolus':            'All SKUs are resolved',
  'ref.pendingSku.autoResolu':             '✓ {{n}} SKU auto-resolved — {{reste}} left.',
  'ref.pendingSku.autoResolus':            '✓ {{n}} SKUs auto-resolved — {{reste}} left.',
  'ref.pendingSku.aucuneResolution':       'No new resolution — {{n}} SKUs pending.',
  'ref.pendingSku.total':                  '{{n}} SKUs in total — sorted by volume of affected rows',
  'ref.pendingSku.detectes':               'These models (',
  'ref.pendingSku.detectesFin':            ') were detected in the rawdata files but match no reference SKU. The staging rows were ',
  'ref.pendingSku.workflow':               ' click ',
  'ref.pendingSku.workflowSpecs':          ' → complete the specs (RAM, ROM, price…) → import via ',
  'ref.pendingSku.refSkus':                'References → SKUs',
  'ref.pendingMarque.enAttente':           '{{n}} brand pending resolution',
  'ref.pendingMarque.nEnAttente':          '{{n}} brands pending resolution',
  'ref.pendingMarque.toutesResolues':      'All brands are resolved',
  'ref.pendingMarque.autoResolue':         '✓ {{n}} brand auto-resolved — {{reste}} left.',
  'ref.pendingMarque.autoResolues':        '✓ {{n}} brands auto-resolved — {{reste}} left.',
  'ref.pendingMarque.aucuneResolution':    'No new resolution — {{n}} brands pending.',
  'ref.pendingMarque.total':               '{{n}} brands in total — sorted by volume of affected rows',
  'ref.pendingMarque.refMarques':          'References → Brands',
  'ref.voirToutes':                        'See all',
  'ref.pendingAgent.autoResolu':           '✓ {{n}} agent auto-resolved — {{reste}} left.',
  'ref.pendingAgent.autoResolus':          '✓ {{n}} agents auto-resolved — {{reste}} left.',
  'ref.pendingAgent.aucuneResolution':     'No new resolution — {{n}} agents pending.',
  'ref.pendingAgent.total':                '{{n}} agents in total — sorted by activity volume',
  'ref.pendingPdv.enAttente':              '{{n}} PoS pending resolution',
  'ref.pendingPdv.nEnAttente':             '{{n}} PoS pending resolution',
  'ref.pendingPdv.tousResolus':            'All PoS are resolved',
  'ref.pendingPdv.autoResolu':             '✓ {{n}} PoS auto-resolved — {{reste}} left.',
  'ref.pendingPdv.autoResolus':            '✓ {{n}} PoS auto-resolved — {{reste}} left.',
  'ref.pendingPdv.aucuneResolution':       'No new resolution — {{n}} PoS pending.',
  'ref.pendingPdv.total':                  '{{n}} PoS in total — sorted by volume of affected rows',
  'ref.voirTous':                          'See all',
  'ref.acceptees':                         'accepted',
  'ref.workflowGras':                      'Workflow:',
  'ref.workflowActualiser':                ' → click ',
  'ref.workflowMarquer':                   ' to mark the entries resolved.',

  // ── `#845` T5-B ter · le workflow des marques ───────────────────────────
  'ref.pendingMarque.workflowColonnes':    ' → complete the cluster and is_client_brand columns → import via ',

  // ── `#845` T5-B quater · la phrase de staging ───────────────────────────
  'ref.lignesStagingAcceptees':    ' The staging rows were ',

  // ── `#845` T5-B quinquies · les agents en attente (#065 : la formulation EST la garde) ───
  'ref.pendingAgent.titre2':               'Merchandisers to identify',
  'ref.pendingAgent.inconnu':              '{{n}} unknown agent pending',
  'ref.pendingAgent.inconnus':             '{{n}} unknown agents pending',
  'ref.pendingAgent.tousIdentifies':       'All agents are identified',
  'ref.piiEntrees':                        'These entries are agents whose name/email was not found in',
  'ref.piiFin':                            ' — the displayed code is a non-reversible SHA256 hash.',
  'ref.pendingAgent.workflowUtilisez':     ' use ',
  'ref.pendingAgent.workflowPlus':         ' + ',
  'ref.pendingAgent.workflowLocaliser':    ' to locate the agent through the HR registers → create the ',
  'ref.pendingAgent.workflowVia':          ' entry via ',
  'ref.refMerchandisers':                  'References → Merchandisers',

  // ── `#845` T5-B sexies · les PDV en attente ─────────────────────────────
  'ref.pendingPdv.detectes':          'These PoS codes were detected in the rawdata or activity report files but match no reference PoS. The ',
  'ref.pendingPdv.vertes':            'green',
  'ref.pendingPdv.prerempliesCsv':    ' columns are pre-filled in the CSV; ',
  'ref.pendingPdv.rouges':            'red',
  'ref.pendingPdv.aCompleter':        ' ones are for the admin to complete.',
  'ref.etape2':                       'Complete the missing columns (🔴)',
  'ref.etape3':                       'Import via References > PoS',
  'ref.rapportRfid':                  'RFID report — activity_report',
  'ref.rawdataFichier':               'Rawdata — rawdata file',

  // ── `#845` T5-B septies · les orphelins RFID / Rawdata ──────────────────
  'ref.colTempsMin':       'TIME (min)',
  'ref.colCodeMerch':      'MERCH CODE',
  'ref.pdvFermesAutoN':    'Closed PoS — collection not expected, auto-accepted ({{n}})',

  // ── `#845` T5-B octies · les derniers restes ────────────────────────────
  'ref.puisCliquer':     ' → then click ',
  'ref.erreurImport':    'Import error.',

  // ── `#847` TROU6 · les 31 sites du sixieme trou ─────────────────────────
  'marche.nav.s1':               'S1 Overview',
  'marche.nav.s2':               'S2 MS',
  'marche.nav.s3':               'S3 Regions',
  'marche.nav.s4':               'S4 Segments',
  'marche.nav.s5':               'S5 Shelf Share',
  'commun.segment.premium':      'Premium',
  'commun.segment.strong':       'Strong',
  'commun.segment.standard':     'Standard',
  'commun.segment.faible':       'Weak',
  'commun.segment.A':            'Segment A',
  'commun.segment.B':            'Segment B',
  'commun.segment.C':            'Segment C',
  'commun.segment.D':            'Segment D',

  // ── `#847` T5-BIS · les neuf fichiers de la famille voisine ─────────────
  'ref.effectif':                     'Headcount',
  'ref.donneesPseudonymisees':        'pseudonymised data',
  'ref.aucunMerchBloc':               'No merchandiser in this block with the current filters.',
  'ref.rechercherMerch':              'Search merchandiser',
  'ref.phCode':                       'Code…',
  'ref.canalMerch':                   'Merchandiser channel',
  'ref.wilayaMerch':                  'Merchandiser wilaya',
  'ref.aucunMerch':                   'No merchandiser',
  'ref.pagePrecedenteMerch':          'Previous page merchandisers',
  'ref.pageSuivanteMerch':            'Next page merchandisers',
  'ref.retailTiret':                  'Retail — ',
  'ref.merchsErreur':                 'Error loading merchandisers.',
  'ref.routingsErreur':               'Error loading routings.',
  'ref.aucuneAffectation':            'No assignment',
  'ref.colPdvCode':                   'PoS CODE',
  'ref.colPdvNom':                    'PoS NAME',
  'ref.colJour':                      'DAY',
  'ref.filtrerCodeMerch':             'Filter by merchandiser code',
  'ref.phCodeMerch':                  'Merch code…',
  'ref.pagePrecedenteRoutings':       'Previous page routings',
  'ref.pageSuivanteRoutings':         'Next page routings',
  'ref.parJour':                      'By day',
  'ref.modifiezFiltre':               'Change the filter.',
  'ref.groupeMarque':                 'Brand group',

  // ── `#847` T5-BIS bis · le reste des merchandisers ──────────────────────
  'ref.aucunMerchReferentiel':    'No merchandiser registered in the reference base.',

  // ── `#847` T5-BIS ter · routings et fiche SKU ───────────────────────────
  'ref.importezRoutings':    'Import a routings file.',

  // ── `#847` T5-BIS quater · la validation des merchs ─────────────────────

  // ── `#847` T5-BIS sexies · le bouton resoudre ───────────────────────────

  // ── `#848` T6 · NOTIFICATIONS — les trois composeurs, la page, la cloche ───
  'commun.source.PHENOMENA':        'Phenomena',
  'commun.source.FLASH_INFO':       'Flash Info',
  'commun.source.SFM_PERF':         'SFM Perf',
  'commun.source.DATA_ACCURACY':    'Data Accuracy',
  'commun.source.PICI':             'PI/CI',
  'commun.source.MX_RM_FLOW':       'MX RM Flow',
  'notifs.pici.nouvelle':           'New coexistence',
  'notifs.pici.enCours':            'Ongoing coexistence',
  'notifs.pici.retour':             'Coexistence back',
  'notifs.depuis':                  ', since {{semaine}}',
  'notifs.officiel':                'official {{n}} u.',
  'notifs.pici':                    'PI/CI {{n}} u.',
  'notifs.mxrm.top5':               'Top 5 SKU {{semaine}}: ',
  'notifs.mxrm.marquePdmVol':       '{{marque}} {{semaine}}: MS qty ',
  'notifs.mxrm.val':                ' · val ',
  'notifs.mxrm.stock':              ' · stock ',
  'notifs.mxrm.ihs':                ' · IHS ',
  'notifs.mxrm.officiel':           ' — official ',
  'notifs.mxrm.ci':                 ' · CI ',
  'notifs.mxrm.marche':             'Market {{semaine}}: ',
  'notifs.mxrm.titreTop5':          'Top SKU — {{semaine}} · {{n}} models',
  'notifs.mxrm.titreMarque':        '{{marque}} — {{semaine}}',
  'notifs.mxrm.titreMarche':        'Market — {{semaine}}',
  'notifs.mxrm.libPdmVol':          'MS qty',
  'notifs.mxrm.libPdmVal':          'MS val',
  'notifs.mxrm.libStock':           'Stock',
  'notifs.orange':                  'orange',
  'notifs.jaune':                   'yellow',
  'notifs.natureNouvelle':          'new',
  'notifs.natureEnCours':           'ongoing',
  'notifs.natureRetour':            'back',
  'notifs.sfmRecalibre':            'SFM thresholds recalibrated — {{n}} alert(s) recomputed',
  'notifs.marquerNonLue':           'Mark as unread',
  'notifs.marquerLue':              'Mark as read',
  'notifs.lueCliquer':              'Read — click to mark unread',
  'notifs.nonLueCliquer':           'Unread — click to mark read',
  'notifs.lue':                     'Read',
  'notifs.nonLue':                  'Unread',
  'notifs.toutesSources':           '← All sources',
  'notifs.aucune':                  'No notification for now.',
  'notifs.centreErreur':            'The center could not be loaded.',
  'notifs.tronquee':                '⚠ {{n}} notification(s) counted but not shown — the list is truncated.',
  'notifs.totalNonLues':            '{{total}} notification(s) · {{n}} unread',
  'notifs.uneNonLue':               '{{n}} unread',
  'notifs.nNonLues':                '{{n}} unread',
  'notifs.detectePar':              'Detected by {{n}} methods: {{liste}}',
  'notifs.envoyerRapport':          'Send the report',
  'notifs.lectureListes':           'Reading lists…',
  'notifs.listesVides':             'Both lists are empty — this send will not go out.',
  'notifs.envoiLance':              'Send started',
  'notifs.comptesListes':           'Orbicore: ',
  'notifs.comptesMarque':           ' · {{marque}}: ',
  'notifs.smtpIncomplet':           'Cannot send: SMTP configuration is incomplete ({{liste}}).',
  'notifs.etatIndisponible':        'Send state unavailable.',
  'notifs.envoiImpossible':         'Cannot send.',

  // ── `#848` T6 bis · le libelle du dialogue de confirmation ──────────────
  'notifs.confirmerEnvoi':    'Confirm the report send',

  // ── `#849` T7 · WELCOME — le bento, niveau 1 ────────────────────────────
  'welcome.hero':                     '{{marque}} Algeria · MX SFMT Dashboard',
  'welcome.baseline':                 'Analyse. Compare. Decide.',
  'welcome.pdvSuivis':                'PoS tracked',
  'welcome.pdvRouting':               'Routing PoS',
  'welcome.skuCatalogues':            'SKUs catalogued',
  'welcome.carteTitre':               'Market by wilaya · Northern Algeria · {{marque}} focus',
  'welcome.carteErreur':              'Failed to load the map.',
  'welcome.aucuneDonneeRegionale':    'No regional data available.',
  'welcome.colorerCarte':             'Colour the map by {{kpi}}',
  'welcome.top5Sku':                  'Top 5 SKU · weekly sales',
  'welcome.aucuneVenteMarque':        'No {{marque}} sales this week.',
  'welcome.classementMarques':        'Brand ranking · MS qty',
  'welcome.aucuneDonneeMarche':       'No market data this week.',
  'nav.mxRmFlow':                     'MX RM Flow',
  'nav.sfmPerf.sous':                 'Field & visits',
  'nav.marche.sous':                  'Retail & Wholesale',

  // ── `#852` §0-BIS · les coquilles de page Référentiels ──────────────────
  'ref.routings.titre':        'References — Routings',
  'ref.routings.sous':         'Merchandiser × PoS × visit day assignments (1=Monday, 7=Sunday).',
  'ref.exportBase':            'Export Database',
  'ref.affectations':          'Assignments',
  'ref.pdvCouverts':           'PoS covered',
  'ref.merchAuReferentiel':    'Merchandisers in reference',
  'commun.supprimerTout':      'Delete all',
  'ref.type.brands':           'brands',
  'ref.type.merchandisers':    'merchandisers',
  'ref.type.pdvs':             'PoS',
  'ref.type.routings':         'routings',
  'ref.type.skus':             'SKUs',
  'ref.colValidFrom':          'valid_from (optional, YYYY-MM-DD)',

  // ── `#853` · les régions — UNE valeur fixe est un LIBELLÉ, pas une donnée ───
  'commun.region.centre':      'Center',
  'commun.region.est':         'East',
  'commun.region.ouest':       'West',
  'commun.region.sud':         'South',
  'commun.region.nord':        'North',
  'commun.region.national':    'National',
  'lab.axesDetectes':          'Detected axes: rows=',
  'lab.axesColonnes':          ' · columns=',
  'lab.axesValeur':            ' · value=',

  // ── `#858` P4-A · le suffixe de marque est un GABARIT, pas une concaténation ───
  'commun.kpiMarque':    '{{kpi}} {{marque}}',


  // ── `#861` P4-B §1 · les gabarits du récit (recit.ts) ─────────────────────────
  'phenomenes.recit.G1':       '{{sujet}}{{qui}} {{verbe}}: {{ecart}} — {{jugement}}',
  'phenomenes.recit.G2':       '{{sujet}}{{qui}} {{verbe}} — {{jugement}}',
  'phenomenes.recit.qui':      ' ({{marque}})',
  'phenomenes.recit.G3':       'The shift in weights adds {{ecart}} that belongs to no {{niveau}}.',
  'phenomenes.recit.G4':       '{{libelle}} {{verbe}} {{ecart}}',
  'phenomenes.recit.G5':       '{{a}} and {{b}} {{verbe}} {{ecart}}',
  'phenomenes.recit.G6':       '{{n}} others {{verbe}} {{ecart}}',
  'phenomenes.recit.G7':       'Concentrated movement: {{tete}} carries {{n}} % of the movement ({{ecart}}){{suite}}',
  'phenomenes.recit.G7suite':  ' — {{membres}}.',
  'phenomenes.recit.G8':       'Concentrated movement, no identified carrier.',
  'phenomenes.recit.G9':       ' {{n}} {{niveau}} {{aller}} against the trend and {{efface}} {{ecart}}.',
  'phenomenes.recit.G10':      'Diffuse movement: a single {{niveau}} takes part ({{n}} %).',
  'phenomenes.recit.G11':      'Diffuse movement: {{n}} {{niveau}} share the movement ({{parts}}) — {{aucunExplique}}.',
  'phenomenes.recit.G12':      'Diffuse movement: {{n}} {{niveau}} take part, {{troisPremiers}} weigh only {{p}} % — {{aucunExplique}}.',

  // ── `#861` P4-B §1 · jugements et verbes de direction ─────────────────────────
  'phenomenes.jugement.favorable':    'favourable',
  'phenomenes.jugement.defavorable':  'unfavourable',
  'phenomenes.jugement.neutre':       'to investigate',
  'phenomenes.direction.up':          'is up',
  'phenomenes.direction.down':        'is down',
  'phenomenes.direction.autre':       'is moving',

  // ── `#861` P4-B §1 · verbes de membre — i18next `_one` / `_other` ─────────────
  'phenomenes.membre.coPorte_one':    'co-carries',
  'phenomenes.membre.coPorte_other':  'co-carry',
  'phenomenes.membre.efface_one':     'offsets',
  'phenomenes.membre.efface_other':   'offset',
  'phenomenes.recit.aller_one':       'goes',
  'phenomenes.recit.aller_other':     'go',

  // ── `#861` P4-B §1 ★ · les niveaux — `PDV` est INVARIABLE en français ─────────

  // ── `#861` P4-B §1 ★ · les formes accordées en GENRE ──────────────────────────
  'phenomenes.accord.aucun.f':          'no',
  'phenomenes.accord.aucun.m':          'no',
  'phenomenes.accord.unSeul.f':         'a single',
  'phenomenes.accord.unSeul.m':         'a single',
  'phenomenes.accord.troisPremiers.f':  'the top three',
  'phenomenes.accord.troisPremiers.m':  'the top three',
  'phenomenes.accord.aucunExplique.f':  'none explains it alone',
  'phenomenes.accord.aucunExplique.m':  'none explains it alone',

  // ── `#861` P4-B §2 · les 23 sujets de phrase (article compris) ────────────────
  'phenomenes.sujet.K01':  'MS Qty',
  'phenomenes.sujet.K02':  'MS Value',
  'phenomenes.sujet.K03':  'Stock Share',
  'phenomenes.sujet.K04':  'ND',
  'phenomenes.sujet.K05':  'WD',
  'phenomenes.sujet.K06':  'shelf share',
  'phenomenes.sujet.K07':  'IHS',
  'phenomenes.sujet.K08':  'sales volume',
  'phenomenes.sujet.K09':  'turnover',
  'phenomenes.sujet.K10':  'ASP',
  'phenomenes.sujet.K11':  'in-store stock',
  'phenomenes.sujet.K12':  'PoS with stock-outs',
  'phenomenes.sujet.K13':  'accumulated stock-outs',
  'phenomenes.sujet.K14':  'stock rotation',
  'phenomenes.sujet.K16':  'route coverage',
  'phenomenes.sujet.K17':  'visit frequency',
  'phenomenes.sujet.K18':  'critical anomalies',
  'phenomenes.sujet.K19':  'readings excluded for quality',
  'phenomenes.sujet.K24':  'the top competitor\'s MS',
  'phenomenes.sujet.K25':  'the gap with the top competitor',
  'phenomenes.sujet.K26':  'the number of brands present',
  'phenomenes.sujet.K27':  'competitors\' ASP',
  'phenomenes.sujet.K28':  'lost PoS',

  // ── `#861` P4-B §2 · le drapeau de pluriel, PAR LANGUE ────────────────────────
  'phenomenes.sujetPluriel.K01':  'non',
  'phenomenes.sujetPluriel.K02':  'non',
  'phenomenes.sujetPluriel.K03':  'non',
  'phenomenes.sujetPluriel.K04':  'non',
  'phenomenes.sujetPluriel.K05':  'non',
  'phenomenes.sujetPluriel.K06':  'non',
  'phenomenes.sujetPluriel.K07':  'non',
  'phenomenes.sujetPluriel.K08':  'non',
  'phenomenes.sujetPluriel.K09':  'non',
  'phenomenes.sujetPluriel.K10':  'non',
  'phenomenes.sujetPluriel.K11':  'non',
  'phenomenes.sujetPluriel.K12':  'oui',
  'phenomenes.sujetPluriel.K13':  'oui',
  'phenomenes.sujetPluriel.K14':  'non',
  'phenomenes.sujetPluriel.K16':  'non',
  'phenomenes.sujetPluriel.K17':  'non',
  'phenomenes.sujetPluriel.K18':  'oui',
  'phenomenes.sujetPluriel.K19':  'oui',
  'phenomenes.sujetPluriel.K24':  'non',
  'phenomenes.sujetPluriel.K25':  'non',
  'phenomenes.sujetPluriel.K26':  'non',
  'phenomenes.sujetPluriel.K27':  'non',
  'phenomenes.sujetPluriel.K28':  'oui',

  // ── `#861` P4-B §2 · les 69 verbes des fiches ─────────────────────────────────

  // ── `#861` P4-B §2 · les sujets a entite (K24, K25) ───────────────────────────
  'phenomenes.sujetEntite.K24':  '{{c}}\'s MS, top competitor,',
  'phenomenes.sujetEntite.K25':  'the gap with {{c}}',

  // ── `#861` P4-B §2 · les patrons — `record` fusionne ses deux branches sans fenetre 
  'phenomenes.patron.recordHaut':         ', at the highest level observed in {{f}} weeks',
  'phenomenes.patron.recordMarque':       ', at the most marked level observed in {{f}} weeks',
  'phenomenes.patron.recordSansFenetre':  ', at the most marked level observed',
  'phenomenes.patron.streak':             ', uninterrupted for {{n}} weeks',
  'phenomenes.patron.streakContinu':      ', continuously',
  'phenomenes.patron.acceleration':       ', and the movement is accelerating',
  'phenomenes.patron.zscorePluriel':      ', well beyond their usual level',
  'phenomenes.patron.zscoreSingulier':    ', well beyond its usual level',
  'phenomenes.patron.yoy':                ', compared with the same period last year',
  'phenomenes.phraseKpi':                 '{{nom}} {{verbe}}{{forme}}{{chiffre}}.',
  'phenomenes.phraseChiffre':             ' ({{signe}}{{n}} %)',

  // ── `#861` P4-B · LE VOCABULAIRE DU MOUVEMENT — `#819` : quatre KPI
  //    disaient « gagne du terrain », c'est UN mot, pas quatre. ─────────────
  'phenomenes.mouvement.gagneDuTerrain':         'gains ground',
  'phenomenes.mouvement.recule':                 'loses ground',
  'phenomenes.mouvement.seMaintient':            'holds',
  'phenomenes.mouvement.sEtend':                 'expands',
  'phenomenes.mouvement.seReduit':               'shrinks',
  'phenomenes.mouvement.resteStable':            'remains stable',
  'phenomenes.mouvement.progresse':              'rises',
  'phenomenes.mouvement.regresse':               'falls',
  'phenomenes.mouvement.progressent':            'rises',
  'phenomenes.mouvement.reculent':               'falls',
  'phenomenes.mouvement.seMaintiennent':         'holds',
  'phenomenes.mouvement.reculeFalls':            'falls',
  'phenomenes.mouvement.monte':                  'goes up',
  'phenomenes.mouvement.baisse':                 'goes down',
  'phenomenes.mouvement.seReconstitue':          'rebuilds',
  'phenomenes.mouvement.sAmenuise':              'dwindles',
  'phenomenes.mouvement.seResorbent':            'are decreasing',
  'phenomenes.mouvement.seMultiplient':          'are multiplying',
  'phenomenes.mouvement.restentAuMemeNiveau':    'remain at the same level',
  'phenomenes.mouvement.sAggravent':             'are worsening',
  'phenomenes.mouvement.sAccelere':              'speeds up',
  'phenomenes.mouvement.ralentit':               'slows down',
  'phenomenes.mouvement.sAmeliore':              'improves',
  'phenomenes.mouvement.seDegrade':              'deteriorates',
  'phenomenes.mouvement.augmente':               'increases',
  'phenomenes.mouvement.diminue':                'decreases',
  'phenomenes.mouvement.diminuent':              'are decreasing',
  'phenomenes.mouvement.augmentent':             'are increasing',
  'phenomenes.mouvement.seCreuse':               'widens',
  'phenomenes.mouvement.seReduitNarrows':        'narrows',
  'phenomenes.mouvement.seReduisent':            'are decreasing',
  'phenomenes.mouvement.sAccumulent':            'are piling up',

  // ── `#861` P4-B · les 4 mots de niveau qui MANQUAIENT (les autres sont reutilises) 
  'phenomenes.niveauRegionsMin':  'regions',
  'phenomenes.niveauWilayasMin':  'wilayas',
  'phenomenes.niveauCanalMin':    'channel',
  'phenomenes.niveauCanauxMin':   'channels',

  // ── `#863` §1 · Brand 360 — les deux chaines qui manquaient reellement (#802 groupe C) 
  'marche.brand360.pdmPct':     'MS {{pct}}',
  'marche.brand360.pctMarque':  '{{pct}} brand',

  // ── `#863` §3 · AlertNarrative — les 4 recits et le repli (valides Hkm 09/09) ─
  'alerte.recit.le':       'On ',
  'alerte.recit.pic':      ', a ',
  'alerte.recit.dq1':      ' spike of Data Quality anomalies was detected. ',
  'alerte.recit.dq2':      'The average trust score dropped significantly ',
  'alerte.recit.dq3':      'over the period concerned. An immediate investigation is recommended to identify the error sources and preserve the reliability of Market data.',
  'alerte.recit.trust1':   ', a drop in trust score was recorded (severity: ',
  'alerte.recit.trust2':   'The observations concerned show anomalies in GPS matching and/or RFID scanning. ',
  'alerte.recit.trust3':   'A field review of the impacted merchandisers is needed to restore collection quality.',
  'alerte.recit.export1':  ', a mass data export attempt was detected (severity: ',
  'alerte.recit.export2':  'This type of event may indicate unauthorised exfiltration. ',
  'alerte.recit.export3':  'Check the access logs and notify the security team if needed.',
  'alerte.recit.burst1':   ', a burst of failed sign-in attempts was detected (severity: ',
  'alerte.recit.burst2':   'This pattern may indicate a brute-force attack. ',
  'alerte.recit.burst3':   'Check the source IPs and consider a temporary block if the pattern persists.',
  'alerte.recit.repli1':   'Alert {{type}} (severity: ',
  'alerte.recit.repli2':   ') triggered on {{date}}. See the technical details below.',

  // ── `#866` P4-C1 · `commun.srv.*` — UNE CLE PAR TEXTE, pas par code (#819) ────
  'commun.srv.AUTH_REQUIRED':                         'Authentication required.',
  'commun.srv.INSUFFICIENT_PERMISSIONS':              'Permission required: {{permission}}',
  'commun.srv.TOO_MANY_REQUESTS':                     'Too many requests.',
  'commun.srv.VALIDATION_ERROR':                      'Invalid request.',
  'commun.srv.INTERNAL_ERROR':                        'An internal error occurred. Retry.',
  'commun.srv.SERVICE_UNAVAILABLE':                   'Service temporarily unavailable. Try again shortly.',
  'commun.srv.NOT_FOUND':                             'Not found.',
  'commun.srv.FORBIDDEN':                             'Action not allowed.',
  'commun.srv.RATE_LIMITED':                          'Quota reached ({{limite}}/h). Try again later.',
  'commun.srv.LAB_INVALID_REQUEST':                   'Invalid Lab query.',
  'commun.srv.LAB_QUERY_NAME_DUPLICATE':              'Query "{{nom}}" already exists.',
  'commun.srv.LAB_PDV_SEARCH_REQUIRED':               'Search (≥ 2 characters) or cascade filter required for PoS.',
  'commun.srv.LAB_INCOMPATIBLE_KPI_TYPE':             'KPI {{kpi}} incompatible with this analysis type.',
  'commun.srv.LAB_INVALID_OUTPUT_FOR_TYPE':           'Output format incompatible with this analysis type.',
  'commun.srv.LAB_DATES_ORDER':                       'dates.from must be ≤ dates.to',
  'commun.srv.LAB_INVALID_FILTERS':                   'Invalid filters',
  'commun.srv.LAB_EXPORT_STREAM_FAILED':              'Export stream failed',
  'commun.srv.PIVOT_BRAND_AXIS_AND_FILTER_CONFLICT':  'Brand is both a dimension and a filter — the brand filter is ignored.',
  'commun.srv.RANKING_KPI_SINGLE_REQUIRED':           'Ranking requires exactly 1 metric KPI.',
  'commun.srv.RANKING_KPI_NOT_ALLOWED':               'KPI {{kpi}} cannot be ranked.',
  'commun.srv.RANKING_KPI_ENTITY_SCOPE':              'KPI {{kpi}} does not accept this entity.',
  'commun.srv.RANKING_RATIO_PDV_UNSUPPORTED':         '{{kpi}} at PoS level not supported (binary presence).',
  'commun.srv.RANKING_NORM_NONFLUX':                  'Per-PoS normalisation requires an additive KPI ({{kpi}} is not).',
  'commun.srv.RANKING_NORM_REQUIRES_AGGREGATE':       'Per-PoS normalisation requires an aggregate entity (wilaya, region, brand, merch).',
  'commun.srv.MISSING_FILE':                          'No file received.',
  'commun.srv.EMPTY_FILE':                            'The file contains no data rows.',
  'commun.srv.INVALID_CONTENT_TYPE':                  'Invalid upload format.',
  'commun.srv.FILE_TOO_LARGE':                        'File too large (max {{max}} MB).',
  'commun.srv.INVALID_IMAGE_FORMAT':                  'Unsupported image format (JPEG, PNG, WebP).',
  'commun.srv.IMAGE_PROCESSING_FAILED':               'Image processing failed.',
  'commun.srv.FI_PHOTO_MAX_REACHED':                  'Maximum number of photos reached.',
  'commun.srv.FI_NOT_FOUND':                          'Flash Info not found.',
  'commun.srv.FI_FORBIDDEN':                          'Action not allowed on this Flash Info.',
  'commun.srv.FI_INVALID_TRANSITION':                 'Status transition not possible.',
  'commun.srv.DEFAULT_CATEGORY_MISSING':              'Default category missing — reference seed required.',
  'commun.srv.INVALID_PARAMS':                    'Invalid parameters.',
  'commun.srv.GEO_WILAYA_NOT_FOUND':                  'Wilaya not found.',
  'commun.srv.GEO_UNKNOWN_BRAND':                     'Unknown brand: {{code}}.',
  'commun.srv.SMTP_NON_CONFIGURE':                    'Cannot send: SMTP configuration incomplete ({{liste}}).',
  'commun.srv.AUCUN_DESTINATAIRE':                    'Both lists are empty.',
  'commun.srv.ACCOUNT_INACTIVE':                      'Account is not active',
  'commun.srv.ACCOUNT_LOCKED':                        'Account temporarily locked. Try again later.',
  'commun.srv.AUTH_2FA_ALREADY':                      'A second factor is already set up on this account.',
  'commun.srv.AUTH_2FA_PREPARE_FIRST':                'Start by preparing the second factor.',
  'commun.srv.AUTH_CODE_6_DIGITS':                    '6-digit code expected.',
  'commun.srv.AUTH_CODE_INVALID':                     'Invalid code.',
  'commun.srv.AUTH_INVALID_CREDENTIALS':              'Invalid credentials',
  'commun.srv.AUTH_SESSION_INVALID':                  'Invalid session',
  'commun.srv.AUTH_TOTP_INVALID':                     'Invalid or expired TOTP code',
  'commun.srv.AUTH_TOTP_REQUIRED':                    'totp_code or backup_code required.',
  'commun.srv.SESSION_EXPIRED':                       'Session invalid or expired',

  // ── `#868` P4-D1 · les categories de la BASE (codes du seed, pas ceux supposes) 
  'commun.categorie.PHONE':          'Telephony',
  'commun.categorie.APPLIANCE':      'Home appliances',
  'commun.categorie.SMARTPHONE':     'Smartphone',
  'commun.categorie.FEATURE_PHONE':  'Feature phone',
  'commun.terrain':                  'Field',

  // ── `#869` P4-D1 bis · les 7 templates du Labo (valides Hkm 10/09) ────────────
  'lab.template.pdm_volume.nom':             'MS trend by brand (monthly)',
  'lab.template.pdm_volume.desc':            'MS Qty of all brands over the last 12 months, monthly grain. Line chart + Excel output.',
  'lab.template.cond_stock.nom':             'PoS with missing stock',
  'lab.template.cond_stock.desc':            'Conditional filter stock = 0 on priority {{marque}} SKUs. Identifies out-of-stock PoS over the last 4 weeks.',
  'lab.template.sales_volume_units.nom':     'Sales comparison, 2 SKUs',
  'lab.template.sales_volume_units.desc':    'Sales volume comparison between 2 {{marque}} SKUs, enriched with % segment and % market over the last 8 weeks.',
  'lab.template.rawdata_export.nom':         'Consolidated rawdata export, 6 months',
  'lab.template.rawdata_export.desc':        'Aggregated rawdata export, category I, weekly grain, over the last 6 months. Requires permission rawdata.export_aggregated.',
  'lab.template.sales_value_dzd.nom':        'Top 10 PoS by {{marque}} sales',
  'lab.template.sales_value_dzd.desc':       'Ranking of the 10 best PoS by {{marque}} sales (DZD value) over the last 4 weeks.',
  'lab.template.cond_visit_frequency.nom':   'Visit frequency < 1/wk',
  'lab.template.cond_visit_frequency.desc':  'Identifies PoS visited less than once a week over the last 8 weeks. Helps optimise routing plans.',
  'lab.template.ihs_samsung.nom':            '{{marque}} IHS > 50% by wilaya',
  'lab.template.ihs_samsung.desc':           '{{marque}} IHS heatmap by wilaya over the last 4 weeks. Identifies areas with a high {{marque}} in-house index (IHS ≥ 50%).',

  // ── #871 reprise Labo : le compteur, l'agregat, la mention de filtre ──────────
  'lab.chart.lignes_one':      '{{count}} row · {{ms}} ms',
  'lab.chart.lignes_other':    '{{count}} rows · {{ms}} ms',
  'lab.export.lignesVendues':  'Sold lines only (sales > 0)',

  // ── #871 (5) les 5 facteurs de segmentation (valides Hkm 10/09) ───────────────
  'admin.facteur.weekly_sales_vs_regional_avg.nom':   'Weekly sales vs regional average',
  'admin.facteur.weekly_sales_vs_regional_avg.desc':  'Ratio of weekly PoS sales / regional average (rolling 4 weeks). Default single factor V1 — identity normalisation.',
  'admin.facteur.stock_avg_4w.nom':                   'Average stock, 4 weeks',
  'admin.facteur.stock_avg_4w.desc':                  'Average stock in units over the rolling 4 weeks. Min-max normalisation across PoS.',
  'admin.facteur.visit_frequency_per_month.nom':      'Visit frequency per month',
  'admin.facteur.visit_frequency_per_month.desc':     'Average number of merchandiser visits per month (rolling 90 days). Min-max normalisation across PoS.',
  'admin.facteur.samsung_shelf_share_avg.nom':        'Average {{marque}} shelf share',
  'admin.facteur.samsung_shelf_share_avg.desc':       '{{marque}} shelf share (brand facings / total facings), rolling 4-week average. Min-max normalisation across PoS.',
  'admin.facteur.samsung_availability_rate.nom':      '{{marque}} availability (presence rate)',
  'admin.facteur.samsung_availability_rate.desc':     'Presence rate of {{marque}} products in stock (visits with stock > 0 / total visits), rolling 4 weeks. Min-max normalisation across PoS.',

  // ── #873 les 4 formats de sortie du Labo (codes en base, #817 (1)) ────────────
  'lab.format.liste':    'list',
  'lab.format.tableau':  'table',
  'lab.format.graph':    'chart',
  'lab.format.excel':    'excel',

  // ══════════════════════════════════════════════════════════════════════
  // `#874` §2 — **LA DETTE `#819` TOMBE À ZÉRO.** Vingt-trois libellés que
  //   deux modules ou plus servaient chacun de leur côté vivent désormais
  //   en `commun.*` : on réutilise, on ne recrée pas.
  // ══════════════════════════════════════════════════════════════════════
  'commun.ca': 'Turnover',
  'commun.dispoAbbr': 'Avail.',
  'commun.dispo': 'Availability',
  'commun.valeur': 'Value',
  'commun.mesureVolume': 'Qty',
  'commun.lectureStrat': 'Strategic reading',
  'commun.vueEnsemble': 'Overview',
  'commun.quatreSem': '4 wks',
  'commun.semaine': 'Week',
  'commun.ventes': 'Sales',
  'commun.officiel': 'Official',
  'commun.semaineMin': 'week',
  'commun.batterie': 'Battery',
  'commun.camera': 'Camera',
  'commun.pdmAbbr': 'MS',
  'commun.pdmValAbbr': 'MS Value',
  'commun.tendance': 'Trend',
  'commun.regionMin': 'region',
  'commun.aucunModele': 'No model',
  'commun.nSem': '{{n}} wks',
  'commun.unites': 'units',
  'commun.tous': 'All',
  'commun.partMarche': 'Market share',

  // ── #878 les 4 derniers codes serveur (table Hkm 10/09) ───────────────────────
  'commun.srv.DELETE_ALREADY_RUNNING':      'A deletion is already running.',
  'commun.srv.DIMENSION_CONTEXT_MISMATCH':  'Dimension incompatible with the phenomenon context.',
  'commun.srv.REASON_REQUIRED':             'A reason is required for this action.',
  'commun.srv.SCOPE_MISMATCH':              'Outside your scope.',
  // ── `#1089` · import refusals state their facts (never a bare “HTTP 422” again) ──
  'commun.srv.INVALID_TEMPLATE':                         'The file does not follow the expected template.',
  'commun.srv.INVALID_TEMPLATE_ENTETE_ABSENTE':          'The file does not follow the expected template: header “{{ancre}}” not found.',
  'commun.srv.INVALID_TEMPLATE_ENTETES_MANQUANTS':       'The file does not follow the expected template: {{trouves}} columns out of {{attendus}} — column {{position}} “{{attendu}}” is missing.',
  'commun.srv.INVALID_TEMPLATE_ENTETE_DIFFERENTE':       'The file does not follow the expected template: column {{position}}, expected “{{attendu}}”, found “{{trouve}}”.',
  'commun.srv.INVALID_TEMPLATE_COLONNES':                'The file does not follow the expected template: {{attendues}} columns expected, {{trouvees}} found.',
  'commun.srv.INVALID_TEMPLATE_COLONNES_OBLIGATOIRES':   'The file does not follow the expected template: required columns missing — {{manquantes}}.',
  'commun.srv.INVALID_TEMPLATE_SEMAINE_INDEDUCTIBLE':    'The week cannot be deduced from the file: {{colonnes}} missing or empty.',
  'commun.srv.BATCH_ALREADY_FINALIZED':                  'An import already exists for {{semaine}} (status {{statut}}, file {{fichier}}). Delete it first in the import history, then run it again.',
  'commun.srv.WEEK_MISMATCH':                            'The file contains week {{semaineFichier}}, you selected {{semaineChoisie}}. Check the file or the wizard week.',
  'commun.srv.ERREUR_ENVOI':                             'The server refused the file (status {{statut}}, code {{code}}).',

  // ── #878 les 19 motifs de rejet d'import — 17 textes (table Hkm 10/09) ────────
  'import.rejet.pdvManquant':             'PoS code missing (required field)',
  'import.rejet.dateManquante':           'Visit date missing (required field)',
  'import.rejet.timeOutAvantTimeIn':      'time_out ({{out}}) is earlier than time_in ({{in}})',
  'import.rejet.tempsNegatif':            'Negative time spent ({{n}} min) — will be ignored',
  'import.rejet.tempsExcessif':           'Excessive time spent ({{n}} min > 240) — will be ignored',
  'import.rejet.latitudeInvalide':        'Invalid begin latitude: {{v}} (must be ∈ [-90, 90])',
  'import.rejet.longitudeInvalide':       'Invalid begin longitude: {{v}} (must be ∈ [-180, 180])',
  'import.rejet.gpsEloigne':              'GPS too far from the reference PoS: {{m}} m (bucket: {{bucket}})',
  'import.rejet.sansPhoto':               'PoS open but no photo provided (neither interior nor exterior)',
  'import.rejet.skuNonResolvable':        'Unresolvable SKU "{{model}}" (brand={{brand}}, RAM={{ram}}, ROM={{rom}}) — will be kept in PENDING_REPLAY at finalisation.',
  'import.rejet.skuManquant':             'SKU code (model) missing (required field)',
  'import.rejet.doublonInterBatch':       'Cross-batch duplicate: observations for ({{pdv}}, {{date}}) already present',
  'import.rejet.ventesSuperieuresStock':  '7-day sales ({{sales}}) > stock × 7 ({{stock7}}) — possible inconsistency',
  'import.rejet.prixNegatif':             'Selling price ≤ 0 ({{p}} DZD) — quality inconsistency to investigate',
  'import.rejet.vitrineNegative':         'Negative display quantity not allowed',
  'import.rejet.productTypeNonStandard':  'product_type \'{{v}}\' non-standard — expected: OFFICIEL or PI-CI',
  'import.rejet.doublonExact':            'Exact duplicate in the file: row identical to a previous row (row {{i}})',

  // ── #878 les 2 gabarits d'anomaly_flags (table Hkm 10/09) ─────────────────────
  'admin.anomalie.SKU_UNKNOWN':                 'SKU unresolved after code→composite→name cascade — model: "{{model}}"',
  'admin.anomalie.CHANNEL_MISMATCH_MERCH_PDV':  'Merch {{merch}} on PoS {{pdv}} — channel violation',

  // ── #878 les 23 descriptions config_display (table Hkm 10/09) ─────────────────
  'admin.config.marche.brand_visibility_threshold_pct.desc':  'Brand visibility threshold (% MS) — brands below → Others bucket',
  'admin.config.marche.weekly_window_weeks.desc':             'Weekly time window: periods within this window → mv_agg_weekly',
  'admin.config.system.export_max_rows.desc':                 'Row cap per export — beyond it, output is TRUNCATED (not refused)',
  'admin.config.system.export_daily_limit.desc':              'Self-Service Lab exports allowed per day and per user',
  'admin.config.sku.inactivity_threshold_weeks.desc':         'SKU inactivity threshold (weeks) — SKU without observation for N weeks → inactive',
  'admin.config.import.reject_threshold_pct.desc':            'Import rejection threshold (%): beyond it, the week fails and is NOT published',
  'admin.config.system.lab_runs_per_hour.desc':               'Self-Service Lab runs allowed per hour and per user',
  'admin.config.audit.retention_ordinary_days.desc':          'Ordinary trail retention (days) — beyond it, the row is forgotten',
  'admin.config.audit.retention_security_days.desc':          'Security signal retention (days): refusals, sign-ins, deletions — kept longer',
  'admin.config.session.idle_timeout_minutes.desc':           'Inactivity (minutes) before a session closes automatically — the absolute cap stays above',
  'admin.config.session.absolute_max_hours.desc':             'Maximum session duration (hours) since opening — beyond it, sign-in required',
  'admin.config.phenomena.zscore_window_weeks.desc':          'Z-score — rolling window (weeks) for mean/std-dev',
  'admin.config.phenomena.zscore_min_points.desc':            'Z-score — minimum points to compute a z (otherwise no detection)',
  'admin.config.phenomena.zscore_gate_z.desc':                'Z-score — EXISTENCE THRESHOLD: below |z| ≤ this, no phenomenon',
  'admin.config.phenomena.zscore_band_eleve_z.desc':          'Z-score — HIGH band (also the CLEAR/BORDERLINE cutoff)',
  'admin.config.phenomena.zscore_band_critique_z.desc':       'Z-score — CRITICAL band',
  'admin.config.phenomena.accel_ref_window_weeks.desc':       'Acceleration — reference window (number of averaged deltas)',
  'admin.config.phenomena.accel_ratio_signal.desc':           'Acceleration — EXISTENCE THRESHOLD: below ratio ≤ this, no phenomenon',
  'admin.config.phenomena.accel_ratio_eleve.desc':            'Acceleration — HIGH band (also the CLEAR/BORDERLINE cutoff)',
  'admin.config.phenomena.accel_ratio_critique.desc':         'Acceleration — CRITICAL band',
  'admin.config.phenomena.streak_signal_weeks.desc':          'Streak — EXISTENCE THRESHOLD: below this length, no phenomenon',
  'admin.config.phenomena.streak_eleve_weeks.desc':           'Streak — HIGH band (also the CLEAR/BORDERLINE cutoff)',
  'admin.config.phenomena.streak_critique_weeks.desc':        'Streak — CRITICAL band',

  // ── #881 les 9 en-tetes du rapport e-mail que le catalogue n'avait pas ────────
  'email.entete.codeMerch':        'Merch code',
  'email.entete.niveau':           'Level',
  'email.entete.partVal':          'Val. share',
  'email.entete.partVol':          'Vol. share',
  'email.entete.marcheEntier':     'Whole market',
  'email.entete.gravite':          'Severity',
  'email.entete.qualiteCollecte':  'Collection quality',
  'email.entete.tauxQualite':      'Quality rate',
  'email.bloc.graphiques':         'Charts',

  // ── #882 les libelles de ligne et les phrases de comptage de l'e-mail ─────────
  'email.entete.tauxPreuves':    'Proof rate',
  'email.compte.majeurs':        'majors',
  'email.compte.rouges':         'reds',
  'email.compte.cohabitation':   '{{marque}} coexistence',
  'email.compte.cohabitations':  '{{marque}} coexistences',
  'email.compte.cetteSemaine':   'this week',
  'email.compte.auDashboard':    'on the dashboard',
  'email.compte.autres_one':     '{{count}} other',
  'email.compte.autres_other':   '{{count}} others',
  'email.piedDePage':            'Automated report from the Orbicore notification center. Merchandisers are identified by their code.',

  // ── #885 le titre d'evolution du tiroir merch (demande Hkm 10/09) ─────────────
  'perf.titreEvolutionMerch':  'Merchandiser Trend — {{n}} wks',

  // ── #886 le classeur des problemes d'un merchandiseur (5 feuilles + resume) ───
  'perf.pb.resume':             'Summary',
  'perf.pb.nonCouverts':        'Uncovered routing PoS',
  'perf.pb.nonVisites':         'Unvisited 3+ weeks',
  'perf.pb.colDateVisite':      'Visit date',
  'perf.pb.colJourRouting':     'Routing day',
  'perf.pb.colSemainePrec':     'Previous week',
  'perf.pb.colDureeNette':      'Net duration (min)',
  'perf.pb.colMerch':           'Merchandiser',
  'perf.pb.colProbleme':        'Problem',
  'perf.pb.colPourcent':        'Share',
  'perf.pb.clustersGps':        'GPS clusters',
  'perf.pb.conformiteGps':      'Begin↔end conformity',
  'perf.pb.colNumCluster':      'Cluster no.',
  'perf.pb.colCentreLat':       'Center lat.',
  'perf.pb.colCentreLon':       'Center lon.',
  'perf.pb.colEcartM':          'Gap (m)',
  'perf.pb.colSeuilM':          'Threshold (m)',
  'perf.pb.colDetail':          'Detail',
  'perf.pb.photoIntAbsente':    'No inside photo',
  'perf.pb.photoExtAbsente':    'No outside photo',
  'perf.pb.gpsTropLoin':        'Visited over 50 m away',
  'perf.pb.colDistanceSeuil':   'Distance (m) — threshold 50 m, source Data Accuracy',
  'perf.pb.colMotifEchec':      'Failure reason',
  'perf.pb.motifPhoto':         'Photo',
  'perf.pb.motifGpsPhoto':      'GPS + photo',
  'perf.pb.detailClusters_one':      '{{count}} cluster',
  'perf.pb.detailClusters_other':    '{{count}} clusters',
  'perf.pb.detailAnalysables_one':   'of {{count}} analysable visit',
  'perf.pb.detailAnalysables_other': 'of {{count}} analysable visits',
  'perf.exportProblemes':       'Download problems',
}

/** La clé de traduction — toute chaîne utilisateur passe par une de ces clés. */
/**
 * ⚠⚠ `#861` P4-B — **UNE CLÉ PLURALISÉE S'ADRESSE PAR SA BASE.** *`i18next` reçoit
 *    `t('phenomenes.countMarques', { count })` et choisit lui-même `_one` ou
 *    `_other` ; la base n'existe donc dans AUCUNE des deux tables. Sans cette
 *    ligne, le type refusait l'appel correct et n'acceptait que les deux formes
 *    — c'est-à-dire exactement ce qu'il ne faut pas écrire.*
 *
 * ⚠ *Les DEUX formes restent adressables : le catalogue les contrôle une par une
 *   (symétrie, orphelines), et `rendre()` les lit directement.*
 */
type BasePlurielle<K> = K extends `${infer B}_one` ? B : never

/**
 * ⚠ **`CleI18n` = la clé STOCKÉE** — celle qui existe dans les deux tables. Les
 *   témoins l'utilisent pour vérifier la symétrie, clé par clé, formes
 *   `_one` / `_other` comprises.
 */
export type CleI18n = keyof typeof fr

/**
 * ⚠⚠ **`CleT` = l'ADRESSE que l'on passe à `t()`.** *Elle ajoute la BASE des
 *    clés pluralisées — `t('phenomenes.countMarques', { count })` — que
 *    `i18next` résout lui-même vers `_one` ou `_other`. **Cette base ne vit dans
 *    aucune table**, et c'est pour ça qu'elle n'est pas une `CleI18n`.*
 */
export type CleT = CleI18n | BasePlurielle<CleI18n>

/** Les deux langues, indexées. Le résolveur et le hook `t()` lisent ici. */
/**
 * ⚠ `#861` — les TABLES sont indexées par les clés réellement présentes
 *   (`_one` / `_other` compris) ; `CleI18n`, lui, porte en plus la BASE
 *   pluralisée, qui ne vit dans aucune table — c'est `i18next` qui la résout.
 */
export const CATALOGUE: Record<'fr' | 'en', Record<keyof typeof fr, string>> = { fr, en }

/**
 * Traduction pure — utilisée par le back (exports/e-mails/récits).
 *
 * ⚠ `#861` — **elle ne PLURALISE PAS.** *Une clé pluralisée adressée par sa base
 *   n'existe dans aucune table : passez par `rendre()` (`shared/i18n/rendu.ts`),
 *   qui choisit la forme et interpole. Ici, la base rend la clé elle-même — et
 *   le témoin « clé nue » (`#850`) le verrait.*
 */
export function traduire(cle: CleI18n, langue: 'fr' | 'en'): string {
  return CATALOGUE[langue][cle as keyof typeof fr] ?? cle
}
